/**
 * Tax Certificate Panel Component
 * Main component for managing tax certificate data for orders.
 * Includes multi-parcel support, jurisdiction selection, and tax calculations.
 * 
 * Extracted from App.js to improve maintainability.
 */

import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import axios from 'axios';
import { isValid, parseISO } from 'date-fns';
import { toZonedTime } from 'date-fns-tz';
import { toast } from 'sonner';
import { notifyOrderCompletion } from '../mls/orderCompletionToast';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../ui/dialog';
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../ui/alert-dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Textarea } from '../ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import {
  JURISDICTION_TYPE_OPTIONS as JX_TYPE_OPTIONS,
  isCanonicalJurisdictionType as jxIsCanonicalType,
  getJurisdictionTypeLabel as jxTypeLabel,
} from '../../lib/jurisdictionTaxonomy';
import { Popover, PopoverContent, PopoverTrigger } from '../ui/popover';
import { Calendar } from '../ui/calendar';
import { Separator } from '../ui/separator';
import { DatePickerInput } from '../ui/date-picker-input';
import { CopyIconButton } from '../ui/copy-field';
import { useAuth } from '../../App';
import MlsDualSection from '../mls/MlsDualSection';
import { useAutoSave } from '../../hooks/useAutoSave';
import { useSessionMonitor } from '../../hooks/useSessionMonitor';
import { SectionHeader, EnhancedCard } from '../shared';
import MailAwayModal from '../MailAwayModal';

import JurisdictionSelector, { requiresSubJurisdiction, COUNTY_LEVEL_TYPES, SUB_JURISDICTION_TYPES, getEffectiveJurisdictionClassification, isMunicipalBoroughState } from './JurisdictionSelector';
import JurisdictionManager from './JurisdictionManager';
import './tax-cert-premium.css';
import { findJurisdictionByCounty, getSuggestionBannerState } from '../../utils/jurisdictionSuggestion';
import { canonicalizeJurisdictionName } from '../../utils/jurisdictionCanonical';
import { fetchSubJurisdictions } from '../../utils/subJurisdictionLookup';
import { formatTaxYear } from '../../utils/taxYearFormatter';
// Feb 2026 P0 (PA-RESOLVER-RESELECT-OVERWRITE) — pull in the formal-
// assignment resolver + PA category helper. The jurisdiction
// type-select handlers below previously hydrated every Make Checks
// Payable To / contact field straight from the legacy directory
// columns on `jurisdictions`, so even when a HIGH-confidence
// assignment existed (e.g. Carbon County → County Tax → Penn Forest
// Township) a re-select would silently clobber the resolver's
// payee back to the legacy "Carbon County Tax Collector". We now
// re-run `resolveCollectorGuidance` after the jurisdiction match
// and prefer the formal assignment's collector when the resolver
// returns HIGH/MEDIUM confidence from the assignment table.
import { resolveCollectorGuidance } from '../../utils/taxCollectorsApi';
import { defaultCategoryForPA } from '../../utils/jurisdictionCategory';
import { calculateDaysLate, getDaysLateLabel } from '../../utils/daysLateCalculator';
import { toTitleCase, formatDateTimeET, formatDateOnly } from '../../utils/formatters';
import { formatStreetAddress, formatCity } from '../../utils/textFormat';
import { normalizeBorrowerName } from '../../utils/nameNormalizer';
import { partitionInstallments } from '../../lib/specialtyInstallments';
import {
  Check,
  ChevronDown,
  ChevronLeft,
  ChevronUp,
  Plus,
  Trash2,
  AlertCircle,
  AlertTriangle,
  Save,
  RefreshCw,
  Calendar as CalendarIcon,
  Edit,
  Copy,
  MapPin,
  Building,
  DollarSign,
  FileText,
  Home as HomeIcon,
  Clock,
  Mail,
  Info,
  X,
  XCircle,
  Loader2,
  Search,
  ExternalLink,
  Link,
  ChevronRight,
  Eye,
  CheckCircle,
  Lock,
  Upload,
  User
} from 'lucide-react';
import { formatDateTimeShortET, formatDateOnlyET } from '../../utils/formatters';
import ScrollToTopButton from '../ui/ScrollToTopButton';

const API = process.env.REACT_APP_BACKEND_URL 
  ? `${process.env.REACT_APP_BACKEND_URL}/api` 
  : '/api';

// Account timezone for date formatting
// V1 REQUIREMENT: All timestamps displayed in America/New_York (ET)
const ACCOUNT_TZ = 'America/New_York';

// Helper function to format currency
const formatCurrency = (value) => {
  if (value === null || value === undefined || value === '') return '';
  const num = typeof value === 'string' ? parseFloat(value.replace(/[^0-9.-]+/g, '')) : value;
  if (isNaN(num)) return '';
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2
  }).format(num);
};

// Round to 2 decimal places — eliminates floating-point artifacts in monetary calculations
const rc = (v) => v !== null && v !== undefined && !isNaN(v) ? Math.round(v * 100) / 100 : v;

// Helper to parse currency input (rounds to 2 decimals on output)
const parseCurrencyInput = (value) => {
  if (!value) return null;
  const cleaned = value.replace(/[^0-9.-]/g, '');
  const num = parseFloat(cleaned);
  return isNaN(num) ? null : rc(num);
};

// Handle currency input change - clean to allow only valid chars
const handleCurrencyChange = (value) => {
  if (!value) return '';
  // Allow digits, one decimal point, and optional leading minus
  const cleaned = value.replace(/[^0-9.-]/g, '');
  // Ensure only one decimal point
  const parts = cleaned.split('.');
  if (parts.length > 2) {
    return parts[0] + '.' + parts.slice(1).join('');
  }
  return cleaned;
};

// Handle currency field blur - format to 2 decimal places
const handleCurrencyBlur = (value) => {
  if (!value || value === '' || value === '-') return '';
  const num = parseFloat(value);
  if (isNaN(num)) return '';
  // Round to 2 decimal places
  return num.toFixed(2);
};

// ============================================================================
// PHASE 2: NESTED JURISDICTION/INSTALLMENT STRUCTURE HELPERS (Mar 2026)
// ============================================================================

// Default installment labels based on tax collection frequency
const DEFAULT_INSTALLMENT_LABELS = {
  annually: ['Annual'],
  semi_annually: ['1st Half', '2nd Half'],
  quarterly: ['Q1', 'Q2', 'Q3', 'Q4'],
  installment_plan: ['1st Installment']
};

/**
 * Check if certificate data uses the new jurisdictions[] structure
 * Returns true if using new nested structure, false if using legacy current_tax_summary[]
 */
const isNewJurisdictionStructure = (certificateData) => {
  // Check if jurisdictions array exists (even if empty) AND current_tax_summary is empty
  // This allows users to switch to new structure without auto-creating a jurisdiction
  const hasJurisdictionsArray = Array.isArray(certificateData?.jurisdictions);
  const hasLegacyData = certificateData?.current_tax_summary?.length > 0;
  
  // If user has explicitly set jurisdictions array (even empty) and no legacy data, use new structure
  if (hasJurisdictionsArray && !hasLegacyData) {
    return true;
  }
  
  // Also check if any existing jurisdiction has the installments array (hallmark of new structure)
  const jurisdictions = certificateData?.jurisdictions || [];
  for (const jur of jurisdictions) {
    if (jur && typeof jur === 'object' && 'installments' in jur) {
      return true;
    }
  }
  
  return false;
};

/**
 * Generate default installments based on tax collection frequency
 * @param {string} taxCollected - 'annually' | 'semi_annually' | 'quarterly'
 * @param {string} taxYear - Optional tax year for context
 * @returns {Array} Array of installment objects with default labels
 */
const generateDefaultInstallments = (taxCollected, taxYear = '') => {
  const labels = DEFAULT_INSTALLMENT_LABELS[taxCollected] || ['Annual'];
  
  return labels.map((label, idx) => ({
    id: crypto.randomUUID(),
    tax_year: taxYear,  // Empty by default — user must explicitly set
    installment_name: label,
    installment_index: idx + 1,
    due_date: null,
    base_tax: null,
    amount_due: null,
    discount_penalty: 0,
    paid_amount: null,
    notes: '',
    pay_status: 'Due',
    paid_date: null,
    balance_amount: null,
    dlq_date: null,
    mailing_issue_date: null
  }));
};

/**
 * Create empty jurisdiction object for new nested structure
 */
const createEmptyJurisdiction = () => {
  return {
    id: crypto.randomUUID(),
    jurisdiction_name: '',
    jurisdiction_type: null,
    jurisdiction_id: null,
    // Jul 2026 — TAX-CATEGORY-001: type of tax BILL (county|city|school|other),
    // independent of jurisdiction_type (the collecting entity type).
    tax_category: null,
    // NOTE: tax_year moved to installment level - each tax record has its own year
    source: null,
    source_notes: '',
    source_evidence: null, // { filename, url, uploaded_at, category } - Evidence file for the selected source
    has_delinquent: null,
    dlq_payoff_amount: null,
    dlq_payoff_manual: false,
    dlq_good_thru_date: null,
    delinquent_taxes_notes: '',
    make_checks_payable_to: '',
    tax_collector_email: '',
    tax_collector_website: '',
    assessor_website: '',
    tax_collector_phone: '',
    tax_collector_phone_ext: '',
    tax_collector_fax: '',
    tax_collector_working_hours: '',
    mlc_required: null,
    mlc_charges: null,
    mlc_24_month_charges: null,
    mlc_eta_days: null,
    sase_required: false,
    info_obtained_back: null,
    changed_by_user: false,
    collection_frequency: 'annually',
    installments: generateDefaultInstallments('annually', ''),
    // Per-jurisdiction exemptions
    exemption_types: [],
    exemption_other: '',
    exemption_value: null,
    exemption_na: false,
    fully_exempt: false,
  };
};

// ============================================================================

// ============================================================================
// HELPER: Group current_tax_summary rows by jurisdiction
// ============================================================================
// Groups rows that have the same jurisdiction_id (or jurisdiction_name + jurisdiction_type)
// so that jurisdiction-level fields can be rendered once per unique jurisdiction,
// while installment-level fields repeat for each row within that group.

const groupRowsByJurisdiction = (rows) => {
  if (!rows || rows.length === 0) return [];
  
  const groups = [];
  const groupMap = new Map(); // Key: jurisdiction identifier, Value: group index
  
  rows.forEach((row, originalIndex) => {
    // Create a unique key for this jurisdiction
    // Priority: jurisdiction_id > (jurisdiction_name + jurisdiction_type)
    let jurisdictionKey;
    if (row.jurisdiction_id) {
      jurisdictionKey = `id:${row.jurisdiction_id}`;
    } else if (row.jurisdiction && row.jurisdiction_type) {
      jurisdictionKey = `name:${row.jurisdiction.toLowerCase().trim()}|type:${row.jurisdiction_type}`;
    } else if (row.jurisdiction) {
      jurisdictionKey = `name:${row.jurisdiction.toLowerCase().trim()}`;
    } else {
      // No jurisdiction selected yet - treat as unique
      jurisdictionKey = `unique:${originalIndex}`;
    }
    
    if (groupMap.has(jurisdictionKey)) {
      // Add to existing group
      const groupIndex = groupMap.get(jurisdictionKey);
      groups[groupIndex].rows.push({ ...row, originalIndex });
    } else {
      // Create new group
      const newGroupIndex = groups.length;
      groupMap.set(jurisdictionKey, newGroupIndex);
      groups.push({
        jurisdictionKey,
        // Jurisdiction-level data (from first row in group)
        jurisdictionData: {
          jurisdiction: row.jurisdiction,
          jurisdiction_type: row.jurisdiction_type,
          jurisdiction_id: row.jurisdiction_id,
          tax_year: row.tax_year,
          source: row.source,
          source_notes: row.source_notes,
          make_checks_payable_to: row.make_checks_payable_to,
          tax_collector_email: row.tax_collector_email,
          tax_collector_website: row.tax_collector_website,
          assessor_website: row.assessor_website,
          tax_collector_phone: row.tax_collector_phone,
          tax_collector_phone_ext: row.tax_collector_phone_ext,
          tax_collector_working_hours: row.tax_collector_working_hours,
          mlc_required: row.mlc_required,
          mlc_charges: row.mlc_charges,
          mlc_24_month_charges: row.mlc_24_month_charges,
          mlc_eta_days: row.mlc_eta_days,
          sase_required: row.sase_required,
          info_obtained_back: row.info_obtained_back,
          has_delinquent: row.has_delinquent,
          dlq_payoff_amount: row.dlq_payoff_amount,
          dlq_good_thru_date: row.dlq_good_thru_date,
          delinquent_taxes_notes: row.delinquent_taxes_notes
        },
        // Rows in this group (installments)
        rows: [{ ...row, originalIndex }],
        // First row's original index (for UI state management)
        firstRowIndex: originalIndex
      });
    }
  });
  
  return groups;
};

// Check if a jurisdiction already exists in current_tax_summary
const findExistingJurisdiction = (rows, jurisdictionId, jurisdictionName, jurisdictionType) => {
  if (!rows || rows.length === 0) return null;
  
  for (let i = 0; i < rows.length; i++) {
    const row = rows[i];
    // Match by jurisdiction_id first
    if (jurisdictionId && row.jurisdiction_id === jurisdictionId) {
      return { index: i, row };
    }
    // Fall back to matching by name + type
    if (!jurisdictionId && row.jurisdiction && jurisdictionName) {
      if (row.jurisdiction.toLowerCase().trim() === jurisdictionName.toLowerCase().trim() &&
          row.jurisdiction_type === jurisdictionType) {
        return { index: i, row };
      }
    }
  }
  return null;
};

// Check if this row is a "child installment" of a previous row with the same jurisdiction
// Returns true if this row should only show installment-level fields (not jurisdiction fields)
const isChildInstallmentRow = (rows, currentIndex) => {
  if (!rows || currentIndex === 0) return false;
  
  const currentRow = rows[currentIndex];
  if (!currentRow) return false;
  
  // Look at all previous rows to see if any have the same jurisdiction
  for (let i = 0; i < currentIndex; i++) {
    const prevRow = rows[i];
    if (!prevRow) continue;
    
    // Match by jurisdiction_id (most reliable)
    if (currentRow.jurisdiction_id && prevRow.jurisdiction_id === currentRow.jurisdiction_id) {
      return true;
    }
    
    // Fall back to matching by name + type
    if (!currentRow.jurisdiction_id && currentRow.jurisdiction && prevRow.jurisdiction) {
      if (currentRow.jurisdiction.toLowerCase().trim() === prevRow.jurisdiction.toLowerCase().trim() &&
          currentRow.jurisdiction_type === prevRow.jurisdiction_type) {
        return true;
      }
    }
  }
  
  return false;
};

// Get the parent jurisdiction index for a child installment row
const getParentJurisdictionIndex = (rows, currentIndex) => {
  if (!rows || currentIndex === 0) return -1;
  
  const currentRow = rows[currentIndex];
  if (!currentRow) return -1;
  
  // Look at all previous rows to find the first one with the same jurisdiction
  for (let i = 0; i < currentIndex; i++) {
    const prevRow = rows[i];
    if (!prevRow) continue;
    
    // Match by jurisdiction_id (most reliable)
    if (currentRow.jurisdiction_id && prevRow.jurisdiction_id === currentRow.jurisdiction_id) {
      return i;
    }
    
    // Fall back to matching by name + type
    if (!currentRow.jurisdiction_id && currentRow.jurisdiction && prevRow.jurisdiction) {
      if (currentRow.jurisdiction.toLowerCase().trim() === prevRow.jurisdiction.toLowerCase().trim() &&
          currentRow.jurisdiction_type === prevRow.jurisdiction_type) {
        return i;
      }
    }
  }
  
  return -1;
};

// ============================================================================

// Format date helper
const formatDate = (dateString) => {
  if (!dateString) return '';
  try {
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return dateString;
    return formatDateOnly(date);
  } catch {
    return dateString;
  }
};


// ============================================
// PREFILL INDICATOR COMPONENT
// ============================================
const PrefillIndicator = ({ isVisible, timestamp, onRemove }) => {
  if (!isVisible) return null;
  
  const formattedTime = timestamp ? formatDateTimeET(timestamp) : 'ingestion';
  
  return (
    <div className="inline-flex items-center gap-1 px-2 py-1 bg-blue-100 text-blue-700 rounded-full text-xs">
      <span>Prefilled</span>
      <button
        type="button"
        className="w-3 h-3 text-blue-500 hover:text-blue-700"
        title={`Loaded from Order Desk at ${formattedTime}`}
      >
        <svg viewBox="0 0 12 12" fill="currentColor">
          <circle cx="6" cy="6" r="5" stroke="currentColor" strokeWidth="1" fill="none"/>
          <text x="6" y="8" textAnchor="middle" fontSize="8" fontWeight="bold">i</text>
        </svg>
      </button>
    </div>
  );
};


// ============================================
// EXEMPTION PILL SELECTOR COMPONENT
// ============================================
const NA_OPTION = 'No Exemptions (N/A)';

const EXEMPTION_PILLS = [
  { stored: NA_OPTION, label: 'No Exemptions', isNone: true },
  { stored: 'Homestead exemptions', label: 'Homestead' },
  { stored: 'Senior citizen exemptions', label: 'Senior' },
  { stored: 'Military exemptions', label: 'Military' },
  { stored: 'Disability exemptions', label: 'Disability' },
];

const ExemptionPillSelector = ({ value = [], onChange, readOnly = false }) => {
  const isNoneSelected = value.includes(NA_OPTION);

  const toggle = (pill) => {
    if (readOnly) return;

    if (pill.isNone) {
      // Mutually exclusive: toggle No Exemptions
      if (isNoneSelected) {
        onChange([]);
      } else {
        onChange([NA_OPTION]);
      }
      return;
    }

    // Regular exemption: remove No Exemptions if present, toggle this pill
    let next = value.filter(v => v !== NA_OPTION);
    if (next.includes(pill.stored)) {
      next = next.filter(v => v !== pill.stored);
    } else {
      next.push(pill.stored);
    }
    onChange(next);
  };

  return (
    <div className="space-y-2">
      <p className="text-xs text-gray-500">
        Select all applicable exemptions. Choose &quot;No Exemptions&quot; if none apply.
      </p>
      <div className="flex flex-wrap gap-2" data-testid="exemption-pills-container">
        {EXEMPTION_PILLS.map((pill) => {
          const selected = value.includes(pill.stored);
          const disabled = readOnly || (isNoneSelected && !pill.isNone);

          return (
            <button
              key={pill.stored}
              type="button"
              disabled={disabled}
              onClick={() => toggle(pill)}
              data-testid={`exemption-pill-${pill.label.toLowerCase().replace(/\s+/g, '-')}`}
              className={`
                inline-flex items-center gap-1.5 px-3.5 py-1.5 rounded-full text-sm font-medium
                border transition-all duration-150
                ${selected
                  ? pill.isNone
                    ? 'bg-slate-600 text-white border-slate-600 shadow-sm'
                    : 'bg-[#4338CA] text-white border-[#4338CA] shadow-sm'
                  : disabled
                    ? 'bg-gray-100 text-gray-400 border-gray-200 cursor-not-allowed opacity-50'
                    : 'bg-white text-gray-700 border-gray-300 hover:border-[#6366F1] hover:text-[#4338CA]'
                }
              `}
            >
              {pill.label}
              {selected && <Check className="w-3 h-3" />}
            </button>
          );
        })}
      </div>
    </div>
  );
};

const ReceivedStatusToggles = ({ orderData, onUpdate }) => {
  const { user } = useAuth();
  const [isUpdating, setIsUpdating] = useState(false);
  const [localMailAwayReceived, setLocalMailAwayReceived] = useState(orderData?.mail_away_received || false);
  const [localClarificationReceived, setLocalClarificationReceived] = useState(orderData?.clarification_received || false);
  const [mailAwayResponseDetails, setMailAwayResponseDetails] = useState('');
  const [clarificationResponseDetails, setClarificationResponseDetails] = useState('');
  const [savedMailAwayDetails, setSavedMailAwayDetails] = useState('');
  const [savedClarificationDetails, setSavedClarificationDetails] = useState('');
  
  // Send Back Modal state
  const [showSendBackModal, setShowSendBackModal] = useState(false);
  const [sendBackType, setSendBackType] = useState(''); // 'clarification' or 'mail_away'
  const [sendBackReason, setSendBackReason] = useState('');
  
  // Validation error states
  const [mailAwayDetailsError, setMailAwayDetailsError] = useState('');
  const [clarificationDetailsError, setClarificationDetailsError] = useState('');
  const [sendBackReasonError, setSendBackReasonError] = useState('');
  
  // Determine if user can edit these flags
  const isAdmin = user?.role === 'admin';
  const isTeamLeader = user?.role === 'tax_search_team_leader' || user?.role === 'title_search_team_leader' || user?.role === 'team_leader';
  const canEdit = isAdmin || isTeamLeader;
  
  // Show this component when order is in mail_away or clarification status
  const showClarificationToggle = orderData?.status === 'clarification';
  const showMailAwayToggle = orderData?.status === 'mail_away';
  
  // Clear textareas whenever orderData changes and sync toggle states
  // Load saved response details ONLY when status is in "received" state
  useEffect(() => {
    // Sync local toggle states with backend data
    setLocalMailAwayReceived(orderData?.mail_away_received || false);
    setLocalClarificationReceived(orderData?.clarification_received || false);
    
    // Load saved clarification response if status is "clarification_received"
    if (orderData?.status === 'clarification_received' && orderData?.status_notes) {
      const clarificationNotes = orderData.status_notes.filter(n => n.type === 'clarification_received');
      if (clarificationNotes.length > 0) {
        const latest = clarificationNotes[clarificationNotes.length - 1];
        setClarificationResponseDetails(latest.message || '');
        setSavedClarificationDetails(latest.message || '');
      }
    } else {
      // Clear textarea for new cycle (when status is "clarification", not "clarification_received")
      setClarificationResponseDetails('');
      setSavedClarificationDetails('');
    }
    
    // Load saved mail-away response if mail_away_received flag is true
    if (orderData?.status === 'mail_away' && orderData?.mail_away_received && orderData?.status_notes) {
      const mailAwayNotes = orderData.status_notes.filter(n => n.type === 'mail_away_received');
      if (mailAwayNotes.length > 0) {
        const latest = mailAwayNotes[mailAwayNotes.length - 1];
        setMailAwayResponseDetails(latest.message || '');
        setSavedMailAwayDetails(latest.message || '');
      }
    } else {
      // Clear textarea for new cycle (when flag is false)
      setMailAwayResponseDetails('');
      setSavedMailAwayDetails('');
    }
  }, [orderData]);
  
  if (!showMailAwayToggle && !showClarificationToggle) {
    return null; // Don't render if order is not in these statuses
  }
  
  const handleSave = async (type) => {
    if (!canEdit) {
      toast.error('You do not have permission to update received status');
      return;
    }
    
    // VALIDATION: Response Details are MANDATORY
    if (type === 'mail_away') {
      if (!mailAwayResponseDetails.trim()) {
        setMailAwayDetailsError('Response details are required when marking a response as received.');
        return;
      }
      setMailAwayDetailsError(''); // Clear error
    } else if (type === 'clarification') {
      if (!clarificationResponseDetails.trim()) {
        setClarificationDetailsError('Response details are required when marking a response as received.');
        return;
      }
      setClarificationDetailsError(''); // Clear error
    }
    
    setIsUpdating(true);
    
    try {
      const API_URL = process.env.REACT_APP_BACKEND_URL;
      const API = API_URL ? `${API_URL}/api` : null;
      const token = localStorage.getItem('token');
      
      // Build request body - always set received to true when saving details
      const requestBody = {};
      
      if (type === 'mail_away') {
        requestBody.mail_away_received = true;
        requestBody.mail_away_response_details = mailAwayResponseDetails.trim();
      } else if (type === 'clarification') {
        requestBody.clarification_received = true;
        requestBody.clarification_response_details = clarificationResponseDetails.trim();
      }
      
      const response = await fetch(
        `${API}/orders/${orderData.id}/received-status`,
        {
          method: 'PATCH',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify(requestBody)
        }
      );
      
      if (response.ok) {
        const result = await response.json();
        
        // Update local state
        if (type === 'mail_away') {
          setLocalMailAwayReceived(true);
          setSavedMailAwayDetails(mailAwayResponseDetails.trim());
        } else if (type === 'clarification') {
          setLocalClarificationReceived(true);
          setSavedClarificationDetails(clarificationResponseDetails.trim());
        }
        
        // Show success message
        const statusType = type === 'mail_away' ? 'Mail-away' : 'Clarification';
        toast.success(`${statusType} response saved.`);
        
        // Call parent callback to refresh
        if (onUpdate) {
          onUpdate(result.order);
        }
        
      } else {
        const errorData = await response.json().catch(() => ({}));
        toast.error(errorData.detail || 'Failed to save response details');
      }
      
    } catch (error) {
      console.error('Error saving response details:', error);
      toast.error('Network error occurred. Please try again.');
    } finally {
      setIsUpdating(false);
    }
  };
  
  const handleToggle = async (field, newValue) => {
    if (!canEdit) {
      toast.error('You do not have permission to update received status');
      return;
    }
    
    // If toggling OFF, just update the flag without details
    if (!newValue) {
      setIsUpdating(true);
      
      try {
        const API_URL = process.env.REACT_APP_BACKEND_URL;
        const API = API_URL ? `${API_URL}/api` : null;
        const token = localStorage.getItem('token');
        
        const response = await fetch(
          `${API}/orders/${orderData.id}/received-status`,
          {
            method: 'PATCH',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${token}`
            },
            body: JSON.stringify({ [field]: false })
          }
        );
        
        if (response.ok) {
          if (field === 'mail_away_received') {
            setLocalMailAwayReceived(false);
          } else if (field === 'clarification_received') {
            setLocalClarificationReceived(false);
          }
          
          const statusType = field === 'mail_away_received' ? 'Mail-Away' : 'Clarification';
          toast.success(`${statusType} response unmarked`);
        } else {
          toast.error('Failed to update status');
        }
        
      } catch (error) {
        console.error('Error updating status:', error);
        toast.error('Network error occurred');
      } finally {
        setIsUpdating(false);
      }
    } else {
      // If toggling ON, use the save function which includes details
      const type = field === 'mail_away_received' ? 'mail_away' : 'clarification';
      await handleSave(type);
    }
  };
  
  const openSendBackModal = (type) => {
    setSendBackType(type);
    setSendBackReason('');
    setShowSendBackModal(true);
  };
  
  const closeSendBackModal = () => {
    setShowSendBackModal(false);
    setSendBackType('');
    setSendBackReason('');
  };
  
  const handleSendBackConfirm = async () => {
    // VALIDATION: Reason is MANDATORY
    if (!sendBackReason.trim()) {
      setSendBackReasonError('A reason is required to send this order back to clarification.');
      return;
    }
    setSendBackReasonError(''); // Clear error
    
    // ALL roles can send back - no permission check needed
    setIsUpdating(true);
    
    try {
      const API_URL = process.env.REACT_APP_BACKEND_URL;
      const API = API_URL ? `${API_URL}/api` : null;
      const token = localStorage.getItem('token');
      
      // Determine the target status based on type
      const targetStatus = sendBackType === 'mail_away' ? 'mail_away' : 'clarification';
      const statusName = sendBackType === 'mail_away' ? 'Mail-Away' : 'Clarification';
      
      // Use dedicated send-back endpoint (allows ALL roles)
      const response = await fetch(
        `${API}/orders/${orderData.id}/send-back`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify({ 
            target_status: targetStatus,
            reason: sendBackReason.trim()
          })
        }
      );
      
      if (response.ok) {
        // Close modal
        closeSendBackModal();
        
        const result = await response.json();
        
        // Update local state
        if (sendBackType === 'mail_away') {
          setLocalMailAwayReceived(false);
        } else if (sendBackType === 'clarification') {
          setLocalClarificationReceived(false);
        }
        
        const statusType = sendBackType === 'mail_away' ? 'Mail-Away' : 'Clarification';
        toast.success(`Order sent back to ${statusType} status`);
        
        // Call parent callback to refresh
        if (onUpdate) {
          onUpdate(result.order);
        }
        
      } else {
        const errorData = await response.json().catch(() => ({}));
        toast.error(errorData.detail || 'Failed to send back order');
      }
      
    } catch (error) {
      console.error('Error sending back order:', error);
      toast.error('Network error occurred. Please try again.');
    } finally {
      setIsUpdating(false);
    }
  };
  
  return (
    <div className="bg-white rounded-lg shadow mt-6">
      <div className="px-6 py-4 border-b bg-gradient-to-r from-indigo-50 to-purple-50">
        <div className="flex items-center gap-2">
          <svg className="w-5 h-5 text-indigo-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          <h3 className="text-lg font-semibold text-gray-900">Response Received Status</h3>
          {!canEdit && (
            <span className="ml-auto text-xs text-gray-500 bg-gray-100 px-2 py-1 rounded">
              Read-only (Team Leader/Admin only)
            </span>
          )}
        </div>
      </div>
      
      <div className="p-6 space-y-4">
        <p className="text-sm text-gray-600 mb-4">
          Mark when customer responses have been received and reviewed.
        </p>
        
        {/* Mail-Away Response - Read-Only Reference View */}
        {showMailAwayToggle && (
          <div className="bg-cyan-50 border border-cyan-200 rounded-lg p-4 space-y-3">
            {/* Header with Title and Status Chip */}
            <div className="flex items-center justify-between">
              <div>
                <h4 className="text-sm font-medium text-gray-900">
                  Mail-Away Response Status
                </h4>
                <p className="text-xs text-gray-600 mt-1">
                  Read-only reference view. Use Order Summary to manage response.
                </p>
              </div>
              {/* Status Chip - P1 UI Cleanup: Compact badges */}
              {localMailAwayReceived ? (
                <span className="inline-flex items-center px-2 py-0.5 text-xs font-medium bg-green-50 text-green-600 rounded-md border border-green-200">
                  ✓ Received
                </span>
              ) : (
                <span className="inline-flex items-center px-2 py-0.5 text-xs font-medium bg-amber-50 text-amber-600 rounded-md border border-amber-200">
                  Pending
                </span>
              )}
            </div>
            
            {/* Response Details - Read-Only */}
            {localMailAwayReceived && mailAwayResponseDetails ? (
              <div>
                <label className="text-xs font-medium text-gray-700 block mb-2">
                  Response Details
                </label>
                <div className="w-full px-3 py-2 text-sm border border-green-300 bg-green-50 rounded-md">
                  <p className="text-gray-800 whitespace-pre-wrap">{mailAwayResponseDetails}</p>
                </div>
                {orderData?.mailaway_received_at && (
                  <p className="text-xs text-gray-600 mt-2">
                    Received: {formatDateTimeShortET(orderData.mailaway_received_at)}
                    {orderData?.mailaway_received_by_name && ` by ${orderData.mailaway_received_by_name}`}
                  </p>
                )}
              </div>
            ) : (
              <div className="text-center py-4">
                <p className="text-sm text-gray-600">
                  No response has been received yet.
                </p>
              </div>
            )}
            
            {/* Helpful Message - Directing to Order Summary */}
            <div className="pt-3 border-t border-cyan-200">
              <div className="flex items-start gap-2 text-xs text-gray-600">
                <svg className="w-4 h-4 text-blue-500 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <p>
                  <strong>To mark response as received or send back:</strong> Use the Mail-Away quick actions in the Order Summary page.
                </p>
              </div>
            </div>
          </div>
        )}
        
        {/* Clarification Response Received Section */}
        {showClarificationToggle && (
          <div className="bg-indigo-50 border border-indigo-200 rounded-lg p-4 space-y-3">
            {/* Header with Title and Status Chip */}
            <div className="flex items-center justify-between">
              <div>
                <h4 className="text-sm font-medium text-gray-900">
                  Clarification Response Received
                </h4>
                <p className="text-xs text-gray-600 mt-1">
                  Enter response details when clarification documents are received
                </p>
              </div>
              {/* Status Chip (Read-only indicator) */}
              {localClarificationReceived ? (
                <span className="inline-flex items-center px-3 py-1 text-xs font-medium bg-green-100 text-green-700 rounded-full">
                  ✓ Received
                </span>
              ) : (
                <span className="inline-flex items-center px-3 py-1 text-xs font-medium bg-gray-100 text-gray-600 rounded-full">
                  Not Received
                </span>
              )}
            </div>
            
            {/* Send Back Button - Available to ALL roles when received */}
            {localClarificationReceived && (
              <div className="pt-2 border-t border-indigo-200">
                <p className="text-xs text-gray-600 mb-2">
                  If the clarification response is incomplete or incorrect, you can send it back:
                </p>
                <Button
                  variant="outline"
                  className="w-full text-indigo-700 border-indigo-300 hover:bg-indigo-50"
                  onClick={() => openSendBackModal('clarification')}
                  disabled={isUpdating}
                >
                  <AlertCircle size={16} />
                  Send Back to Clarification
                </Button>
              </div>
            )}
            
            {/* Response Details Section */}
            <div>
              {/* Show saved response when status is "clarification_received" */}
              {localClarificationReceived && clarificationResponseDetails && (
                <div className="mb-3">
                  <label className="text-xs font-medium text-gray-700 block mb-1">
                    Saved Response Details
                  </label>
                  <div className="w-full px-3 py-2 text-sm border border-green-300 bg-green-50 rounded-md">
                    <p className="text-gray-800">{clarificationResponseDetails}</p>
                  </div>
                  <p className="text-xs text-green-600 mt-1 flex items-center gap-1">
                    <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                    </svg>
                    Response saved and logged in Activity Timeline
                  </p>
                </div>
              )}
              
              {/* Show textarea input ONLY when NOT yet received */}
              {!localClarificationReceived && (
                <>
                  <label htmlFor="clarification_details" className="text-xs font-medium text-gray-700 block mb-1">
                    Response Details <span className="text-red-600">*</span>
                  </label>
                  <textarea
                    id="clarification_details"
                    value={clarificationResponseDetails}
                    onChange={(e) => {
                      setClarificationResponseDetails(e.target.value);
                      if (clarificationDetailsError) setClarificationDetailsError(''); // Clear error on change
                    }}
                    readOnly={!canEdit}
                    disabled={isUpdating}
                    placeholder={canEdit ? "Enter clarification details received from customer..." : ""}
                    className={`w-full px-3 py-2 text-sm border ${clarificationDetailsError ? 'border-red-500' : 'border-indigo-300'} rounded-md resize-none ${
                      canEdit ? 'bg-white focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500' : 'bg-gray-50 cursor-text'
                    } ${isUpdating ? 'opacity-50 cursor-not-allowed' : ''}`}
                    rows="3"
                    maxLength="2000"
                  />
                  {clarificationDetailsError && (
                    <p className="text-xs text-red-600 mt-1">{clarificationDetailsError}</p>
                  )}
                  <div className="flex items-center justify-between mt-1">
                    <p className="text-xs text-gray-500">
                      {clarificationResponseDetails.length}/2000 characters
                    </p>
                  </div>
                  
                  {/* Save Response Button - Only visible for Admin/Team Leader when NOT yet received */}
                  {canEdit && (
                    <Button
                      className="mt-2 w-full"
                      onClick={() => handleSave('clarification')}
                      disabled={isUpdating || !clarificationResponseDetails.trim()}
                      isLoading={isUpdating}
                    >
                      {isUpdating ? 'Saving...' : (
                        <>
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                          </svg>
                          Save Response
                        </>
                      )}
                    </Button>
                  )}
                </>
              )}
            </div>
          </div>
        )}
        
        {isUpdating && (
          <div className="flex items-center gap-2 text-sm text-gray-600">
            <div className="w-4 h-4 border-2 border-indigo-600 border-t-transparent rounded-full animate-spin"></div>
            Updating...
          </div>
        )}
      </div>
      
      {/* Send Back Modal */}
      {showSendBackModal && (
        <div className="fixed inset-0 z-50 overflow-y-auto" aria-labelledby="modal-title" role="dialog" aria-modal="true">
          <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0">
            {/* Background overlay */}
            <div 
              className="fixed inset-0 transition-opacity bg-gray-500 bg-opacity-75" 
              aria-hidden="true"
              onClick={closeSendBackModal}
            ></div>

            {/* Center modal */}
            <span className="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true">&#8203;</span>

            {/* Modal panel */}
            <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
              {/* Header */}
              <div className="bg-gradient-to-r from-indigo-50 to-blue-50 px-6 py-4 border-b border-indigo-200">
                <div className="flex items-center justify-between">
                  <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                    <AlertCircle className="w-5 h-5 text-indigo-600" />
                    Send Back to {sendBackType === 'mail_away' ? 'Mail-Away' : 'Clarification'}
                  </h3>
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={closeSendBackModal}
                    className="text-gray-400 hover:text-gray-600"
                  >
                    <XCircle size={20} />
                  </Button>
                </div>
              </div>

              {/* Body */}
              <div className="px-6 py-4">
                <p className="text-sm text-gray-600 mb-4">
                  Please enter the reason for sending this order back to {sendBackType === 'mail_away' ? 'mail-away' : 'clarification'}.
                </p>
                
                <label htmlFor="send_back_reason" className="text-xs font-medium text-gray-700 block mb-1">
                  Reason <span className="text-red-600">*</span>
                </label>
                
                <textarea
                  id="send_back_reason"
                  value={sendBackReason}
                  onChange={(e) => {
                    setSendBackReason(e.target.value);
                    if (sendBackReasonError) setSendBackReasonError(''); // Clear error on change
                  }}
                  placeholder="Enter reason for sending back..."
                  className={`w-full px-3 py-2 text-sm border ${sendBackReasonError ? 'border-red-500' : 'border-gray-300'} rounded-md focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 resize-none`}
                  rows="4"
                  maxLength="2000"
                  disabled={isUpdating}
                />
                
                {sendBackReasonError && (
                  <p className="text-xs text-red-600 mt-1">{sendBackReasonError}</p>
                )}
                
                <p className="text-xs text-gray-500 mt-2">
                  {sendBackReason.length}/2000 characters
                </p>
              </div>

              {/* Footer */}
              <div className="bg-gray-50 px-6 py-4 flex items-center justify-end gap-3">
                <Button
                  variant="outline"
                  onClick={closeSendBackModal}
                  disabled={isUpdating}
                >
                  Cancel
                </Button>
                <Button
                  onClick={handleSendBackConfirm}
                  disabled={isUpdating || !sendBackReason.trim()}
                  isLoading={isUpdating}
                >
                  {isUpdating ? 'Sending...' : (
                    <>
                      <AlertCircle size={16} />
                      Send Back
                    </>
                  )}
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const TaxCertificatePanel = ({ orderData, readOnly: readOnlyProp = false, externalCertificateData = null, onCertificateDataChange = null, workflowActions = null }) => {
  
  // External-portal routing state (Feb 27, 2026 — Phase 1).
  // isExternalPortalClient: resolved via the unified backend resolver
  //   (order > product > contract mapping > client > legacy).
  // externalPortalResolved: false until the resolver responds — used to
  //   render a neutral loading state so the wrong panel never flashes.
  const [isExternalPortalClient, setIsExternalPortalClient] = useState(false);
  const [externalPortalResolved, setExternalPortalResolved] = useState(false);
  const [externalPortalSource, setExternalPortalSource] = useState(null);

  // Feb 2026 — Extracted into a stable callback so the visibility/focus
  // listeners below can re-invoke it. Previously this lived inline inside
  // the mount effect with `[orderData?.id]` as its only dep, which meant
  // once an order was loaded the resolver fired exactly once per session.
  // Admins toggling `delivery_method` on the Client Code (or the Product /
  // Contract Mapping) had no way to make the open Fulfillment tab reflect
  // the new value short of a hard refresh — that was the reported bug.
  //
  // Feb 2026 v2 — Hardened against fetch storms (network-tab feedback):
  //   • In-flight `AbortController` cancels the prior fetch so only the
  //     LATEST response wins. Eliminates the (pending) request that
  //     appeared when rapid events fired and one fetch got abandoned
  //     mid-flight while the older completed response set stale state.
  //   • Resolver reads `orderData?.id` via a ref so the listener effect
  //     below does NOT re-subscribe every time `orderData` changes —
  //     this alone was causing 3–5 duplicate fetches per page load.
  const orderIdRef = useRef(orderData?.id);
  useEffect(() => { orderIdRef.current = orderData?.id; }, [orderData?.id]);
  const epAbortRef = useRef(null);

  const resolveExternalPortal = useCallback(async () => {
    // Jun 3, 2026 — Tax Summary flash hotfix.
    // Track whether THIS call is still the freshest one before opening
    // the gate. Previously the `finally` block unconditionally set
    // `externalPortalResolved=true`, which meant a superseded/aborted
    // call would open the gate with the default `isExternalPortalClient=false`
    // — causing the Tax Summary tab to flash for ~500ms on external_portal
    // orders before the winning call flipped it. Only the winner now
    // toggles the gate; superseded calls exit without touching state.
    const orderId = orderIdRef.current;
    if (!orderId) {
      setIsExternalPortalClient(false);
      setExternalPortalResolved(true);
      return;
    }
    // Cancel any in-flight EP request so only the freshest result wins.
    if (epAbortRef.current) {
      try { epAbortRef.current.abort(); } catch { /* noop */ }
    }
    const controller = new AbortController();
    epAbortRef.current = controller;

    let isWinner = true; // flips to false if this call gets superseded mid-flight
    try {
      const token = localStorage.getItem('token');
      const resp = await axios.get(
        `${API}/orders/${orderId}/external-portal-status`,
        { headers: { Authorization: `Bearer ${token}` }, signal: controller.signal }
      );
      // If a newer call superseded us between request start and now, bail
      // WITHOUT opening the gate — the newer call will do that itself.
      if (epAbortRef.current !== controller) {
        isWinner = false;
        return;
      }
      const isEP = !!resp.data?.is_external_portal;
      const src = resp.data?.source || null;
      if (isEP) {
        console.log(`[EXTERNAL PORTAL] resolved=true via source=${src} (order=${orderId})`);
      }
      setIsExternalPortalClient(isEP);
      setExternalPortalSource(src);
    } catch (error) {
      // Aborted requests are expected during rapid event sequences — silent.
      // Critically, do NOT open the gate for aborted calls; the winning
      // call will do that. Otherwise the gate opens with stale default
      // state, flashing the wrong panel.
      if (error?.name === 'CanceledError' || error?.code === 'ERR_CANCELED') {
        isWinner = false;
        return;
      }
      // Fall back to non-external on real failure — safer to render the
      // default panel than to hide tax data we can't confirm.
      console.error('Error resolving external portal status:', error);
      setIsExternalPortalClient(false);
      setExternalPortalSource('error');
    } finally {
      // Only the winning call opens the gate. Superseded/aborted calls
      // exit silently and leave the gate state untouched so the freshest
      // call's setIsExternalPortalClient lands BEFORE the gate opens.
      if (isWinner) setExternalPortalResolved(true);
    }
  }, []); // stable identity — uses refs for live state

  // Resolve external portal routing on order load
  useEffect(() => {
    resolveExternalPortal();
  }, [orderData?.id, resolveExternalPortal]);

  // Feb 2026 — Live config sync.
  // Re-resolve external-portal status whenever the user returns to this
  // tab. Listeners are subscribed ONCE (stable resolver) — previously
  // they re-subscribed on every `orderData?.id` change which caused a
  // fetch storm during the initial order-data hydration sequence.
  // The `pageshow` listener intentionally ignores the initial-page-load
  // fire (event.persisted=false), only acting on bfcache restores.
  useEffect(() => {
    const onVisibility = () => {
      if (document.visibilityState === 'visible') resolveExternalPortal();
    };
    const onFocus = () => resolveExternalPortal();
    const onPageShow = (ev) => { if (ev?.persisted) resolveExternalPortal(); };
    document.addEventListener('visibilitychange', onVisibility);
    window.addEventListener('focus', onFocus);
    window.addEventListener('pageshow', onPageShow);
    return () => {
      document.removeEventListener('visibilitychange', onVisibility);
      window.removeEventListener('focus', onFocus);
      window.removeEventListener('pageshow', onPageShow);
      // Abort any in-flight EP fetch on unmount
      if (epAbortRef.current) {
        try { epAbortRef.current.abort(); } catch { /* noop */ }
      }
    };
  }, [resolveExternalPortal]);

  // Detect if this is an MLS product (uses Tax shell but hides tax-specific sections)
  const isMLS = useMemo(() => {
    const cp = orderData?.client_product?.trim?.();
    return cp === 'MLS' || cp === 'MLS Update' || cp === 'Update MLS';
  }, [orderData?.client_product]);

  // MLS-DUAL-001: dual-component detection (new stamped orders) vs LEGACY
  // pre-dual MLS orders. Dual orders' Tax workspace = normal Tax Search UX;
  // only legacy MLS keeps the historical hiding behavior (user decision Aug 22).
  const isMlsDual = isMLS && !!orderData?.components;
  const isLegacyMls = isMLS && !isMlsDual;

  // Combined flag: hide tax-specific UI (Tax Summary tab, installments, assessed values, delinquent fields)
  // True for External Portal clients and LEGACY (non-dual) MLS products only.
  const hideTaxDetails = isExternalPortalClient || isLegacyMls;
  
  // MLS-DUAL-001 Phase 2: dual-component rollup reported by MlsDualSection.
  // Used to pre-block the parent Complete action with an actionable message
  // (the backend 409 guard remains authoritative).
  const [mlsRollup, setMlsRollup] = useState(null);
  const [mlsComponents, setMlsComponents] = useState(null);
  const handleMlsRollupChange = useCallback((rollup, components) => {
    setMlsRollup(rollup);
    setMlsComponents(components);
  }, []);

  // WF-CONSOLIDATE (Aug 23, 2026): the existing Tax Workflow Actions section
  // is the single authoritative Tax action area. While the TAX COMPONENT of a
  // dual order is open, its Complete button becomes "Complete Tax Search" and
  // uses the EXISTING /components/tax/complete flow (same validation +
  // mail-away gates). After the component completes, the button reverts to the
  // existing parent behavior (blocked until both components complete).
  const [mlsRefreshKey, setMlsRefreshKey] = useState(0);
  const [taxComponentBusy, setTaxComponentBusy] = useState(false);
  const taxComponentStatus = (((mlsComponents || orderData?.components || {}).tax) || {}).status || 'pending';
  const handleCompleteTaxComponent = useCallback(async () => {
    setTaxComponentBusy(true);
    try {
      const res = await axios.post(`${API}/orders/${orderData.id}/components/tax/complete`);
      const cert = res.data?.certificate;
      const finalized = notifyOrderCompletion(res.data?.order_completion); // MLS-AUTO-FINALIZE-001
      if (finalized) {
        // page reloads shortly; skip the component-level toast
      } else if (cert?.status === 'failed') {
        toast.warning(
          <div data-testid="tax-cert-generation-failed-toast">
            <strong>Tax Search completed — but the Tax Certificate PDF could not be generated.</strong>
            <div style={{ fontSize: 12, marginTop: 4 }}>{cert.error}</div>
            <div style={{ fontSize: 12, marginTop: 4 }}>Use “Retry certificate” in the Tax Search workspace once the cause is fixed.</div>
          </div>, { duration: 10000 });
      } else {
        toast.success(cert?.filename ? `Tax Search completed — Tax Certificate ${cert.filename} added to Files → Tax` : 'Tax Search completed');
      }
      setMlsRefreshKey((k) => k + 1);
    } catch (error) {
      const detail = error.response?.data?.detail;
      if (detail?.error === 'tax_certificate_incomplete') {
        toast.error(
          <div>
            <strong>Tax Certificate is incomplete:</strong>
            <ul style={{ margin: '4px 0 0 0', paddingLeft: 16 }}>
              {(detail.validation_errors || []).map((er, i) => <li key={i} style={{ fontSize: 12 }}>{er}</li>)}
            </ul>
          </div>, { duration: 8000 });
      } else if (detail?.error === 'tax_sheet_review_required') {
        toast.error(detail.message || 'Review the Tax Sheet before completing', { id: 'tax-review-required' });
      } else if (detail?.error === 'mail_away_outstanding') {
        toast.error(detail.message, { duration: 7000 });
      } else {
        toast.error(detail?.message || (typeof detail === 'string' ? detail : 'Failed to complete Tax Search'));
      }
    } finally {
      setTaxComponentBusy(false);
    }
  }, [orderData?.id]);

  // MLS-DUAL-001 UI refinement (Aug 2026): dual-workspace tab state.
  // Defaults to the MLS workspace while the MLS component is open; Tax after.
  const [mlsWsTabChoice, setMlsWsTabChoice] = useState(null);
  // MLS-PARCELS-001: MLS parcel state reported by MlsDualSection so the top
  // "Parcel x of N / + Add Parcel" bar acts on MLS parcels while the MLS tab is active.
  const [mlsParcelState, setMlsParcelState] = useState({ index: 1, count: 1, writable: false });
  useEffect(() => {
    const h = (e) => setMlsParcelState(e.detail || { index: 1, count: 1, writable: false });
    window.addEventListener('mls-parcel-state', h);
    return () => window.removeEventListener('mls-parcel-state', h);
  }, []);
  const mlsActiveTab = !isMlsDual ? 'tax'
    : (mlsWsTabChoice
      || (((mlsComponents?.mls?.status ?? orderData?.components?.mls?.status) === 'complete') ? 'tax' : 'mls'));

  const mlsParcelMode = isMlsDual && mlsActiveTab === 'mls';

  // MLS-UI-CLEANUP (Aug 2026): let OrderTabs know which dual workspace is
  // active so it can hide the Tax "Mail-Away (All Jurisdictions)" banner
  // while the user works in the MLS Search workspace.
  useEffect(() => {
    if (!isMlsDual) return;
    window.dispatchEvent(new CustomEvent('mls-ws-tab-change', { detail: { tab: mlsActiveTab } }));
  }, [isMlsDual, mlsActiveTab]);
  
  // MLS-DUAL-001 Phase 2: derive the Complete-button guard message from live
  // component state (incl. outstanding component Mail-Away requests)
  const mlsGuard = useMemo(() => {
    if (!isMlsDual || !mlsComponents) return null;
    if (mlsRollup === 'ready_to_complete' || mlsRollup === 'complete') return null;
    const MA_ACTIVE = ['pending_send', 'sent', 'confirmed', 'awaiting_response'];
    const parts = [];
    ['tax', 'mls'].forEach((c) => {
      const cd = mlsComponents[c] || {};
      if ((cd.status || 'pending') !== 'complete') {
        const label = c === 'tax' ? 'Tax Search' : 'MLS Search';
        // MLS-STATUS-LABELS-001: user-facing wording (internal pending = not started)
        const STATUS_WORDS = { pending: 'open', in_progress: 'in progress', clarification: 'in clarification', on_hold: 'pending', complete: 'complete' };
        let seg = `${label} is ${STATUS_WORDS[cd.status || 'pending'] || (cd.status || 'pending').replace('_', ' ')}`;
        if (cd.assignee_name) seg += ` (assigned to ${cd.assignee_name})`;
        const ma = (orderData?.mail_away_requests || []).filter(
          (r) => (r.component || 'tax') === c && MA_ACTIVE.includes(r.status)
        ).length;
        if (ma > 0) seg += ` — ${ma} Mail-Away request(s) outstanding`;
        parts.push(seg);
      }
    });
    if (parts.length === 0) return null;
    return { blocked: true, message: `Cannot complete — ${parts.join('; ')}.` };
  }, [isMlsDual, mlsComponents, mlsRollup, orderData?.mail_away_requests]);
  
  // Auto-save integration - MUST be declared before any useEffect/callback that references them
  const [initialLoad, setInitialLoad] = useState(true);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const dataLoadCompleteRef = useRef(false); // Track when initial data load is fully complete
  // Feb 27, 2026 — Phase 2: snapshot of the last server-known state.
  // The dirty watcher compares the live certificateData to this and
  // only flips hasUnsavedChanges when they actually differ. Replaces
  // the fragile 500ms `dataLoadCompleteRef` timing gate.
  const lastSavedSnapshotRef = useRef(null);
  // May 26, 2026 — Phase 3: post-load "settle window".
  // After data load completes, child components (JurisdictionManager
  // auto-notes, sub-jurisdiction enrichments, parcel-sync useEffect,
  // exemption migration, etc.) often emit one or more setCertificateData
  // calls as they hydrate. These are NOT user edits but the prior
  // hash-diff watcher saw them as differences and turned on the
  // "Unsaved changes" warning before the user typed anything.
  // While the settle window is active, the watcher REFRESHES the
  // baseline snapshot instead of marking the form dirty, absorbing
  // those initial emissions. The window auto-closes after 2 s; after
  // that, any change is a genuine user edit.
  const settleWindowActiveRef = useRef(false);
  const settleTimerRef = useRef(null);
  
  // Scroll to top when component mounts to fix auto-scroll to bottom issue
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, []);
  
  // Utility function to sanitize certificate data for API requests
  // ============================================================================
  // MULTI-PARCEL ADAPTER FUNCTIONS (Phase 3A: API Boundary Transformation)
  // ============================================================================
  
  /**
   * Adapter: Transform backend multi-parcel structure → flat frontend state + parcels array
   * Called when loading certificate data from backend
   * Extracts Parcel 1 data into flat structure for current UI AND populates parcels array
   * Returns: { flatData, parcels }
   */
  const adaptBackendToFrontend = (backendData) => {
    console.log('[ADAPTER] Backend → Frontend:', backendData);
    
    // Check if data is in new multi-parcel format
    const hasMultiParcelStructure = backendData.parcels && Array.isArray(backendData.parcels) && backendData.parcels.length > 0;
    
    if (hasMultiParcelStructure) {
      // New format: Extract Parcel 1 (index 0) into flat structure
      const parcel1 = backendData.parcels[0];
      
      // PHASE 3B: Also extract parcels array
      const parcelsArray = backendData.parcels.map((parcel, index) => ({
        parcel_index: index + 1,
        parcel_apn: parcel.parcel_apn || '',
        source: parcel.source,
        source_notes: parcel.source_notes || '',
        tax_collected: parcel.tax_collected || 'annually',
        jurisdiction_id: parcel.jurisdiction_id,
        jurisdiction_type: parcel.jurisdiction_type,
        make_checks_payable_to: parcel.make_checks_payable_to || '',
        assessed_land_value: parcel.assessed_land_value,
        assessed_building_value: parcel.assessed_building_value,
        assessed_total_value: parcel.assessed_total_value,
        assessed_value_na: parcel.assessed_value_na,
        assessed_value_na_reason: parcel.assessed_value_na_reason,
        assessed_value_na_explanation: parcel.assessed_value_na_explanation,
        assessed_total_manual: parcel.assessed_total_manual || false,
        exemption_types: parcel.exemption_types || [],
        exemption_other: parcel.exemption_other || '',
        exemption_value: parcel.exemption_value,
        exemption_na: parcel.exemption_na || false,
        current_tax_summary: parcel.current_tax_summary || [],
        has_delinquent: parcel.has_delinquent,
        dlq_payoff_amount: parcel.dlq_payoff_amount,
        dlq_good_thru_date: parcel.dlq_good_thru_date,
        delinquent_taxes_notes: parcel.delinquent_taxes_notes || '',
        mlc_charges: parcel.mlc_charges,
        mlc_24_month_charges: parcel.mlc_24_month_charges,
        mlc_eta_days: parcel.mlc_eta_days,
        sase_required: parcel.sase_required || false,
        info_obtained_back: parcel.info_obtained_back,
        tax_collector_email: parcel.tax_collector_email || '',
        tax_collector_website: parcel.tax_collector_website || '',
        assessor_website: parcel.assessor_website || '',
        tax_collector_working_hours: parcel.tax_collector_working_hours || '',
        notes: parcel.notes || '',
        // ── New Parcel mode (May 18, 2026 — v7.2 hydration fix) ──
        // Restore the toggle + override values from MongoDB on every
        // reopen. Without these three lines the UI mounted with the
        // toggle OFF even when the backend draft had is_new_parcel=true,
        // causing the standard tax notes to reappear on PDF generation.
        is_new_parcel: !!parcel.is_new_parcel,
        new_parcel_first_year: parcel.new_parcel_first_year || '',
        new_parcel_first_due_date: parcel.new_parcel_first_due_date || '',
        // UI↔PDF NOTES PARITY (Jun 2026) — hydrate the per-parcel
        // reviewed Combined Tax Certification Notes on reopen.
        auto_generated_notes: parcel.auto_generated_notes || '',
        // Per-parcel jurisdictions (new nested structure)
        jurisdictions: parcel.jurisdictions || []
      }));
      
      const flatData = {
        // Order-level fields (preserved at root)
        order_number: backendData.order_number || '',
        client_product: backendData.client_product || '',
        product_id: backendData.product_id || '',
        client_code: backendData.client_code || '',
        status: backendData.status || '',
        priority: backendData.priority || '',
        
        // Property/Borrower fields — INTENTIONALLY OMITTED
        // These fields (full_address, city, state, zip, county, borrower_owner_name)
        // are sourced from orderData, NOT from the certificate.
        // The bug fix useEffect at L4509-4564 syncs them from orderData.
        // If we include them here, the stale certificate values would overwrite
        // the fresh orderData values, causing the Property Summary Strip bug.
        // See: BUG FIX (Feb 22, 2026) — Property Summary Strip showed stale county
        
        // Multiple parcels toggle
        multiple_parcels: backendData.multiple_parcels || false,
        
        // Parcel 1 fields mapped to flat structure
        parcel_apns: parcel1.parcel_apn || '',
        source: parcel1.source || null,
        source_notes: parcel1.source_notes || '',
        collection_frequency: parcel1.tax_collected || 'annually', // Renamed in backend
        
        // Jurisdiction fields from Parcel 1
        jurisdiction_id: parcel1.jurisdiction_id || null,
        jurisdiction_type: parcel1.jurisdiction_type || null,
        make_checks_payable_to: parcel1.make_checks_payable_to || '',
        
        // Assessed Values from Parcel 1
        assessed_land_value: parcel1.assessed_land_value,
        assessed_building_value: parcel1.assessed_building_value,
        assessed_total_value: parcel1.assessed_total_value,
        assessed_value_na: parcel1.assessed_value_na,
        assessed_value_na_reason: parcel1.assessed_value_na_reason,
        assessed_value_na_explanation: parcel1.assessed_value_na_explanation,
        assessed_total_manual: parcel1.assessed_total_manual || false,
        
        // Exemptions from Parcel 1
        exemption_types: parcel1.exemption_types || [],
        exemption_other: parcel1.exemption_other || '',
        exemption_value: parcel1.exemption_value,
        exemption_na: parcel1.exemption_na || false,
        
        // Current Tax Summary from Parcel 1
        current_tax_summary: parcel1.current_tax_summary || [],
        
        // Delinquent Taxes from Parcel 1
        has_delinquent: parcel1.has_delinquent,
        dlq_payoff_amount: parcel1.dlq_payoff_amount,
        dlq_good_thru_date: parcel1.dlq_good_thru_date,
        delinquent_taxes_notes: parcel1.delinquent_taxes_notes || '',
        
        // MLC & Workflow from Parcel 1
        mlc_charges: parcel1.mlc_charges,
        mlc_24_month_charges: parcel1.mlc_24_month_charges,
        mlc_eta_days: parcel1.mlc_eta_days,
        sase_required: parcel1.sase_required || false,
        info_obtained_back: parcel1.info_obtained_back,
        
        // Tax Collector Contact from Parcel 1
        tax_collector_email: parcel1.tax_collector_email || '',
        tax_collector_website: parcel1.tax_collector_website || '',
        tax_collector_working_hours: parcel1.tax_collector_working_hours || '',
        
        // ── New Parcel mode (May 18, 2026 — v7.2 hydration fix) ──
        // Mirror the active parcel's New Parcel flag and overrides to
        // the flat certificateData root. The UI toggle, banner, and
        // PDF preview all read these root keys, so without this the
        // toggle re-mounted OFF on every reopen even though the data
        // was correctly persisted in MongoDB.
        is_new_parcel: !!parcel1.is_new_parcel,
        new_parcel_first_year: parcel1.new_parcel_first_year || '',
        new_parcel_first_due_date: parcel1.new_parcel_first_due_date || '',
        
        // Legacy fields (may exist for backward compatibility)
        tax_year: backendData.tax_year || '2025',
        mailing_date: backendData.mailing_date || '',
        due_date_1: backendData.due_date_1 || '',
        due_date_2: backendData.due_date_2 || '',
        delinquencies_present: backendData.delinquencies_present,
        installment_plan: backendData.installment_plan || 'none',
        overall_status: backendData.overall_status || 'due',
        changed_by_user: backendData.changed_by_user || false,
        
        // ========================================================================
        // PHASE 2: NESTED JURISDICTIONS/INSTALLMENTS (Mar 2026)
        // ========================================================================
        // Load jurisdictions from active parcel (index 0 on initial load)
        // Fallback: if parcel has no jurisdictions, check root-level (pre-migration data)
        // SAFETY GUARD: For multi-parcel with root-only jurisdictions, distribute
        // root jurisdictions to parcel 0 only; other parcels start with empty array
        jurisdictions: (parcelsArray[0]?.jurisdictions?.length > 0 
          ? parcelsArray[0].jurisdictions 
          : backendData.jurisdictions || [])
      };
      
      // SAFETY: If multi-parcel + root-level jurisdictions but no parcel-level,
      // log warning and ensure parcel 0 gets the root jurisdictions for safe editing
      const rootJurisdictions = backendData.jurisdictions || [];
      if (parcelsArray.length > 1 && rootJurisdictions.length > 0 && 
          !parcelsArray.some(p => p.jurisdictions?.length > 0)) {
        console.warn('[ADAPTER] SAFETY: Multi-parcel order has root-level jurisdictions[] but no parcel-level. ' +
          'Assigning root jurisdictions to Parcel 0 only. Run migration script to fix permanently.');
        parcelsArray[0].jurisdictions = rootJurisdictions;
      }
      
      console.log('[ADAPTER] Extracted Parcel 1 to flat structure:', flatData);
      console.log('[ADAPTER] Extracted parcels array:', parcelsArray);
      console.log('[ADAPTER] Extracted jurisdictions array:', flatData.jurisdictions);
      return { flatData, parcels: parcelsArray };
    } else {
      // Old format or already flat: Create single-parcel array from legacy data
      console.log('[ADAPTER] Using legacy flat format, creating single-parcel array');
      
      const singleParcel = {
        parcel_index: 1,
        parcel_apn: backendData.parcel_apns || '',
        source: backendData.source,
        source_notes: backendData.source_notes || '',
        tax_collected: backendData.collection_frequency || 'annually',
        jurisdiction_id: backendData.jurisdiction_id,
        jurisdiction_type: backendData.jurisdiction_type,
        make_checks_payable_to: backendData.make_checks_payable_to || '',
        assessed_land_value: backendData.assessed_land_value,
        assessed_building_value: backendData.assessed_building_value,
        assessed_total_value: backendData.assessed_total_value,
        assessed_value_na: backendData.assessed_value_na,
        assessed_value_na_reason: backendData.assessed_value_na_reason,
        assessed_value_na_explanation: backendData.assessed_value_na_explanation,
        assessed_total_manual: backendData.assessed_total_manual || false,
        exemption_types: backendData.exemption_types || [],
        exemption_other: backendData.exemption_other || '',
        exemption_value: backendData.exemption_value,
        exemption_na: backendData.exemption_na || false,
        current_tax_summary: backendData.current_tax_summary || [],
        has_delinquent: backendData.has_delinquent,
        dlq_payoff_amount: backendData.dlq_payoff_amount,
        dlq_good_thru_date: backendData.dlq_good_thru_date,
        delinquent_taxes_notes: backendData.delinquent_taxes_notes || '',
        mlc_charges: backendData.mlc_charges,
        mlc_24_month_charges: backendData.mlc_24_month_charges,
        mlc_eta_days: backendData.mlc_eta_days,
        sase_required: backendData.sase_required || false,
        info_obtained_back: backendData.info_obtained_back,
        tax_collector_email: backendData.tax_collector_email || '',
        tax_collector_website: backendData.tax_collector_website || '',
        assessor_website: backendData.assessor_website || '',
        tax_collector_working_hours: backendData.tax_collector_working_hours || '',
        notes: backendData.notes || '',
        // ── New Parcel mode (May 18, 2026 — v7.2 hydration fix, legacy branch) ──
        // Pre-migration single-parcel saves store these at root; preserve them
        // on hydration so reopens of legacy orders also restore New Parcel state.
        is_new_parcel: !!backendData.is_new_parcel,
        new_parcel_first_year: backendData.new_parcel_first_year || '',
        new_parcel_first_due_date: backendData.new_parcel_first_due_date || '',
        // Per-parcel jurisdictions: inherit from root for legacy data
        jurisdictions: backendData.jurisdictions || []
      };
      
      // For legacy format, load jurisdictions from the single parcel
      // IMPORTANT: Exclude order-level property fields (county, city, state, zip, full_address, borrower_owner_name)
      // These are sourced from orderData, not from the certificate.
      // See: BUG FIX (Feb 22, 2026) — Property Summary Strip showed stale county
      const { 
        county, city, state, zip, full_address, borrower_owner_name,
        ...backendDataWithoutPropertyFields 
      } = backendData;
      
      const flatDataWithJurisdictions = {
        ...backendDataWithoutPropertyFields,
        // ========================================================================
        // PHASE 2: NESTED JURISDICTIONS/INSTALLMENTS (Mar 2026)
        // ========================================================================
        jurisdictions: singleParcel.jurisdictions
      };
      
      return { flatData: flatDataWithJurisdictions, parcels: [singleParcel] };
    }
  };
  
  /**
   * Adapter: Transform frontend parcels array → backend multi-parcel structure
   * Called when saving certificate data to backend
   * PHASE 3B: Now builds from parcels array (after syncing flat → parcel)
   */
  const adaptFrontendToBackend = (flatData, currentParcels) => {
    console.log('[ADAPTER] === FRONTEND → BACKEND ADAPTATION ===');
    console.log('[ADAPTER] Flat data parcel_apns:', flatData.parcel_apns);
    console.log('[ADAPTER] Received parcels array:', currentParcels);
    console.log('[ADAPTER] Parcel 0:', currentParcels[0]);
    console.log('[ADAPTER] Parcel 0 parcel_apn:', currentParcels[0]?.parcel_apn);
    
    // Build parcels array for backend (map parcel_apn back to backend field name)
    const backendParcels = currentParcels.map(parcel => ({
      parcel_index: parcel.parcel_index,
      parcel_apn: parcel.parcel_apn || '',
      source: parcel.source,
      source_notes: parcel.source_notes || '',
      tax_collected: parcel.tax_collected || 'annually',
      jurisdiction_id: parcel.jurisdiction_id,
      jurisdiction_type: parcel.jurisdiction_type,
      make_checks_payable_to: parcel.make_checks_payable_to || '',
      assessed_land_value: parcel.assessed_land_value,
      assessed_building_value: parcel.assessed_building_value,
      assessed_total_value: parcel.assessed_total_value,
      assessed_value_na: parcel.assessed_value_na,
      assessed_value_na_reason: parcel.assessed_value_na_reason,
      assessed_value_na_explanation: parcel.assessed_value_na_explanation,
      assessed_total_manual: parcel.assessed_total_manual || false,
      exemption_types: parcel.exemption_types || [],
      exemption_other: parcel.exemption_other || '',
      exemption_value: parcel.exemption_value,
      exemption_na: parcel.exemption_na || false,
      current_tax_summary: parcel.current_tax_summary || [],
      has_delinquent: parcel.has_delinquent,
      dlq_payoff_amount: parcel.dlq_payoff_amount,
      dlq_good_thru_date: parcel.dlq_good_thru_date,
      delinquent_taxes_notes: parcel.delinquent_taxes_notes || '',
      mlc_charges: parcel.mlc_charges,
      mlc_24_month_charges: parcel.mlc_24_month_charges,
      mlc_eta_days: parcel.mlc_eta_days,
      sase_required: parcel.sase_required || false,
      info_obtained_back: parcel.info_obtained_back,
      tax_collector_email: parcel.tax_collector_email || '',
      tax_collector_website: parcel.tax_collector_website || '',
      tax_collector_working_hours: parcel.tax_collector_working_hours || '',
      tax_collector_phone: parcel.tax_collector_phone || '',
      tax_collector_phone_ext: parcel.tax_collector_phone_ext || '',
      // ── New Parcel mode (May 18, 2026 — v7 persistence fix) ──
      // These three fields were being silently dropped by this
      // adapter, so the toggle and overrides set in the UI never
      // reached the backend draft document. Without them the PDF
      // service cannot detect `is_new_parcel` on render time and
      // falls back to the standard tax-notes engine, producing the
      // exact "All prior taxes are paid…" wording the user reported.
      is_new_parcel: !!parcel.is_new_parcel,
      new_parcel_first_year: parcel.new_parcel_first_year || '',
      new_parcel_first_due_date: parcel.new_parcel_first_due_date || '',
      // UI↔PDF NOTES PARITY (Jun 2026) — the reviewed Combined Tax
      // Certification Notes MUST survive this explicit-field adapter
      // (same class of silent-drop bug as the New Parcel fields above).
      auto_generated_notes: parcel.auto_generated_notes || '',
      // Per-parcel jurisdictions (new nested structure)
      jurisdictions: parcel.jurisdictions || []
    }));
    
    // Build multi-parcel payload
    const multiParcelData = {
      // Order-level fields (read-only context — NOT used to update order status/priority)
      // P0 SAFETY: status & priority are intentionally EXCLUDED from autosave payload.
      // The order workflow status lives in the `orders` collection and must only be
      // changed via PATCH /orders/{id}/status.  Including them here risks a stale
      // autosave overwriting a legitimate status change.
      order_number: flatData.order_number || '',
      client_product: flatData.client_product || '',
      product_id: flatData.product_id || '',
      client_code: flatData.client_code || '',
      
      // Property/Borrower fields
      full_address: flatData.full_address || '',
      city: flatData.city || '',
      state: flatData.state || '',
      zip: flatData.zip || '',
      county: flatData.county || '',
      borrower_owner_name: flatData.borrower_owner_name || '',
      
      // Multi-parcel structure (PHASE 3B: built from parcels array)
      multiple_parcels: flatData.multiple_parcels || false,
      current_parcel_index: 0, // Will be updated when navigation is added
      parcels: backendParcels,
      
      // Legacy fields (for backward compatibility with backend - use first parcel)
      parcel_apns: backendParcels[0]?.parcel_apn || '',
      source: backendParcels[0]?.source,
      source_notes: backendParcels[0]?.source_notes || '',
      collection_frequency: backendParcels[0]?.tax_collected || 'annually',
      current_tax_summary: backendParcels[0]?.current_tax_summary || [],
      assessed_land_value: backendParcels[0]?.assessed_land_value,
      assessed_building_value: backendParcels[0]?.assessed_building_value,
      assessed_total_value: backendParcels[0]?.assessed_total_value,
      assessed_value_na: backendParcels[0]?.assessed_value_na,
      assessed_value_na_reason: backendParcels[0]?.assessed_value_na_reason,
      assessed_value_na_explanation: backendParcels[0]?.assessed_value_na_explanation,
      assessed_total_manual: backendParcels[0]?.assessed_total_manual || false,
      exemption_types: backendParcels[0]?.exemption_types || [],
      exemption_other: backendParcels[0]?.exemption_other || '',
      exemption_value: backendParcels[0]?.exemption_value,
      exemption_na: backendParcels[0]?.exemption_na || false,
      has_delinquent: backendParcels[0]?.has_delinquent,
      dlq_payoff_amount: backendParcels[0]?.dlq_payoff_amount,
      dlq_good_thru_date: backendParcels[0]?.dlq_good_thru_date,
      delinquent_taxes_notes: backendParcels[0]?.delinquent_taxes_notes || '',
      jurisdiction_id: backendParcels[0]?.jurisdiction_id,
      jurisdiction_type: backendParcels[0]?.jurisdiction_type,
      make_checks_payable_to: backendParcels[0]?.make_checks_payable_to || '',
      mlc_charges: backendParcels[0]?.mlc_charges,
      mlc_24_month_charges: backendParcels[0]?.mlc_24_month_charges,
      mlc_eta_days: backendParcels[0]?.mlc_eta_days,
      sase_required: backendParcels[0]?.sase_required || false,
      info_obtained_back: backendParcels[0]?.info_obtained_back,
      tax_collector_email: backendParcels[0]?.tax_collector_email || '',
      tax_collector_website: backendParcels[0]?.tax_collector_website || '',
      assessor_website: backendParcels[0]?.assessor_website || '',
      tax_collector_working_hours: backendParcels[0]?.tax_collector_working_hours || '',
      tax_collector_phone: backendParcels[0]?.tax_collector_phone || '',
      tax_collector_phone_ext: backendParcels[0]?.tax_collector_phone_ext || '',
      notes: backendParcels[0]?.notes || '',
      
      // Other legacy fields from flat data
      tax_year: flatData.tax_year || '2025',
      mailing_date: flatData.mailing_date || '',
      due_date_1: flatData.due_date_1 || '',
      due_date_2: flatData.due_date_2 || '',
      delinquencies_present: flatData.delinquencies_present,
      installment_plan: flatData.installment_plan || 'none',
      overall_status: flatData.overall_status || 'due',
      changed_by_user: flatData.changed_by_user || false,
      
      // ========================================================================
      // PHASE 2: NESTED JURISDICTIONS/INSTALLMENTS (Mar 2026)
      // ========================================================================
      // DEPRECATED: Root-level jurisdictions[] — now stored in parcels[].jurisdictions[]
      // Keep empty for backward compat; actual data saved in backendParcels[].jurisdictions
      jurisdictions: [],
      
      // Auto-generated notes from Fulfillment (single source of truth for PDF)
      auto_generated_notes: flatData.auto_generated_notes || ''
    };
    
    console.log('[ADAPTER] Built multi-parcel payload:', multiParcelData);
    return multiParcelData;
  };
  
  // ============================================================================
  // PHASE 3B: SYNC HELPER FUNCTIONS
  // ============================================================================
  
  /**
   * Sync flat certificateData → active parcel in parcels array
   * Call this BEFORE switching parcels to save current edits
   */
  const syncFlatToParcel = (returnUpdatedParcels = false, dataToSync = null) => {
    console.log('[SYNC] Flat → Parcel:', activeParcelIndex);
    
    // Use provided data or fall back to certificateData from closure
    const sourceData = dataToSync || certificateData;
    
    console.log('[SYNC DEBUG] Source data parcel_apns:', sourceData.parcel_apns);
    
    // Normalize parcel_apns to string (handles both string and legacy array format)
    const normalizedApn = typeof sourceData.parcel_apns === 'string'
      ? sourceData.parcel_apns
      : (Array.isArray(sourceData.parcel_apns) 
          ? sourceData.parcel_apns.join(', ') 
          : '');
    
    console.log('[SYNC DEBUG] Normalized APN:', normalizedApn);
    
    // May 5, 2026 — `Other (Custom)` persistence fix.
    // The compact exemption panel writes to `certificateData.jurisdictions[i].exemption_types`
    // (per-jurisdiction). The flat top-level `sourceData.exemption_types` is only populated by
    // the legacy single-source flow. When only the per-jurisdiction values are set, mirror
    // them down to the parcel-level fields so `adaptFrontendToBackend` (line 1418) sees the
    // exemption data and the backend persists it. parcel.jurisdictions[] still keeps the
    // authoritative per-jurisdiction breakdown for the PDF render.
    const _activeJur = (sourceData.jurisdictions && sourceData.jurisdictions.length > 0)
      ? sourceData.jurisdictions[0]
      : null;
    const _exTypes = (sourceData.exemption_types && sourceData.exemption_types.length > 0)
      ? sourceData.exemption_types
      : (_activeJur?.exemption_types || []);
    const _exOther = sourceData.exemption_other || _activeJur?.exemption_other || '';
    const _exValue = (sourceData.exemption_value !== undefined && sourceData.exemption_value !== null)
      ? sourceData.exemption_value
      : _activeJur?.exemption_value;
    const _exNa = sourceData.exemption_na || _activeJur?.exemption_na || false;

    const updatedParcel = {
      ...parcels[activeParcelIndex],
      parcel_apn: normalizedApn,
      source: sourceData.source,
      source_notes: sourceData.source_notes || '',
      tax_collected: sourceData.collection_frequency || 'annually',
      jurisdiction_id: sourceData.jurisdiction_id,
      jurisdiction_type: sourceData.jurisdiction_type,
      make_checks_payable_to: sourceData.make_checks_payable_to || '',
      assessed_land_value: sourceData.assessed_land_value,
      assessed_building_value: sourceData.assessed_building_value,
      assessed_total_value: sourceData.assessed_total_value,
      assessed_value_na: sourceData.assessed_value_na,
      assessed_value_na_reason: sourceData.assessed_value_na_reason,
      assessed_value_na_explanation: sourceData.assessed_value_na_explanation,
      assessed_total_manual: sourceData.assessed_total_manual || false,
      exemption_types: _exTypes,
      exemption_other: _exOther,
      exemption_value: _exValue,
      exemption_na: _exNa,
      current_tax_summary: sourceData.current_tax_summary || [],
      has_delinquent: sourceData.has_delinquent,
      dlq_payoff_amount: sourceData.dlq_payoff_amount,
      dlq_good_thru_date: sourceData.dlq_good_thru_date,
      delinquent_taxes_notes: sourceData.delinquent_taxes_notes || '',
      mlc_charges: sourceData.mlc_charges,
      mlc_24_month_charges: sourceData.mlc_24_month_charges,
      mlc_eta_days: sourceData.mlc_eta_days,
      sase_required: sourceData.sase_required || false,
      info_obtained_back: sourceData.info_obtained_back,
      tax_collector_email: sourceData.tax_collector_email || '',
      tax_collector_website: sourceData.tax_collector_website || '',
      assessor_website: sourceData.assessor_website || '',
      tax_collector_working_hours: sourceData.tax_collector_working_hours || '',
      notes: sourceData.notes || '',
      // UI↔PDF NOTES PARITY (Jun 2026) — persist the reviewed Combined
      // Tax Certification Notes per parcel so the PDF renders the EXACT
      // narrative the processor reviewed (multi-parcel certs previously
      // regenerated notes backend-side, producing divergent wording).
      auto_generated_notes: sourceData.auto_generated_notes || '',
      // New Parcel mode (May 17, 2026) — persist per-parcel flag + overrides
      is_new_parcel: sourceData.is_new_parcel || false,
      new_parcel_first_year: sourceData.new_parcel_first_year || '',
      new_parcel_first_due_date: sourceData.new_parcel_first_due_date || '',
      // Per-parcel jurisdictions (new nested structure)
      jurisdictions: sourceData.jurisdictions || []
    };
    
    const updatedParcels = [...parcels];
    updatedParcels[activeParcelIndex] = updatedParcel;
    
    setParcels(updatedParcels);
    
    console.log('[SYNC] Updated parcel:', updatedParcel);
    
    // Optionally return the updated parcels for immediate use (before state updates)
    if (returnUpdatedParcels) {
      return updatedParcels;
    }
  };
  
  /**
   * Sync active parcel → flat certificateData
   * Call this AFTER switching parcels to load new parcel data into UI
   */
  const syncParcelToFlat = () => {
    console.log('[SYNC] Parcel → Flat:', activeParcelIndex);
    
    if (!parcels[activeParcelIndex]) {
      console.warn('[SYNC] No parcel at index:', activeParcelIndex);
      return;
    }
    
    const currentParcel = parcels[activeParcelIndex];
    console.log('[SYNC DEBUG] Current parcel parcel_apn:', currentParcel.parcel_apn);
    
    setCertificateData(prev => ({
      ...prev,
      // Sync parcel-specific fields
      parcel_apns: currentParcel.parcel_apn || '',
      source: currentParcel.source,
      source_notes: currentParcel.source_notes || '',
      collection_frequency: currentParcel.tax_collected || 'annually',
      jurisdiction_id: currentParcel.jurisdiction_id,
      jurisdiction_type: currentParcel.jurisdiction_type,
      make_checks_payable_to: currentParcel.make_checks_payable_to || '',
      assessed_land_value: currentParcel.assessed_land_value,
      assessed_building_value: currentParcel.assessed_building_value,
      assessed_total_value: currentParcel.assessed_total_value,
      assessed_value_na: currentParcel.assessed_value_na,
      assessed_value_na_reason: currentParcel.assessed_value_na_reason,
      assessed_value_na_explanation: currentParcel.assessed_value_na_explanation,
      assessed_total_manual: currentParcel.assessed_total_manual || false,
      exemption_types: currentParcel.exemption_types || [],
      exemption_other: currentParcel.exemption_other || '',
      exemption_value: currentParcel.exemption_value,
      exemption_na: currentParcel.exemption_na || false,
      current_tax_summary: currentParcel.current_tax_summary || [],
      has_delinquent: currentParcel.has_delinquent,
      dlq_payoff_amount: currentParcel.dlq_payoff_amount,
      dlq_good_thru_date: currentParcel.dlq_good_thru_date,
      delinquent_taxes_notes: currentParcel.delinquent_taxes_notes || '',
      mlc_charges: currentParcel.mlc_charges,
      mlc_24_month_charges: currentParcel.mlc_24_month_charges,
      mlc_eta_days: currentParcel.mlc_eta_days,
      sase_required: currentParcel.sase_required || false,
      info_obtained_back: currentParcel.info_obtained_back,
      tax_collector_email: currentParcel.tax_collector_email || '',
      tax_collector_website: currentParcel.tax_collector_website || '',
      assessor_website: currentParcel.assessor_website || '',
      tax_collector_working_hours: currentParcel.tax_collector_working_hours || '',
      notes: currentParcel.notes || '',
      // UI↔PDF NOTES PARITY (Jun 2026) — restore the per-parcel reviewed
      // notes into the flat state on parcel switch (JurisdictionManager
      // re-emits fresh notes shortly after, but this avoids a stale gap).
      auto_generated_notes: currentParcel.auto_generated_notes || '',
      // ── New Parcel mode (May 17, 2026) ─────────────────────────
      // Per-parcel operational flag. When ON, a standardised note is
      // appended to the PDF and amount-based validations are relaxed.
      // `new_parcel_first_year` / `new_parcel_first_due_date` are
      // optional overrides; when blank the note falls back to the
      // first installment row's tax_year / due_date.
      is_new_parcel: currentParcel.is_new_parcel || false,
      new_parcel_first_year: currentParcel.new_parcel_first_year || '',
      new_parcel_first_due_date: currentParcel.new_parcel_first_due_date || '',
      // Per-parcel jurisdictions (new nested structure)
      jurisdictions: currentParcel.jurisdictions || []
    }));
    
    console.log('[SYNC] Loaded parcel data into flat state');
  };

  const sanitizeCertificateData = (data) => {
    return {
      ...data,
      // Ensure all required header fields are strings
      order_number: data.order_number || '',
      client_product: data.client_product || '',
      product_id: data.product_id || '',
      client_code: data.client_code || '',
      status: data.status || '',
      priority: data.priority || '',
      
      // Ensure all required property fields are strings
      full_address: data.full_address || '',
      city: data.city || '',
      state: data.state || '',
      zip: data.zip || '',
      county: data.county || '',
      parcel_apns: data.parcel_apns || '',
      borrower_owner_name: data.borrower_owner_name || '',
      
      // Taxing Authority / Remittance
      jurisdiction_id: data.jurisdiction_id || null, // Phase 3A: Save jurisdiction reference
      jurisdiction_type: data.jurisdiction_type || null, // Type selected (county/city/etc)
      make_checks_payable_to: data.make_checks_payable_to || '',
      changed_by_user: data.changed_by_user || false,
      
      // MLC & Workflow Information (from jurisdiction - READ-ONLY)
      mlc_charges: data.mlc_charges || null,
      mlc_24_month_charges: data.mlc_24_month_charges || null,
      mlc_eta_days: data.mlc_eta_days || null,
      sase_required: data.sase_required || false,
      info_obtained_back: data.info_obtained_back || null,
      
      // Tax Collector Contact Details (from jurisdiction - READ-ONLY)
      tax_collector_email: data.tax_collector_email || '',
      tax_collector_website: data.tax_collector_website || '',
      assessor_website: data.assessor_website || '',
      tax_collector_working_hours: data.tax_collector_working_hours || '',
      notes: data.notes || '',
      
      // Ensure optional fields have proper defaults
      source: data.source || null,
      source_notes: data.source_notes || '',
      multiple_parcels: data.multiple_parcels !== undefined ? data.multiple_parcels : false, // Default to No
      collection_frequency: data.collection_frequency || 'annually',
      
      // Tax Notes Fields
      tax_year: data.tax_year || '2025',
      mailing_date: data.mailing_date || '',
      due_date_1: data.due_date_1 || '',
      due_date_2: data.due_date_2 || '',
      delinquencies_present: data.delinquencies_present,
      installment_plan: data.installment_plan || 'none',
      overall_status: data.overall_status || 'due',
      
      current_tax_summary: data.current_tax_summary || [],
      assessed_land_value: data.assessed_land_value,
      assessed_building_value: data.assessed_building_value,
      assessed_total_value: data.assessed_total_value,
      assessed_value_na: data.assessed_value_na,
      assessed_value_na_reason: data.assessed_value_na_reason,
      assessed_value_na_explanation: data.assessed_value_na_explanation,
      assessed_total_manual: data.assessed_total_manual || false,
      exemption_types: data.exemption_types || [],
      exemption_other: data.exemption_other || null,
      exemption_value: data.exemption_value,
      exemption_na: data.exemption_na || false,
      has_delinquent: data.has_delinquent,
      dlq_payoff_amount: data.dlq_payoff_amount,
      dlq_good_thru_date: data.dlq_good_thru_date,
      delinquent_taxes_notes: data.delinquent_taxes_notes || ''
    };
  };
  
  const [certificateData, setCertificateData] = useState({
    // Header fields (read-only, populated from orderData)
    order_number: orderData.order_number,
    client_product: orderData.client_product,
    product_id: orderData.product_id,
    client_code: orderData.client_code,
    status: orderData.status,
    priority: orderData.priority,
    
    // Property/Borrower fields (with prefill support)
    full_address: orderData.property?.address || orderData.property?.property_address_full || '',
    city: orderData.property_city || orderData.property?.city || '',
    state: orderData.property_state || orderData.property?.state || '',
    zip: orderData.property_zip || orderData.property?.zip_code || '',
    county: orderData.property_county || orderData.property?.county || '',
    parcel_apns: Array.isArray(orderData.parcel_apns) ? orderData.parcel_apns : (orderData.parcel_apns ? [orderData.parcel_apns] : []),
    borrower_owner_name: orderData.borrower_name || orderData.customer?.name || orderData.customer?.customer_name || '',
    
    // Source fields (updated structure)
    source: null, // 'calling' | 'online' | 'fax_email' | 'mailaway' | null
    source_notes: '',
    
    // Tax Collection Schedule
    collection_frequency: 'annually',
    
    // Tax Notes Fields (for dynamic notes generation)
    tax_year: '2025', // Active tax year for notes generation
    mailing_date: '', // MM/DD/YYYY format (e.g. "08/10/2025")
    due_date_1: '', // MM/DD/YYYY format (e.g. "09/10/2025") 
    due_date_2: '', // MM/DD/YYYY format (e.g. "12/10/2025")
    delinquencies_present: null, // true/false for delinquency status
    installment_plan: 'none', // none | 2-installments | 4-installments
    overall_status: 'due', // paid_in_full | due | delinquent | partially_paid
    
    // Current Tax Summary (repeatable rows)
    current_tax_summary: [],
    
    // ========================================================================
    // PHASE 2: NESTED JURISDICTIONS/INSTALLMENTS (Mar 2026)
    // ========================================================================
    // New structure for new orders - jurisdiction-level fields with nested installments
    // - Only populated for NEW orders in Internal workflow
    // - Legacy orders continue using current_tax_summary[]
    // - isNewJurisdictionStructure() determines which structure to render
    jurisdictions: [], // Array of TaxJurisdiction objects with nested installments[]
    // ========================================================================
    
    // Assessed Values & Exemptions (updated structure)
    assessed_land_value: null,
    assessed_building_value: null,
    assessed_total_value: null,
    assessed_value_na: false,
    assessed_value_na_reason: null,
    assessed_value_na_explanation: '',
    assessed_total_manual: false,
    exemption_types: [], // Multi-select array
    exemption_other: '', // Description when "Other" is selected
    exemption_value: null, // Currency value
    exemption_na: false, // Flag to track if Exemption N/A has been applied
    
    // Delinquent Taxes (now required fields)
    has_delinquent: null, // Required at completion - null not allowed
    dlq_payoff_amount: null, // Required if has_delinquent = true  
    dlq_good_thru_date: null, // ISO date, must be > today when has_delinquent = true
    delinquent_taxes_notes: '',
    
    // Taxing Authority/Remittance (auto-fill, editable)
    jurisdiction_id: null, // Selected jurisdiction from directory
    jurisdiction_type: null, // Type selected by user (county/city/township/village/school)
    make_checks_payable_to: '',
    changed_by_user: false,
    
    // MLC & Workflow Information (from jurisdiction - READ-ONLY)
    mlc_charges: null,
    mlc_24_month_charges: null,
    mlc_eta_days: null,
    sase_required: false,
    info_obtained_back: null, // 'mail' | 'email' | 'fax'
    
    // Tax Collector Contact Details (from jurisdiction - READ-ONLY)
    tax_collector_email: '',
    tax_collector_website: '',
    assessor_website: '',
    tax_collector_working_hours: '',
    notes: '', // Jurisdiction internal notes
    
    // Supporting Documents
    supporting_documents: []
  });
  
  const [loading, setLoading] = useState(false);
  const [validationErrors, setValidationErrors] = useState({});
  const [hasShownValidationToast, setHasShownValidationToast] = useState(false);
  const [saveValidationWarnings, setSaveValidationWarnings] = useState([]);
  
  // Reset toast flag when all validation errors are cleared
  useEffect(() => {
    const hasAssessedErrors = validationErrors.assessed_land_value || 
                             validationErrors.assessed_building_value || 
                             validationErrors.assessed_total_value;
    
    if (!hasAssessedErrors && hasShownValidationToast) {
      // All errors cleared, reset the flag for future errors
      setHasShownValidationToast(false);
    }
  }, [validationErrors, hasShownValidationToast]);
  
  // uploadingFiles state removed - Supporting Documents section removed
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [pendingFrequency, setPendingFrequency] = useState('');
  
  // Auto-linking state
  const [previousOrder, setPreviousOrder] = useState(null);
  const [isPrefilling, setIsPrefilling] = useState(false);
  const [isPrefilled, setIsPrefilled] = useState(false);
  const [relatedFiles, setRelatedFiles] = useState([]);
  const [orderFiles, setOrderFiles] = useState([]);
  
  // Prefill tracking state
  const [prefilledFields, setPrefilledFields] = useState(new Set());
  const [prefilledAt, setPrefilledAt] = useState(null);
  
  const { user } = useAuth();

  // MLS-COMPONENT-LOCK (UAT Aug 2026): on dual MLS orders the Tax workspace
  // is editable only while the Tax component is ASSIGNED — and, for
  // processor-level users, assigned to THEM. Admin/TL/QC keep their existing
  // edit privileges once assigned. Backend guards mirror this rule.
  // TAX-COMPONENT-LOCK-001 (Nov 2026): a COMPLETE Tax component is read-only
  // for every role until reopened (previously `complete` short-circuited to
  // editable). Backend: enforce_tax_component_open().
  const _lockRole = (user?.role || '').toLowerCase().replace(/\s+/g, '_').replace(/-/g, '_');
  const taxComponentLocked = (() => {
    if (!isMlsDual) return false;
    const cdoc = (mlsComponents || orderData?.components || {}).tax || {};
    if ((cdoc.status || 'pending') === 'complete') return true;
    if (!cdoc.assignee_id) return true;
    if (['admin', 'tax_search_team_leader', 'qc_specialist'].includes(_lockRole)) return false;
    return cdoc.assignee_id !== user?.id;
  })();
  const readOnly = readOnlyProp || taxComponentLocked;

  // SECURITY: Property/Borrower fields are only editable by Admin role
  const isAdminUser = user?.role === 'admin';
  const propertyBorrowerReadOnly = readOnly || !isAdminUser;
  
  // SECURITY: Jurisdiction REFERENCE/CONTACT fields (master data) - restricted editing
  // Only these specific fields are restricted for Tax Processor and Tax Team Leader:
  // - Make Checks Payable To, Tax Collector Email/Website, Assessor Website, 
  // - Tax Collector Phone, Phone Extension, Working Hours
  // Jurisdiction Type and normal tax workflow fields remain EDITABLE for all processing roles
  const isJurisdictionManager = user?.role === 'jurisdiction_manager';
  const isTaxProcessor = (() => {
    const r = (user?.role || '').toLowerCase().replace(/\s+/g, '_').replace(/-/g, '_');
    return r === 'tax_processor' || r === 'processor';
  })();
  const isTaxTeamLeader = user?.role === 'tax_search_team_leader' || user?.role === 'title_search_team_leader' || user?.role === 'team_leader';
  const isQCSpecialist = user?.role === 'qc_specialist' || user?.role === 'qc';
  const canEditJurisdictionContactFields = isAdminUser || isJurisdictionManager;
  // PROCESSOR-JURISDICTION-REQUEST-001: processors / tax TLs may file a missing-jurisdiction request from an empty state.
  // Only the contact/reference fields are read-only for Tax Processor and Tax Team Leader
  const jurisdictionContactFieldsReadOnly = readOnly || !canEditJurisdictionContactFields;

  // Feb 2026 — JURIS-BOROUGH-PICKER-001 follow-up.
  // Linking / unlinking a collector from the master Tax Collector
  // Directory is a STRUCTURED, audit-friendly action: it atomically
  // writes the FK + denormalized payee/phone/address fields from the
  // canonical collector row. It is fundamentally different from
  // hand-editing free-text contact fields. Permit Tax Processors,
  // Tax Team Leaders, and QC Specialists to pick (and unlink) from
  // the directory; free-text edit of payee / phone / address /
  // working hours / email / website stays restricted to Admin or
  // Jurisdiction Manager.
  const canLinkJurisdictionCollector = canEditJurisdictionContactFields
    || isTaxProcessor || isTaxTeamLeader || isQCSpecialist;
  
  // UX: Fulfillment roles see compact Property/Borrower header instead of full section
  // Order Desk (admin) sees the full editable form - DO NOT CHANGE Order Desk behavior
  const isFulfillmentRole = isTaxProcessor || isTaxTeamLeader || isQCSpecialist;
  
  // ============================================================================
  // PHASE 3B: MULTI-PARCEL STATE MANAGEMENT
  // ============================================================================
  
  // Parcels array - holds data for all parcels
  const [parcels, setParcels] = useState([
    {
      parcel_index: 1,
      parcel_apn: '',
      source: null,
      source_notes: '',
      tax_collected: 'annually',
      jurisdiction_id: null,
      jurisdiction_type: null,
      make_checks_payable_to: '',
      assessed_land_value: null,
      assessed_building_value: null,
      assessed_total_value: null,
      assessed_value_na: false,
      assessed_value_na_reason: null,
      assessed_value_na_explanation: '',
      assessed_total_manual: false,
      exemption_types: [],
      exemption_other: '',
      exemption_value: null,
      exemption_na: false,
      current_tax_summary: [],
      has_delinquent: null,
      dlq_payoff_amount: null,
      dlq_good_thru_date: null,
      delinquent_taxes_notes: '',
      mlc_charges: null,
      mlc_24_month_charges: null,
      mlc_eta_days: null,
      sase_required: false,
      info_obtained_back: null,
      tax_collector_email: '',
      tax_collector_website: '',
      assessor_website: '',
      tax_collector_working_hours: '',
      notes: ''
    }
  ]);
  
  // Active parcel index (0-indexed: Parcel 1 = 0, Parcel 2 = 1, etc.)
  // NOTE: This state MUST be declared BEFORE the useEffect that uses it to avoid "Cannot access before initialization" error
  const [activeParcelIndex, setActiveParcelIndex] = useState(0);
  
  // Sync certificate data with parent component (for Mail-Away modal access and Complete button validation)
  // P0 FIX: Include parcels array so FinalizeOrderCard validation can check parcel data
  // P0 FIX: ALWAYS sync flat data to parcels before sending to parent to ensure consistency
  // PERF FIX: Debounce parent sync to prevent render cascade (child → parent → child) on every keystroke
  const parentSyncTimerRef = useRef(null);
  useEffect(() => {
    if (!onCertificateDataChange) return;
    
    // Clear any pending sync
    if (parentSyncTimerRef.current) clearTimeout(parentSyncTimerRef.current);
    
    // Debounce: sync to parent 400ms after last change (avoids cascade on every keystroke)
    parentSyncTimerRef.current = setTimeout(() => {
      // P0 FIX: Build the most up-to-date parcel data by merging flat certificateData into active parcel
      // This ensures the parent component always gets the latest data, not stale parcel state
      const currentParcel = parcels[activeParcelIndex] || {};
      
      // P0 FIX: Extract jurisdiction_type and make_checks_payable_to from current_tax_summary if not at top level
      // In the new UI, these are stored per-row in current_tax_summary, not at certificateData level
      // P0 FIX: Also check jurisdictions[] array (separate data structure for jurisdiction-level data)
      const taxSummary = certificateData.current_tax_summary || [];
      const firstRow = taxSummary[0] || {};
      const jurisdictionsArray = certificateData.jurisdictions || [];
      const firstJurisdiction = jurisdictionsArray[0] || {};
      
      // Priority: certificateData level > first row of current_tax_summary > first jurisdiction > currentParcel > null
      const effectiveJurisdictionType = certificateData.jurisdiction_type 
        || firstRow.jurisdiction_type 
        || firstJurisdiction.jurisdiction_type
        || currentParcel.jurisdiction_type 
        || null;
      
      // For make_checks_payable_to, check top-level first, then jurisdictions array, then current_tax_summary
      const effectiveMakeChecksPayableTo = certificateData.make_checks_payable_to 
        || firstJurisdiction.make_checks_payable_to
        || firstRow.make_checks_payable_to
        || currentParcel.make_checks_payable_to 
        || '';
      
      // P0 FIX: Normalize parcel_apn - handle both string and array formats
      const normalizedParcelApn = typeof certificateData.parcel_apns === 'string'
        ? certificateData.parcel_apns
        : (Array.isArray(certificateData.parcel_apns) 
            ? certificateData.parcel_apns.join(', ') 
            : currentParcel.parcel_apn || '');
      
      // P0 FIX (Feb 2026 — Activity Log stale-county bug):
      // jurisdiction_id (and jurisdiction_name) MUST be part of the
      // reconciliation overlay. Previously this whitelist omitted them,
      // so a fuzzy-accept that updated only certificateData would leave
      // parcels[active].jurisdiction_id pointing at the previous
      // (stale) jurisdiction. The save serializer then shipped the
      // stale UUID and the backend resolved it to the wrong county
      // name in the activity log.
      const effectiveJurisdictionId = certificateData.jurisdiction_id
        || firstRow.jurisdiction_id
        || firstJurisdiction.jurisdiction_id
        || currentParcel.jurisdiction_id
        || null;
      const effectiveJurisdictionName = certificateData.jurisdiction_name
        || firstRow.jurisdiction_name
        || firstJurisdiction.jurisdiction_name
        || currentParcel.jurisdiction_name
        || '';

      const updatedParcel = {
        ...currentParcel,
        parcel_apn: normalizedParcelApn,
        source: certificateData.source || currentParcel.source,
        tax_collected: certificateData.collection_frequency || currentParcel.tax_collected || 'annually',
        jurisdiction_id: effectiveJurisdictionId,             // ✅ NEW
        jurisdiction_name: effectiveJurisdictionName,         // ✅ NEW
        jurisdiction_type: effectiveJurisdictionType,
        make_checks_payable_to: effectiveMakeChecksPayableTo,
        // P0 FIX: CRITICAL - Always use certificateData.current_tax_summary as the source of truth
        // This is where the UI stores the tax summary rows, NOT in parcels[].current_tax_summary
        current_tax_summary: certificateData.current_tax_summary || [],
        // P0 FIX (Feb 27, 2026 — Jurisdiction Selection NOT in Activity Log):
        // JurisdictionManager writes to certificateData.jurisdictions[] (per-jurisdiction
        // nested array — Contact Info / MLC / Tax Summary tabs). Previously this overlay
        // omitted the array, so the user's Nevada bind never reached parcels[active]
        // and the autosave payload looked identical to the prior save → no diff →
        // no activity entry. Mirror it explicitly so every jurisdiction binding,
        // contact edit, or MLC update gets persisted AND audit-logged.
        jurisdictions: certificateData.jurisdictions || currentParcel.jurisdictions || []
      };
      
      // P0 FIX: If parcels array is empty but we have parcel data, create a parcel
      // This handles the case where the user fills out the form on a new order
      let syncedParcels;
      if (parcels.length === 0) {
        // No existing parcels - create one from flat data if we have parcel_apn
        if (normalizedParcelApn || taxSummary.length > 0) {
          syncedParcels = [updatedParcel];
        } else {
          // No data to create parcel from
          syncedParcels = [];
        }
      } else {
        // Build complete parcels array with updated active parcel
        syncedParcels = parcels.map((p, idx) => 
          idx === activeParcelIndex ? updatedParcel : p
        );
      }
      
      // Send complete data to parent
      onCertificateDataChange({
        ...certificateData,
        parcels: syncedParcels
      });
    }, 400);
    
    return () => {
      if (parentSyncTimerRef.current) clearTimeout(parentSyncTimerRef.current);
    };
  }, [certificateData, parcels, activeParcelIndex, onCertificateDataChange]);
  
  // PHASE 3D: Delete confirmation modal state
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [parcelToDelete, setParcelToDelete] = useState(null);

  // ── v7.5 (May 18, 2026) — New Parcel turn-OFF confirmation modal ──
  // When the user turns OFF the New Parcel toggle we now intercept
  // the click and show a 3-action confirmation so the workflow never
  // ends up in a silent mixed state where the standard tax flow is
  // active but New Parcel-generated `$0.00` + "Refer Note" rows are
  // still visible underneath.
  const [showNewParcelOffConfirm, setShowNewParcelOffConfirm] = useState(false);

  // Helper that disables New Parcel mode and updates BOTH
  // certificateData AND parcels[active] in the same tick (mirrors
  // the v7.3 dual-state pattern so parcels[] remains the source of
  // truth and the autosave debounce ships the correct value).
  //
  // When `revertAndClear` is true:
  //   - Restore base_tax / paid_amount / total_amount / balance_amount
  //     of every installment row to '' (empty string — the standard
  //     workflow default that lets users re-enter real values).
  //   - Revert any "Refer Note" pay_status back to "Due".
  //   - Clear auto_generated_notes so the standard tax-notes engine
  //     regenerates cleanly on the next render.
  // When `revertAndClear` is false:
  //   - Just flip the mode off — preserve all currently entered tax
  //     rows and values.
  const applyNewParcelOff = (revertAndClear) => {
    const wipeInstallment = (inst) => {
      const next = { ...inst };
      if (!revertAndClear) return next;
      // Reset the four financial amounts that the v7.3 turn-ON
      // handler seeded to 0. Use empty string so the standard
      // workflow shows placeholder hints rather than literal $0.
      next.base_tax = '';
      next.paid_amount = '';
      next.total_amount = '';
      next.balance_amount = '';
      const ps = (inst.pay_status || '').toLowerCase();
      if (ps === 'refer note') {
        next.pay_status = 'Due';
      }
      return next;
    };
    const wipeJurs = (jurs) => (jurs || []).map((jur) => ({
      ...jur,
      installments: (jur.installments || []).map(wipeInstallment),
    }));

    setCertificateData(prev => ({
      ...prev,
      is_new_parcel: false,
      new_parcel_first_year: '',
      new_parcel_first_due_date: '',
      jurisdictions: revertAndClear
        ? wipeJurs(prev.jurisdictions)
        : (prev.jurisdictions || []),
      // Clear stored auto_generated_notes so the standard notes
      // engine regenerates on next render. Without this, the
      // previously-baked New Parcel sentence would linger in the
      // certificate's notes block.
      ...(revertAndClear ? { auto_generated_notes: '' } : {}),
    }));

    setParcels(prevParcels => {
      const next = [...prevParcels];
      const idx = activeParcelIndex;
      if (!next[idx]) return prevParcels;
      next[idx] = {
        ...next[idx],
        is_new_parcel: false,
        new_parcel_first_year: '',
        new_parcel_first_due_date: '',
        jurisdictions: revertAndClear
          ? wipeJurs(next[idx].jurisdictions)
          : (next[idx].jurisdictions || []),
      };
      return next;
    });

    // Trigger autosave debounce immediately so the change persists
    // without requiring a second user action.
    setHasUnsavedChanges(true);
    setShowNewParcelOffConfirm(false);
  };
  
  // PHASE 5: Track parcels with validation errors
  const [parcelsWithErrors, setParcelsWithErrors] = useState(new Set());
  
  // P1 UX: Collapsible Jurisdiction Sections (Accordion style)
  // Default: First jurisdiction expanded, others collapsed
  const [expandedJurisdictions, setExpandedJurisdictions] = useState(new Set([0]));
  
  // Refs for jurisdiction cards (for scrolling when adding new)
  const jurisdictionCardRefs = useRef({});
  const activeFetchingRowsRef = useRef(new Set()); // Tracks rows with active sub-jurisdiction fetches
  

  // Stable callback for JurisdictionManager's onNotesChange — prevents infinite re-render loop
  // (inline arrow would be a new reference every render → useEffect in JurisdictionManager would re-fire)
  // Skip for MLS/hideTaxDetails orders — no tax notes needed
  const handleAutoNotesChange = useCallback((combinedNotes) => {
    if (hideTaxDetails) return; // MLS / External Portal: no tax notes
    setCertificateData(prev => ({
      ...prev,
      auto_generated_notes: combinedNotes
    }));
    // Skip during the post-load settle window — child's first emission
    // is part of initial hydration, not a user edit.
    if (dataLoadCompleteRef.current && !settleWindowActiveRef.current) {
      setHasUnsavedChanges(true);
    }
  }, [hideTaxDetails]);

  // Stable callback for JurisdictionManager's onJurisdictionsChange
  // (inline arrow creates new ref every render → destabilizes all downstream callbacks)
  const handleJurisdictionsChange = useCallback((newJurisdictions) => {
    setCertificateData(prev => ({
      ...prev,
      jurisdictions: newJurisdictions
    }));
    // Skip during the post-load settle window — child's first emission
    // is part of initial hydration, not a user edit.
    if (dataLoadCompleteRef.current && !settleWindowActiveRef.current) {
      setHasUnsavedChanges(true);
      // Immediately invalidate review state so UI reflects pending status
      // without waiting for the save round-trip. Backend confirms on next auto-save.
      window.dispatchEvent(new CustomEvent('taxSheetReviewUpdate', {
        detail: { status: 'pending', reset_reason: 'tax_data_changed' }
      }));
    }
  }, []);

  // Propagate jurisdiction structure changes (add/remove) to ALL other parcels in real-time.
  // When a jurisdiction is added to the active parcel, all other parcels get an empty copy.
  // When a jurisdiction is removed, it's removed from all other parcels too.
  const prevJurKeysRef = useRef('');
  useEffect(() => {
    if (parcels.length <= 1 || initialLoad) return;
    
    const activeJurs = certificateData.jurisdictions || [];
    const currentKeys = activeJurs
      .filter(j => j.jurisdiction_name && j.jurisdiction_type)
      .map(j => `${j.jurisdiction_name.toLowerCase().trim()}|||${j.jurisdiction_type.toLowerCase().trim()}`)
      .sort()
      .join('###');
    
    // Only react to structural changes (name+type additions or removals), not field edits
    if (currentKeys === prevJurKeysRef.current) return;
    const prevKeys = prevJurKeysRef.current;
    prevJurKeysRef.current = currentKeys;
    
    // Skip the very first evaluation (initial data load)
    if (!prevKeys) return;
    
    const currentKeySet = new Set(currentKeys.split('###').filter(Boolean));
    const prevKeySet = new Set(prevKeys.split('###').filter(Boolean));
    
    // Detect additions: keys in current but not in previous
    const added = [...currentKeySet].filter(k => !prevKeySet.has(k));
    // Detect removals: keys in previous but not in current
    const removed = [...prevKeySet].filter(k => !currentKeySet.has(k));
    
    if (added.length === 0 && removed.length === 0) return;
    
    let changed = false;
    const newParcels = parcels.map((parcel, idx) => {
      if (idx === activeParcelIndex) return parcel;
      let parcelJurs = [...(parcel.jurisdictions || [])];
      
      // Add missing jurisdictions
      if (added.length > 0) {
        const parcelJurKeys = new Set(
          parcelJurs.map(j => `${(j.jurisdiction_name || '').toLowerCase().trim()}|||${(j.jurisdiction_type || '').toLowerCase().trim()}`)
        );
        const toAdd = added.filter(k => !parcelJurKeys.has(k));
        toAdd.forEach(key => {
          const [name, type] = key.split('|||');
          const sourceJur = activeJurs.find(j =>
            j.jurisdiction_name?.toLowerCase().trim() === name &&
            j.jurisdiction_type?.toLowerCase().trim() === type
          );
          if (sourceJur) {
            changed = true;
            parcelJurs.push({
              id: crypto.randomUUID(),
              jurisdiction_name: sourceJur.jurisdiction_name || '',
              jurisdiction_type: sourceJur.jurisdiction_type || null,
              jurisdiction_id: sourceJur.jurisdiction_id || null,
              tax_collector_email: sourceJur.tax_collector_email || '',
              tax_collector_website: sourceJur.tax_collector_website || '',
              assessor_website: sourceJur.assessor_website || '',
              tax_collector_phone: sourceJur.tax_collector_phone || '',
              tax_collector_phone_ext: sourceJur.tax_collector_phone_ext || '',
              tax_collector_fax: sourceJur.tax_collector_fax || '',
              tax_collector_working_hours: sourceJur.tax_collector_working_hours || '',
              make_checks_payable_to: sourceJur.make_checks_payable_to || '',
              mlc_charges: sourceJur.mlc_charges,
              mlc_24_month_charges: sourceJur.mlc_24_month_charges,
              mlc_eta_days: sourceJur.mlc_eta_days,
              sase_required: sourceJur.sase_required || false,
              info_obtained_back: sourceJur.info_obtained_back,
              source: null,
              source_notes: '',
              source_evidence: null,
              collection_frequency: sourceJur.collection_frequency || 'annually',
              changed_by_user: false,
              has_delinquent: null,
              dlq_payoff_amount: null,
              dlq_payoff_manual: false,
              dlq_good_thru_date: null,
              delinquent_taxes_notes: '',
              installments: generateDefaultInstallments(sourceJur.collection_frequency || 'annually', '')
            });
          }
        });
      }
      
      // Remove deleted jurisdictions
      if (removed.length > 0) {
        const removedSet = new Set(removed);
        const before = parcelJurs.length;
        parcelJurs = parcelJurs.filter(j => {
          const key = `${(j.jurisdiction_name || '').toLowerCase().trim()}|||${(j.jurisdiction_type || '').toLowerCase().trim()}`;
          return !removedSet.has(key);
        });
        if (parcelJurs.length !== before) changed = true;
      }
      
      return { ...parcel, jurisdictions: parcelJurs };
    });
    
    if (changed) {
      setParcels(newParcels);
      console.log(`[JUR-SYNC] Propagated jurisdiction structure: +${added.length} added, -${removed.length} removed across ${parcels.length - 1} other parcel(s)`);
    }
  }, [
    JSON.stringify(
      (certificateData.jurisdictions || [])
        .filter(j => j.jurisdiction_name && j.jurisdiction_type)
        .map(j => `${j.jurisdiction_name.toLowerCase().trim()}|||${j.jurisdiction_type.toLowerCase().trim()}`)
        .sort()
    ),
    parcels.length, activeParcelIndex, initialLoad
  ]);


  // Toggle jurisdiction expand/collapse
  const toggleJurisdictionExpand = useCallback((index) => {
    setExpandedJurisdictions(prev => {
      const newSet = new Set(prev);
      if (newSet.has(index)) {
        newSet.delete(index);
      } else {
        newSet.add(index);
      }
      return newSet;
    });
  }, []);
  
  // Expand a specific jurisdiction and optionally scroll to it
  const expandAndScrollToJurisdiction = useCallback((index) => {
    setExpandedJurisdictions(prev => {
      const newSet = new Set(prev);
      newSet.add(index);
      return newSet;
    });
    // Scroll into view after a short delay to allow render
    setTimeout(() => {
      const cardRef = jurisdictionCardRefs.current[index];
      if (cardRef) {
        cardRef.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    }, 100);
  }, []);
  
  // Check if a jurisdiction has validation errors (for collapsed state indicator)
  const getJurisdictionValidationErrors = useCallback((row, index) => {
    const errors = [];
    
    // DEBUG: Log row data for troubleshooting (can be removed after fix is confirmed)
    console.log(`[VALIDATION DEBUG] Jurisdiction #${index + 1} validation:`, {
      jurisdiction_type: row.jurisdiction_type,
      jurisdiction: row.jurisdiction,
      tax_year: row.tax_year,
      installment: row.installment,
      pay_status: row.pay_status,
      mailing_issue_date: row.mailing_issue_date,
      mailing_date: row.mailing_date,
      issue_date: row.issue_date,
      base_tax: row.base_tax,
      due_date: row.due_date,
      dlq_date: row.dlq_date
    });
    
    // Required field checks
    if (!row.jurisdiction_type) errors.push('Jurisdiction Type');
    if (!row.jurisdiction?.trim()) errors.push('Jurisdiction Name');
    if (!row.tax_year?.trim()) errors.push('Tax Year');
    if (!row.installment) errors.push('Installment');
    if (!row.pay_status) errors.push('Pay Status');
    // P0 FIX: Check mailing_issue_date (the actual field used in the form)
    // Also check legacy fields mailing_date/issue_date for backward compatibility
    if (!row.mailing_issue_date && !row.mailing_date && !row.issue_date) errors.push('Mailing/Issue Date');
    if (!row.base_tax && row.base_tax !== 0) errors.push('Base Tax');
    
    // Due date required if status is Due or Partially Paid
    const needsDueDate = ['Due', 'Partially Paid'].includes(row.pay_status);
    if (needsDueDate && !row.due_date) errors.push('Due Date');
    
    // Delinquent fields required if status is Delinquent or Partially Dlq
    const needsDelinquent = ['Delinquent', 'Partially Dlq'].includes(row.pay_status);
    if (needsDelinquent && !row.dlq_date) errors.push('Delinquent Date');
    
    // DEBUG: Log validation result
    console.log(`[VALIDATION DEBUG] Jurisdiction #${index + 1} errors:`, errors.length > 0 ? errors : 'NONE - ALL VALID');
    
    return errors;
  }, []);
  
  // Jurisdiction directory integration (Phase 3)
  const [jurisdictions, setJurisdictions] = useState([]);
  const [jurisdictionsLoading, setJurisdictionsLoading] = useState(false);
  
  // Phase 4: Property-based jurisdiction suggestion
  const [suggestionBanner, setSuggestionBanner] = useState({ type: 'none', message: null, jurisdiction: null });
  
  // ============================================================================
  // MULTI-JURISDICTION ROW-SPECIFIC STATE
  // Each jurisdiction row has its own isolated UI state to prevent cross-row leakage
  // State is keyed by row index for efficient lookup
  // ============================================================================
  const [rowJurisdictionState, setRowJurisdictionState] = useState({});
  
  // Helper to get row-specific state with defaults
  const getRowState = useCallback((rowIndex) => {
    return rowJurisdictionState[rowIndex] || {
      subJurisdictions: [],
      subJurisdictionsLoading: false,
      selectedCountyForSubJurisdiction: null,
      showSubJurisdictionSelector: false,
      jurisdictionMatchError: null
    };
  }, [rowJurisdictionState]);
  
  // Helper to update row-specific state
  // IMPORTANT: Uses prev state directly to avoid stale closure issues
  const setRowState = useCallback((rowIndex, updates) => {
    setRowJurisdictionState(prev => {
      const currentRowState = prev[rowIndex] || {
        subJurisdictions: [],
        subJurisdictionsLoading: false,
        selectedCountyForSubJurisdiction: null,
        showSubJurisdictionSelector: false,
        jurisdictionMatchError: null
      };
      return {
        ...prev,
        [rowIndex]: {
          ...currentRowState,
          ...updates
        }
      };
    });
  }, []);
  
  // Helper to clear row state when row is removed
  const clearRowState = useCallback((rowIndex) => {
    setRowJurisdictionState(prev => {
      const newState = { ...prev };
      delete newState[rowIndex];
      // Re-index states for rows after the deleted one
      const reindexed = {};
      Object.keys(newState).forEach(key => {
        const idx = parseInt(key);
        if (idx > rowIndex) {
          reindexed[idx - 1] = newState[key];
        } else {
          reindexed[idx] = newState[key];
        }
      });
      return reindexed;
    });
  }, []);
  
  // May 26, 2026 — Fuzzy "Did you mean?" handler.
  // Called when user clicks a suggestion chip. Binds the suggested
  // jurisdiction (full contact block) to the indicated row OR nested
  // sub-jurisdiction slot. No silent auto-bind — only triggered by
  // explicit click.
  const handleAcceptFuzzySuggestion = useCallback((suggestion, ctx) => {
    if (!suggestion || !ctx) return;
    const isNested = ctx.jurIndex !== undefined && ctx.jurIndex !== null;
    const targetType = (suggestion.jurisdiction_type || ctx.selectedType || 'county').toLowerCase();

    // P0 FIX (Feb 2026 — Activity Log "PLACER COUNTY" bug):
    // Build the contact block once and apply to BOTH certificateData and
    // parcels[active] in the same tick. Previously this handler only
    // mutated certificateData; the parcels[] state retained the stale
    // jurisdiction_id which then survived adaptFrontendToBackend() and
    // produced a Frankenstein autosave payload, causing the activity
    // log to resolve the stale ID and print the old county name.
    //
    // changed_by_user is set to TRUE because this code path is an
    // explicit user-confirmation click on a suggestion chip — no
    // subsequent auto-detect effect should re-overwrite the binding.
    const contactBlock = {
      jurisdiction_type: targetType,
      jurisdiction_name: suggestion.jurisdiction_name,
      jurisdiction_id: suggestion.id,
      make_checks_payable_to: suggestion.tax_collector_name || '',
      tax_collector_name: suggestion.tax_collector_name || '',
      tax_collector_phone: suggestion.tax_collector_phone || '',
      tax_collector_phone_ext: suggestion.tax_collector_phone_ext || '',
      tax_collector_email: suggestion.tax_collector_email || '',
      tax_collector_fax: suggestion.tax_collector_fax || '',
      tax_collector_website: suggestion.tax_collector_website || '',
      assessor_website: suggestion.assessor_website || '',
      tax_collector_working_hours: suggestion.tax_collector_working_hours || '',
      tax_collector_mailing_address: suggestion.tax_collector_mailing_address || '',
      tax_collector_physical_address: suggestion.tax_collector_physical_address || '',
      mlc_charges: suggestion.mlc_charges ?? null,
      mlc_24_month_charges: suggestion.mlc_24_month_charges ?? null,
      mlc_eta_days: suggestion.mlc_eta_days ?? null,
      sase_required: !!suggestion.sase_required,
      info_obtained_back: suggestion.info_obtained_back ?? null,
      changed_by_user: true,
    };

    setCertificateData(prev => {
      if (isNested) {
        return {
          ...prev,
          jurisdictions: prev.jurisdictions.map((jur, idx) =>
            idx === ctx.jurIndex ? { ...jur, ...contactBlock } : jur
          )
        };
      }
      // Top-level (current_tax_summary row) bind
      const isMultiJurisdiction = prev.current_tax_summary?.length > 1;
      const updatedData = {
        ...prev,
        current_tax_summary: prev.current_tax_summary?.length > 0
          ? prev.current_tax_summary.map((row, idx) =>
              idx === ctx.rowIndex ? {
                ...row,
                jurisdiction_type: targetType,
                jurisdiction_name: suggestion.jurisdiction_name,
                jurisdiction_id: suggestion.id,
              } : row
            )
          : prev.current_tax_summary
      };
      if (!isMultiJurisdiction || ctx.rowIndex === 0) {
        Object.assign(updatedData, contactBlock);
      }
      return updatedData;
    });

    // P0 FIX: Mirror the bind into parcels[activeParcelIndex] so the
    // autosave serializer (adaptFrontendToBackend) does not ship a
    // stale jurisdiction_id from the parcel record. Without this the
    // activity log would resolve the stale UUID and surface the wrong
    // county name (e.g. "Placer County" after binding "Nevada County").
    if (!isNested) {
      setParcels(prev => {
        if (!prev || prev.length === 0) return prev;
        const idx = activeParcelIndex;
        if (idx < 0 || idx >= prev.length) return prev;
        const target = prev[idx] || {};
        const isMultiJurisdiction = (target.current_tax_summary?.length || 0) > 1;
        const updatedTaxSummary = (target.current_tax_summary || []).length > 0
          ? target.current_tax_summary.map((row, rIdx) =>
              rIdx === ctx.rowIndex ? {
                ...row,
                jurisdiction_type: targetType,
                jurisdiction_name: suggestion.jurisdiction_name,
                jurisdiction_id: suggestion.id,
              } : row
            )
          : target.current_tax_summary;
        const updatedParcel = {
          ...target,
          current_tax_summary: updatedTaxSummary,
        };
        if (!isMultiJurisdiction || ctx.rowIndex === 0) {
          Object.assign(updatedParcel, contactBlock);
        }
        return prev.map((p, i) => (i === idx ? updatedParcel : p));
      });
    } else {
      // Nested sub-jurisdiction bind — mirror into parcels[active].jurisdictions[jurIndex]
      setParcels(prev => {
        if (!prev || prev.length === 0) return prev;
        const idx = activeParcelIndex;
        if (idx < 0 || idx >= prev.length) return prev;
        const target = prev[idx] || {};
        const existing = target.jurisdictions || [];
        const updatedJurs = existing.map((jur, jIdx) =>
          jIdx === ctx.jurIndex ? { ...jur, ...contactBlock } : jur
        );
        return prev.map((p, i) =>
          i === idx ? { ...target, jurisdictions: updatedJurs } : p
        );
      });
    }

    // Clear the warning and suggestions for that row
    const rowKey = isNested ? `nested_${ctx.jurIndex}` : ctx.rowIndex;
    setRowState(rowKey, {
      jurisdictionMatchError: null,
      fuzzySuggestions: [],
      fuzzyQueryContext: null,
    });
    toast.success(`Matched: ${suggestion.jurisdiction_name}`);
  }, [setRowState, activeParcelIndex]);

  // Fetch active jurisdictions on mount
  useEffect(() => {
    const fetchJurisdictions = async () => {
      setJurisdictionsLoading(true);
      try {
        const token = localStorage.getItem('token');
        const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/jurisdictions?active_only=true&page_size=1000`, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });
        
        if (response.ok) {
          const data = await response.json();
          setJurisdictions(data.jurisdictions || []);
        }
      } catch (err) {
        console.error('Error fetching jurisdictions:', err);
      } finally {
        setJurisdictionsLoading(false);
      }
    };
    
    fetchJurisdictions();
  }, []);
  
  // Phase 4: Auto-detect jurisdiction based on property county
  useEffect(() => {
    // Only run when jurisdictions are loaded and we have property county
    if (jurisdictionsLoading || jurisdictions.length === 0) {
      return;
    }
    
    // IMPORTANT: If user has already selected a jurisdiction type, don't show suggestion
    if (certificateData.jurisdiction_type) {
      setSuggestionBanner({ type: 'none', message: null, jurisdiction: null });
      return;
    }
    
    const propertyCounty = orderData?.property_county || orderData?.county || certificateData?.county;
    
    if (!propertyCounty) {
      // No county - skip silently
      setSuggestionBanner({ type: 'none', message: null, jurisdiction: null });
      return;
    }
    
    // P0 STATE-COLLISION FIX (May 5, 2026): pass property state so
    // `findJurisdictionByCounty` can scope to the order's state. Without
    // this, ambiguous names like "Knox County" silently resolved to the
    // first match in the all-states array (e.g., TN instead of OH).
    const propertyState = orderData?.property_state
      || orderData?.state
      || certificateData?.state
      || '';

    // Find matching jurisdiction (state-scoped)
    const suggestedJurisdiction = findJurisdictionByCounty(
      propertyCounty,
      jurisdictions,
      propertyState
    );
    
    // Get banner state based on current selection and suggestion
    const bannerState = getSuggestionBannerState({
      suggestedJurisdiction,
      propertyCounty,
      currentJurisdictionId: certificateData.jurisdiction_id,
      jurisdictions
    });
    
    setSuggestionBanner(bannerState);
  }, [jurisdictions, jurisdictionsLoading, orderData, certificateData.jurisdiction_id, certificateData.jurisdiction_type, certificateData.state]);
  
  // Handle jurisdiction selection (accepts either ID string or jurisdiction object)
  const handleJurisdictionSelect = (jurisdictionIdOrObject, rowIndex = 0) => {
    let selectedJurisdiction;
    
    // Support both string ID and object
    if (typeof jurisdictionIdOrObject === 'string') {
      selectedJurisdiction = jurisdictions.find(j => j.id === jurisdictionIdOrObject);
    } else {
      selectedJurisdiction = jurisdictionIdOrObject;
    }
    
    if (selectedJurisdiction) {
      // Build "Make Checks Payable To" — ONLY the payable name (tax_collector_name)
      // Address, phone, fax are stored in their own dedicated fields and must NOT be concatenated here
      const jurisdictionName = (selectedJurisdiction.jurisdiction_name || selectedJurisdiction.name || '').trim();
      const taxCollectorName = (selectedJurisdiction.tax_collector_name || '').trim();
      // PJR-001 P2: the jurisdiction's saved payee wins over the collector name (no assignment on this path).
      const makeChecksPayableTo = (selectedJurisdiction.make_checks_payable_to || '').trim() || taxCollectorName || jurisdictionName;
      const jurisdictionType = (selectedJurisdiction.jurisdiction_type || selectedJurisdiction.type || '').toLowerCase();
      
      setCertificateData(prev => {
        const isMultiJurisdiction = prev.current_tax_summary?.length > 1;
        
        // Build row-specific jurisdiction data
        const rowJurisdictionData = {
          jurisdiction_type: jurisdictionType,
          jurisdiction: selectedJurisdiction.jurisdiction_name || '',
          jurisdiction_id: selectedJurisdiction.id,
          make_checks_payable_to: makeChecksPayableTo,
          tax_collector_phone: selectedJurisdiction.tax_collector_phone || '',
          tax_collector_phone_ext: selectedJurisdiction.tax_collector_phone_ext || '',
          tax_collector_email: selectedJurisdiction.tax_collector_email || '',
          tax_collector_website: selectedJurisdiction.tax_collector_website || '',
          assessor_website: selectedJurisdiction.assessor_website || '',
          tax_collector_working_hours: selectedJurisdiction.tax_collector_working_hours || '',
          mlc_charges: selectedJurisdiction.mlc_charges || null,
          mlc_24_month_charges: selectedJurisdiction.mlc_24_month_charges || null,
          mlc_eta_days: selectedJurisdiction.mlc_eta_days || null,
          sase_required: selectedJurisdiction.sase_required || false,
          info_obtained_back: selectedJurisdiction.info_obtained_back || null,
          changed_by_user: false
        };
        
        // Start with base update (always update current_tax_summary row)
        const updatedData = {
          ...prev,
          current_tax_summary: prev.current_tax_summary?.length > 0 
            ? prev.current_tax_summary.map((row, idx) => 
                idx === rowIndex ? { ...row, ...rowJurisdictionData } : row
              )
            : prev.current_tax_summary
        };
        
        // MULTI-JURISDICTION FIX: Only update global fields for single-jurisdiction (row 0)
        // or for backward compatibility when there's only one row
        if (!isMultiJurisdiction || rowIndex === 0) {
          updatedData.jurisdiction_id = selectedJurisdiction.id;
          updatedData.jurisdiction_type = jurisdictionType;
          updatedData.make_checks_payable_to = makeChecksPayableTo;
          updatedData.changed_by_user = false;
          updatedData.tax_collector_phone = selectedJurisdiction.tax_collector_phone || '';
          updatedData.tax_collector_phone_ext = selectedJurisdiction.tax_collector_phone_ext || '';
          updatedData.mlc_charges = selectedJurisdiction.mlc_charges || null;
          updatedData.mlc_24_month_charges = selectedJurisdiction.mlc_24_month_charges || null;
          updatedData.mlc_eta_days = selectedJurisdiction.mlc_eta_days || null;
          updatedData.sase_required = selectedJurisdiction.sase_required || false;
          updatedData.info_obtained_back = selectedJurisdiction.info_obtained_back || null;
          updatedData.tax_collector_email = selectedJurisdiction.tax_collector_email || '';
          updatedData.tax_collector_website = selectedJurisdiction.tax_collector_website || '';
          updatedData.assessor_website = selectedJurisdiction.assessor_website || '';
          updatedData.tax_collector_working_hours = selectedJurisdiction.tax_collector_working_hours || '';
          updatedData.notes = selectedJurisdiction.notes || '';
        }
        
        return updatedData;
      });
      
      // Clear suggestion banner after applying
      setSuggestionBanner({ type: 'none', message: null, jurisdiction: null });
    }
  };
  
  // Phase 4: Handle applying jurisdiction suggestion
  const handleApplySuggestion = (jurisdiction) => {
    handleJurisdictionSelect(jurisdiction);
    const jurisdictionName = jurisdiction.jurisdiction_name || jurisdiction.name || 'Unknown';
    toast.success(`Applied suggestion: ${jurisdictionName}`);
  };

  // Feb 2026 P0 (PA-RESOLVER-RESELECT-OVERWRITE) — Resolve the formal
  // collector-assignment for a (jurisdiction, type) pair AFTER the
  // jurisdiction match completes. Returns one of:
  //   • { source: 'assignment', collector, confidence } — caller MUST
  //     use these payee fields instead of legacy directory columns.
  //   • { source: 'legacy_jurisdiction', collector, confidence } —
  //     resolver itself fell back to legacy, caller can use either
  //     the resolver collector or the matched jurisdiction's legacy
  //     fields (they should be equivalent).
  //   • null — non-PA jurisdiction, no jurisdiction id, or network
  //     error. Caller falls back to legacy fields (legacy behaviour).
  //
  // Why this is necessary: `matchedJurisdiction` is built from the
  // jurisdictions collection, whose `tax_collector_*` columns are
  // the *legacy* directory snapshot. They never reflect an active
  // assignment row. Both callers below previously spread these
  // columns straight into `setCertificateData`, silently clobbering
  // any HIGH-confidence resolver result that the initial-load path
  // had already written.
  const resolveFormalCollectorForJurisdiction = async (matchedJurisdiction, selectedType) => {
    try {
      if (!matchedJurisdiction || !matchedJurisdiction.id) return null;
      const state = String(matchedJurisdiction.state || '').toUpperCase();
      if (state !== 'PA') return null;
      const taxCategoryCode = defaultCategoryForPA(selectedType, 'current') || null;
      const refKey = `typeselect|${matchedJurisdiction.id}|current|${taxCategoryCode || ''}`;
      const res = await resolveCollectorGuidance([
        {
          ref_id: refKey,
          jurisdiction_id: matchedJurisdiction.id,
          tax_period: 'current',
          tax_category_code: taxCategoryCode,
        },
      ]);
      const g = (res?.guidance || [])[0] || null;
      if (!g || !g.collector) return null;
      return g; // { source, confidence, collector }
    } catch (err) {
      // Non-fatal — caller will use legacy directory data as before.
      console.warn('[TaxCertificatePanel] Formal collector resolve failed; using legacy directory data.', err);
      return null;
    }
  };

  // Build the per-row contact field block to feed into setCertificateData
  // from whichever source won. When the resolver returns an `assignment`
  // source we PREFER its collector fields and only fall back to legacy
  // directory columns per-field when the assignment's field is empty.
  const buildContactFieldsFromGuidance = (matchedJurisdiction, guidance) => {
    // PJR-001 P2: jurisdiction-level payee first, then collector name, then jurisdiction name (legacy, no assignment).
    const legacyMakeChecks = (matchedJurisdiction.make_checks_payable_to || '').trim() || (matchedJurisdiction.tax_collector_name || '').trim();
    const legacyFallback = legacyMakeChecks || (matchedJurisdiction.jurisdiction_name || matchedJurisdiction.name || '').trim();

    // Only trust the resolver when it landed on a real ASSIGNMENT.
    // Legacy-source guidance just re-states what's already on
    // `matchedJurisdiction`, so there's no value in preferring it.
    const useAssignment = guidance && guidance.source === 'assignment' && guidance.collector;

    if (useAssignment) {
      const c = guidance.collector;
      return {
        // Phase A.7.18 (Feb 28, 2026) — single source of truth.
        // Write `current_collector_id` onto the cert snapshot so
        // Mail-Away grouping, PDFs, and the Fulfillment Contact
        // Info display all resolve through the SAME collector. The
        // backend's priority-first resolver decided the winner;
        // capturing its id here means the choice is durable
        // (a later category-only re-query can't overturn it).
        current_collector_id:   c.id || null,
        current_collector_name: c.display_name || c.legal_name || c.name || null,
        make_checks_payable_to: (c.make_checks_payable_to || c.display_name || legacyFallback).trim(),
        tax_collector_name:     c.display_name || matchedJurisdiction.tax_collector_name || '',
        tax_collector_phone:    c.phone || matchedJurisdiction.tax_collector_phone || '',
        tax_collector_phone_ext: c.phone_ext || matchedJurisdiction.tax_collector_phone_ext || '',
        tax_collector_fax:      c.fax || matchedJurisdiction.tax_collector_fax || '',
        tax_collector_email:    c.email || matchedJurisdiction.tax_collector_email || '',
        tax_collector_website:  c.website || matchedJurisdiction.tax_collector_website || '',
        tax_collector_working_hours:    c.working_hours    || matchedJurisdiction.tax_collector_working_hours    || '',
        tax_collector_mailing_address:  c.mailing_address  || matchedJurisdiction.tax_collector_mailing_address  || '',
        tax_collector_physical_address: c.physical_address || matchedJurisdiction.tax_collector_physical_address || '',
        // Provenance — surfaces in audit logs / dev tools.
        _collector_source: 'assignment',
        _collector_confidence: guidance.confidence || null,
      };
    }
    return {
      // Phase A.7.18 — when we fell through to the legacy directory
      // path (no formal assignment), make sure we DO NOT carry a
      // stale `current_collector_id` forward. Returning null here
      // means the caller's onBatchUpdate clears any previously-set
      // id, keeping the snapshot consistent with the displayed
      // legacy fields.
      current_collector_id:   null,
      current_collector_name: null,
      make_checks_payable_to: legacyFallback,
      tax_collector_name:     matchedJurisdiction.tax_collector_name || '',
      tax_collector_phone:    matchedJurisdiction.tax_collector_phone || '',
      tax_collector_phone_ext: matchedJurisdiction.tax_collector_phone_ext || '',
      tax_collector_fax:      matchedJurisdiction.tax_collector_fax || '',
      tax_collector_email:    matchedJurisdiction.tax_collector_email || '',
      tax_collector_website:  matchedJurisdiction.tax_collector_website || '',
      tax_collector_working_hours:    matchedJurisdiction.tax_collector_working_hours    || '',
      tax_collector_mailing_address:  matchedJurisdiction.tax_collector_mailing_address  || '',
      tax_collector_physical_address: matchedJurisdiction.tax_collector_physical_address || '',
      _collector_source: 'legacy_jurisdiction',
      _collector_confidence: guidance?.confidence || 'low',
    };
  };
  
  // Smart jurisdiction type selection - matches based on property location
  // PHASE 5: Updated to use state filtering and require sub-jurisdiction selection
  // MULTI-JURISDICTION: Uses row-specific state for complete isolation
  const handleJurisdictionTypeSelect = async (selectedType, rowIndex = 0) => {
    // Clear row-specific state for this row
    setRowState(rowIndex, {
      jurisdictionMatchError: null,
      showSubJurisdictionSelector: false,
      subJurisdictions: [],
      subJurisdictionsLoading: false,
      selectedCountyForSubJurisdiction: null
    });
    
    // Get property location data
    const propertyState = orderData?.property_state || orderData?.state || '';
    const propertyCounty = orderData?.property_county || orderData?.county || certificateData?.county;
    const propertyCity = orderData?.property_city || orderData?.city || certificateData?.city;
    
    // Normalize type for matching.
    // BUG FIX (Jurisdiction School Lookup Mismatch, May 27 2026):
    // The dropdown's "School" value must map to the directory's
    // canonical type "school_district". Without this, the type filter
    // query `?jurisdiction_type=school` returns 0 candidates while
    // active records exist under `school_district`. Doing the rename
    // here means every downstream query, save-path mapping, and
    // classification reference uses the canonical value.
    let normalizedType = selectedType.toLowerCase();
    if (normalizedType === 'school') normalizedType = 'school_district';
    
    // BUG FIX (Borough/Parish/Special District selection skipped):
    // Classification using canonical lists imported from JurisdictionSelector —
    // keeps every consumer aligned on what's county-level vs what needs a
    // sub-jurisdiction selector.
    //   - Parish (LA) and Borough (AK ONLY) are county-level equivalents,
    //     matched directly against the directory.
    //   - Borough in PA / NJ / CT — and any non-AK state — is a
    //     sub-jurisdiction (municipality). Phase A.7.5 (Feb 7, 2026).
    //   - Special District is a sub-jurisdiction — user picks one from the
    //     state-wide list (no parent-county prerequisite).
    const classification = getEffectiveJurisdictionClassification(normalizedType, propertyState);
    const isCountyLevelType = classification === 'county_level';
    const isSubJurisdictionType = classification === 'sub_jurisdiction' || classification === 'special_district';
    const isSpecialDistrict = classification === 'special_district';
    
    // AUTO-POPULATE: Generate jurisdiction name based on type.
    // `propertyState` is threaded so the borough auto-stamp is
    // suppressed for PA / NJ / CT (mailing city ≠ taxing jurisdiction).
    const autoPopulatedJurisdictionName = generateJurisdictionName(selectedType, propertyCounty, propertyCity, propertyState);
    
    if (isCountyLevelType) {
      // COUNTY / PARISH / BOROUGH: Direct matching with STATE filter (existing behavior, improved)
      //
      // BUG FIX (Feb 28, 2026 — NJ Borough mismatch, prod): mirror
      // the search-seed derivation from the nested handler so this
      // legacy single-jurisdiction path is also bug-free. "Borough"
      // is county-level in NY/AK but municipal in NJ/PA/CT — see
      // detailed comment in handleNestedJurisdictionTypeSelect.
      //
      // Feb 28, 2026 (AK-BOROUGH-RESOLVE) — INVERT the precedence for
      // COUNTY-LEVEL borough states (Alaska only). The borough IS
      // the county-equivalent in AK (e.g. Palmer city sits inside
      // MATANUSKA-SUSITNA BOROUGH); seeding from property_city
      // produces an unfindable "Palmer" while seeding from
      // property_county finds the directory entry.
      const _seedFromCounty = propertyCounty || '';
      const _seedFromCity = propertyCity || '';
      const _akCountyLevelBorough =
        normalizedType === 'borough' && !isMunicipalBoroughState(propertyState);
      const searchSeed = normalizedType === 'borough'
        ? (_akCountyLevelBorough
            ? (_seedFromCounty || _seedFromCity)
            : (_seedFromCity || _seedFromCounty))
        : _seedFromCounty;
      if (!searchSeed) {
        setRowState(rowIndex, { jurisdictionMatchError: 'Property location not specified. Cannot auto-match jurisdiction.' });
        setCertificateData(prev => {
          const isMultiJurisdiction = prev.current_tax_summary?.length > 1;
          const updatedData = {
            ...prev,
            current_tax_summary: prev.current_tax_summary?.length > 0
              ? prev.current_tax_summary.map((row, idx) =>
                  idx === rowIndex ? { ...row, jurisdiction_type: selectedType } : row
                )
              : prev.current_tax_summary
          };
          // Only update type for single-jurisdiction mode — preserve other fields
          if (!isMultiJurisdiction || rowIndex === 0) {
            updatedData.jurisdiction_type = selectedType;
          }
          return updatedData;
        });
        toast.error('Property location not specified. Cannot auto-match jurisdiction.');
        return;
      }
      
      const normalizedCounty = searchSeed.trim().toLowerCase();
      const normalizedState = propertyState.trim().toUpperCase();
      
      // Normalize for matching: strip "county", "parish", "borough" suffixes
      const stripSuffix = (s) => (s || '').toLowerCase().replace(/\b(county|parish|borough)\b/g, '').trim();
      const strippedCounty = stripSuffix(normalizedCounty);
      // Canonical form — handles punctuation, apostrophes, St./Saint, Mc/Mc*,
      // Mt./Mount, etc. (`St. Mary's County` vs `ST MARYS COUNTY` vs
      // `Saint Marys County` all map to `SAINT MARYS`).
      const canonicalCounty = canonicalizeJurisdictionName(searchSeed);
      
      // Match county with STATE filter — deterministic two-pass matcher
      // See matching bug-fix doc in handleNestedJurisdictionTypeSelect:
      // the previous 3-strategy matcher used raw `.includes()` fallbacks
      // which silently collapsed longer county names (e.g. "Lauderdale")
      // into shorter alphabetical predecessors (e.g. "Dale"). Replaced with
      // exact stripped equality, then whole-word regex — no substring.
      //
      // BUG FIX (NY type-strict lookup Feb 22, 2026): filter by the EXACT
      // selected jurisdiction_type, not the county-level equivalence class.
      // `COUNTY_LEVEL_TYPES` decides which code path we take (direct match vs
      // sub-jurisdiction selector); it is NOT a lookup-widening filter.
      // Previously, selecting Parish in NY returned a Borough record because
      // the candidate pool accepted any county/parish/borough row.
      const countyCandidates = jurisdictions.filter(j => {
        const jType = (j.jurisdiction_type || j.type || '').toLowerCase();
        const jState = (j.state || '').toUpperCase();
        if (jType !== normalizedType) return false;
        if (normalizedState && jState && jState !== normalizedState) return false;
        return true;
      });
      
      // Pass 1a: canonical equality (most robust — format-tolerant).
      let matchedJurisdiction = canonicalCounty
        ? countyCandidates.find(j => {
            const jCanonical = j.canonical_name || canonicalizeJurisdictionName(j.jurisdiction_name || j.name);
            return jCanonical && jCanonical === canonicalCounty;
          })
        : null;
      
      // Pass 1b: exact stripped equality (legacy fallback)
      if (!matchedJurisdiction) {
        matchedJurisdiction = countyCandidates.find(j => {
          const jName = (j.jurisdiction_name || j.name || '').toLowerCase();
          return stripSuffix(jName) === strippedCounty;
        });
      }
      
      // Pass 2: whole-word match (safe variant of the old substring strategy)
      if (!matchedJurisdiction && strippedCounty) {
        const escapeRegex = (s) => s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
        const propertyRe = new RegExp(`\\b${escapeRegex(strippedCounty)}\\b`);
        matchedJurisdiction = countyCandidates.find(j => {
          const jStripped = stripSuffix((j.jurisdiction_name || j.name || '').toLowerCase());
          if (!jStripped) return false;
          const dirRe = new RegExp(`\\b${escapeRegex(jStripped)}\\b`);
          return propertyRe.test(jStripped) || dirRe.test(strippedCounty);
        });
      }
      
      if (matchedJurisdiction) {
        // Feb 2026 P0 (PA-RESOLVER-RESELECT-OVERWRITE) — Run the
        // formal-assignment resolver BEFORE we touch certificateData.
        // For PA jurisdictions with an active County/Municipal/School
        // assignment this returns the resolved collector with HIGH
        // confidence; we then merge those payee fields into the row
        // instead of the legacy `tax_collector_*` columns. Non-PA
        // (or no-assignment) flows are unaffected — the helper
        // returns null and we use legacy fields exactly as before.
        const guidance = await resolveFormalCollectorForJurisdiction(matchedJurisdiction, selectedType);
        const contactFields = buildContactFieldsFromGuidance(matchedJurisdiction, guidance);

        // CRITICAL FIX: For multi-jurisdiction scenarios, only update the row-specific state
        // to prevent "state bleed" between jurisdiction rows
        setCertificateData(prev => {
          const isMultiJurisdiction = prev.current_tax_summary?.length > 1;
          
          // Build row-specific jurisdiction data
          const rowJurisdictionData = {
            jurisdiction_type: selectedType,
            jurisdiction: autoPopulatedJurisdictionName || matchedJurisdiction.jurisdiction_name,
            jurisdiction_id: matchedJurisdiction.id,
            ...contactFields,
            assessor_website: matchedJurisdiction.assessor_website || '',
            mlc_charges: matchedJurisdiction.mlc_charges || null,
            mlc_24_month_charges: matchedJurisdiction.mlc_24_month_charges || null,
            mlc_eta_days: matchedJurisdiction.mlc_eta_days || null,
            sase_required: matchedJurisdiction.sase_required || false,
            info_obtained_back: matchedJurisdiction.info_obtained_back || null,
            changed_by_user: false
          };
          
          // Start with base update (always update current_tax_summary row)
          const updatedData = {
            ...prev,
            current_tax_summary: prev.current_tax_summary?.length > 0 
              ? prev.current_tax_summary.map((row, idx) => 
                  idx === rowIndex ? { ...row, ...rowJurisdictionData } : row
                )
              : prev.current_tax_summary
          };
          
          // MULTI-JURISDICTION FIX: Only update global fields for single-jurisdiction (row 0)
          // or for backward compatibility when there's only one row
          if (!isMultiJurisdiction || rowIndex === 0) {
            updatedData.jurisdiction_id = matchedJurisdiction.id;
            updatedData.jurisdiction_type = selectedType;
            updatedData.changed_by_user = false;
            // Resolver-aware payee + contact fields (replaces the
            // legacy-only spread that used to live here).
            Object.assign(updatedData, contactFields);
            updatedData.assessor_website = matchedJurisdiction.assessor_website || '';
            updatedData.mlc_charges = matchedJurisdiction.mlc_charges || null;
            updatedData.mlc_24_month_charges = matchedJurisdiction.mlc_24_month_charges || null;
            updatedData.mlc_eta_days = matchedJurisdiction.mlc_eta_days || null;
            updatedData.sase_required = matchedJurisdiction.sase_required || false;
            updatedData.info_obtained_back = matchedJurisdiction.info_obtained_back || null;
            updatedData.notes = matchedJurisdiction.notes || '';
          }
          
          return updatedData;
        });
        
        // Clear suggestion banner after applying
        setSuggestionBanner({ type: 'none', message: null, jurisdiction: null });
        // Resolver-aware match toast — distinguish formal-assignment
        // wins from legacy fallback so the operator can immediately
        // see WHY the payee changed (or didn't).
        if (contactFields._collector_source === 'assignment') {
          toast.success(
            `Matched: ${matchedJurisdiction.jurisdiction_name || 'Unknown'} — Payee: ${contactFields.make_checks_payable_to}`,
            { description: `Resolved via formal assignment (confidence: ${contactFields._collector_confidence || 'n/a'}).` },
          );
        } else {
          toast.success(`Matched: ${matchedJurisdiction.jurisdiction_name || 'Unknown'}`);
        }
      } else {
        const typeLabel = selectedType.charAt(0).toUpperCase() + selectedType.slice(1);

        // Fuzzy "Did you mean?" fallback — catches typos like
        // "Naveda" → "NEVADA COUNTY". State + type scoped. User
        // explicitly clicks to accept; no silent auto-bind.
        let fuzzySuggestions = [];
        try {
          const sToken = localStorage.getItem('token');
          const fuzzyResp = await fetch(
            `${process.env.REACT_APP_BACKEND_URL}/api/jurisdictions/search/suggest`
              + `?name=${encodeURIComponent(searchSeed.trim())}`
              + (normalizedState ? `&state=${normalizedState}` : '')
              + `&jurisdiction_type=${encodeURIComponent(selectedType.toLowerCase())}`
              + `&limit=3&cutoff=0.6`,
            { headers: { 'Authorization': `Bearer ${sToken}` } }
          );
          if (fuzzyResp.ok) {
            const fuzzyData = await fuzzyResp.json();
            fuzzySuggestions = fuzzyData.suggestions || [];
          }
        } catch (_e) { /* silent — fall through to manual entry */ }

        const suggestSnippet = fuzzySuggestions.length > 0
          ? ` Did you mean: ${fuzzySuggestions.map(s => s.jurisdiction_name).join(', ')}?`
          : '';
        const errorMessage = fuzzySuggestions.length > 0
          ? `No exact ${typeLabel} match for "${searchSeed}".${suggestSnippet}`
          : `No ${typeLabel} jurisdiction configured for ${searchSeed}${normalizedState ? `, ${normalizedState}` : ''}. Please contact Admin to add this jurisdiction.`;

        setRowState(rowIndex, {
          jurisdictionMatchError: errorMessage,
          fuzzySuggestions,
          fuzzyQueryContext: { rowIndex, selectedType, propertyCounty: searchSeed },
        });
        setCertificateData(prev => {
          const isMultiJurisdiction = prev.current_tax_summary?.length > 1;
          const updatedData = {
            ...prev,
            current_tax_summary: prev.current_tax_summary?.length > 0
              ? prev.current_tax_summary.map((row, idx) =>
                  idx === rowIndex ? { 
                    ...row, 
                    jurisdiction_type: selectedType, 
                  } : row
                )
              : prev.current_tax_summary
          };
          // Only update global type for single-jurisdiction mode — preserve all other fields
          if (!isMultiJurisdiction || rowIndex === 0) {
            updatedData.jurisdiction_type = selectedType;
          }
          return updatedData;
        });
        if (fuzzySuggestions.length > 0) {
          toast.warning(`Possible ${typeLabel} match for "${searchSeed}".${suggestSnippet} Click chip to confirm.`);
        } else {
          toast.warning(errorMessage);
        }
      }
    } else if (isSubJurisdictionType) {
      // SUB-JURISDICTION TYPES: Township, City, Village, School, Special District, etc.
      // CRITICAL FIX: Do NOT auto-pick first match. Require user to select from filtered list.
      
      // First, determine the parent county
      // Use property_county from order OR let user select
      // BUG FIX (Borough/Parish/Special District selection skipped):
      // Special District is state-wide by nature — do NOT require a
      // property_county, just fetch all special districts in the state.
      const parentCountyName = propertyCounty;
      
      if (!parentCountyName && !isSpecialDistrict) {
        // No county available - show error and require user to specify
        const typeLabel = selectedType.charAt(0).toUpperCase() + selectedType.slice(1);
        setRowState(rowIndex, { jurisdictionMatchError: `Property county not specified. Please ensure the order has a county before selecting ${typeLabel} jurisdiction.` });
        setCertificateData(prev => {
          const isMultiJurisdiction = prev.current_tax_summary?.length > 1;
          const updatedData = {
            ...prev,
            current_tax_summary: prev.current_tax_summary?.length > 0
              ? prev.current_tax_summary.map((row, idx) =>
                  idx === rowIndex ? { 
                    ...row, 
                    jurisdiction_type: selectedType, 
                  } : row
                )
              : prev.current_tax_summary
          };
          // Only update type for single-jurisdiction mode — preserve other fields
          if (!isMultiJurisdiction || rowIndex === 0) {
            updatedData.jurisdiction_type = selectedType;
          }
          return updatedData;
        });
        toast.warning(`Property county required for ${typeLabel} selection.`);
        return;
      }
      
      // Set state for sub-jurisdiction selector
      // Store parent county context for this row and show sub-jurisdiction selector
      setRowState(rowIndex, {
        selectedCountyForSubJurisdiction: parentCountyName,
        showSubJurisdictionSelector: true,
        subJurisdictionsLoading: true,
        subJurisdictions: [],
        jurisdictionMatchError: null
      });
      
      // Fetch filtered sub-jurisdictions from API
      try {
        const normalizedState = propertyState.trim().toUpperCase();
        
        // JX-LOOKUP-001: shared lookup (Tier 1 county match → Tier 2 unparented/same-county
        // fallback, Town↔Township siblings). Special District records are state-wide.
        const { items: filteredJurisdictions } = await fetchSubJurisdictions({
          type: normalizedType === 'school' ? 'school_district' : normalizedType,
          state: normalizedState,
          county: parentCountyName,
          useCountyFilter: !isSpecialDistrict,
        });
        
        {
          setRowState(rowIndex, { subJurisdictions: filteredJurisdictions });
          
          if (filteredJurisdictions.length === 0) {
            const typeLabel = selectedType.charAt(0).toUpperCase() + selectedType.slice(1);
            setRowState(rowIndex, {
              jurisdictionMatchError: `No ${typeLabel} jurisdictions configured under ${parentCountyName} County${normalizedState ? `, ${normalizedState}` : ''}. ` +
                `Please contact Admin to add this jurisdiction to the directory.`
            });
          }
        }
      } catch (err) {
        console.error('Error fetching sub-jurisdictions:', err);
      } finally {
        setRowState(rowIndex, { subJurisdictionsLoading: false });
      }
      
      // Update certificate data with type but NO jurisdiction selected yet
      // User MUST select from dropdown - no auto-pick
      setCertificateData(prev => {
        const isMultiJurisdiction = prev.current_tax_summary?.length > 1;
        
        // Build row-specific update — preserve existing data
        const rowUpdate = {
          jurisdiction_type: selectedType,
          jurisdiction: autoPopulatedJurisdictionName,
        };
        
        const updatedData = {
          ...prev,
          current_tax_summary: prev.current_tax_summary?.length > 0 
            ? prev.current_tax_summary.map((row, idx) => 
                idx === rowIndex ? { ...row, ...rowUpdate } : row
              )
            : prev.current_tax_summary
        };
        
        // MULTI-JURISDICTION FIX: Only update type for single-jurisdiction mode
        if (!isMultiJurisdiction || rowIndex === 0) {
          updatedData.jurisdiction_type = selectedType;
        }
        
        return updatedData;
      });
      
      const typeLabel = selectedType.charAt(0).toUpperCase() + selectedType.slice(1);
      toast.info(`Please select a ${typeLabel} from the list below.`);
    } else {
      // Other types - generic handling
      setRowState(rowIndex, { jurisdictionMatchError: `Jurisdiction type "${selectedType}" is not yet configured.` });
      setCertificateData(prev => {
        const isMultiJurisdiction = prev.current_tax_summary?.length > 1;
        const updatedData = {
          ...prev,
          current_tax_summary: prev.current_tax_summary?.length > 0
            ? prev.current_tax_summary.map((row, idx) =>
                idx === rowIndex ? { 
                  ...row, 
                  jurisdiction_type: selectedType, 
                } : row
              )
            : prev.current_tax_summary
        };
        // Only update type for single-jurisdiction mode — preserve other fields
        if (!isMultiJurisdiction || rowIndex === 0) {
          updatedData.jurisdiction_type = selectedType;
        }
        return updatedData;
      });
    }
  };
  
  // Handle sub-jurisdiction selection from dropdown
  // MULTI-JURISDICTION: Uses row-specific state for complete isolation
  const handleSubJurisdictionSelect = (jurisdictionId, rowIndex) => {
    if (!jurisdictionId || rowIndex === null || rowIndex === undefined) return;
    
    // Get row-specific sub-jurisdictions list
    const rowState = getRowState(rowIndex);
    const selectedJurisdiction = rowState.subJurisdictions.find(j => j.id === jurisdictionId);
    
    if (selectedJurisdiction) {
      handleJurisdictionSelect(selectedJurisdiction, rowIndex);
      
      // Clear row-specific selector state after selection
      setRowState(rowIndex, {
        jurisdictionMatchError: null,
        showSubJurisdictionSelector: false
      });
      
      const jurisdictionName = selectedJurisdiction.jurisdiction_name || 'Unknown';
      toast.success(`Selected: ${jurisdictionName}`);
    }
  };
  
  // ============================================================================
  // PHASE 2: NESTED JURISDICTION/INSTALLMENT HANDLERS (Mar 2026)
  // These handlers manage state for the NEW jurisdictions[].installments[] structure
  // Row-specific state is stored with 'nested_' prefix to differentiate from legacy
  // ============================================================================
  
  /**
   * Handle jurisdiction type selection for NESTED structure
   * Similar to handleJurisdictionTypeSelect but targets jurisdictions[] array
   */
  const handleNestedJurisdictionTypeSelect = async (selectedType, jurIndex) => {
    // Clear row-specific state for this jurisdiction
    setRowState(`nested_${jurIndex}`, {
      jurisdictionMatchError: null,
      showSubJurisdictionSelector: false,
      subJurisdictions: [],
      subJurisdictionsLoading: false,
      selectedCountyForSubJurisdiction: null
    });
    
    // Get property location data
    const propertyState = orderData?.property_state || orderData?.state || '';
    const propertyCounty = orderData?.property_county || orderData?.county || certificateData?.county;
    const propertyCity = orderData?.property_city || orderData?.city || certificateData?.city;
    
    // Normalize type for matching.
    // BUG FIX (Jurisdiction School Lookup Mismatch, May 27 2026):
    // The dropdown's "School" value must map to the directory's
    // canonical type "school_district" — see sibling handler comment.
    let normalizedType = selectedType.toLowerCase();
    if (normalizedType === 'school') normalizedType = 'school_district';
    
    // BUG FIX (Borough/Parish/Special District selection skipped):
    // Type classification via shared constants — see sibling handler.
    // Feb 7, 2026 — Borough is now state-aware (PA/NJ/CT vs AK).
    const classification = getEffectiveJurisdictionClassification(normalizedType, propertyState);
    const isCountyLevelType = classification === 'county_level';
    const isSubJurisdictionType = classification === 'sub_jurisdiction' || classification === 'special_district';
    const isSpecialDistrict = classification === 'special_district';
    
    // AUTO-POPULATE: Generate jurisdiction name based on type.
    // `propertyState` is threaded so the borough auto-stamp is
    // suppressed for PA / NJ / CT (mailing city ≠ taxing jurisdiction).
    const autoPopulatedJurisdictionName = generateJurisdictionName(selectedType, propertyCounty, propertyCity, propertyState);
    
    if (isCountyLevelType) {
      // COUNTY / PARISH / BOROUGH: Direct matching with STATE filter
      //
      // BUG FIX (Feb 28, 2026 — NJ Borough mismatch, prod):
      // "Borough" is county-level in NY (5 NYC boroughs are county-
      // equivalent) and AK (boroughs replace counties), but in NJ /
      // PA / CT a borough is a MUNICIPALITY. Searching by
      // `property_county` for those rows produced the user-visible
      // bug: order for "Pompton Lakes Borough" (Passaic County, NJ)
      // searched the directory for "Passaic" → no match → false
      // "Please contact Admin to add" warning even when the borough
      // was registered.
      //
      // Resolution: derive the search seed using the SAME precedence
      // `generateJurisdictionName()` uses to render the display name.
      // For Borough, prefer `property_city` (the actual borough name)
      // and only fall back to `property_county` when city is missing.
      // For County / Parish, keep the legacy `property_county` seed.
      //
      // Feb 28, 2026 (AK-BOROUGH-RESOLVE) — INVERT the precedence for
      // COUNTY-LEVEL borough states (Alaska only). In AK the borough
      // IS the county-equivalent (e.g. MATANUSKA-SUSITNA BOROUGH);
      // using property_city first produces an unfindable seed like
      // "Palmer" that never matches the directory. For AK the seed
      // must come from property_county.
      const seedFromCounty = propertyCounty || '';
      const seedFromCity = propertyCity || '';
      const akCountyLevelBorough =
        normalizedType === 'borough' && !isMunicipalBoroughState(propertyState);
      const searchSeed = normalizedType === 'borough'
        ? (akCountyLevelBorough
            ? (seedFromCounty || seedFromCity)
            : (seedFromCity || seedFromCounty))
        : seedFromCounty;
      if (!searchSeed) {
        setRowState(`nested_${jurIndex}`, { jurisdictionMatchError: 'Property location not specified. Cannot auto-match jurisdiction.' });
        // BUG FIX (P1 Jurisdiction Data Mismatch): Clear stale contact
        // fields carried from the prior jurisdiction — switching type
        // invalidates the old county's payee/address/phone/email.
        setCertificateData(prev => ({
          ...prev,
          jurisdictions: prev.jurisdictions.map((jur, idx) =>
            idx === jurIndex ? {
              ...jur,
              jurisdiction_type: selectedType,
              jurisdiction_id: null,
              make_checks_payable_to: '',
              tax_collector_name: '',
              tax_collector_phone: '',
              tax_collector_phone_ext: '',
              tax_collector_email: '',
              tax_collector_fax: '',
              tax_collector_website: '',
              assessor_website: '',
              tax_collector_working_hours: '',
              tax_collector_mailing_address: '',
              tax_collector_physical_address: '',
              mlc_charges: null,
              mlc_24_month_charges: null,
              mlc_eta_days: null,
              sase_required: false,
              info_obtained_back: null,
              changed_by_user: false,
            } : jur
          )
        }));
        toast.error('Property location not specified. Cannot auto-match jurisdiction.');
        return;
      }
      
      const normalizedCounty = searchSeed.trim().toLowerCase();
      const normalizedState = propertyState.trim().toUpperCase();
      
      // Normalize for matching: strip "county", "parish", "borough" suffixes
      const stripSuffix = (s) => (s || '').toLowerCase().replace(/\b(county|parish|borough)\b/g, '').trim();
      const strippedCounty = stripSuffix(normalizedCounty);
      // Canonical form (punctuation/abbreviation-tolerant).
      const canonicalCounty = canonicalizeJurisdictionName(searchSeed);
      
      // ─────────────────────────────────────────────────────────────────
      // BUG FIX (P1 Jurisdiction Data Mismatch — root cause):
      //
      // The previous 3-strategy matcher used raw `String.includes()`
      // fallbacks that silently collapsed longer county names into
      // shorter ones that appeared earlier in the alphabetical directory.
      // Example that shipped to production:
      //   property_county = "Lauderdale" (stripped)
      //   directory iterates: ..., "Dale County", ..., "Lauderdale County"
      //   Strategy 3: "lauderdale".includes("dale") → TRUE → Dale wins
      //   before the exact-match for Lauderdale is ever evaluated.
      //
      // Fix: deterministic priority matching.
      //   Pass 0 — canonical equality              (format-tolerant —
      //                                             St. Mary's ↔ ST MARYS)
      //   Pass 1 — exact stripped-suffix equality  (unambiguous winner)
      //   Pass 2 — WHOLE-WORD regex match          (tolerates "Montgomery AL"
      //                                             style variants)
      // Substring `.includes()` is intentionally removed: it is the only
      // class that produces false positives like Lauderdale→Dale.
      // ─────────────────────────────────────────────────────────────────
      const countyCandidates = jurisdictions.filter(j => {
        const jType = (j.jurisdiction_type || j.type || '').toLowerCase();
        const jState = (j.state || '').toUpperCase();
        // BUG FIX (NY type-strict lookup Feb 22, 2026): exact-type filter —
        // county-level equivalence is for hierarchy routing, NOT directory
        // lookup. See sibling handler (handleJurisdictionTypeSelect).
        if (jType !== normalizedType) return false;
        if (normalizedState && jState && jState !== normalizedState) return false;
        return true;
      });
      
      // Pass 0: canonical equality (format-tolerant).
      let matchedJurisdiction = canonicalCounty
        ? countyCandidates.find(j => {
            const jCanonical = j.canonical_name || canonicalizeJurisdictionName(j.jurisdiction_name || j.name);
            return jCanonical && jCanonical === canonicalCounty;
          })
        : null;
      
      // Pass 1: exact stripped equality (legacy fallback)
      if (!matchedJurisdiction) {
        matchedJurisdiction = countyCandidates.find(j => {
          const jName = (j.jurisdiction_name || j.name || '').toLowerCase();
          return stripSuffix(jName) === strippedCounty;
        });
      }
      
      // Pass 2: whole-word match (directory name CONTAINS property county as
      // a full word, OR vice-versa). Word boundaries prevent the
      // Lauderdale↔Dale substring collapse.
      if (!matchedJurisdiction && strippedCounty) {
        const escapeRegex = (s) => s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
        const propertyRe = new RegExp(`\\b${escapeRegex(strippedCounty)}\\b`);
        matchedJurisdiction = countyCandidates.find(j => {
          const jStripped = stripSuffix((j.jurisdiction_name || j.name || '').toLowerCase());
          if (!jStripped) return false;
          const dirRe = new RegExp(`\\b${escapeRegex(jStripped)}\\b`);
          return propertyRe.test(jStripped) || dirRe.test(strippedCounty);
        });
      }
      
      if (matchedJurisdiction) {
        // Feb 2026 P0 (PA-RESOLVER-RESELECT-OVERWRITE) — Same fix
        // as `handleJurisdictionTypeSelect`. Run the formal-assignment
        // resolver before spreading legacy directory columns into
        // the nested jurisdiction row. Without this, re-selecting a
        // PA jurisdiction silently reverted the row's payee from
        // the resolved collector back to the legacy directory data.
        const guidance = await resolveFormalCollectorForJurisdiction(matchedJurisdiction, selectedType);
        const contactFields = buildContactFieldsFromGuidance(matchedJurisdiction, guidance);

        // BUG FIX (P1 Jurisdiction Data Mismatch): Always replace every
        // contact field with the matched jurisdiction's data. Previously
        // each field used `matched.xxx || jur.xxx || ''` which silently
        // preserved stale values from a prior jurisdiction whenever the
        // matched record had an empty field — causing mismatched
        // payee/address/phone/email to appear under a different county
        // name in the UI.
        setCertificateData(prev => ({
          ...prev,
          jurisdictions: prev.jurisdictions.map((jur, idx) =>
            idx === jurIndex ? {
              ...jur,
              jurisdiction_type: selectedType,
              jurisdiction_name: autoPopulatedJurisdictionName || matchedJurisdiction.jurisdiction_name,
              jurisdiction_id: matchedJurisdiction.id,
              ...contactFields,
              assessor_website: matchedJurisdiction.assessor_website || '',
              mlc_charges: matchedJurisdiction.mlc_charges ?? null,
              mlc_24_month_charges: matchedJurisdiction.mlc_24_month_charges ?? null,
              mlc_eta_days: matchedJurisdiction.mlc_eta_days ?? null,
              sase_required: matchedJurisdiction.sase_required ?? false,
              info_obtained_back: matchedJurisdiction.info_obtained_back ?? null,
              changed_by_user: false
            } : jur
          )
        }));
        
        if (contactFields._collector_source === 'assignment') {
          toast.success(
            `${selectedType.charAt(0).toUpperCase() + selectedType.slice(1)} matched: ${matchedJurisdiction.jurisdiction_name || matchedJurisdiction.name} — Payee: ${contactFields.make_checks_payable_to}`,
            { description: `Resolved via formal assignment (confidence: ${contactFields._collector_confidence || 'n/a'}).` },
          );
        } else {
          toast.success(`${selectedType.charAt(0).toUpperCase() + selectedType.slice(1)} matched: ${matchedJurisdiction.jurisdiction_name || matchedJurisdiction.name}`);
        }
      } else {
        // No local match found — try API search as fallback
        console.log('[TYPE_SELECT] No local match. Trying API fallback for:', normalizedCounty, normalizedState);
        let apiMatch = null;
        try {
          const token = localStorage.getItem('token');
          const searchTerm = encodeURIComponent(searchSeed.trim());
          const stateParam = normalizedState ? `&state=${normalizedState}` : '';
          // BUG FIX (P0 wrong-jurisdiction-loaded): strictly restrict to
          // jurisdiction_name / canonical_name via `search_field=county`.
          // Without this the server's default OR also matches
          // tax_collector_physical_address / tax_collector_mailing_address,
          // which returns unrelated counties whose address text happens to
          // contain the query term (e.g., search="Washington" returned
          // Baltimore County because its physical address is "400 WASHINGTON
          // AVENUE, ROOM 150 TOWSON, MD 21204").
          //
          // Also passes jurisdiction_type=<selected> for type-strict lookup
          // (NY Borough/Parish bug fix Feb 22, 2026).
          const resp = await fetch(
            `${process.env.REACT_APP_BACKEND_URL}/api/jurisdictions?search=${searchTerm}${stateParam}&jurisdiction_type=${encodeURIComponent(normalizedType)}&search_field=county&active_only=true&page_size=10`,
            { headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' } }
          );
          if (resp.ok) {
            const data = await resp.json();
            // BUG FIX (NY type-strict lookup Feb 22, 2026): exact-type filter
            // only. County-level equivalence applies to hierarchy, NOT lookup.
            const candidates = (data.jurisdictions || []).filter(j =>
              (j.jurisdiction_type || '').toLowerCase() === normalizedType
            );
            if (candidates.length > 0) {
              // BUG FIX (P1 Jurisdiction Data Mismatch — root cause hardening):
              // Previously this fell back to `candidates[0]` whenever the
              // exact-stripped match missed — which could silently pick a
              // WRONG county when the API search returned multiple records
              // (e.g., search="Dale" returned both Dale & Lauderdale and
              // we'd arbitrarily pick the first). The API search can also
              // return partial substring hits.
              //
              // Safer behavior: prefer canonical equality, then exact
              // stripped equality, then whole-word match, and only pick
              // `candidates[0]` when there is LITERALLY one candidate left.
              // Otherwise return null and surface the "no match" error,
              // which keeps the user in control instead of silently
              // binding the wrong county.
              const canonicalCountyApi = canonicalizeJurisdictionName(searchSeed);
              apiMatch = canonicalCountyApi
                ? candidates.find(j => {
                    const jc = j.canonical_name || canonicalizeJurisdictionName(j.jurisdiction_name);
                    return jc && jc === canonicalCountyApi;
                  })
                : null;
              if (!apiMatch) {
                apiMatch = candidates.find(j => stripSuffix(j.jurisdiction_name) === strippedCounty);
              }
              if (!apiMatch && strippedCounty) {
                const escapeRegex = (s) => s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
                const propertyRe = new RegExp(`\\b${escapeRegex(strippedCounty)}\\b`);
                apiMatch = candidates.find(j => {
                  const jStripped = stripSuffix((j.jurisdiction_name || '').toLowerCase());
                  if (!jStripped) return false;
                  const dirRe = new RegExp(`\\b${escapeRegex(jStripped)}\\b`);
                  return propertyRe.test(jStripped) || dirRe.test(strippedCounty);
                });
              }
              if (!apiMatch && candidates.length === 1) {
                apiMatch = candidates[0];
              }
              if (apiMatch) {
                console.log('[TYPE_SELECT] API fallback MATCHED:', apiMatch.jurisdiction_name, apiMatch.id);
              } else {
                console.log('[TYPE_SELECT] API fallback: no unambiguous match among', candidates.length, 'candidates — bailing out');
              }
            }
          }
        } catch (err) {
          console.warn('[TYPE_SELECT] API fallback failed:', err);
        }
        
        if (apiMatch) {
          // Feb 2026 P0 (PA-RESOLVER-RESELECT-OVERWRITE) — Same fix
          // on the API-fallback path: prefer the formal assignment's
          // collector when the resolver returns one, otherwise use
          // the legacy directory columns from the API response.
          const apiGuidance = await resolveFormalCollectorForJurisdiction(apiMatch, selectedType);
          const apiContactFields = buildContactFieldsFromGuidance(apiMatch, apiGuidance);

          // BUG FIX (P1 Jurisdiction Data Mismatch): Replace every contact
          // field from the API-matched jurisdiction. No fallback to the
          // previously-stored value — that was the source of cross-county
          // contact-block contamination (e.g., Lauderdale name + Dale
          // address/phone/email).
          setCertificateData(prev => ({
            ...prev,
            jurisdictions: prev.jurisdictions.map((jur, idx) =>
              idx === jurIndex ? {
                ...jur,
                jurisdiction_type: selectedType,
                jurisdiction_name: apiMatch.jurisdiction_name || apiMatch.name,
                jurisdiction_id: apiMatch.id,
                ...apiContactFields,
                assessor_website: apiMatch.assessor_website || '',
                mlc_charges: apiMatch.mlc_charges ?? null,
                mlc_24_month_charges: apiMatch.mlc_24_month_charges ?? null,
                mlc_eta_days: apiMatch.mlc_eta_days ?? null,
                sase_required: apiMatch.sase_required ?? false,
                info_obtained_back: apiMatch.info_obtained_back ?? null,
                changed_by_user: false
              } : jur
            )
          }));
          if (apiContactFields._collector_source === 'assignment') {
            toast.success(
              `${selectedType.charAt(0).toUpperCase() + selectedType.slice(1)} matched: ${apiMatch.jurisdiction_name} — Payee: ${apiContactFields.make_checks_payable_to}`,
              { description: `Resolved via formal assignment (confidence: ${apiContactFields._collector_confidence || 'n/a'}).` },
            );
          } else {
            toast.success(`${selectedType.charAt(0).toUpperCase() + selectedType.slice(1)} matched: ${apiMatch.jurisdiction_name}`);
          }
        } else {
          // Truly no strict match — BEFORE falling through to manual
          // entry, try a fuzzy "Did you mean?" lookup (state-scoped) to
          // catch typos like "Naveda" → "NEVADA COUNTY". Suggestions
          // are surfaced as clickable chips; user must explicitly
          // accept (no silent auto-bind).
          let fuzzySuggestions = [];
          try {
            const sToken = localStorage.getItem('token');
            const fuzzyResp = await fetch(
              `${process.env.REACT_APP_BACKEND_URL}/api/jurisdictions/search/suggest`
                + `?name=${encodeURIComponent(searchSeed.trim())}`
                + (normalizedState ? `&state=${normalizedState}` : '')
                + `&jurisdiction_type=${encodeURIComponent(normalizedType)}`
                + `&limit=3&cutoff=0.6`,
              { headers: { 'Authorization': `Bearer ${sToken}` } }
            );
            if (fuzzyResp.ok) {
              const fuzzyData = await fuzzyResp.json();
              fuzzySuggestions = fuzzyData.suggestions || [];
            }
          } catch (_e) { /* fail silently — fall through to manual entry */ }

          // BUG FIX (P1 Jurisdiction Data Mismatch):
          // Clear stale contact data so the user isn't left with a
          // previous jurisdiction's payee/address/phone bound to the new
          // county name. User will manually enter details from here, OR
          // click a fuzzy suggestion chip below.
          setCertificateData(prev => ({
            ...prev,
            jurisdictions: prev.jurisdictions.map((jur, idx) =>
              idx === jurIndex ? {
                ...jur,
                jurisdiction_type: selectedType,
                jurisdiction_name: autoPopulatedJurisdictionName || '', // PJR-001 r4: never carry a stale name across types
                jurisdiction_id: null,
                make_checks_payable_to: '',
                tax_collector_name: '',
                tax_collector_phone: '',
                tax_collector_phone_ext: '',
                tax_collector_email: '',
                tax_collector_fax: '',
                tax_collector_website: '',
                assessor_website: '',
                tax_collector_working_hours: '',
                tax_collector_mailing_address: '',
                tax_collector_physical_address: '',
                mlc_charges: null,
                mlc_24_month_charges: null,
                mlc_eta_days: null,
                sase_required: false,
                info_obtained_back: null,
                changed_by_user: false,
              } : jur
            )
          }));
          const suggestMsg = fuzzySuggestions.length > 0
            ? ` Did you mean: ${fuzzySuggestions.map(s => s.jurisdiction_name).join(', ')}?`
            : '';
          setRowState(`nested_${jurIndex}`, {
            jurisdictionMatchError: `No matching ${normalizedType.charAt(0).toUpperCase() + normalizedType.slice(1)} found for "${searchSeed}" in ${normalizedState || 'directory'}.${suggestMsg ? '' : ' You can pick a Tax Collector below or enter contact details manually.'}`,
            fuzzySuggestions,
            fuzzyQueryContext: {
              jurIndex,
              selectedType,
              normalizedType,
              propertyCounty: searchSeed,
              autoPopulatedJurisdictionName,
            },
          });
          if (fuzzySuggestions.length > 0) {
            toast.warning(`Possible match for "${searchSeed}"${suggestMsg} Click to confirm.`);
          } else {
            toast.warning(`No matching ${normalizedType} found for "${searchSeed}" in ${normalizedState || 'directory'}. You can pick a Tax Collector below or enter contact details manually.`);
          }
        }
      }
    } else if (isSubJurisdictionType) {
      // SUB-JURISDICTION TYPES: Show selector dropdown
      // BUG FIX (P1 Jurisdiction Data Mismatch): Clear all stale contact
      // fields carried over from a prior jurisdiction selection. If we
      // merely updated jurisdiction_type + name here, the old county's
      // payee/address/phone/email would linger in the UI until the user
      // picked a sub-jurisdiction — producing a Lauderdale-name-with-Dale-
      // contact mismatch. Clearing now guarantees the contact block stays
      // blank until the sub-jurisdiction selection repopulates it.
      // Mark row as actively fetching BEFORE updating certificateData to prevent preloader race
      activeFetchingRowsRef.current.add(`nested_${jurIndex}`);
      
      setCertificateData(prev => ({
        ...prev,
        jurisdictions: prev.jurisdictions.map((jur, idx) =>
          idx === jurIndex ? {
            ...jur,
            jurisdiction_type: selectedType,
            jurisdiction_name: autoPopulatedJurisdictionName || '', // PJR-001 r4: never carry a stale name across types
            jurisdiction_id: null,
            make_checks_payable_to: '',
            tax_collector_name: '',
            tax_collector_phone: '',
            tax_collector_phone_ext: '',
            tax_collector_email: '',
            tax_collector_fax: '',
            tax_collector_website: '',
            assessor_website: '',
            tax_collector_working_hours: '',
            tax_collector_mailing_address: '',
            tax_collector_physical_address: '',
            mlc_charges: null,
            mlc_24_month_charges: null,
            mlc_eta_days: null,
            sase_required: false,
            info_obtained_back: null,
            changed_by_user: false,
          } : jur
        )
      }));
      
      // Show sub-jurisdiction selector
      setRowState(`nested_${jurIndex}`, {
        showSubJurisdictionSelector: true,
        subJurisdictionsLoading: true,
        subJurisdictions: [],
        selectedCountyForSubJurisdiction: propertyCounty
      });
      
      try {
        const normalizedState = (orderData?.property_state || '').trim().toUpperCase();
        
        // JX-LOOKUP-001: Tier 1 parent_county_name match → Tier 2 (only when empty) state-scoped
        // records with NO parent county or the same county; Town↔Township siblings included.
        // Special District records are state-wide — no county filter.
        const { items: filteredJurisdictions } = await fetchSubJurisdictions({
          type: normalizedType,
          state: normalizedState,
          county: propertyCounty,
          useCountyFilter: !isSpecialDistrict,
        });
        
        setRowState(`nested_${jurIndex}`, { 
          subJurisdictions: filteredJurisdictions,
          subJurisdictionsLoading: false 
        });
        
        // Clear active fetch flag — preloader can now operate on this row if needed
        activeFetchingRowsRef.current.delete(`nested_${jurIndex}`);
        
        if (filteredJurisdictions.length === 0) {
          toast.warning(`No ${selectedType} jurisdictions found under ${propertyCounty || 'any county'}${normalizedState ? `, ${normalizedState}` : ''}. Contact Admin to add.`);
        } else {
          // Feb 7, 2026 — Stronger guidance for PA Borough specifically.
          // The mailing city ≠ taxing jurisdiction nuance trips operators
          // up (a "Darby, PA" parcel can sit in Collingdale, Colwyn,
          // Sharon Hill, Darby Borough, etc.), so we surface a directive
          // prompt instead of a passive "Please select one".
          if (normalizedType === 'borough' && normalizedState && normalizedState !== 'AK') {
            toast.info(
              `Select the correct borough from the directory (${filteredJurisdictions.length} available). ` +
              `Mailing city "${propertyCity || ''}" and the actual taxing borough can be different in PA — ` +
              `verify against the parcel ID, not the postal city.`
            );
          } else {
            toast.info(`Found ${filteredJurisdictions.length} ${selectedType} jurisdiction(s). Please select one.`);
          }
        }
      } catch (error) {
        console.error('[NESTED] Error filtering sub-jurisdictions:', error);
        activeFetchingRowsRef.current.delete(`nested_${jurIndex}`);
        setRowState(`nested_${jurIndex}`, { subJurisdictionsLoading: false });
      }
    }
  };
  
  /**
   * Handle sub-jurisdiction selection for NESTED structure
   */
  const handleNestedSubJurisdictionSelect = (jurisdictionId, jurIndex) => {
    if (!jurisdictionId || jurIndex === null || jurIndex === undefined) return;
    
    // Get row-specific sub-jurisdictions list
    const rowState = getRowState(`nested_${jurIndex}`);
    const selectedJurisdiction = rowState.subJurisdictions.find(j => j.id === jurisdictionId);
    
    if (selectedJurisdiction) {
      // Build "Make Checks Payable To" — ONLY the payable name (tax_collector_name)
      // Address, phone, fax are stored in their own dedicated fields
      const taxCollectorName = (selectedJurisdiction.tax_collector_name || '').trim();
      // PJR-001 P2: the jurisdiction's saved payee wins over the collector name (no assignment on this path).
      const makeChecksPayableTo = (selectedJurisdiction.make_checks_payable_to || '').trim() || taxCollectorName || (selectedJurisdiction.jurisdiction_name || selectedJurisdiction.name || '').trim();
      
      // Update the jurisdiction in the jurisdictions[] array
      setCertificateData(prev => ({
        ...prev,
        jurisdictions: prev.jurisdictions.map((jur, idx) =>
          idx === jurIndex ? {
            ...jur,
            // JX-LOOKUP-001: adopt the directory's stored type (e.g. Township row → Town record)
            jurisdiction_type: (selectedJurisdiction.jurisdiction_type || jur.jurisdiction_type),
            jurisdiction_name: selectedJurisdiction.jurisdiction_name || selectedJurisdiction.name,
            jurisdiction_id: selectedJurisdiction.id,
            make_checks_payable_to: makeChecksPayableTo,
            // JURDIR-PAYEE-002: provenance of the payee ('stored' = true directory value)
            _payee_source: (selectedJurisdiction.make_checks_payable_to || '').trim() ? 'stored'
              : taxCollectorName ? 'collector_name_fallback' : 'jurisdiction_name_fallback',
            tax_collector_phone: selectedJurisdiction.tax_collector_phone || '',
            tax_collector_phone_ext: selectedJurisdiction.tax_collector_phone_ext || '',
            tax_collector_email: selectedJurisdiction.tax_collector_email || '',
            tax_collector_website: selectedJurisdiction.tax_collector_website || '',
            assessor_website: selectedJurisdiction.assessor_website || '',
            tax_collector_working_hours: selectedJurisdiction.tax_collector_working_hours || '',
            mlc_charges: selectedJurisdiction.mlc_charges || null,
            mlc_24_month_charges: selectedJurisdiction.mlc_24_month_charges || null,
            mlc_eta_days: selectedJurisdiction.mlc_eta_days || null,
            sase_required: selectedJurisdiction.sase_required || false,
            info_obtained_back: selectedJurisdiction.info_obtained_back || null,
            changed_by_user: false
          } : jur
        )
      }));
      
      // Clear row-specific selector state after selection
      setRowState(`nested_${jurIndex}`, {
        jurisdictionMatchError: null,
        showSubJurisdictionSelector: false
      });
      
      toast.success(`Selected: ${selectedJurisdiction.jurisdiction_name || 'Unknown'}`);
    }
  };
  
  /**
   * Clear jurisdiction selection for NESTED structure
   */
  const handleNestedClearJurisdiction = (jurIndex) => {
    setCertificateData(prev => ({
      ...prev,
      jurisdictions: prev.jurisdictions.map((jur, idx) =>
        idx === jurIndex ? {
          ...jur,
          jurisdiction_type: null,
          jurisdiction_name: '',
          jurisdiction_id: null,
          make_checks_payable_to: '',
          tax_collector_name: '',
          tax_collector_phone: '',
          tax_collector_phone_ext: '',
          tax_collector_email: '',
          tax_collector_fax: '',
          tax_collector_website: '',
          assessor_website: '',
          tax_collector_working_hours: '',
          tax_collector_mailing_address: '',
          tax_collector_physical_address: '',
          mlc_charges: null,
          mlc_24_month_charges: null,
          mlc_eta_days: null,
          sase_required: false,
          info_obtained_back: null,
          // Jul 2026 (Bullskin/Leslie Wiltrout UAT #2) — Clearing the
          // jurisdiction type from the dropdown MUST also drop the
          // master-data foreign keys. If we don't, the TaxCollectorPicker
          // fetches the still-linked collector row and (correctly, per
          // Phase 23.2 precedence) renders its make_checks_payable_to
          // (e.g. "LESLIE WILTROUT") even though the jurisdiction row is
          // otherwise blank. The operator sees a phantom payee.
          //
          // Both current-tax AND delinquent-tax FKs + their derived
          // payee/phone/address snapshots are reset so the picker falls
          // back to the plain "Search or select…" trigger with no chip.
          current_collector_id: null,
          current_collector_name: null,
          delinquent_collector_id: null,
          delinquent_collector_name: null,
          delinquent_payable_to_name: '',
          delinquent_payable_to_address: '',
          delinquent_payable_to_phone: '',
        } : jur
      )
    }));
    
    setRowState(`nested_${jurIndex}`, {
      jurisdictionMatchError: null,
      showSubJurisdictionSelector: false,
      subJurisdictions: [],
      subJurisdictionsLoading: false
    });
  };
  
  // ============================================================================

  /**
   * Generate jurisdiction name based on type and property location
   * AUTO-POPULATE helper function
   */
  const generateJurisdictionName = (jurisdictionType, propertyCounty, propertyCity, propertyState) => {
    if (!jurisdictionType) return '';
    
    const type = jurisdictionType.toLowerCase();
    
    // County: "<County Name> County"
    if (type === 'county' && propertyCounty) {
      return `${propertyCounty.trim()} County`;
    }
    
    // PJR-001 round 4 (Oct 2026): NO auto-stamped sub-county names. "<city> City" /
    // "<county> Township" / "<city> Village" fabricated names that never existed in
    // the directory (e.g. "New Castle Township", DE) and were persisted into drafts.
    // Sub-county rows must be picked from the type-filtered directory list.
    if (type === 'city' || type === 'township' || type === 'village') {
      return '';
    }
    
    // Borough: "<Name> Borough"
    //   ⚠️ Feb 7, 2026 — DO NOT auto-stamp "{property_city} Borough"
    //   for states where Borough is a municipality (PA, NJ, CT …). The
    //   postal mailing city and the actual taxing jurisdiction are
    //   routinely DIFFERENT in PA (e.g. "Darby, PA 19023" addresses
    //   sit inside Collingdale Borough, Darby Borough, Colwyn Borough,
    //   etc.). Auto-stamping forces a wrong jurisdiction onto the
    //   parcel and traps the operator with the "No matching Borough
    //   found" warning. We return '' instead so the operator MUST
    //   pick from the master directory dropdown surfaced by the
    //   sub-jurisdiction selector. Alaska keeps the legacy behaviour
    //   because AK boroughs ARE the top-level taxing jurisdiction.
    //
    //   ⚠️ Feb 28, 2026 (AK-BOROUGH-RESOLVE) — for COUNTY-LEVEL borough
    //   states (Alaska), the borough IS the county-equivalent. Palmer
    //   sits inside MATANUSKA-SUSITNA BOROUGH; using property_city
    //   first produced "Palmer Borough" which never matches the
    //   directory. Prefer property_county for AK so the auto-name
    //   reflects the actual taxing jurisdiction. Municipal-borough
    //   states (PA/NJ/CT) already short-circuit above.
    if (type === 'borough') {
      if (isMunicipalBoroughState(propertyState)) {
        return '';
      }
      const name = (propertyCounty || propertyCity || '').trim();
      if (name) return `${name} Borough`;
    }
    
    
    // Parish: "<Name> Parish"
    if (type === 'parish') {
      const name = propertyCounty || propertyCity || '';
      if (name) return `${name.trim()} Parish`;
    }
    
    // Municipality: "<Name> Municipality"
    if (type === 'municipality') {
      const name = propertyCity || propertyCounty || '';
      if (name) return `${name.trim()} Municipality`;
    }
    
    // School: Keep empty (no clear naming pattern)
    return '';
  };
  
  /**
   * Generate tax certification notes based on current_tax_summary rows
   * Supports ALL tax collection frequencies and manually added jurisdictions
   * 
   * Logic:
   * 1. Check if any delinquent taxes exist across all rows
   * 2. For each jurisdiction row, generate notes based on Pay Status:
   *    - Paid: "YYYY [installment] taxes paid."
   *    - Due: "YYYY [installment] taxes are due on MM/DD/YYYY and become delinquent after MM/DD/YYYY."
   *    - Delinquent: "YYYY taxes are delinquent as of MM/DD/YYYY."
   * 3. If no delinquent taxes exist, add "All priors are paid." at the beginning
   */
  const generateTaxCertificationNotesV2 = (currentTaxSummary, certificateDlqPayoffAmount, certificateDlqGoodThruDate, collectionFrequency) => {
    console.log('[GENERATE NOTES V2] ========== FUNCTION CALLED ==========');
    console.log('[GENERATE NOTES V2] currentTaxSummary:', currentTaxSummary);
    console.log('[GENERATE NOTES V2] Certificate dlq_payoff_amount:', certificateDlqPayoffAmount);
    console.log('[GENERATE NOTES V2] Certificate dlq_good_thru_date:', certificateDlqGoodThruDate);
    console.log('[GENERATE NOTES V2] Collection frequency:', collectionFrequency);
    console.log('[GENERATE NOTES V2] ========================================');
    
    if (!currentTaxSummary || currentTaxSummary.length === 0) {
      console.log('[GENERATE NOTES V2] No rows to process');
      return '';
    }
    
    try {
      const notesLines = [];
      
      // Step 1: Check if any delinquent taxes exist
      const hasDelinquent = currentTaxSummary.some(row => {
        const payStatus = (row.pay_status || '').toLowerCase();
        return payStatus === 'delinquent' || payStatus === 'partially dlq' || payStatus === 'partially delinquent';
      });
      
      // Step 2: Add "All priors are paid" if no delinquent taxes
      if (!hasDelinquent) {
        notesLines.push('All priors are paid.');
      }
      
      // Step 3: Add "Taxes are paid <frequency>." line based on Tax Collected selection
      if (collectionFrequency) {
        const frequencyLower = collectionFrequency.toLowerCase().replace(/_/g, '-'); // Normalize underscore to hyphen
        if (frequencyLower === 'annually' || frequencyLower === 'annual') {
          notesLines.push('Taxes are paid annually.');
        } else if (frequencyLower === 'semi-annually' || frequencyLower === 'semiannually' || frequencyLower === 'semi-annual') {
          notesLines.push('Taxes are paid semi-annually.');
        } else if (frequencyLower === 'quarterly') {
          notesLines.push('Taxes are paid quarterly.');
        } else if (frequencyLower === 'monthly') {
          notesLines.push('Taxes are paid monthly.');
        }
      }
      
      // Step 3.5: For semi-annual taxes, add the two due dates sentence
      // E.g., "2025 semi-annual taxes are due on 09/30/2025 and 03/01/2026."
      const isSemiAnnual = collectionFrequency && 
        (collectionFrequency.toLowerCase().replace(/_/g, '-') === 'semi-annually' || 
         collectionFrequency.toLowerCase() === 'semiannually' ||
         collectionFrequency.toLowerCase() === 'semi-annual' ||
         collectionFrequency.toLowerCase() === 'semi_annually');
      
      // Helper function to format date as MM/DD/YYYY
      const formatDateLocal = (dateInput) => {
        if (!dateInput) return '';
        try {
          if (typeof dateInput === 'string' && dateInput.includes('/')) {
            const parts = dateInput.split('/');
            if (parts.length === 3) {
              let [month, day, year] = parts;
              month = parseInt(month, 10);
              day = parseInt(day, 10);
              year = parseInt(year, 10);
              if (year < 100) year = year < 50 ? 2000 + year : 1900 + year;
              if (month >= 1 && month <= 12 && day >= 1 && day <= 31) {
                return `${String(month).padStart(2, '0')}/${String(day).padStart(2, '0')}/${year}`;
              }
            }
          }
          // Try ISO format
          const d = new Date(dateInput);
          if (!isNaN(d.getTime())) {
            return `${String(d.getMonth() + 1).padStart(2, '0')}/${String(d.getDate()).padStart(2, '0')}/${d.getFullYear()}`;
          }
        } catch (e) {}
        return dateInput;
      };
      
      if (isSemiAnnual && currentTaxSummary.length >= 2) {
        // Get unique tax years from the rows
        const taxYears = [...new Set(currentTaxSummary.map(row => row.tax_year).filter(Boolean))];
        
        // For each tax year, find the two installment due dates
        taxYears.forEach(taxYear => {
          const rowsForYear = currentTaxSummary.filter(row => row.tax_year === taxYear);
          const dueDates = rowsForYear
            .map(row => row.due_date)
            .filter(Boolean)
            .map(date => formatDateLocal(date))
            .filter(Boolean);
          
          // If we have exactly 2 due dates for this year, add the semi-annual sentence
          if (dueDates.length === 2) {
            notesLines.push(`${taxYear} semi-annual taxes are due on ${dueDates[0]} and ${dueDates[1]}.`);
          }
        });
      }
      
      // Step 4: Generate notes for each jurisdiction row
      currentTaxSummary.forEach((row, index) => {
        const taxYear = row.tax_year;
        const installment = row.installment || '';
        const payStatus = (row.pay_status || '').toLowerCase();
        const dueDate = row.due_date;
        const dlqDate = row.dlq_date;
        const mailingDate = row.mailing_issue_date || row.mailing_date || row.issue_date;
        
        // Skip if no tax year
        if (!taxYear) {
          console.log(`[GENERATE NOTES V2] Skipping row ${index}: no tax_year`);
          return;
        }
        
        // Format installment text (e.g., "1st installment", "2nd installment", "full installment", or just "")
        const installmentLower = (installment || '').toLowerCase();
        const isFullInstallment = installmentLower === 'full' || installmentLower === 'annual' || installmentLower === 'yearly' || !installment;
        const installmentText = installment ? (isFullInstallment ? 'full installment ' : `${installment} installment `) : '';
        
        // Helper to format date as MM/DD only (no year)
        const formatDateMonthDayOnly = (dateInput) => {
          if (!dateInput) return '';
          try {
            if (typeof dateInput === 'string' && dateInput.includes('/')) {
              const parts = dateInput.split('/');
              if (parts.length >= 2) {
                const month = parts[0].padStart(2, '0');
                const day = parts[1].padStart(2, '0');
                return `${month}/${day}`;
              }
            }
            // Try parsing as Date
            const d = new Date(dateInput);
            if (!isNaN(d.getTime())) {
              return `${String(d.getMonth() + 1).padStart(2, '0')}/${String(d.getDate()).padStart(2, '0')}`;
            }
          } catch (e) {}
          return '';
        };
        
        // Helper to get next year mailing date
        const getNextYearMailingDate = (mailingDateInput, nextYear) => {
          if (!mailingDateInput) return '';
          try {
            if (typeof mailingDateInput === 'string' && mailingDateInput.includes('/')) {
              const parts = mailingDateInput.split('/');
              if (parts.length >= 2) {
                const month = parts[0].padStart(2, '0');
                const day = parts[1].padStart(2, '0');
                return `${month}/${day}/${nextYear}`;
              }
            }
            // Try parsing as Date
            const d = new Date(mailingDateInput);
            if (!isNaN(d.getTime())) {
              return `${String(d.getMonth() + 1).padStart(2, '0')}/${String(d.getDate()).padStart(2, '0')}/${nextYear}`;
            }
          } catch (e) {}
          return '';
        };
        
        // Generate note based on Pay Status
        if (payStatus === 'paid' || payStatus === 'current') {
          if (isFullInstallment && !hasDelinquent) {
            notesLines.push(`The ${taxYear} full installment is paid.`);
          } else {
            // Original format for partial installments or when delinquent exists
            // Paid: "2025 1st installment taxes paid."
            notesLines.push(`${taxYear} ${installmentText}taxes paid.`);
          }
          
        } else if (payStatus === 'due' || payStatus === 'pending') {
          // Due: "2025 2nd installment taxes are due on 03/01/2026 and become delinquent after 03/02/2026."
          if (dueDate && dlqDate) {
            const formattedDueDate = formatDateForNotes(dueDate);
            const formattedDlqDate = formatDateForNotes(dlqDate);
            notesLines.push(`${taxYear} ${installmentText}taxes are due on ${formattedDueDate} and become delinquent after ${formattedDlqDate}.`);
          } else if (dueDate) {
            const formattedDueDate = formatDateForNotes(dueDate);
            notesLines.push(`${taxYear} ${installmentText}taxes are due on ${formattedDueDate}.`);
          } else {
            notesLines.push(`${taxYear} ${installmentText}taxes are due.`);
          }
          
        } else if (payStatus === 'delinquent' || payStatus === 'partially dlq' || payStatus === 'partially delinquent') {
          // Delinquent: Enhanced format with all details
          // Format: "YYYY [installment] taxes are delinquent. Total payoff amount is $XXX good thru MM/DD/YYYY. Normal due date is MM/DD/YYYY."
          
          // Use certificate-level delinquent data (passed as function parameters)
          const payoffAmount = certificateDlqPayoffAmount || row.balance_amount || row.amount_due || '';
          const goodThruDate = certificateDlqGoodThruDate || row.dlq_good_thru_date || row.dlq_date;
          
          console.log('[DELINQUENT NOTE] ==================== BUILDING DELINQUENT NOTE ====================');
          console.log('[DELINQUENT NOTE] certificateDlqPayoffAmount (param):', certificateDlqPayoffAmount);
          console.log('[DELINQUENT NOTE] row.balance_amount:', row.balance_amount);
          console.log('[DELINQUENT NOTE] row.amount_due:', row.amount_due);
          console.log('[DELINQUENT NOTE] FINAL payoffAmount:', payoffAmount);
          console.log('[DELINQUENT NOTE] payoffAmount type:', typeof payoffAmount);
          console.log('[DELINQUENT NOTE] payoffAmount truthy?:', !!payoffAmount);
          console.log('[DELINQUENT NOTE] certificateDlqGoodThruDate (param):', certificateDlqGoodThruDate);
          console.log('[DELINQUENT NOTE] FINAL goodThruDate:', goodThruDate);
          console.log('[DELINQUENT NOTE] dueDate:', dueDate);
          console.log('[DELINQUENT NOTE] taxYear:', taxYear);
          console.log('[DELINQUENT NOTE] installment:', installment);
          console.log('[DELINQUENT NOTE] ================================================================');
          
          // Determine installment type for better formatting
          let installmentDesc = '';
          if (installment) {
            // If full year (no specific installment), say "full installment"
            const instLower = installment.toLowerCase();
            if (instLower === 'annual' || instLower === 'full' || instLower === 'yearly') {
              installmentDesc = 'full installment ';
            } else {
              installmentDesc = `${installment} installment `;
            }
          } else {
            // No installment specified, assume full year
            installmentDesc = 'full installment ';
          }
          
          // Build the delinquent note
          let delinquentNote = `${taxYear} ${installmentDesc}taxes are delinquent.`;
          
          // Add payoff amount if available
          if (payoffAmount) {
            // Format amount with commas for thousands
            const numericAmount = typeof payoffAmount === 'number' ? payoffAmount : parseFloat(payoffAmount);
            const formattedAmount = isNaN(numericAmount) 
              ? `$${payoffAmount}`
              : `$${numericAmount.toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ',')}`;
            
            if (goodThruDate) {
              const formattedGoodThruDate = formatDateForNotes(goodThruDate);
              delinquentNote += ` Total payoff amount is ${formattedAmount} good thru ${formattedGoodThruDate}.`;
            } else {
              delinquentNote += ` Total payoff amount is ${formattedAmount}.`;
            }
          }
          
          // Add normal due date if available
          if (dueDate) {
            const formattedDueDate = formatDateForNotes(dueDate);
            delinquentNote += ` Normal due date is ${formattedDueDate}.`;
          }
          
          notesLines.push(delinquentNote);
        }
      });
      
      // Step 4: Join all notes with newlines
      const generatedNotes = notesLines.join('\n');
      console.log('[GENERATE NOTES V2] Generated notes:', generatedNotes);
      return generatedNotes;
      
    } catch (error) {
      console.error('[GENERATE NOTES V2] Error:', error);
      return '';
    }
  };
  
  /**
   * Format date for notes display (MM/DD/YYYY)
   */
  const formatDateForNotes = (dateInput) => {
    try {
      if (!dateInput || (typeof dateInput === 'string' && !dateInput.trim())) {
        return '';
      }
      
      // First, try to parse MM/DD/YYYY format directly (most common input format)
      if (typeof dateInput === 'string' && dateInput.includes('/')) {
        const parts = dateInput.split('/');
        if (parts.length === 3) {
          let [month, day, year] = parts;
          month = parseInt(month, 10);
          day = parseInt(day, 10);
          year = parseInt(year, 10);
          
          // Handle two-digit years - convert to four-digit
          if (year < 100) {
            // If year is 00-49, assume 2000s; if 50-99, assume 1900s
            // But for tax purposes, we should assume current century
            year = year < 50 ? 2000 + year : 1900 + year;
          }
          
          if (month >= 1 && month <= 12 && day >= 1 && day <= 31 && year >= 1900) {
            return `${String(month).padStart(2, '0')}/${String(day).padStart(2, '0')}/${year}`;
          }
        }
      }
      
      // Fallback to standard Date parsing for ISO format (YYYY-MM-DD)
      const date = new Date(dateInput);
      if (isNaN(date.getTime())) return dateInput; // Return as-is if invalid
      
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      const year = date.getFullYear();
      return `${month}/${day}/${year}`;
    } catch (error) {
      return dateInput; // Return as-is if error
    }
  };

  // AUTO-POPULATE: Update jurisdiction name when county or city changes
  // NOTE: Uses certificateData (editable form state) to allow users to update jurisdiction
  useEffect(() => {
    if (!orderData || !certificateData.jurisdiction_type) return;
    
    // Get location data for auto-population
    const propertyCounty = orderData?.property_county || orderData?.county || certificateData?.county;
    const propertyCity = orderData?.property_city || certificateData?.property_city;
    const propertyState = orderData?.property_state || certificateData?.state || '';

    // Generate new auto-populated jurisdiction name
    const newAutoPopulatedName = generateJurisdictionName(
      certificateData.jurisdiction_type,
      propertyCounty,
      propertyCity,
      propertyState
    );
    
    // Only update if we have a new auto-populated name
    if (newAutoPopulatedName && certificateData.current_tax_summary?.length > 0) {
      const firstRow = certificateData.current_tax_summary[0];
      const currentJurisdiction = firstRow.jurisdiction || '';
      
      // Determine if we should update:
      // 1. If jurisdiction is empty - always update
      // 2. If jurisdiction appears to be auto-generated (matches expected patterns) - update it
      // 3. If user manually edited it (doesn't match patterns) - don't update
      
      const shouldUpdate = 
        !currentJurisdiction.trim() || // Empty - always update
        isAutoGeneratedJurisdiction(currentJurisdiction, certificateData.jurisdiction_type); // Matches pattern - update
      
      if (shouldUpdate) {
        setCertificateData(prev => ({
          ...prev,
          current_tax_summary: prev.current_tax_summary.map((row, index) => 
            index === 0 ? { ...row, jurisdiction: newAutoPopulatedName } : row
          )
        }));
      }
    }
  }, [certificateData.county, certificateData.city, certificateData.jurisdiction_type, orderData]);
  
  /**
   * Check if a jurisdiction name appears to be auto-generated (follows our patterns)
   * This allows us to update auto-generated names while preserving manual edits
   */
  const isAutoGeneratedJurisdiction = (jurisdictionName, jurisdictionType) => {
    if (!jurisdictionName || !jurisdictionType) return false;
    
    const name = jurisdictionName.trim().toLowerCase();
    const type = jurisdictionType.toLowerCase();
    
    // Check if the jurisdiction name ends with the expected type keyword
    // County: ends with "county"
    // City: ends with "city"  
    // Township: ends with "township"
    // Borough: ends with "borough"
    // Village: ends with "village"
    // Parish: ends with "parish"
    // Municipality: ends with "municipality"
    
    const typeKeywords = {
      'county': 'county',
      'city': 'city',
      'township': 'township',
      'borough': 'borough',
      'village': 'village',
      'parish': 'parish',
      'municipality': 'municipality'
    };
    
    const expectedKeyword = typeKeywords[type];
    if (!expectedKeyword) return false;
    
    // Check if name ends with the expected keyword (case-insensitive)
    return name.endsWith(expectedKeyword);
  };

  /**
   * CLEAR NOTES: When tax collection frequency changes, clear the notes field
   * This ensures stale notes from the previous frequency don't persist
   */
  useEffect(() => {
    // Only clear if we have auto-generated notes (preserve manual edits)
    const currentNotes = certificateData.delinquent_taxes_notes || '';
    const looksAutoGenerated = currentNotes.includes('All priors are paid') || 
                                currentNotes.includes('taxes paid') || 
                                currentNotes.includes('taxes are due') ||
                                currentNotes.includes('taxes are delinquent');
    
    if (looksAutoGenerated && certificateData.collection_frequency) {
      console.log('[TAX NOTES CLEAR] Clearing notes due to collection_frequency change:', certificateData.collection_frequency);
      setCertificateData(prev => ({
        ...prev,
        delinquent_taxes_notes: ''
      }));
    }
  }, [certificateData.collection_frequency]);

  /**
   * AUTO-POPULATE: Generate tax certification notes when relevant fields change
   * Supports ALL tax collection frequencies and manually added jurisdictions
   * Uses V2 logic that generates notes row-by-row
   */
  useEffect(() => {
    console.log('[TAX NOTES V2 DEBUG] useEffect triggered');
    console.log('[TAX NOTES V2 DEBUG] current_tax_summary:', certificateData.current_tax_summary);
    
    // Only auto-generate if current_tax_summary has at least one row
    if (!certificateData.current_tax_summary || certificateData.current_tax_summary.length === 0) {
      console.log('[TAX NOTES V2 DEBUG] No current_tax_summary rows, skipping generation');
      return;
    }
    
    // Generate notes using V2 function (works for ALL frequencies)
    // Pass certificate-level delinquent data for enhanced notes
    console.log('[TAX NOTES V2 DEBUG] ============ CALLING FUNCTION ============');
    console.log('[TAX NOTES V2 DEBUG] certificateData.dlq_payoff_amount:', certificateData.dlq_payoff_amount);
    console.log('[TAX NOTES V2 DEBUG] certificateData.dlq_good_thru_date:', certificateData.dlq_good_thru_date);
    console.log('[TAX NOTES V2 DEBUG] certificateData object keys:', Object.keys(certificateData));
    console.log('[TAX NOTES V2 DEBUG] ===========================================');
    
    const generatedNotes = generateTaxCertificationNotesV2(
      certificateData.current_tax_summary,
      certificateData.dlq_payoff_amount,
      certificateData.dlq_good_thru_date,
      certificateData.collection_frequency
    );
    
    console.log('[TAX NOTES V2 DEBUG] Generated notes:', generatedNotes);
    
    // Only update if we generated something AND current notes are empty or match a previous auto-gen pattern
    if (generatedNotes) {
      const currentNotes = certificateData.delinquent_taxes_notes || '';
      
      console.log('[TAX NOTES V2 DEBUG] Current notes:', currentNotes);
      
      // Auto-update if notes are empty OR if notes look like they were auto-generated
      // (we preserve manual edits by checking if notes match our pattern)
      const looksAutoGenerated = currentNotes.includes('All priors are paid') || 
                                  currentNotes.includes('taxes paid') || 
                                  currentNotes.includes('taxes are due') ||
                                  currentNotes.includes('taxes are delinquent') ||
                                  currentNotes.includes('Taxes are paid annually') ||
                                  currentNotes.includes('Taxes are paid semi-annually') ||
                                  currentNotes.includes('Taxes are paid quarterly') ||
                                  currentNotes.includes('Taxes are paid monthly') ||
                                  currentNotes.trim() === '';
      
      console.log('[TAX NOTES V2 DEBUG] Looks auto-generated?:', looksAutoGenerated);
      
      if (looksAutoGenerated) {
        console.log('[TAX NOTES V2 DEBUG] Updating delinquent_taxes_notes with:', generatedNotes);
        setCertificateData(prev => ({
          ...prev,
          delinquent_taxes_notes: generatedNotes
        }));
      } else {
        console.log('[TAX NOTES V2 DEBUG] Skipping update - manual edits detected');
      }
    } else {
      console.log('[TAX NOTES V2 DEBUG] No notes generated');
    }
  }, [
    // Watch the entire current_tax_summary array (stringified to detect any changes)
    JSON.stringify(certificateData.current_tax_summary?.map(row => ({
      tax_year: row.tax_year,
      installment: row.installment,
      pay_status: row.pay_status,
      due_date: row.due_date,
      dlq_date: row.dlq_date,
      mailing_issue_date: row.mailing_issue_date,
      mailing_date: row.mailing_date,
      issue_date: row.issue_date
    }))),
    // CRITICAL FIX: Watch certificate-level delinquent fields to regenerate when they change
    certificateData.dlq_payoff_amount,
    certificateData.dlq_good_thru_date,
    certificateData.collection_frequency
  ]);

  /**
   * AUTO-POPULATE: Certificate-level Payoff Amount from per-row totals
   * Sums (base_tax + amounts_fees) for all delinquent rows to set certificate-level dlq_payoff_amount.
   * Per-row auto-sync is handled in handleArrayChange.
   */
  useEffect(() => {
    const hasDelinquent = certificateData.current_tax_summary?.some(row => {
      const status = row.pay_status?.toUpperCase();
      return status === 'DELINQUENT' || status === 'PARTIALLY DLQ' || 
             status === 'PARTIALLY DELINQUENT' || status === 'PARTIALLYDELINQUENT' || 
             status === 'PARTIALLYDLQ';
    });

    if (hasDelinquent) {
      const totalPayoff = certificateData.current_tax_summary?.reduce((sum, row) => {
        const status = row.pay_status?.toUpperCase();
        const isDelinquent = status === 'DELINQUENT' || status === 'PARTIALLY DLQ' || 
                            status === 'PARTIALLY DELINQUENT' || status === 'PARTIALLYDELINQUENT' || 
                            status === 'PARTIALLYDLQ';
        
        if (isDelinquent) {
          // Use per-row payoff if available, otherwise calculate from base_tax + amounts_fees
          if (row.dlq_payoff_amount != null && row.dlq_payoff_amount !== '' && !isNaN(parseFloat(row.dlq_payoff_amount))) {
            return rc(sum + parseFloat(row.dlq_payoff_amount));
          }
          const baseTax = parseFloat(row.base_tax) || 0;
          const fees = parseFloat(row.amounts_fees) || 0;
          return rc(sum + baseTax + fees);
        }
        return sum;
      }, 0) || 0;

      if (totalPayoff > 0 && certificateData.dlq_payoff_amount !== totalPayoff) {
        setCertificateData(prev => ({
          ...prev,
          dlq_payoff_amount: totalPayoff
        }));
      }
    }
  }, [
    JSON.stringify(certificateData.current_tax_summary?.map(row => ({
      base_tax: row.base_tax,
      amounts_fees: row.amounts_fees,
      pay_status: row.pay_status,
      dlq_payoff_amount: row.dlq_payoff_amount
    })))
  ]);

  // Constants for new fields
  const SOURCE_OPTIONS = [
    { value: 'calling', label: 'Calling' },
    { value: 'online', label: 'Online' },
    { value: 'fax_email', label: 'Fax/Email' },
    { value: 'mailaway', label: 'Mailaway' }
  ];
  
  // Helper functions for new UI elements
  const isFieldPrefilled = (fieldName) => {
    return prefilledFields.has(fieldName);
  };
  
  const clearPrefilledStatus = (fieldName) => {
    setPrefilledFields(prev => {
      const newSet = new Set(prev);
      newSet.delete(fieldName);
      return newSet;
    });
  };
  
  const formatAPNsArray = (apns) => {
    console.log('[FORMAT APN] Input apns:', apns);
    console.log('[FORMAT APN] Type:', typeof apns);
    
    if (Array.isArray(apns)) {
      const filtered = apns.filter(apn => apn && apn.trim());
      console.log('[FORMAT APN] Array filtered result:', filtered);
      return filtered;
    }
    if (typeof apns === 'string' && apns.trim()) {
      const split = apns.split(/[,;\s]+/).filter(apn => apn.trim());
      console.log('[FORMAT APN] String split result:', split);
      return split;
    }
    console.log('[FORMAT APN] Returning empty array');
    return [];
  };
  
  // addAPN, removeAPN, and handleAPNChange functions removed
  // Parcel/APN now uses simple text input with direct handleChange
  
  const handleExemptionTypeChange = (selectedTypes) => {
    const isNA = selectedTypes.includes('No Exemptions (N/A)');
    setCertificateData(prev => ({
      ...prev,
      exemption_types: selectedTypes,
      exemption_na: isNA,
      // Auto-set value to 0 and clear other description when N/A
      ...(isNA ? { exemption_value: 0, exemption_other: '' } : {}),
      // Clear "Other" description if "Other" is not selected
      ...(!selectedTypes.includes('Other') ? { exemption_other: '' } : {})
    }));
  };

  
  // Auto-save hook
  const {
    isSaving,
    lastSaved,
    lastSavedFormatted,
    error: saveError,
    lockedError,
    manualSave,
    getLocalBackup,
    clearLocalBackup
  } = useAutoSave({
    isDirty: hasUnsavedChanges && !readOnly,
    data: certificateData,
    onSaveSuccess: () => {
      // Clear unsaved changes flag after successful save
      setHasUnsavedChanges(false);
      // Feb 27, 2026 — Phase 2: refresh the dirty-diff baseline so
      // subsequent edits compare against the latest server state.
      try {
        lastSavedSnapshotRef.current = JSON.stringify(certificateData);
      } catch (_e) { /* circular ref — fall back to legacy gate */ }
    },
    saveFn: async (data) => {
      // Validate assessed values before saving (double-check with direct value inspection)
      const assessedFields = [
        { field: 'assessed_land_value', label: 'Land Value', value: data.assessed_land_value },
        { field: 'assessed_building_value', label: 'Building Value', value: data.assessed_building_value },
        { field: 'assessed_total_value', label: 'Total Assessed Value', value: data.assessed_total_value }
      ];
      
      const fieldsWithCents = [];
      for (const {field, label, value} of assessedFields) {
        if (value !== null && value !== undefined && value > 0) {
          // Check if value has cents (decimal portion that's not .00)
          const hasCents = (value % 1) !== 0;
          if (hasCents) {
            fieldsWithCents.push(label);
          }
        }
      }
      
      if (fieldsWithCents.length > 0) {
        // Block auto-save and show toast only once per error state
        if (!hasShownValidationToast) {
          toast.error(
            '⚠️ Fix validation errors to continue auto-saving.',
            { duration: 5000 }
          );
          setHasShownValidationToast(true);
        }
        // Return rejected promise to prevent save without causing uncaught error
        return Promise.reject({ 
          isValidationError: true, 
          message: 'Validation error: Assessed values contain cents',
          silent: true // Flag to suppress error display
        });
      }
      
      try {
        // PHASE 3B: Sync flat → parcel before saving to capture current edits
        // Pass the current data to avoid stale closure
        // Get the updated parcels immediately (don't wait for React state update)
        const updatedParcelsForSave = syncFlatToParcel(true, data);
        
        console.log('[SAVE DEBUG] Data to sync:', data);
        console.log('[SAVE DEBUG] Data parcel_apns:', data.parcel_apns);
        console.log('[SAVE DEBUG] Parcels to save:', updatedParcelsForSave);
        console.log('[SAVE DEBUG] Parcel 0 parcel_apn:', updatedParcelsForSave[0]?.parcel_apn);
        
        // PRE-SAVE: Check for duplicate APNs across parcels before sending to backend
        if (updatedParcelsForSave.length > 1) {
          const seenApns = {};
          for (let i = 0; i < updatedParcelsForSave.length; i++) {
            const apn = (updatedParcelsForSave[i].parcel_apn || '').trim();
            if (!apn) continue;
            const apnLower = apn.toLowerCase();
            if (seenApns[apnLower] !== undefined) {
              const dupParcel = seenApns[apnLower] + 1;
              const currentParcel = i + 1;
              // Set inline validation error on the Parcel/APN field
              setValidationErrors(prev => ({
                ...prev,
                parcel_apns: `Parcel ${currentParcel}: This Parcel/APN duplicates Parcel ${dupParcel}. Each parcel must be unique.`
              }));
              return Promise.reject({
                isValidationError: true,
                message: 'Duplicate Parcel/APN detected',
                silent: true
              });
            }
            seenApns[apnLower] = i;
          }
          // Clear any previous duplicate APN error if now resolved
          setValidationErrors(prev => {
            if (prev.parcel_apns && prev.parcel_apns.includes('duplicates Parcel')) {
              const { parcel_apns, ...rest } = prev;
              return rest;
            }
            return prev;
          });
        }
        
        // Sanitize payload to ensure all required fields are present
        const sanitizedData = sanitizeCertificateData(data);
        
        // PHASE 3B: Apply adapter to transform flat frontend + parcels → multi-parcel backend
        // Use the freshly updated parcels, not the state (which might be stale)
        const multiParcelPayload = adaptFrontendToBackend(sanitizedData, updatedParcelsForSave);
        
        console.log('[SAVE] === FINAL PAYLOAD BEING SENT TO BACKEND ===');
        console.log('[SAVE] Full payload:', multiParcelPayload);
        console.log('[SAVE] Payload parcels array:', multiParcelPayload.parcels);
        console.log('[SAVE] Parcel 0 from payload:', multiParcelPayload.parcels[0]);
        console.log('[SAVE] Parcel 0 parcel_apn:', multiParcelPayload.parcels[0]?.parcel_apn);
        console.log('[SAVE] Legacy parcel_apns field:', multiParcelPayload.parcel_apns);
        
        const response = await axios.post(`${API}/orders/${orderData.id}/tax-cert`, {
          certificate: multiParcelPayload
        });
        
        // Process structured validation warnings from backend (Soft Save model)
        const warnings = response.data?.validation_warnings || [];
        setSaveValidationWarnings(warnings);
        if (warnings.length > 0) {
          console.log(`[SAVE] Saved with ${warnings.length} validation warning(s):`, warnings);
        }
        
        // Propagate review status changes from backend (auto-reset after tax data edit)
        if (response.data?.tax_sheet_review) {
          window.dispatchEvent(new CustomEvent('taxSheetReviewUpdate', {
            detail: response.data.tax_sheet_review
          }));
        }
        
        return response.data;
      } catch (error) {
        // Re-throw with flag to identify as validation error if it's a 422
        if (error.response?.status === 422) {
          return Promise.reject({
            isValidationError: true,
            message: 'Server validation failed',
            silent: true
          });
        }
        // Jun 2, 2026 — Intercept HTTP 423 LOCKED and surface a clear,
        // actionable message instead of the cryptic "Request failed with
        // status code 423". Backend returns:
        //   { detail: { message: "...", locked: true,
        //               requires_override_reason?: true, order_status: "..." } }
        // After we set the lock state, throw silent:true so the autosave
        // hook stops the retry storm — saves will resume only after the
        // user navigates / refreshes (and the backend lock clears or the
        // user is granted an override).
        if (error.response?.status === 423) {
          const d = error.response?.data?.detail || {};
          const reason = (typeof d === 'object' && d.message) ? d.message
                       : (typeof d === 'string' ? d : 'This order is locked for editing.');
          const orderStatus = d.order_status;
          const friendly = orderStatus
            ? `${reason} (Status: ${orderStatus})`
            : reason;
          // Persist a sticky banner-style error so the user sees WHY,
          // not just "Save failed". The setter wraps the next paint —
          // pulling from the local ref keeps the hook stable.
          try {
            toast.error(friendly, { duration: 8000, id: 'tax-cert-lock' });
          } catch (_) { /* sonner unavailable — fall through silently */ }
          return Promise.reject({
            isLockedError: true,
            isValidationError: true,  // re-use the silent path in useAutoSave
            silent: true,
            message: friendly,
            lockedOrderStatus: orderStatus || null,
            requiresOverrideReason: !!d.requires_override_reason,
          });
        }
        // Intercept 400 duplicate APN errors from backend — show friendly UI message
        if (error.response?.status === 400) {
          const detail = error.response?.data?.detail || '';
          if (typeof detail === 'string' && detail.includes('Duplicate Parcel/APN')) {
            // Parse "Parcel X (same as Parcel Y)" from backend message
            const match = detail.match(/Parcel (\d+) \(same as Parcel (\d+)\)/);
            const friendlyMsg = match
              ? `Parcel ${match[1]}: This Parcel/APN duplicates Parcel ${match[2]}. Each parcel must be unique.`
              : 'Duplicate Parcel/APN detected. Each parcel must have a unique APN.';
            setValidationErrors(prev => ({ ...prev, parcel_apns: friendlyMsg }));
            return Promise.reject({
              isValidationError: true,
              message: 'Duplicate Parcel/APN detected',
              silent: true
            });
          }
        }
        // Re-throw other errors normally
        throw error;
      }
    },
    debounceMs: 1500,
    intervalMs: 30000,
    formId: 'tax-certificate',
    orderId: orderData.id
  });
  
  // Session monitoring
  const sessionMonitor = useSessionMonitor({
    onTimeout: () => {
      // Handle logout - this will be handled at the app level
      console.log('Session timeout - component level');
    },
    emergencySave: async () => {
      if (hasUnsavedChanges && !readOnly) {
        await manualSave();
      }
    },
    isDirty: hasUnsavedChanges
  });
  
  // Track unsaved changes - only after initial data load is FULLY complete
  // Hash-diff dirty watcher (Feb 27, 2026 — Phase 2).
  // Replaces the prior `dataLoadCompleteRef && !initialLoad` timing
  // gate that fired false-positive "Unsaved changes" when child
  // emitters (JurisdictionManager auto-notes, sub-jurisdiction
  // enrichments, etc.) landed after the 500ms band-aid expired.
  //
  // The watcher now requires BOTH:
  //   (a) the initial server-known snapshot has been captured into
  //       lastSavedSnapshotRef (set inside fetchCertificateData), AND
  //   (b) a structural diff exists between the current certificateData
  //       and that snapshot — guards against children emitting their
  //       own value back at us unchanged.
  useEffect(() => {
    if (!dataLoadCompleteRef.current || initialLoad) return;
    if (!lastSavedSnapshotRef.current) return;
    try {
      const currentSnap = JSON.stringify(certificateData);
      if (currentSnap === lastSavedSnapshotRef.current) return;
      if (settleWindowActiveRef.current) {
        // Post-load settle window — absorb child-component initial
        // emissions into the baseline rather than marking dirty.
        lastSavedSnapshotRef.current = currentSnap;
        return;
      }
      setHasUnsavedChanges(true);
    } catch (_e) {
      // Fallback to legacy behavior if stringify fails (circular ref, etc.)
      setHasUnsavedChanges(true);
    }
  }, [certificateData, initialLoad]);

  // Load existing certificate data
  useEffect(() => {
    const fetchCertificateData = async () => {
      try {
        // First, check for local backup
        const localBackup = await getLocalBackup();
        
        // Then get server data
        let serverData = null;
        try {
          const response = await axios.get(`${API}/orders/${orderData.id}/tax-cert`);
          serverData = response.data?.certificate;
        } catch (error) {
          if (error.response?.status !== 404) {
            console.error('Error fetching certificate:', error);
          }
        }
        
        // Load server data directly without restore prompt
        // Validation will automatically run and show errors for invalid data
        if (serverData) {
          // PHASE 3B: Apply adapter to transform multi-parcel backend → flat frontend + parcels array
          const { flatData, parcels: loadedParcels } = adaptBackendToFrontend(serverData);
          
          console.log('[LOAD] Loaded flat data jurisdictions count:', (flatData.jurisdictions || []).length);
          console.log('[LOAD] Loaded parcels count:', loadedParcels.length);
          if (loadedParcels[0]) {
            console.log('[LOAD] Parcel 0 jurisdictions count:', (loadedParcels[0].jurisdictions || []).length);
          }
          console.log('[LOAD] Server data root jurisdictions:', (serverData.jurisdictions || []).length);
          console.log('[LOAD] Server data parcels:', (serverData.parcels || []).length);
          if (serverData.parcels?.[0]) {
            console.log('[LOAD] Server parcels[0].jurisdictions:', (serverData.parcels[0].jurisdictions || []).length);
          }
          
          // Set parcels first
          setParcels(loadedParcels);
          
          // MIGRATION: Move certificate-level exemption data to per-jurisdiction
          // Old structure: flatData.exemption_types = [...], flatData.exemption_value = X
          // New structure: each jurisdiction has its own exemption_types, exemption_value, exemption_other
          if (flatData.jurisdictions?.length > 0) {
            const hasRootExemptions = (flatData.exemption_types || []).length > 0 || flatData.exemption_value != null;
            const firstJurHasExemptions = (flatData.jurisdictions[0].exemption_types || []).length > 0;
            if (hasRootExemptions && !firstJurHasExemptions) {
              // Migrate root exemption data to first jurisdiction
              flatData.jurisdictions = flatData.jurisdictions.map((j, idx) => {
                if (idx === 0) {
                  return {
                    ...j,
                    exemption_types: flatData.exemption_types || [],
                    exemption_value: flatData.exemption_value,
                    exemption_other: flatData.exemption_other || '',
                    exemption_na: flatData.exemption_na || false,
                  };
                }
                return {
                  ...j,
                  exemption_types: j.exemption_types || [],
                  exemption_value: j.exemption_value ?? null,
                  exemption_other: j.exemption_other || '',
                  exemption_na: j.exemption_na || false,
                };
              });
            } else {
              // Ensure all jurisdictions have exemption fields initialized
              flatData.jurisdictions = flatData.jurisdictions.map(j => ({
                ...j,
                exemption_types: j.exemption_types || [],
                exemption_value: j.exemption_value ?? null,
                exemption_other: j.exemption_other || '',
                exemption_na: j.exemption_na || false,
              }));
            }
          }
          
          // CRITICAL FIX: Set flatData which includes parcel_apns from Parcel 1
          // This ensures the UI shows the correct data immediately
          setCertificateData(prev => ({ ...prev, ...flatData }));
          
          // Set active parcel index (if already 0, won't trigger useEffect)
          setActiveParcelIndex(0);
          
          // CRITICAL FIX: Force sync parcel to flat to ensure UI is populated
          // This is needed because if activeParcelIndex was already 0, the useEffect won't trigger
          // We need to manually sync the parcel data to the flat state
          if (loadedParcels.length > 0 && loadedParcels[0]) {
            const parcel0 = loadedParcels[0];
            console.log('[LOAD] Forcing sync of Parcel 0 to flat state');
            console.log('[LOAD] Parcel 0 parcel_apn to sync:', parcel0.parcel_apn);
            
            // Update certificateData with Parcel 0's data
            console.log('[LOAD] === SETTING certificateData.parcel_apns ===');
            console.log('[LOAD] Value from parcel0.parcel_apn:', parcel0.parcel_apn);
            console.log('[LOAD] Will set certificateData.parcel_apns to:', parcel0.parcel_apn || '');
            
            setCertificateData(prev => ({
              ...prev,
              parcel_apns: parcel0.parcel_apn || '',
              source: parcel0.source,
              source_notes: parcel0.source_notes || '',
              collection_frequency: parcel0.tax_collected || 'annually',
              jurisdiction_id: parcel0.jurisdiction_id,
              jurisdiction_type: parcel0.jurisdiction_type,
              make_checks_payable_to: parcel0.make_checks_payable_to || '',
              assessed_land_value: parcel0.assessed_land_value,
              assessed_building_value: parcel0.assessed_building_value,
              assessed_total_value: parcel0.assessed_total_value,
              assessed_value_na: parcel0.assessed_value_na,
              assessed_value_na_reason: parcel0.assessed_value_na_reason,
              assessed_value_na_explanation: parcel0.assessed_value_na_explanation,
              assessed_total_manual: parcel0.assessed_total_manual || false,
              exemption_types: parcel0.exemption_types || [],
              exemption_other: parcel0.exemption_other || '',
              exemption_value: parcel0.exemption_value,
              exemption_na: parcel0.exemption_na || false,
              current_tax_summary: parcel0.current_tax_summary || [],
              has_delinquent: parcel0.has_delinquent,
              dlq_payoff_amount: parcel0.dlq_payoff_amount,
              dlq_good_thru_date: parcel0.dlq_good_thru_date,
              delinquent_taxes_notes: parcel0.delinquent_taxes_notes || '',
              mlc_charges: parcel0.mlc_charges,
              mlc_24_month_charges: parcel0.mlc_24_month_charges,
              mlc_eta_days: parcel0.mlc_eta_days,
              sase_required: parcel0.sase_required || false,
              info_obtained_back: parcel0.info_obtained_back,
              tax_collector_email: parcel0.tax_collector_email || '',
              tax_collector_website: parcel0.tax_collector_website || '',
              tax_collector_working_hours: parcel0.tax_collector_working_hours || '',
              // Per-parcel jurisdictions (new nested structure)
              jurisdictions: parcel0.jurisdictions || []
            }));
          }
          
          setHasUnsavedChanges(false);
          
          // Clear any old local backup since we're loading fresh from server
          if (localBackup) {
            await clearLocalBackup();
          }
        } else if (localBackup && localBackup.data) {
          // No server data but have local backup — restore it silently.
          // We intentionally DO NOT flip hasUnsavedChanges here. The
          // snapshot captured in the `finally` block below includes
          // the restored values, so the hash-diff watcher will only
          // mark the form dirty when the user actually edits a field.
          // (Earlier this set hasUnsavedChanges=true, which surfaced
          // a false "Unsaved changes" warning on every reload of any
          // order that had stale IndexedDB backup data — most often
          // External Portal orders where partial-data autosave 422s.)
          setCertificateData(prev => ({ ...prev, ...localBackup.data }));
          setHasUnsavedChanges(false);
        } else {
          // No data at all - use defaults
          if (false) { // Placeholder to maintain structure
            setCertificateData(prev => ({ ...prev, ...serverData }));
          }
          setHasUnsavedChanges(false);
        }
        
      } catch (error) {
        console.error('Error in fetchCertificateData:', error);
        toast.error('Failed to load tax certificate');
      } finally {
        setInitialLoad(false);
        setLoading(false);
        
        // Feb 27, 2026 — Phase 2: capture the post-load snapshot on
        // the NEXT microtask so React has flushed all setCertificateData
        // calls above. The dirty watcher (line ~4459) then compares
        // every subsequent certificateData update against this snapshot
        // and only flips `hasUnsavedChanges` on real diffs. The 500ms
        // setTimeout is preserved as a backstop, but the snapshot does
        // the real work.
        setTimeout(() => {
          setCertificateData((current) => {
            try {
              lastSavedSnapshotRef.current = JSON.stringify(current);
            } catch (_e) {
              lastSavedSnapshotRef.current = null;
            }
            dataLoadCompleteRef.current = true;
            // Open the settle window so child-component initial
            // emissions are absorbed into the baseline. Auto-closes
            // after 2 s; subsequent changes are real user edits.
            settleWindowActiveRef.current = true;
            if (settleTimerRef.current) clearTimeout(settleTimerRef.current);
            settleTimerRef.current = setTimeout(() => {
              settleWindowActiveRef.current = false;
              settleTimerRef.current = null;
            }, 2000);
            return current;  // no state change
          });
        }, 0);
      }
    };
    
    const loadAutoLinkingData = async () => {
      // Also fetch auto-linking data for Update Tax Search orders
      if (orderData.client_product === 'Update Tax Search') {
        await fetchPreviousOrder();
        await fetchRelatedFiles();
      }
    };
    
    fetchCertificateData();
    loadAutoLinkingData();
    fetchOrderFiles();
  }, [orderData.id]);

  // Re-fetch order files when Files tab changes (upload/delete)
  useEffect(() => {
    const handler = () => fetchOrderFiles();
    window.addEventListener('filesChangedInOrder', handler);
    return () => window.removeEventListener('filesChangedInOrder', handler);
  }, [orderData.id]);

  // Cleanup the post-load settle timer on unmount or order id change.
  useEffect(() => {
    return () => {
      if (settleTimerRef.current) {
        clearTimeout(settleTimerRef.current);
        settleTimerRef.current = null;
      }
      settleWindowActiveRef.current = false;
    };
  }, [orderData.id]);

  
  // Detect prefilled fields from Order Desk data
  useEffect(() => {
    if (!initialLoad) {
      const prefilledFieldsToSet = new Set();
      const now = new Date().toISOString();
      
      // Check for prefilled property fields
      if (orderData.property_city) prefilledFieldsToSet.add('city');
      if (orderData.property_state) prefilledFieldsToSet.add('state');
      if (orderData.property_zip) prefilledFieldsToSet.add('zip');
      if (orderData.property_county) prefilledFieldsToSet.add('county');
      if (orderData.parcel_apns && orderData.parcel_apns.length > 0) prefilledFieldsToSet.add('parcel_apns');
      if (orderData.borrower_name) prefilledFieldsToSet.add('borrower_owner_name');
      if (orderData.property?.property_address_full) prefilledFieldsToSet.add('full_address');
      
      if (prefilledFieldsToSet.size > 0) {
        setPrefilledFields(prefilledFieldsToSet);
        setPrefilledAt(now);
      }
    }
  }, [initialLoad, orderData]);

  // ─────────────────────────────────────────────────────────────────────
  // BUG FIX (Feb 22, 2026) — Property Summary Strip showed stale county
  // ─────────────────────────────────────────────────────────────────────
  // When county/city/state/zip/address/borrower are edited via the Order
  // Desk "Edit Unlocked Fields" flow, the parent re-fetches `orderData`
  // with the new values. But `certificateData` for these 6 fields was
  // only hydrated in the `useState(() => ({...}))` initializer at mount
  // (≈ L1727–1733). Nothing re-synced them, so the compact Property
  // Summary Strip at the top of the Tax Certificate panel (≈ L7022)
  // kept rendering the old county (e.g. "Newton") even though the
  // jurisdiction panel below, which reads from `tax_certificates`, was
  // already showing the fresh value ("Boone County").
  //
  // Fix: mirror the canonical order-level property/borrower fields from
  // `orderData` → `certificateData` whenever they change post-initial-
  // load. Safe to clobber because TaxCertificatePanel does NOT expose
  // any in-panel edit inputs for these fields (`propertyBorrowerReadOnly`
  // is declared but unused; property/borrower edits happen in the
  // Order Summary / Order Desk flow, not here).
  useEffect(() => {
    if (initialLoad) return;

    const nextFullAddress =
      orderData?.property?.address
      || orderData?.property?.property_address_full
      || '';
    const nextCity = orderData?.property_city || orderData?.property?.city || '';
    const nextState = orderData?.property_state || orderData?.property?.state || '';
    const nextZip = orderData?.property_zip || orderData?.property?.zip_code || '';
    const nextCounty = orderData?.property_county || orderData?.property?.county || '';
    const nextBorrower =
      orderData?.borrower_name
      || orderData?.customer?.name
      || orderData?.customer?.customer_name
      || '';

    setCertificateData(prev => {
      // Only patch the fields that actually differ — avoids an
      // unnecessary render cascade through the parent-sync effect.
      if (
        prev.full_address === nextFullAddress &&
        prev.city === nextCity &&
        prev.state === nextState &&
        prev.zip === nextZip &&
        prev.county === nextCounty &&
        prev.borrower_owner_name === nextBorrower
      ) {
        return prev;
      }
      return {
        ...prev,
        full_address: nextFullAddress,
        city: nextCity,
        state: nextState,
        zip: nextZip,
        county: nextCounty,
        borrower_owner_name: nextBorrower,
      };
    });
  }, [
    initialLoad,
    orderData?.property?.address,
    orderData?.property?.property_address_full,
    orderData?.property_city,
    orderData?.property?.city,
    orderData?.property_state,
    orderData?.property?.state,
    orderData?.property_zip,
    orderData?.property?.zip_code,
    orderData?.property_county,
    orderData?.property?.county,
    orderData?.borrower_name,
    orderData?.customer?.name,
    orderData?.customer?.customer_name,
  ]);

  // Pre-load sub-jurisdictions for existing nested jurisdictions that require sub-selection
  // This ensures the Jurisdiction Name dropdown appears for already-saved data
  useEffect(() => {
    if (initialLoad || !certificateData.jurisdictions?.length) return;

    const propertyCounty = (certificateData.county || orderData.property_county || '').trim();
    const normalizedState = (orderData?.property_state || '').trim().toUpperCase();

    certificateData.jurisdictions.forEach(async (jur, jurIndex) => {
      const jurType = (jur.jurisdiction_type || '').toLowerCase();
      const rowKey = `nested_${jurIndex}`;
      const currentRowState = getRowState(rowKey);

      // Only load if type requires sub-jurisdiction AND sub-jurisdictions not yet loaded
      // CRITICAL: Skip if the handler is actively loading (to prevent race condition)
      //
      // Feb 6, 2026 — Added `subJurisdictionsLoadAttempted` idempotency
      // marker so an EMPTY API result (e.g. PA School type with no matching
      // directory row) terminates this effect's check. Previously, an
      // empty result wrote `subJurisdictions: []` back; the guard
      // `subJurisdictions.length === 0` remained true; combined with
      // `getRowState`/`setRowState` being in the dep array (each
      // rowJurisdictionState write produced new function identities),
      // this effect re-ran indefinitely. Network tabs showed 170+
      // identical `jurisdictions?jurisdiction_type=school&...` requests
      // and starved save-settlement.
      // See /app/memory/CHANGELOG.md "Feb 6, 2026 — Sub-jurisdiction
      // preload infinite-loop fix" for the full RCA.
      if (SUB_JURISDICTION_TYPES.includes(jurType)
          && currentRowState.subJurisdictions.length === 0
          && !currentRowState.subJurisdictionsLoading
          && !currentRowState.subJurisdictionsLoadAttempted  // ← Fix 3: stop after one attempt even when empty
          && !currentRowState.showSubJurisdictionSelector
          && !activeFetchingRowsRef.current.has(rowKey)) {

        // Fix 2: mark this rowKey in-flight BEFORE any await. Mirrors the
        // pattern used by the [TYPE_SELECT] handler. Prevents re-entrant
        // effect runs from issuing duplicate fetches even before the
        // setRowState write lands.
        activeFetchingRowsRef.current.add(rowKey);

        let filteredJurisdictions = [];
        try {
          // JX-LOOKUP-001: shared lookup (Tier 1 county → Tier 2 unparented/same-county; Town↔Township siblings).
          const result = await fetchSubJurisdictions({
            type: jurType,
            state: normalizedState,
            county: propertyCounty,
            useCountyFilter: jurType !== 'special_district',
          });
          filteredJurisdictions = result.items;
        } catch (err) {
          console.error('[PRELOAD] Error fetching sub-jurisdictions:', err);
        } finally {
          // Fix 2: always release the in-flight gate, even on network error.
          activeFetchingRowsRef.current.delete(rowKey);
        }

        setRowState(rowKey, {
          subJurisdictions: filteredJurisdictions,
          subJurisdictionsLoading: false,
          subJurisdictionsLoadAttempted: true,   // ← Fix 3: mark as attempted so empty results don't re-fire
          // Preserve existing showSubJurisdictionSelector — don't overwrite if handler already set it
          selectedCountyForSubJurisdiction: propertyCounty
        });
      }
    });
    // Fix 1: `getRowState` and `setRowState` REMOVED from the dep array.
    // Both are `useCallback`s with `[rowJurisdictionState]` deps, so each
    // call to `setRowState` produces a new `getRowState` identity, which
    // would re-trigger this effect. The functions close over rowState
    // freshly per render so the values they read remain correct without
    // needing to be deps here.
  }, [initialLoad, certificateData.jurisdictions, certificateData.county, orderData.property_county, orderData.property_state]);

  // Handle form changes
  const handleChange = (field, value) => {
    setCertificateData(prev => ({ ...prev, [field]: value }));
    // Clear validation error for this field
    if (validationErrors[field]) {
      setValidationErrors(prev => ({ ...prev, [field]: null }));
    }
    // Clear prefilled status for this field
    clearPrefilledStatus(field);
  };

  // Handle array field changes
  const handleArrayChange = (arrayName, index, field, value) => {
    setCertificateData(prev => {
      const updated = prev[arrayName].map((item, i) => 
        i === index ? { ...item, [field]: value } : item
      );

      // Auto-sync dlq_payoff_amount for delinquent rows when amounts change
      if (arrayName === 'current_tax_summary' && ['base_tax', 'amounts_fees', 'pay_status'].includes(field)) {
        const row = updated[index];
        const isDelinquent = ['Delinquent', 'Partially Dlq'].includes(row.pay_status);

        if (isDelinquent && !row.dlq_payoff_manual) {
          const baseTax = parseFloat(row.base_tax) || 0;
          const fees = parseFloat(row.amounts_fees) || 0;
          const calc = rc(baseTax + fees);
          updated[index] = { ...updated[index], dlq_payoff_amount: calc > 0 ? calc : null };
        }
        // If status changed away from delinquent, reset manual flag
        if (field === 'pay_status' && !isDelinquent) {
          updated[index] = { ...updated[index], dlq_payoff_manual: false };
        }
      }

      return { ...prev, [arrayName]: updated };
    });
    
    // BUG FIX: Clear parcel-level validation error for current_tax_summary when user updates
    if (arrayName === 'current_tax_summary') {
      setValidationErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors.current_tax_summary;
        // Also clear individual row errors for this field
        delete newErrors[`tax_summary_${index}_${field}`];
        return newErrors;
      });
    }
  };

  /**
   * SPEC: Auto-default Delinquency Date = Due Date + 1 day
   * When Due Date is entered/changed and Delinquency Date is empty or equal to old Due Date,
   * automatically set Delinquency Date to Due Date + 1 day
   */
  const handleDueDateChange = (index, newDueDate) => {
    const row = certificateData.current_tax_summary[index];
    const oldDueDate = row?.due_date || '';
    const currentDlqDate = row?.dlq_date || '';
    
    // Helper to parse MM/DD/YYYY and add 1 day
    const calculateDlqDate = (dueDateStr) => {
      if (!dueDateStr || !dueDateStr.trim()) return '';
      try {
        const parts = dueDateStr.split('/');
        if (parts.length !== 3) return '';
        const [month, day, year] = parts;
        const dueDate = new Date(parseInt(year), parseInt(month) - 1, parseInt(day));
        if (isNaN(dueDate.getTime())) return '';
        
        // Add 1 day to get Delinquency Date
        dueDate.setDate(dueDate.getDate() + 1);
        
        const newMonth = String(dueDate.getMonth() + 1).padStart(2, '0');
        const newDay = String(dueDate.getDate()).padStart(2, '0');
        const newYear = dueDate.getFullYear();
        return `${newMonth}/${newDay}/${newYear}`;
      } catch {
        return '';
      }
    };
    
    // Calculate what the old default dlq_date would have been
    const oldDefaultDlqDate = calculateDlqDate(oldDueDate);
    const newDefaultDlqDate = calculateDlqDate(newDueDate);
    
    // Auto-set Delinquency Date if:
    // 1. dlq_date is empty, OR
    // 2. dlq_date equals the old default (meaning user never manually changed it)
    const shouldAutoSetDlqDate = !currentDlqDate || currentDlqDate === oldDefaultDlqDate || currentDlqDate === oldDueDate;
    
    setCertificateData(prev => ({
      ...prev,
      current_tax_summary: prev.current_tax_summary.map((item, i) => {
        if (i !== index) return item;
        return {
          ...item,
          due_date: newDueDate,
          // Auto-set dlq_date only if conditions are met and Pay Status requires it
          dlq_date: shouldAutoSetDlqDate && getFieldEnabledState(item.pay_status).delinquencyDate
            ? newDefaultDlqDate
            : item.dlq_date
        };
      })
    }));
    
    // Clear validation errors for this field
    setValidationErrors(prev => {
      const newErrors = { ...prev };
      delete newErrors.current_tax_summary;
      delete newErrors[`tax_summary_${index}_due_date`];
      delete newErrors[`tax_summary_${index}_dlq_date`];
      return newErrors;
    });
  };

  // Validate all date fields for a specific row (cross-field validation)
  const validateAllDateFieldsForRow = (index) => {
    const row = certificateData.current_tax_summary[index];
    if (!row) return;

    const parseDate = (dateStr) => {
      if (!dateStr || !dateStr.trim()) return null;
      try {
        const [month, day, year] = dateStr.split('/');
        if (!month || !day || !year) return null;
        const date = new Date(year, parseInt(month) - 1, parseInt(day));
        // Validate the date is actually valid (e.g., not Feb 30)
        if (date.getMonth() !== parseInt(month) - 1) return null;
        return date;
      } catch {
        return null;
      }
    };

    const mailingDate = parseDate(row.mailing_issue_date);
    const dueDate = parseDate(row.due_date);
    const delinquencyDate = parseDate(row.dlq_date);
    const paidDate = parseDate(row.paid_date);
    const isPaidStatus = row.pay_status === 'Paid' || row.pay_status === 'PAID' || row.pay_status === 'paid';
    
    // Get field enabled state based on current Pay Status
    const fieldEnabledState = getFieldEnabledState(row.pay_status);

    const newErrors = { ...validationErrors };

    // Clear all existing errors for this row's date fields
    delete newErrors[`tax_summary_${index}_mailing_issue_date`];
    delete newErrors[`tax_summary_${index}_due_date`];
    delete newErrors[`tax_summary_${index}_dlq_date`];
    delete newErrors[`tax_summary_${index}_paid_date`];
    delete newErrors[`tax_summary_${index}_balance_amount`];

    // 1. Validate Mailing/Issue Date
    if (!row.mailing_issue_date || !row.mailing_issue_date.trim()) {
      newErrors[`tax_summary_${index}_mailing_issue_date`] = 'Mailing / Issue Date is required';
    } else if (!mailingDate) {
      newErrors[`tax_summary_${index}_mailing_issue_date`] = 'Please enter a valid Mailing / Issue Date in MM/DD/YYYY format';
    } else if (dueDate && mailingDate > dueDate) {
      newErrors[`tax_summary_${index}_mailing_issue_date`] = 'Mailing / Issue Date cannot be after the Due Date';
    }

    // 2. Validate Due Date
    if (!row.due_date || !row.due_date.trim()) {
      newErrors[`tax_summary_${index}_due_date`] = 'Due Date is required';
    } else if (!dueDate) {
      newErrors[`tax_summary_${index}_due_date`] = 'Please enter a valid Due Date in MM/DD/YYYY format';
    } else if (mailingDate && dueDate < mailingDate) {
      newErrors[`tax_summary_${index}_due_date`] = 'Due Date must be on or after the Mailing / Issue Date';
    }

    // 3. Validate Delinquency Date (ONLY if field is enabled for current Pay Status)
    if (fieldEnabledState.delinquencyDate) {
      if (row.dlq_date && row.dlq_date.trim()) {
        if (!delinquencyDate) {
          newErrors[`tax_summary_${index}_dlq_date`] = 'Please enter a valid Delinquency Date in MM/DD/YYYY format';
        } else if (dueDate && delinquencyDate <= dueDate) {
          newErrors[`tax_summary_${index}_dlq_date`] = 'Delinquency Date must be after the Due Date';
        } else if (row.tax_year && delinquencyDate) {
          // Additional validation: Delinquency Date year should be >= Tax Year
          // FISCAL-DLQ-001: fiscal-year jurisdictions (e.g. MA FY2027 = Jul 2026–Jun 2027)
          // legitimately have due/dlq dates in the calendar year before the tax year.
          const taxYearNum = parseInt(row.tax_year, 10);
          const dlqYear = delinquencyDate.getFullYear();
          if (dlqYear < taxYearNum) {
            const fiscalRow =
              (dueDate && dueDate.getFullYear() === taxYearNum - 1) ||
              (mailingDate && mailingDate.getFullYear() === taxYearNum - 1);
            if (!(fiscalRow && dlqYear === taxYearNum - 1)) {
              newErrors[`tax_summary_${index}_dlq_date`] = `Delinquency Date year (${dlqYear}) cannot be before Tax Year (${taxYearNum})`;
            }
          }
        }
      }
    }

    // 4. Validate Paid Date (ONLY if field is enabled for current Pay Status)
    if (fieldEnabledState.paidDate) {
      const isPartiallyPaid = row.pay_status?.includes('Partially') || row.pay_status?.includes('PARTIALLY');
      const paidDateRequired = isPaidStatus || isPartiallyPaid;
      
      // First check: Paid Date is REQUIRED when Pay Status = "Paid", "Partially Paid", or "Partially Dlq"
      if (paidDateRequired && (!row.paid_date || !row.paid_date.trim())) {
        newErrors[`tax_summary_${index}_paid_date`] = 'Paid Date is required for this Pay Status';
      }
      
      // Second check: If Paid Date is entered, validate it
      if (row.paid_date && row.paid_date.trim()) {
        // Check format validity
        if (!paidDate) {
          newErrors[`tax_summary_${index}_paid_date`] = 'Please enter a valid Paid Date in MM/DD/YYYY format';
        } else {
          // Check date relationship: Paid Date MUST be on or after Mailing/Issue Date
          // Note: Paid Date can be BEFORE Due Date (early payment is valid)
          if (mailingDate && paidDate < mailingDate) {
            newErrors[`tax_summary_${index}_paid_date`] = 'Paid Date must be on or after the Mailing / Issue Date';
          }
        }
      }
    }
    
    // 5. Validate Balance Amount (ONLY if field is enabled for current Pay Status)
    if (fieldEnabledState.balanceAmount) {
      const isPartiallyPaid = row.pay_status?.includes('Partially') || row.pay_status?.includes('PARTIALLY');
      const balanceAmountRequired = isPartiallyPaid;
      
      if (balanceAmountRequired) {
        if (!row.balance_amount || !row.balance_amount.trim()) {
          newErrors[`tax_summary_${index}_balance_amount`] = 'Balance Amount is required for Partially Paid/Delinquent status';
        } else {
          const balanceAmount = parseFloat(row.balance_amount);
          if (isNaN(balanceAmount) || balanceAmount < 0) {
            newErrors[`tax_summary_${index}_balance_amount`] = 'Balance Amount must be a positive number';
          } else {
            // Check if balance exceeds total (base tax + fees)
            const baseTax = parseFloat(row.base_tax) || 0;
            const fees = parseFloat(row.amounts_fees) || 0;
            const total = rc(baseTax + fees);
            
            if (total > 0 && balanceAmount > total) {
              newErrors[`tax_summary_${index}_balance_amount`] = 'Balance Amount cannot exceed Base Tax + Fees';
            }
          }
        }
      }
    }

    setValidationErrors(newErrors);
  };

  // Validate individual date field on blur (now validates all related fields)
  const validateDateField = (index, field) => {
    // Validate all date fields in the row to catch cross-field validation issues
    validateAllDateFieldsForRow(index);
  };

  // Helper function to determine field enable/disable state based on Pay Status
  const getFieldEnabledState = (payStatus) => {
    const status = payStatus?.toUpperCase() || 'DUE';
    
    switch(status) {
      case 'PAID':
        return {
          paidDate: true,
          delinquencyDate: true,  // ENABLED: User can input delinquency period for accurate certificate/notes
          balanceAmount: false,
          delinquentFields: false  // Payoff Amount, Good Thru Date, Notes
        };
      case 'DUE':
        return {
          paidDate: false,
          delinquencyDate: true,  // REQUIRED: Must enter delinquency date for Due status
          balanceAmount: false,
          delinquentFields: false
        };
      case 'PARTIALLY PAID':
      case 'PARTIALLYPAID':
        return {
          paidDate: true,
          delinquencyDate: true,  // REQUIRED: Must enter delinquency date for Partially Paid
          balanceAmount: true,
          delinquentFields: false
        };
      case 'DELINQUENT':
        return {
          paidDate: false,  // DISABLED: Delinquent taxes are not paid, so no Paid Date
          delinquencyDate: false,  // DISABLED: Already delinquent, no need for delinquency date
          balanceAmount: false,
          delinquentFields: true  // Enable Payoff, Good Thru, Notes
        };
      case 'PARTIALLY DLQ':
      case 'PARTIALLY DELINQUENT':
      case 'PARTIALLYDELINQUENT':
      case 'PARTIALLYDLQ':
        return {
          paidDate: false,  // DISABLED: Partially delinquent taxes have not been fully paid
          delinquencyDate: false,  // DISABLED: Already partially delinquent, no need for delinquency date
          balanceAmount: true,
          delinquentFields: true  // Enable Payoff, Good Thru, Notes
        };
      default:
        return {
          paidDate: true,
          delinquencyDate: true,
          balanceAmount: false,
          delinquentFields: false
        };
    }
  };

  // Handle Pay Status change with field clearing logic and validation reset
  const handlePayStatusChange = (index, newPayStatus) => {
    const enabledState = getFieldEnabledState(newPayStatus);
    
    // Check if ANY row has delinquent status (for global delinquent fields)
    const hasDelinquentStatus = enabledState.delinquentFields;
    
    // STEP 1: Clear ALL validation errors for this row to ensure clean state
    const newErrors = { ...validationErrors };
    
    // Always clear errors for all conditional fields
    delete newErrors[`tax_summary_${index}_paid_date`];
    delete newErrors[`tax_summary_${index}_dlq_date`];
    delete newErrors[`tax_summary_${index}_balance_amount`];
    
    // Also clear delinquent field errors when they become disabled
    if (!hasDelinquentStatus) {
      delete newErrors['dlq_payoff_amount'];
      delete newErrors['dlq_good_thru_date'];
    }
    
    // Immediately update validation errors state
    setValidationErrors(newErrors);
    
    // Helper to calculate Delinquency Date = Due Date + 1 day
    const calculateDlqDate = (dueDateStr) => {
      if (!dueDateStr || !dueDateStr.trim()) return '';
      try {
        const parts = dueDateStr.split('/');
        if (parts.length !== 3) return '';
        const [month, day, year] = parts;
        const dueDate = new Date(parseInt(year), parseInt(month) - 1, parseInt(day));
        if (isNaN(dueDate.getTime())) return '';
        
        // Add 1 day to get Delinquency Date
        dueDate.setDate(dueDate.getDate() + 1);
        
        const newMonth = String(dueDate.getMonth() + 1).padStart(2, '0');
        const newDay = String(dueDate.getDate()).padStart(2, '0');
        const newYear = dueDate.getFullYear();
        return `${newMonth}/${newDay}/${newYear}`;
      } catch {
        return '';
      }
    };
    
    // STEP 2: Update certificate data with new status and handle conditional fields
    setCertificateData(prev => {
      const updatedSummary = prev.current_tax_summary.map((item, i) => {
        if (i === index) {
          const updatedItem = { ...item, pay_status: newPayStatus };
          
          // Clear field values for fields that are being disabled
          if (!enabledState.paidDate) {
            updatedItem.paid_date = '';
          }
          
          // Handle Delinquency Date based on new Pay Status
          if (!enabledState.delinquencyDate) {
            // Clear dlq_date when field is disabled (Delinquent, Partially DLQ, Paid statuses)
            updatedItem.dlq_date = '';
          } else {
            // SPEC: Auto-populate Delinquency Date when Pay Status changes to "Due"
            // If delinquencyDate is now required/enabled and is empty or invalid, auto-set to Due Date + 1
            const currentDlqDate = item.dlq_date || '';
            const dueDate = item.due_date || '';
            
            if (dueDate && (!currentDlqDate || !currentDlqDate.trim())) {
              // Auto-populate: Delinquency Date = Due Date + 1 day
              updatedItem.dlq_date = calculateDlqDate(dueDate);
            }
          }
          
          if (!enabledState.balanceAmount) {
            updatedItem.balance_amount = '';
          }
          
          return updatedItem;
        }
        return item;
      });
      
      // Check if any row in the updated summary has delinquent status
      const anyDelinquent = updatedSummary.some(row => {
        const state = getFieldEnabledState(row.pay_status);
        return state.delinquentFields;
      });
      
      // Clear delinquent tax fields if no row is delinquent
      const updates = {
        ...prev,
        current_tax_summary: updatedSummary
      };
      
      if (!anyDelinquent) {
        updates.dlq_payoff_amount = null;
        updates.dlq_good_thru_date = null;
      }
      
      return updates;
    });
    
    // STEP 3: Do NOT re-validate immediately to avoid race conditions
    // Validation will happen naturally when user interacts with fields
    // or when they attempt to save/complete the form
    
    // BUG FIX: Clear parcel-level Current Tax Summary validation error when user updates rows
    setValidationErrors(prev => {
      const newErrors = { ...prev };
      delete newErrors.current_tax_summary;
      return newErrors;
    });
  };

  // Add row to array
  const addRow = (arrayName, emptyObject) => {
    setCertificateData(prev => ({
      ...prev,
      [arrayName]: [...prev[arrayName], emptyObject]
    }));
  };

  // Remove row from array
  const removeRow = (arrayName, index) => {
    setCertificateData(prev => ({
      ...prev,
      [arrayName]: prev[arrayName].filter((_, i) => i !== index)
    }));
  };

  const calculatePayoffAmount = () => {
    // Calculate payoff amount from delinquent rows
    const delinquentRows = certificateData.current_tax_summary.filter(row => 
      row.pay_status === 'Delinquent' || row.pay_status === 'Partially Dlq'
    );
    
    const payoffAmount = delinquentRows.reduce((sum, row) => {
      const baseTax = parseFloat(row.base_tax) || 0;
      const fees = parseFloat(row.amounts_fees) || 0;
      return sum + baseTax + fees;
    }, 0);
    
    handleChange('dlq_payoff_amount', payoffAmount);
  };

  // Generate delinquent notes button handler
  // Auto-linking functions
  const fetchPreviousOrder = async () => {
    try {
      const response = await axios.get(`${API}/orders/${orderData.id}/previous`);
      if (response.data.previous_order_id) {
        setPreviousOrder(response.data);
      }
    } catch (error) {
      // 403 = role not permitted to view linked orders — expected, not an error.
      if (error.response?.status !== 403) console.error('Failed to fetch previous order:', error);
    }
  };

  const fetchRelatedFiles = async () => {
    try {
      const response = await axios.get(`${API}/orders/${orderData.id}/related-files`);
      setRelatedFiles(response.data.files || []);
    } catch (error) {
      if (error.response?.status !== 403) console.error('Failed to fetch related files:', error);
    }
  };

  const fetchOrderFiles = async () => {
    try {
      const response = await axios.get(`${API}/orders/${orderData.id}/files`);
      setOrderFiles(response.data.files || []);
    } catch (error) {
      console.error('Failed to fetch order files for evidence resolution:', error);
    }
  };

  const handlePrefill = async () => {
    if (!previousOrder) return;
    
    setIsPrefilling(true);
    try {
      const response = await axios.post(`${API}/orders/${orderData.id}/tax-cert/prefill`);
      
      if (response.data.success) {
        toast.success('Tax certificate prefilled from previous order');
        setIsPrefilled(true);
        
        // Reload certificate data
        window.location.reload();
      }
    } catch (error) {
      console.error('Prefill failed:', error);
      toast.error('Failed to prefill from previous order');
    } finally {
      setIsPrefilling(false);
    }
  };

  const openPreviousOrder = () => {
    if (previousOrder) {
      const newWindow = window.open(`/orders/${previousOrder.previous_order_id}`, '_blank');
      if (newWindow) newWindow.focus();
    }
  };

  const handleReapplyData = async () => {
    if (window.confirm('This will overwrite current changes with data from the previous order. Continue?')) {
      await handlePrefill();
    }
  };



  // Helper function to render prefilled field indicator
  const PrefillIndicator = ({ fieldName, className = "" }) => {
    if (!isFieldPrefilled(fieldName)) return null;
    
    return (
      <div className={`inline-flex items-center gap-1 text-xs text-blue-600 ${className}`}>
        <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.102m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" />
        </svg>
        <span>Loaded from {previousOrder?.order_number}</span>
      </div>
    );
  };

  // Validate required fields
  const validateForm = () => {
    const errors = {};
    
    // Property/Borrower validation
    // Skip for LEGACY MLS only — dual MLS Tax workspace validates like normal Tax
    if (!isLegacyMls) {
    if (!certificateData.full_address.trim()) {
      errors.full_address = 'Full Address is required';
    }
    
    if (!certificateData.city.trim()) {
      errors.city = 'City is required';
    }
    
    if (!certificateData.state.trim()) {
      errors.state = 'State is required';
    }
    
    if (!certificateData.zip.trim()) {
      errors.zip = 'ZIP is required';
    }
    
    if (!certificateData.county.trim()) {
      errors.county = 'County is required';
    }
    } // end !isMLS property/borrower validation
    
    const apnArray = formatAPNsArray(certificateData.parcel_apns);
    if (apnArray.length === 0) {
      errors.parcel_apns = 'At least one Parcel/APN is required';
    }
    
    // Skip borrower validation for LEGACY MLS only
    if (!isLegacyMls) {
    if (!certificateData.borrower_owner_name.trim()) {
      errors.borrower_owner_name = 'Borrower/Owner Name is required';
    }
    }
    
    // Current Tax Summary validation - at least one row with required data
    // Skip for External Portal / MLS clients - tax details managed externally
    if (!hideTaxDetails) {
    const hasValidTaxSummary = certificateData.current_tax_summary.some(row => 
      row.jurisdiction.trim() && row.tax_year.trim() && row.base_tax.trim()
    );
    
    if (!hasValidTaxSummary) {
      errors.current_tax_summary = 'At least one Current Tax Summary row with Jurisdiction, Tax Year, and Base Tax is required';
    }

    // Delinquent taxes validation - based on Pay Status (auto-enabled)
    const hasDelinquentStatus = certificateData.current_tax_summary.some(row => {
      const state = getFieldEnabledState(row.pay_status);
      return state.delinquentFields;
    });
    
    if (hasDelinquentStatus) {
      // Validate payoff amount (required when delinquent status exists)
      if (!certificateData.dlq_payoff_amount || certificateData.dlq_payoff_amount <= 0) {
        errors.dlq_payoff_amount = 'Payoff amount must be greater than 0';
      }
      
      // Validate good thru date
      if (!certificateData.dlq_good_thru_date) {
        errors.dlq_good_thru_date = 'Good thru date is required';
      } else {
        const goodThruDate = new Date(certificateData.dlq_good_thru_date);
        const today = new Date();
        today.setHours(23, 59, 59, 999); // End of today
        if (goodThruDate <= today) {
          errors.dlq_good_thru_date = 'Good thru date must be in the future';
        }
      }
    }
    } // end !hideTaxDetails tax summary validation
    
    // Exemption validation (Internal Portal / non-MLS only)
    if (!hideTaxDetails) {
      const exemptionTypes = certificateData.exemption_types || [];
      if (exemptionTypes.length === 0) {
        errors.exemption_types = 'Select at least one exemption type, or choose "No Exemptions."';
      }
    }
    if (certificateData.exemption_types.includes('Other') && (!certificateData.exemption_other || certificateData.exemption_other.trim() === '')) {
      errors.exemption_other = 'Other description is required when "Other" exemption type is selected';
    }

    // Date validation for Current Tax Summary - Skip for External Portal / MLS
    if (!hideTaxDetails) {
    certificateData.current_tax_summary.forEach((row, index) => {
      // Helper function to parse MM/DD/YYYY dates
      const parseDate = (dateStr) => {
        if (!dateStr || !dateStr.trim()) return null;
        try {
          const [month, day, year] = dateStr.split('/');
          if (!month || !day || !year) return null;
          const date = new Date(year, parseInt(month) - 1, parseInt(day));
          // Validate the date is valid
          if (date.getMonth() !== parseInt(month) - 1) return null;
          return date;
        } catch {
          return null;
        }
      };

      const mailingDate = parseDate(row.mailing_issue_date);
      const dueDate = parseDate(row.due_date);
      const delinquencyDate = parseDate(row.dlq_date);
      const paidDate = parseDate(row.paid_date);

      // 1. Mailing/Issue Date validation
      if (!row.mailing_issue_date || !row.mailing_issue_date.trim()) {
        errors[`tax_summary_${index}_mailing_issue_date`] = 'Mailing / Issue Date is required';
      } else if (!mailingDate) {
        errors[`tax_summary_${index}_mailing_issue_date`] = 'Please enter a valid Mailing / Issue Date in MM/DD/YYYY format';
      } else if (dueDate && mailingDate > dueDate) {
        errors[`tax_summary_${index}_mailing_issue_date`] = 'Mailing / Issue Date cannot be after the Due Date';
      }

      // 2. Due Date validation
      if (!row.due_date || !row.due_date.trim()) {
        errors[`tax_summary_${index}_due_date`] = 'Due Date is required';
      } else if (!dueDate) {
        errors[`tax_summary_${index}_due_date`] = 'Please enter a valid Due Date in MM/DD/YYYY format';
      } else if (mailingDate && dueDate < mailingDate) {
        errors[`tax_summary_${index}_due_date`] = 'Due Date must be on or after the Mailing / Issue Date';
      }

      // 3. Delinquency Date validation
      const payStatusUpper = (row.pay_status || '').toUpperCase();
      const isDueStatus = payStatusUpper === 'DUE';
      const isPartiallyPaidStatus = payStatusUpper === 'PARTIALLY PAID' || payStatusUpper === 'PARTIALLYPAID';
      const isDelinquentStatus = payStatusUpper === 'DELINQUENT';
      const isPartiallyDlqStatus = payStatusUpper === 'PARTIALLY DLQ' || payStatusUpper === 'PARTIALLYDLQ' || payStatusUpper === 'PARTIALLY DELINQUENT';
      
      // DEBUG: Log validation check
      console.log(`[VALIDATION] Row ${index}: Pay Status = "${row.pay_status}", isDue = ${isDueStatus}, isPartiallyPaid = ${isPartiallyPaidStatus}, isDelinquent = ${isDelinquentStatus}, isPartiallyDlq = ${isPartiallyDlqStatus}, dlq_date = "${row.dlq_date}"`);
      
      // REQUIRED: Delinquency Date is required when Pay Status = "Due" or "Partially Paid"
      // NOT required when status is "Delinquent" or "Partially Dlq" (field is disabled)
      const requiresDelinquencyDate = isDueStatus || isPartiallyPaidStatus;
      const delinquencyDateDisabled = isDelinquentStatus || isPartiallyDlqStatus;
      
      if (requiresDelinquencyDate && (!row.dlq_date || !row.dlq_date.trim())) {
        console.log(`[VALIDATION] Row ${index}: Adding dlq_date error because Pay Status requires delinquency date and it's empty`);
        errors[`tax_summary_${index}_dlq_date`] = 'Delinquency Date is required when Pay Status is Due or Partially Paid';
      }
      
      // Format and relationship validation (if Delinquency Date is provided AND field is enabled)
      if (!delinquencyDateDisabled && row.dlq_date && row.dlq_date.trim()) {
        if (!delinquencyDate) {
          errors[`tax_summary_${index}_dlq_date`] = 'Please enter a valid Delinquency Date in MM/DD/YYYY format';
        } else if (dueDate && delinquencyDate <= dueDate) {
          errors[`tax_summary_${index}_dlq_date`] = 'Delinquency Date must be after the Due Date';
        }
      }

      // 4. Paid Date validation
      const isPaidStatus = row.pay_status === 'Paid' || row.pay_status === 'PAID' || row.pay_status === 'paid';
      const isPartiallyPaid = row.pay_status?.includes('Partially') || row.pay_status?.includes('PARTIALLY');
      const paidDateRequired = isPaidStatus || isPartiallyPaid;
      
      // First check: Paid Date is REQUIRED when Pay Status = "Paid", "Partially Paid", or "Partially Dlq"
      if (paidDateRequired && (!row.paid_date || !row.paid_date.trim())) {
        errors[`tax_summary_${index}_paid_date`] = 'Paid Date is required for this Pay Status';
      }
      
      // Second check: If Paid Date is entered (regardless of Pay Status), validate it
      if (row.paid_date && row.paid_date.trim()) {
        // Check format validity
        if (!paidDate) {
          errors[`tax_summary_${index}_paid_date`] = 'Please enter a valid Paid Date in MM/DD/YYYY format';
        } else {
          // Check date relationship: Paid Date MUST be on or after Mailing/Issue Date
          // Note: Paid Date can be BEFORE Due Date (early payment is valid)
          if (mailingDate && paidDate < mailingDate) {
            errors[`tax_summary_${index}_paid_date`] = 'Paid Date must be on or after the Mailing / Issue Date';
          }
        }
      }
      
      // 5. Validate Balance Amount (new field)
      const balanceAmountRequired = isPartiallyPaid;
      
      if (balanceAmountRequired) {
        if (!row.balance_amount || !row.balance_amount.trim()) {
          errors[`tax_summary_${index}_balance_amount`] = 'Balance Amount is required for Partially Paid/Delinquent status';
        } else {
          const balanceAmount = parseFloat(row.balance_amount);
          if (isNaN(balanceAmount) || balanceAmount < 0) {
            errors[`tax_summary_${index}_balance_amount`] = 'Balance Amount must be a positive number';
          } else {
            // Check if balance exceeds total (base tax + fees)
            const baseTax = parseFloat(row.base_tax) || 0;
            const fees = parseFloat(row.amounts_fees) || 0;
            const total = rc(baseTax + fees);
            
            if (total > 0 && balanceAmount > total) {
              errors[`tax_summary_${index}_balance_amount`] = 'Balance Amount cannot exceed Base Tax + Fees';
            }
          }
        }
      }
    });
    } // end !hideTaxDetails date validation
    
    // Taxing Authority validation
    if (!certificateData.make_checks_payable_to.trim()) {
      errors.make_checks_payable_to = 'Make Checks Payable To is required';
    }
    
    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  // ============================================================================
  // PHASE 4: MULTI-PARCEL VALIDATION
  // ============================================================================
  
  /**
   * Validate a single parcel
   * @param {Object} parcel - The parcel object to validate
   * @param {number} parcelIndex - 0-indexed position in parcels array
   * @returns {Object} - Object with error messages keyed by field name
   */
  const validateParcel = (parcel, parcelIndex, allParcels = []) => {
    const errors = {};
    const parcelNumber = parcelIndex + 1; // 1-indexed for display
    
    // Helper function to parse MM/DD/YYYY date strings
    const parseDate = (dateStr) => {
      if (!dateStr || !dateStr.trim()) return null;
      try {
        const [month, day, year] = dateStr.split('/');
        if (!month || !day || !year) return null;
        const date = new Date(year, parseInt(month) - 1, parseInt(day));
        // Validate the date is actually valid (e.g., not Feb 30)
        if (date.getMonth() !== parseInt(month) - 1) return null;
        return date;
      } catch {
        return null;
      }
    };
    
    // Parcel/APN validation (required) - handle both string and array formats
    const parcelApn = typeof parcel.parcel_apn === 'string' 
      ? parcel.parcel_apn 
      : (Array.isArray(parcel.parcel_apn) ? parcel.parcel_apn.join(', ') : '');
    
    if (!parcelApn || !parcelApn.trim()) {
      errors.parcel_apn = `Parcel ${parcelNumber}: Parcel/APN is required`;
    } else if (allParcels.length > 1) {
      // Check for duplicate APN across other parcels
      const normalizedApnThis = parcelApn.trim().toLowerCase();
      for (let i = 0; i < allParcels.length; i++) {
        if (i === parcelIndex) continue;
        const otherApn = typeof allParcels[i].parcel_apn === 'string'
          ? allParcels[i].parcel_apn
          : (Array.isArray(allParcels[i].parcel_apn) ? allParcels[i].parcel_apn.join(', ') : '');
        if (otherApn && otherApn.trim().toLowerCase() === normalizedApnThis) {
          errors.parcel_apn = `Parcel ${parcelNumber}: Duplicate APN "${parcelApn.trim()}" — same as Parcel ${i + 1}. Each parcel must have a unique APN.`;
          break;
        }
      }
    }
    
    // Tax Collected validation (required) - Skip for External Portal Complete clients
    // Tax Collected validation (required) - Skip for External Portal / MLS clients
    if (!hideTaxDetails && !parcel.tax_collected) {
      errors.tax_collected = `Parcel ${parcelNumber}: Tax Collected is required`;
    }
    
    // Jurisdiction Type validation (required)
    // Skip top-level check if using multi-jurisdiction model where types are set per-jurisdiction
    if (!parcel.jurisdiction_type) {
      const hasJurTypeInJurisdictions = (parcel.jurisdictions || []).some(j => j.jurisdiction_type);
      if (!hasJurTypeInJurisdictions) {
        errors.jurisdiction_type = `Parcel ${parcelNumber}: Jurisdiction Type is required`;
      }
    }
    
    // Make Checks Payable To validation (required)
    const makeChecksPayable = parcel.make_checks_payable_to || '';
    if (!makeChecksPayable.trim()) {
      errors.make_checks_payable_to = `Parcel ${parcelNumber}: Make Checks Payable To is required`;
    }
    
    // ========================================================================
    // Information Source validation (required for ALL order types)
    // Each jurisdiction must have an Information Source selected before completion.
    // This runs OUTSIDE the hideTaxDetails gate so external portal orders are covered.
    //
    // Jun 2, 2026: cross-parcel coverage — when the SAME jurisdiction
    // (matched by name + type, case-insensitive) already has a source
    // filled on a DIFFERENT parcel, skip this error. Mirrors the
    // `isSourceCoveredByAnotherParcel` helper in JurisdictionManager.jsx
    // which drives the "Source already captured from Parcel N" banner —
    // the two MUST agree so users aren't blocked from completing an
    // order whose UI says it's already covered.
    // ========================================================================
    const isSourceCoveredFromOther = (jurName, jurType) => {
      if (!jurName || !Array.isArray(allParcels) || allParcels.length <= 1) return false;
      const name = (jurName || '').toLowerCase().trim();
      const type = (jurType || '').toLowerCase().trim();
      if (!name) return false;
      return allParcels.some((p, pIdx) => {
        if (pIdx === parcelIndex) return false;
        return (p.jurisdictions || []).some(j => {
          const jn = (j.jurisdiction_name || '').toLowerCase().trim();
          const jt = (j.jurisdiction_type || '').toLowerCase().trim();
          return jn === name && jt === type && j.source;
        });
      });
    };
    const parcelJursForSource = parcel.jurisdictions || [];
    if (parcelJursForSource.length > 0) {
      parcelJursForSource.forEach((jur, jIdx) => {
        if (!jur.source && !isSourceCoveredFromOther(jur.jurisdiction_name, jur.jurisdiction_type)) {
          const jurLabel = toTitleCase(jur.jurisdiction_name) || `Jurisdiction ${jIdx + 1}`;
          errors[`p${parcelIndex}_jur_${jIdx}_source`] = `Parcel ${parcelNumber}, ${jurLabel}: Information Source is required — open the "MLC & Workflow" tab to set it.`;
        }
      });
    }
    
    // Current Tax Summary validation - at least one row with required data
    // Skip for External Portal / MLS clients - tax details managed externally
    if (!hideTaxDetails) {
    
    // Check BOTH the legacy flat model (current_tax_summary) AND the new jurisdiction model (jurisdictions[].installments[])
    const parcelJurisdictions = parcel.jurisdictions || [];
    const hasJurisdictionModel = parcelJurisdictions.length > 0;
    
    if (hasJurisdictionModel) {
      // NEW MODEL: Validate jurisdictions[].installments[]
      parcelJurisdictions.forEach((jur, jIdx) => {
        const jurLabel = toTitleCase(jur.jurisdiction_name) || `Jurisdiction ${jIdx + 1}`;
        const prefix = `Parcel ${parcelNumber}, ${jurLabel}`;
        
        if (!jur.jurisdiction_type) {
          errors[`p${parcelIndex}_jur_${jIdx}_type`] = `${prefix}: Jurisdiction type is not selected`;
        }
        
        const installments = jur.installments || [];
        if (installments.length === 0) {
          errors[`p${parcelIndex}_jur_${jIdx}_empty`] = `${prefix}: No tax records have been entered`;
        }
        
        // Jun 2026 — UAT-COMBINED-PAYOFF-BASE-BYPASS. When the
        // jurisdiction is in Combined Payoff mode, the collector
        // only provides the combined payoff + good-thru — per-year
        // Base Tax cannot be filled. The PDF auto-prints the
        // "Tax Collector will not provide … base amount" note.
        // Year-rows must still exist (the PDF lists the covered
        // tax years), but the row-level Base Tax requirement is
        // bypassed for completion gating.
        const isCombinedPayoff = (jur.dlq_payoff_breakdown_mode || 'year_wise') === 'combined';
        
        let hasValidRecord = false;
        installments.forEach((inst, iIdx) => {
          const instLabel = inst.installment_name || `Record ${iIdx + 1}`;
          const instPrefix = `${prefix} > ${instLabel}`;
          
          if (inst.tax_year && (isCombinedPayoff || (inst.base_tax !== null && inst.base_tax !== undefined && inst.base_tax !== ''))) {
            hasValidRecord = true;
          }
          
          if (!inst.tax_year) {
            errors[`p${parcelIndex}_jur_${jIdx}_inst_${iIdx}_year`] = `${instPrefix}: Tax year is missing`;
          }
          
          const hasBaseTax = inst.base_tax !== null && inst.base_tax !== undefined && inst.base_tax !== '';
          if (!hasBaseTax && !isCombinedPayoff) {
            errors[`p${parcelIndex}_jur_${jIdx}_inst_${iIdx}_base`] = `${instPrefix}: Base tax amount is missing`;
          }
        });
        
        if (!hasValidRecord && installments.length > 0) {
          errors[`p${parcelIndex}_jur_${jIdx}_nodata`] = isCombinedPayoff
            ? `${prefix}: At least one tax record with Tax Year is required (Combined Payoff covers the amounts)`
            : `${prefix}: No tax records contain required data (tax year + base tax)`;
        }
      });
    } else {
      // LEGACY MODEL: Validate current_tax_summary[]
    const hasValidTaxSummary = parcel.current_tax_summary && parcel.current_tax_summary.some(row => 
      row.jurisdiction && row.jurisdiction.trim() && 
      row.tax_year && row.tax_year.trim() && 
      row.base_tax && row.base_tax.trim()
    );
    
    if (!hasValidTaxSummary) {
      errors.current_tax_summary = `Parcel ${parcelNumber}: At least one Current Tax Summary row with Jurisdiction, Tax Year, and Base Tax is required`;
    }
    
    // Validate each tax summary row
    if (parcel.current_tax_summary) {
      parcel.current_tax_summary.forEach((row, rowIndex) => {
        // Mailing/Issue Date (required)
        if (!row.mailing_issue_date || !row.mailing_issue_date.trim()) {
          errors[`tax_summary_${rowIndex}_mailing_issue_date`] = `Parcel ${parcelNumber}, Row ${rowIndex + 1}: Mailing/Issue Date is required`;
        }
        
        // Tax Year (required)
        if (!row.tax_year || !row.tax_year.trim()) {
          errors[`tax_summary_${rowIndex}_tax_year`] = `Parcel ${parcelNumber}, Row ${rowIndex + 1}: Tax Year is required`;
        }
        
        // Installment (required)
        if (!row.installment || !row.installment.trim()) {
          errors[`tax_summary_${rowIndex}_installment`] = `Parcel ${parcelNumber}, Row ${rowIndex + 1}: Installment is required`;
        }
        
        // Pay Status (required)
        if (!row.pay_status || !row.pay_status.trim()) {
          errors[`tax_summary_${rowIndex}_pay_status`] = `Parcel ${parcelNumber}, Row ${rowIndex + 1}: Pay Status is required`;
        }
        
        // Tax Amount (amounts_fees) - REQUIRED for all rows
        if (!row.amounts_fees || !row.amounts_fees.trim()) {
          errors[`tax_summary_${rowIndex}_amounts_fees`] = `Parcel ${parcelNumber}, Row ${rowIndex + 1}: Tax Amount is required`;
        } else {
          const taxAmount = parseFloat(row.amounts_fees);
          if (isNaN(taxAmount) || taxAmount <= 0) {
            errors[`tax_summary_${rowIndex}_amounts_fees`] = `Parcel ${parcelNumber}, Row ${rowIndex + 1}: Tax Amount must be greater than 0`;
          }
        }
        
        // Delinquency Date validation - REQUIRED when Pay Status is "Due" or "Partially Paid"
        const fieldState = getFieldEnabledState(row.pay_status);
        if (fieldState.delinquencyDate) {
          // delinquencyDate is required (enabled) for Due and Partially Paid statuses
          if (!row.dlq_date || !row.dlq_date.trim()) {
            errors[`tax_summary_${rowIndex}_dlq_date`] = `Parcel ${parcelNumber}, Row ${rowIndex + 1}: Delinquency Date is required when Pay Status is ${row.pay_status}`;
          } else {
            // Validate date format
            const dlqDate = parseDate(row.dlq_date);
            if (!dlqDate) {
              errors[`tax_summary_${rowIndex}_dlq_date`] = `Parcel ${parcelNumber}, Row ${rowIndex + 1}: Please enter a valid Delinquency Date in MM/DD/YYYY format`;
            } else {
              // Validate delinquency date is on or after due date
              const dueDate = parseDate(row.due_date);
              if (dueDate && dlqDate <= dueDate) {
                errors[`tax_summary_${rowIndex}_dlq_date`] = `Parcel ${parcelNumber}, Row ${rowIndex + 1}: Delinquency Date must be after the Due Date`;
              }
              // Additional validation: Delinquency Date year should be >= Tax Year
              // FISCAL-DLQ-001: fiscal-year jurisdictions (e.g. MA FY2027 = Jul 2026–Jun 2027)
              // legitimately have due/dlq dates in the calendar year before the tax year.
              if (row.tax_year) {
                const taxYearNum = parseInt(row.tax_year, 10);
                const dlqYear = dlqDate.getFullYear();
                if (dlqYear < taxYearNum) {
                  const mailDateForFiscal = parseDate(row.mailing_issue_date);
                  const fiscalRow =
                    (dueDate && dueDate.getFullYear() === taxYearNum - 1) ||
                    (mailDateForFiscal && mailDateForFiscal.getFullYear() === taxYearNum - 1);
                  if (!(fiscalRow && dlqYear === taxYearNum - 1)) {
                    errors[`tax_summary_${rowIndex}_dlq_date`] = `Parcel ${parcelNumber}, Row ${rowIndex + 1}: Delinquency Date year (${dlqYear}) cannot be before Tax Year (${taxYearNum})`;
                  }
                }
              }
            }
          }
        }
        
        // Paid Date validation - REQUIRED when Pay Status is "Paid"
        if (row.pay_status === 'Paid' && (!row.paid_date || !row.paid_date.trim())) {
          errors[`tax_summary_${rowIndex}_paid_date`] = `Parcel ${parcelNumber}, Row ${rowIndex + 1}: Paid Date is required when Pay Status is Paid`;
        }
      });
    }
    
    // Delinquent taxes validation
    const hasDelinquentStatus = parcel.current_tax_summary && parcel.current_tax_summary.some(row => {
      const state = getFieldEnabledState(row.pay_status);
      return state.delinquentFields;
    });
    
    if (hasDelinquentStatus) {
      if (!parcel.dlq_payoff_amount || parcel.dlq_payoff_amount <= 0) {
        errors.dlq_payoff_amount = `Parcel ${parcelNumber}: Payoff amount must be greater than 0`;
      }
      
      if (!parcel.dlq_good_thru_date) {
        errors.dlq_good_thru_date = `Parcel ${parcelNumber}: Good thru date is required`;
      } else {
        const goodThruDate = new Date(parcel.dlq_good_thru_date);
        const today = new Date();
        today.setHours(23, 59, 59, 999);
        if (goodThruDate <= today) {
          errors.dlq_good_thru_date = `Parcel ${parcelNumber}: Good thru date must be in the future`;
        }
      }
    }
    } // end else (legacy model)
    } // end !hideTaxDetails parcel tax summary validation
    
    return errors;
  };
  
  /**
   * Validate all parcels
   * @returns {Object} - Combined errors from all parcels + property/borrower validation
   * PHASE 5: Also tracks which parcels have errors for visual indicators
   */
  const validateAllParcels = () => {
    const allErrors = {};
    const errorParcels = new Set();
    
    // Validate Property/Borrower (only for Parcel 1, but required for order)
    // Skip for LEGACY MLS only — dual MLS Tax workspace validates like normal Tax
    if (!isLegacyMls) {
    if (!certificateData.full_address || !certificateData.full_address.trim()) {
      allErrors.full_address = 'Full Address is required';
    }
    
    if (!certificateData.city || !certificateData.city.trim()) {
      allErrors.city = 'City is required';
    }
    
    if (!certificateData.state || !certificateData.state.trim()) {
      allErrors.state = 'State is required';
    }
    
    if (!certificateData.zip || !certificateData.zip.trim()) {
      allErrors.zip = 'ZIP is required';
    }
    
    if (!certificateData.county || !certificateData.county.trim()) {
      allErrors.county = 'County is required';
    }
    
    if (!certificateData.borrower_owner_name || !certificateData.borrower_owner_name.trim()) {
      allErrors.borrower_owner_name = 'Borrower/Owner Name is required';
    }
    } // end !isMLS property/borrower validation
    
    // Sync current flat state to active parcel before validating all
    syncFlatToParcel();
    
    // Validate each parcel (pass full array for cross-parcel duplicate APN check)
    parcels.forEach((parcel, index) => {
      const parcelErrors = validateParcel(parcel, index, parcels);
      if (Object.keys(parcelErrors).length > 0) {
        errorParcels.add(index);
      }
      Object.assign(allErrors, parcelErrors);
    });
    
    // PHASE 5: Update parcels with errors state
    setParcelsWithErrors(errorParcels);
    
    return allErrors;
  };
  
  /**
   * Find which parcel has errors
   * @param {Object} errors - Error object from validateAllParcels
   * @returns {number|null} - 0-indexed parcel number with first error, or null
   */
  const findParcelWithErrors = (errors) => {
    // Check if any error message contains "Parcel X:"
    for (const key in errors) {
      const errorMsg = errors[key];
      const match = errorMsg.match(/Parcel (\d+):/);
      if (match) {
        return parseInt(match[1]) - 1; // Convert to 0-indexed
      }
    }
    return null;
  };

  // Supporting Documents section removed per user request
  // File upload functions (handleFileUpload, removeFile, reorderFiles) removed

  // Auto-populate remittance data based on jurisdiction
  const handleJurisdictionChange = async (index, value) => {
    handleArrayChange('current_tax_summary', index, 'jurisdiction', value);
    
    // Try to auto-populate remittance data
    if (value.trim() && !certificateData.changed_by_user) {
      try {
        // This would call a resolver/remittance API in real implementation
        // For now, we'll show that auto-population is available
        toast.info(`Jurisdiction "${value}" entered. Remittance data would be auto-populated from resolver DB.`);
      } catch (error) {
        console.error('Error fetching remittance data:', error);
      }
    }
  };

  // Mark remittance as manually changed
  const handleRemittanceChange = (field, value) => {
    handleChange(field, value);
    if (!certificateData.changed_by_user) {
      handleChange('changed_by_user', true);
    }
  };

  // ============================================================================
  // PHASE 3C: PARCEL NAVIGATION HANDLERS
  // ============================================================================
  
  /**
   * Copy shared jurisdiction setup from source jurisdictions (contact info, MLC, source/evidence).
   * Tax installments and per-parcel financial data are NOT copied.
   */
  /**
   * Check if a parcel's jurisdictions are effectively empty (no jurisdiction_type set).
   * Used by auto-copy logic: a parcel with only empty skeleton jurisdictions should still
   * receive the shared jurisdiction setup from the source parcel.
   */
  const isJurisdictionSetupEmpty = (jurs) => {
    if (!jurs || jurs.length === 0) return true;
    return jurs.every(j => !j.jurisdiction_type);
  };

  const copySharedJurisdictionSetup = (sourceJurisdictions) => {
    return sourceJurisdictions.map(jur => ({
      id: crypto.randomUUID(),
      jurisdiction_name: jur.jurisdiction_name || '',
      jurisdiction_type: jur.jurisdiction_type || null,
      jurisdiction_id: jur.jurisdiction_id || null,
      tax_collector_email: jur.tax_collector_email || '',
      tax_collector_website: jur.tax_collector_website || '',
      assessor_website: jur.assessor_website || '',
      tax_collector_phone: jur.tax_collector_phone || '',
      tax_collector_phone_ext: jur.tax_collector_phone_ext || '',
      tax_collector_fax: jur.tax_collector_fax || '',
      tax_collector_working_hours: jur.tax_collector_working_hours || '',
      make_checks_payable_to: jur.make_checks_payable_to || '',
      mlc_charges: jur.mlc_charges,
      mlc_24_month_charges: jur.mlc_24_month_charges,
      mlc_eta_days: jur.mlc_eta_days,
      sase_required: jur.sase_required || false,
      info_obtained_back: jur.info_obtained_back,
      source: jur.source || null,
      source_notes: jur.source_notes || '',
      source_evidence: jur.source_evidence || null,
      collection_frequency: jur.collection_frequency || 'annually',
      changed_by_user: jur.changed_by_user || false,
      has_delinquent: null,
      dlq_payoff_amount: null,
      dlq_payoff_manual: false,
      dlq_good_thru_date: null,
      delinquent_taxes_notes: '',
      // Copy installment structure (row skeleton) without financial values
      // External portal / MLS: no installments (tax summary not used)
      installments: hideTaxDetails ? [] : (jur.installments || []).map((inst, idx) => ({
        id: crypto.randomUUID(),
        tax_year: inst.tax_year || '',
        installment_name: inst.installment_name || '',
        installment_index: idx + 1,
        due_date: null,
        base_tax: null,
        amount_due: null,
        discount_penalty: 0,
        paid_amount: null,
        notes: '',
        pay_status: 'Due',
        paid_date: null,
        balance_amount: null,
        dlq_date: null,
        mailing_issue_date: null,
        payoff_amount: null,
        payoff_good_through_date: null,
        payoff_manual: false
      })),
    }));
  };
  
  /**
   * ==================== SEQUENTIAL PARCEL ENFORCEMENT ====================
   * Internal portal rule: P(n+1) is only accessible when P(n) is complete.
   * Completeness = Parcel/APN + Assessed Value (>0) + Exemption Type.
   * For the ACTIVE parcel, reads from certificateData (flat state) since
   * parcels[activeParcelIndex] may be stale (not yet synced).
   * Skipped entirely for MLS / External Portal (hideTaxDetails).
   *
   * May 17, 2026 — New Parcel mode bypass. When a parcel's
   * `is_new_parcel` flag is on, the Assessed Value (>0) requirement
   * is dropped because new parcels frequently lack assessed values at
   * the time the certificate is completed. APN + Jurisdiction Type +
   * a Complete-enough Tax Record stay required. Exemption Type also
   * stays required (operations still need to capture the intended
   * exemption status even for new parcels). This bypass is the
   * footer-level twin to the row-level gates in JurisdictionManager;
   * both flips are needed because completeness has two enforcement
   * layers (per-parcel readiness + per-row validation).
   */
  // Jul 2026 — AV-NA-001: "Assessed Value Not Available" is a valid
  // substitute for a numeric TAV only when a reason is selected (and an
  // explanation is provided when reason = Other).
  const isAssessedNaValid = (src) => !!src?.assessed_value_na && !!src?.assessed_value_na_reason &&
    (src.assessed_value_na_reason !== 'other' || !!String(src.assessed_value_na_explanation || '').trim());

  const isParcelDataComplete = useCallback((parcelIndex) => {
    if (hideTaxDetails) return true;
    let apn, tav, jurs, isNewParcel, avNa, avNaOk;
    if (parcelIndex === activeParcelIndex) {
      // Active parcel: read from flat certificateData (real-time)
      const raw = certificateData.parcel_apns;
      apn = typeof raw === 'string' ? raw : (Array.isArray(raw) ? raw.join(', ') : '');
      tav = certificateData.assessed_total_value;
      jurs = certificateData.jurisdictions || [];
      isNewParcel = !!certificateData.is_new_parcel;
      avNa = !!certificateData.assessed_value_na;
      avNaOk = isAssessedNaValid(certificateData);
    } else {
      // Other parcels: read from parcels array (synced when user left them)
      const p = parcels[parcelIndex];
      if (!p) return false;
      apn = typeof p.parcel_apn === 'string' ? p.parcel_apn : '';
      tav = p.assessed_total_value;
      jurs = p.jurisdictions || [];
      isNewParcel = !!p.is_new_parcel;
      avNa = !!p.assessed_value_na;
      avNaOk = isAssessedNaValid(p);
    }
    const apnOk = !!apn.trim();
    // May 17, 2026 — Assessed Value > 0 is bypassed in New Parcel mode.
    // Jul 2026 — AV-NA-001: when the N/A toggle is ON, a VALID reason is
    // what satisfies the requirement — a retained positive TAV must NOT
    // (the toggle declares those values unofficial).
    const tavOk = isNewParcel || (avNa ? avNaOk : !!(tav && tav > 0));
    // Jurisdiction check: ALL configured jurisdictions (with type set) must have
    // at least one COMPLETE tax record. Not just one — every jurisdiction must pass.
    const configuredJurs = jurs.filter(j => !!j.jurisdiction_type);
    // Per-jurisdiction exemption check: every configured jurisdiction must have exemptions selected
    const exOk = configuredJurs.length > 0 && configuredJurs.every(j => (j.exemption_types || []).length > 0);
    const jurOk = configuredJurs.length > 0 && configuredJurs.every(j => {
      const insts = j.installments || [];
      return insts.some(inst => {
        const hasTaxYear = !!inst.tax_year;
        const hasInstName = !!(inst.installment_name && inst.installment_name.toString().trim());
        // May 17, 2026 — base_tax > 0 / non-empty requirement bypassed
        // in New Parcel mode (rows ship with $0.00).
        const hasBaseTax = isNewParcel
          ? true
          : (inst.base_tax !== null && inst.base_tax !== undefined && inst.base_tax !== '');
        return hasTaxYear && hasInstName && hasBaseTax;
      });
    });
    const result = apnOk && tavOk && exOk && jurOk;
    return result;
  }, [hideTaxDetails, activeParcelIndex, certificateData.parcel_apns, certificateData.assessed_total_value, certificateData.jurisdictions, certificateData.is_new_parcel, certificateData.assessed_value_na, certificateData.assessed_value_na_reason, certificateData.assessed_value_na_explanation, parcels]);

  const getParcelMissingFields = useCallback((parcelIndex) => {
    const missing = [];
    let apn, tav, jurs, isNewParcel, avNa, avNaOk;
    if (parcelIndex === activeParcelIndex) {
      const raw = certificateData.parcel_apns;
      apn = typeof raw === 'string' ? raw : (Array.isArray(raw) ? raw.join(', ') : '');
      tav = certificateData.assessed_total_value;
      jurs = certificateData.jurisdictions || [];
      isNewParcel = !!certificateData.is_new_parcel;
      avNa = !!certificateData.assessed_value_na;
      avNaOk = isAssessedNaValid(certificateData);
    } else {
      const p = parcels[parcelIndex];
      apn = typeof p?.parcel_apn === 'string' ? p.parcel_apn : '';
      tav = p?.assessed_total_value;
      jurs = p?.jurisdictions || [];
      isNewParcel = !!p?.is_new_parcel;
      avNa = !!p?.assessed_value_na;
      avNaOk = isAssessedNaValid(p);
    }
    if (!apn.trim()) missing.push('Parcel/APN');
    // May 17, 2026 — Assessed Value missing-field suppressed in New
    // Parcel mode (mirrors the isParcelDataComplete bypass above).
    // Jul 2026 — AV-NA-001: with the toggle ON, a valid reason is
    // required regardless of any retained numeric TAV.
    if (!isNewParcel) {
      if (avNa && !avNaOk) {
        missing.push('Assessed Value N/A Reason');
      } else if (!avNa && (!tav || tav <= 0)) {
        missing.push('Assessed Value');
      }
    }
    // Check ALL configured jurisdictions — report each one that's incomplete
    const configuredJurs = jurs.filter(j => !!j.jurisdiction_type);
    if (configuredJurs.length === 0) {
      missing.push('Jurisdiction Type');
    } else {
      configuredJurs.forEach(j => {
        const name = j.jurisdiction_name || j.jurisdiction_type || 'Jurisdiction';
        // Per-jurisdiction exemption check
        if ((j.exemption_types || []).length === 0) {
          missing.push(`${name}: Exemption Type`);
        }
        // Jun 2026 — UAT-COMBINED-PAYOFF-BASE-BYPASS. Combined Payoff
        // mode supplies the amount at the jurisdiction level — the
        // collector doesn't disclose per-year Base Tax. Treat the
        // base-tax existence check like New Parcel mode so the
        // completion gate doesn't block a legitimately filled
        // Combined Payoff cert.
        const isCombinedPayoff = (j.dlq_payoff_breakdown_mode || 'year_wise') === 'combined';
        const hasCompleteRecord = (j.installments || []).some(inst => {
          const hasTaxYear = !!inst.tax_year;
          const hasInstName = !!(inst.installment_name && inst.installment_name.toString().trim());
          // May 17, 2026 — base_tax existence is not required in New
          // Parcel mode; rows can ship with empty/$0.00.
          // Jun 2026 — same bypass for Combined Payoff mode (see comment above).
          const hasBaseTax = (isNewParcel || isCombinedPayoff)
            ? true
            : (inst.base_tax !== null && inst.base_tax !== undefined && inst.base_tax !== '');
          return hasTaxYear && hasInstName && hasBaseTax;
        });
        if (!hasCompleteRecord) {
          missing.push(isCombinedPayoff
            ? `${name}: Tax year + Installment name required (Combined Payoff covers the amounts)`
            : `${name}: Complete Tax Record (installment name + base tax)`);
        }
      });
    }
    return missing;
  }, [activeParcelIndex, certificateData.parcel_apns, certificateData.assessed_total_value, certificateData.jurisdictions, certificateData.is_new_parcel, certificateData.assessed_value_na, certificateData.assessed_value_na_reason, certificateData.assessed_value_na_explanation, parcels]);

  // canAccessParcel[i] = always true — free navigation between parcels
  // Validation is enforced only at Review Tax Sheet / Complete Order
  const canAccessParcel = useMemo(() => {
    return parcels.map(() => true);
  }, [parcels]);

  // Whether Add Parcel is allowed — always true (only max limit applies)
  // Validation is enforced only at Review Tax Sheet / Complete Order
  const canAddParcel = useMemo(() => {
    return parcels.length < 10;
  }, [parcels.length]);

  /**
   * Switch to a different parcel
   * Syncs current flat data to current parcel, then loads target parcel to flat data
   * Free navigation — no completeness gate. Validation at Review/Complete only.
   */
  const switchToParcel = (newIndex) => {
    if (newIndex < 0 || newIndex >= parcels.length) {
      console.warn('[NAV] Invalid parcel index:', newIndex);
      return;
    }
    
    console.log('[NAV] Switching from Parcel', activeParcelIndex + 1, 'to Parcel', newIndex + 1);
    
    // Step 1: Save current flat data to current parcel and get updated parcels
    const updatedParcels = syncFlatToParcel(true);
    let finalParcels = updatedParcels ? [...updatedParcels] : parcels.map(p => ({ ...p }));
    
    // Step 2: Auto-copy jurisdictions if target parcel is empty and source has data
    const targetParcel = finalParcels[newIndex];
    if (finalParcels.length > 1 && targetParcel &&
        isJurisdictionSetupEmpty(targetParcel.jurisdictions)) {
      const sourceParcel = finalParcels[activeParcelIndex];
      if (sourceParcel?.jurisdictions?.length > 0) {
        finalParcels[newIndex] = {
          ...targetParcel,
          jurisdictions: copySharedJurisdictionSetup(sourceParcel.jurisdictions)
        };
        toast.success(`Jurisdiction setup copied to Parcel ${newIndex + 1}`, { duration: 2000 });
      }
    }
    
    // Step 3: Commit parcels and switch
    setParcels(finalParcels);
    setActiveParcelIndex(newIndex);
    
    // Step 4: Clear validation errors when switching parcels
    setValidationErrors({});
  };
  
  /**
   * Parcel switch from JurisdictionManager pills:
   * If the target parcel has empty jurisdictions, auto-copies shared setup
   * (jurisdiction name, type, contact info, MLC/workflow) from the current parcel.
   * Tax installments are NOT copied — they remain independent per parcel.
   */
  const handleParcelSwitchFromJurisdiction = useCallback((targetIndex) => {
    if (targetIndex < 0 || targetIndex >= parcels.length) return;
    if (targetIndex === activeParcelIndex) return;
    
    // Step 1: Get updated parcels after syncing current edits
    const updatedParcels = syncFlatToParcel(true);
    let finalParcels = updatedParcels ? [...updatedParcels] : parcels.map(p => ({ ...p }));
    
    const targetParcel = finalParcels[targetIndex];
    const sourceParcel = finalParcels[activeParcelIndex];
    
    // Step 2: Sync jurisdiction STRUCTURE across parcels
    // Case A: Target has NO jurisdictions → full copy from source
    // Case B: Target has some jurisdictions but is MISSING ones that source has → merge missing
    if (targetParcel && sourceParcel?.jurisdictions?.length > 0) {
      if (isJurisdictionSetupEmpty(targetParcel.jurisdictions)) {
        // Case A: Full copy
        finalParcels[targetIndex] = {
          ...targetParcel,
          jurisdictions: copySharedJurisdictionSetup(sourceParcel.jurisdictions)
        };
        toast.success(`Jurisdiction setup copied to Parcel ${targetIndex + 1}`, { duration: 2000 });
      } else {
        // Case B: Merge any missing jurisdictions from source into target
        const targetJurs = targetParcel.jurisdictions || [];
        const targetJurKeys = new Set(
          targetJurs.map(j => `${(j.jurisdiction_name || '').toLowerCase().trim()}|||${(j.jurisdiction_type || '').toLowerCase().trim()}`)
        );
        const missingJurs = sourceParcel.jurisdictions.filter(j => {
          if (!j.jurisdiction_name || !j.jurisdiction_type) return false;
          return !targetJurKeys.has(`${j.jurisdiction_name.toLowerCase().trim()}|||${j.jurisdiction_type.toLowerCase().trim()}`);
        });
        if (missingJurs.length > 0) {
          const emptyJurCopies = missingJurs.map(jur => ({
            id: crypto.randomUUID(),
            jurisdiction_name: jur.jurisdiction_name || '',
            jurisdiction_type: jur.jurisdiction_type || null,
            jurisdiction_id: jur.jurisdiction_id || null,
            tax_collector_email: jur.tax_collector_email || '',
            tax_collector_website: jur.tax_collector_website || '',
            assessor_website: jur.assessor_website || '',
            tax_collector_phone: jur.tax_collector_phone || '',
            tax_collector_phone_ext: jur.tax_collector_phone_ext || '',
            tax_collector_fax: jur.tax_collector_fax || '',
            tax_collector_working_hours: jur.tax_collector_working_hours || '',
            make_checks_payable_to: jur.make_checks_payable_to || '',
            mlc_charges: jur.mlc_charges,
            mlc_24_month_charges: jur.mlc_24_month_charges,
            mlc_eta_days: jur.mlc_eta_days,
            sase_required: jur.sase_required || false,
            info_obtained_back: jur.info_obtained_back,
            source: null,
            source_notes: '',
            source_evidence: null,
            collection_frequency: jur.collection_frequency || 'annually',
            changed_by_user: false,
            has_delinquent: null,
            dlq_payoff_amount: null,
            dlq_payoff_manual: false,
            dlq_good_thru_date: null,
            delinquent_taxes_notes: '',
            installments: generateDefaultInstallments(jur.collection_frequency || 'annually', '')
          }));
          finalParcels[targetIndex] = {
            ...finalParcels[targetIndex],
            jurisdictions: [...targetJurs, ...emptyJurCopies]
          };
          console.log(`[PARCEL-SYNC] Merged ${missingJurs.length} missing jurisdiction(s) into Parcel ${targetIndex + 1}`);
        }
      }
    }
    
    // Step 3: Commit parcels and switch
    setParcels(finalParcels);
    setActiveParcelIndex(targetIndex);
    setValidationErrors({});
  }, [activeParcelIndex, parcels, syncFlatToParcel]);
  
  /**
   * Add Parcel from JurisdictionManager + button:
   * Creates a new parcel with jurisdiction setup pre-copied from the currently active parcel.
   */
  const handleAddParcelWithCopy = useCallback(() => {
    if (parcels.length >= 10) {
      toast.error('Maximum of 10 parcels allowed per order');
      return;
    }
    
    // No completeness gate — parcels can be added freely
    // Validation is enforced only at Review Tax Sheet / Complete Order
    
    // Step 1: Sync current edits
    const updatedParcels = syncFlatToParcel(true);
    const currentParcels = updatedParcels ? [...updatedParcels] : parcels.map(p => ({ ...p }));
    const sourceParcel = currentParcels[activeParcelIndex];
    
    // Step 2: Build copied jurisdictions from source parcel
    let copiedJurisdictions = [];
    if (sourceParcel?.jurisdictions?.length > 0) {
      copiedJurisdictions = copySharedJurisdictionSetup(sourceParcel.jurisdictions);
    }
    
    // Step 3: Create new parcel
    const newParcel = {
      parcel_index: currentParcels.length + 1,
      parcel_apn: '',
      source: '',
      source_notes: '',
      tax_collected: '',
      jurisdiction_id: '',
      jurisdiction_type: '',
      make_checks_payable_to: '',
      assessed_land_value: null,
      assessed_building_value: null,
      assessed_total_value: null,
      assessed_value_na: false,
      assessed_value_na_reason: null,
      assessed_value_na_explanation: '',
      assessed_total_manual: false,
      exemption_types: [],
      exemption_other: '',
      exemption_value: null,
      exemption_na: false,
      current_tax_summary: [],
      jurisdictions: copiedJurisdictions,
      has_delinquent: null,
      dlq_payoff_amount: null,
      dlq_good_thru_date: null,
      delinquent_taxes_notes: '',
      mlc_charges: null,
      mlc_24_month_charges: null,
      mlc_eta_days: null,
      sase_required: false,
      info_obtained_back: null,
      tax_collector_email: '',
      tax_collector_website: '',
      assessor_website: '',
      tax_collector_working_hours: '',
      notes: ''
    };
    
    // Step 4: Commit all at once
    const newParcels = [...currentParcels, newParcel];
    setParcels(newParcels);
    setActiveParcelIndex(newParcels.length - 1);
    setCertificateData(prev => ({ ...prev, multiple_parcels: true }));
    setValidationErrors({});
    
    toast.success(`Parcel ${newParcel.parcel_index} added with jurisdiction setup copied`);
  }, [activeParcelIndex, parcels, syncFlatToParcel]);
  
  /**
   * Sync parcel to flat when activeParcelIndex changes
   */
  useEffect(() => {
    if (parcels.length > 0 && parcels[activeParcelIndex]) {
      syncParcelToFlat();
    }
  }, [activeParcelIndex]);
  
  /**
   * Auto-populate jurisdiction when switching to a parcel
   * This ensures each parcel gets its jurisdiction auto-filled based on its own city/county data
   */
  useEffect(() => {
    // Only run after initial load and when switching parcels
    if (certificateData.jurisdiction_type && certificateData.current_tax_summary?.length > 0) {
      const firstRow = certificateData.current_tax_summary[0];
      const currentJurisdiction = firstRow.jurisdiction || '';
      
      // Check if we should auto-populate
      const shouldAutoPopulate = 
        !currentJurisdiction.trim() || 
        isAutoGeneratedJurisdiction(currentJurisdiction, certificateData.jurisdiction_type);
      
      if (shouldAutoPopulate) {
        const propertyCounty = orderData?.property_county || orderData?.county || certificateData.county;
        const propertyCity = orderData?.property_city || orderData?.city || certificateData.city;
        const propertyState = orderData?.property_state || certificateData.state || '';

        const autoName = generateJurisdictionName(certificateData.jurisdiction_type, propertyCounty, propertyCity, propertyState);
        
        if (autoName && autoName !== currentJurisdiction) {
          setCertificateData(prev => ({
            ...prev,
            current_tax_summary: prev.current_tax_summary.map((row, index) => 
              index === 0 ? { ...row, jurisdiction: autoName } : row
            )
          }));
        }
      }
    }
  }, [activeParcelIndex, certificateData.jurisdiction_type]);
  
  /**
   * Navigate to previous parcel
   */
  const handlePrevParcel = () => {
    if (activeParcelIndex > 0) {
      switchToParcel(activeParcelIndex - 1);
    }
  };
  
  /**
   * Navigate to next parcel — free navigation, no completeness gate
   */
  const handleNextParcel = () => {
    if (activeParcelIndex < parcels.length - 1) {
      switchToParcel(activeParcelIndex + 1);
    }
  };
  
  /**
   * Add a new parcel
   * Pre-populates certain fields from Parcel 1, leaves others blank
   */
  const handleAddParcel = () => {
    // Check max parcels limit
    if (parcels.length >= 10) {
      toast.error('Maximum of 10 parcels allowed per order');
      return;
    }
    
    console.log('[NAV] Adding new parcel');
    
    // Step 1: Sync current edits to current parcel
    syncFlatToParcel();
    
    // Step 2: Create a CLEAN blank parcel — no data from existing parcels
    const newParcel = {
      parcel_index: parcels.length + 1,
      parcel_apn: '',
      source: '',
      source_notes: '',
      tax_collected: '',
      jurisdiction_id: '',
      jurisdiction_type: '',
      make_checks_payable_to: '',
      assessed_land_value: null,
      assessed_building_value: null,
      assessed_total_value: null,
      assessed_value_na: false,
      assessed_value_na_reason: null,
      assessed_value_na_explanation: '',
      assessed_total_manual: false,
      exemption_types: [],
      exemption_other: '',
      exemption_value: null,
      exemption_na: false,
      current_tax_summary: [],
      jurisdictions: [],
      has_delinquent: null,
      dlq_payoff_amount: null,
      dlq_good_thru_date: null,
      delinquent_taxes_notes: '',
      mlc_charges: null,
      mlc_24_month_charges: null,
      mlc_eta_days: null,
      sase_required: false,
      info_obtained_back: null,
      tax_collector_email: '',
      tax_collector_website: '',
      assessor_website: '',
      tax_collector_working_hours: '',
      notes: ''
    };
    
    // Step 3: Add to parcels array using functional updater
    // to avoid overwriting the syncFlatToParcel update
    setParcels(prev => [...prev, newParcel]);
    
    // Step 4: Switch to new parcel
    const newIndex = parcels.length; // current length = index of new parcel
    setActiveParcelIndex(newIndex);
    
    // Step 5: Update multiple_parcels flag
    setCertificateData(prev => ({ ...prev, multiple_parcels: true }));
    
    toast.success(`Parcel ${newParcel.parcel_index} added`);
    console.log('[NAV] Added Parcel', newParcel.parcel_index, '- switching to it');
  };
  
  /**
   * Delete a parcel with premium modal confirmation
   * Rules: Cannot delete last remaining parcel, reindex after deletion
   */
  const handleDeleteParcel = () => {
    // Cannot delete if only 1 parcel remains
    if (parcels.length === 1) {
      toast.error('Cannot delete the last remaining parcel');
      return;
    }
    
    const currentParcel = parcels[activeParcelIndex];
    const parcelNumber = activeParcelIndex + 1;
    const parcelAPN = currentParcel.parcel_apn || 'N/A';
    
    // Show premium confirmation modal
    setParcelToDelete({
      index: activeParcelIndex,
      parcelNumber: parcelNumber,
      apn: parcelAPN
    });
    setDeleteModalOpen(true);
    
    console.log('[NAV] Opening delete confirmation modal for Parcel', parcelNumber);
  };

  // Save draft (manual save)
  const handleSaveDraft = async () => {
    try {
      setLoading(true);
      const success = await manualSave();
      if (success) {
        setHasUnsavedChanges(false);
        // Clear local backup after successful manual save to prevent false restore prompts
        await clearLocalBackup();
        if (saveValidationWarnings.length > 0) {
          toast.warning(`Saved with ${saveValidationWarnings.length} validation issue${saveValidationWarnings.length > 1 ? 's' : ''} — fix before reviewing`);
        } else {
          toast.success('Draft saved successfully');
        }
      } else {
        toast.error('Failed to save draft');
      }
    } catch (error) {
      console.error('Error saving draft:', error);
      toast.error('Failed to save draft');
    } finally {
      setLoading(false);
    }
  };

  /**
   * Check if the form is ready for completion
   * Returns true if all required fields are filled and valid
   */
  const isFormReadyForCompletion = () => {
    // Sync current flat state to active parcel to get latest data
    const normalizedApn = typeof certificateData.parcel_apns === 'string'
      ? certificateData.parcel_apns
      : (Array.isArray(certificateData.parcel_apns) 
          ? certificateData.parcel_apns.join(', ') 
          : '');
    
    const updatedCurrentParcel = {
      ...parcels[activeParcelIndex],
      parcel_apn: normalizedApn,
      source: certificateData.source,
      source_notes: certificateData.source_notes || '',
      tax_collected: certificateData.collection_frequency || 'annually',
      jurisdiction_id: certificateData.jurisdiction_id,
      jurisdiction_type: certificateData.jurisdiction_type,
      make_checks_payable_to: certificateData.make_checks_payable_to || '',
      assessed_land_value: certificateData.assessed_land_value,
      assessed_building_value: certificateData.assessed_building_value,
      assessed_total_value: certificateData.assessed_total_value,
      assessed_value_na: certificateData.assessed_value_na,
      assessed_value_na_reason: certificateData.assessed_value_na_reason,
      assessed_value_na_explanation: certificateData.assessed_value_na_explanation,
      assessed_total_manual: certificateData.assessed_total_manual || false,
      exemption_types: certificateData.exemption_types || [],
      exemption_other: certificateData.exemption_other || '',
      exemption_value: certificateData.exemption_value,
      exemption_na: certificateData.exemption_na || false,
      current_tax_summary: certificateData.current_tax_summary || [],
      has_delinquent: certificateData.has_delinquent,
      dlq_payoff_amount: certificateData.dlq_payoff_amount,
      dlq_good_thru_date: certificateData.dlq_good_thru_date,
      delinquent_taxes_notes: certificateData.delinquent_taxes_notes || '',
      mlc_charges: certificateData.mlc_charges,
      mlc_24_month_charges: certificateData.mlc_24_month_charges,
      mlc_eta_days: certificateData.mlc_eta_days,
      sase_required: certificateData.sase_required || false,
      info_obtained_back: certificateData.info_obtained_back,
      tax_collector_email: certificateData.tax_collector_email || '',
      tax_collector_website: certificateData.tax_collector_website || '',
      tax_collector_working_hours: certificateData.tax_collector_working_hours || ''
    };
    
    // Create updated parcels array with current active parcel
    const updatedParcels = parcels.map((p, idx) => 
      idx === activeParcelIndex ? updatedCurrentParcel : p
    );
    
    // Check property/borrower fields (required for order)
    if (!certificateData.full_address?.trim()) return false;
    if (!certificateData.city?.trim()) return false;
    if (!certificateData.state?.trim()) return false;
    if (!certificateData.zip?.trim()) return false;
    if (!certificateData.county?.trim()) return false;
    if (!certificateData.borrower_owner_name?.trim()) return false;
    
    // Validate all parcels (pass full array for cross-parcel duplicate APN check)
    for (let i = 0; i < updatedParcels.length; i++) {
      const parcelErrors = validateParcel(updatedParcels[i], i, updatedParcels);
      if (Object.keys(parcelErrors).length > 0) {
        return false;
      }
    }
    
    return true;
  };

  // Mark complete - PHASE 4: Validate all parcels
  const handleMarkComplete = async () => {
    // PHASE 4: Validate all parcels before completing
    const allErrors = validateAllParcels();
    
    if (Object.keys(allErrors).length > 0) {
      console.log('[VALIDATION] Errors found:', allErrors);
      
      // Find which parcel has errors
      const errorParcelIndex = findParcelWithErrors(allErrors);
      
      if (errorParcelIndex !== null && errorParcelIndex !== activeParcelIndex) {
        // Switch to the parcel with errors (skip validation check)
        switchToParcel(errorParcelIndex, true);
        toast.error(`Please fix errors on Parcel ${errorParcelIndex + 1} before completing`);
      } else {
        // Errors are on current parcel or property/borrower
        // Create a more specific error message
        const errorKeys = Object.keys(allErrors);
        const firstError = allErrors[errorKeys[0]];
        
        // Show detailed error toast with field names
        toast.error(
          <div>
            <strong>Please fix the following errors:</strong>
            <ul style={{ margin: '4px 0 0 0', paddingLeft: '16px' }}>
              {errorKeys.slice(0, 3).map((key, idx) => (
                <li key={idx} style={{ fontSize: '12px' }}>{allErrors[key]}</li>
              ))}
              {errorKeys.length > 3 && (
                <li style={{ fontSize: '12px', fontStyle: 'italic' }}>
                  +{errorKeys.length - 3} more errors
                </li>
              )}
            </ul>
          </div>,
          { duration: 8000 }
        );
      }
      
      // Set validation errors to display them inline
      setValidationErrors(allErrors);
      
      // Scroll to first error field
      setTimeout(() => {
        const firstErrorKey = Object.keys(allErrors)[0];
        let elementId = null;
        
        // Map error key to element ID
        if (firstErrorKey.includes('tax_summary_')) {
          // Extract row index from key like "tax_summary_0_dlq_date"
          const match = firstErrorKey.match(/tax_summary_(\d+)_(\w+)/);
          if (match) {
            const rowIndex = match[1];
            const fieldName = match[2];
            elementId = `${fieldName}_${rowIndex}`;
          }
        } else {
          elementId = firstErrorKey;
        }
        
        if (elementId) {
          const element = document.getElementById(elementId);
          if (element) {
            element.scrollIntoView({ behavior: 'smooth', block: 'center' });
            element.focus();
          }
        }
      }, 100);
      
      return;
    }
    
    // MLS-DUAL-001 Phase 2: pre-block parent completion with the actionable
    // component reason (backend 409 guard remains authoritative).
    if (isMlsDual && mlsRollup && mlsRollup !== 'ready_to_complete' && mlsComponents) {
      const blocking = ['tax', 'mls']
        .filter((c) => (mlsComponents[c]?.status || 'pending') !== 'complete')
        .map((c) => {
          const cd = mlsComponents[c] || {};
          const label = c === 'tax' ? 'Tax Search' : 'MLS Search';
          return `${label} is ${(cd.status || 'pending').replace('_', ' ')}${cd.assignee_name ? ` (assigned to ${cd.assignee_name})` : ''}`;
        });
      toast.error(`Cannot complete order — ${blocking.join('; ')}.`, { duration: 8000 });
      return;
    }
    
    try {
      setLoading(true);
      
      // PHASE 3B: Sync flat → parcel before completing to capture final edits
      syncFlatToParcel();
      
      // Sanitize payload to ensure all required fields are present
      const sanitizedData = sanitizeCertificateData(certificateData);
      
      // PHASE 3B: Apply adapter to transform flat frontend + parcels → multi-parcel backend
      const multiParcelPayload = adaptFrontendToBackend(sanitizedData, parcels);
      
      await axios.post(`${API}/orders/${orderData.id}/tax-cert/complete`, {
        certificate: multiParcelPayload
      });
      setHasUnsavedChanges(false);
      // Clear local backup after successful completion
      await clearLocalBackup();
      toast.success(isMLS ? 'MLS order completed successfully' : 'Tax Certificate completed successfully');
      // Refresh page to show updated status
      window.location.reload();
    } catch (error) {
      console.error('Error completing certificate:', error);
      // MLS-DUAL-001: backend-authoritative component guard (409)
      if (error.response?.status === 409 && error.response?.data?.detail?.error === 'components_incomplete') {
        toast.error(error.response.data.detail.message || 'Cannot complete order: MLS components are incomplete.', { duration: 8000 });
      } else if (error.response?.status === 409 && error.response?.data?.detail?.error === 'TAX_NARRATIVE_MISSING') {
        // TAXCERT-TIER3-FALLBACK-001: no reviewed narrative for a parcel with tax data
        toast.error(error.response.data.detail.message, { duration: 12000, id: 'tax-narrative-missing' });
      } else
      // Enhanced error handling to show specific validation errors
      if (error.response?.status === 422 || error.response?.status === 400) {
        const errorData = error.response?.data;
        if (errorData?.detail?.errors && Array.isArray(errorData.detail.errors)) {
          // Multiple validation errors - show first 3
          const errorMessages = errorData.detail.errors.slice(0, 3);
          toast.error(
            <div>
              <strong>Validation Failed:</strong>
              <ul style={{ margin: '4px 0 0 0', paddingLeft: '16px' }}>
                {errorMessages.map((err, idx) => (
                  <li key={idx} style={{ fontSize: '12px' }}>{err}</li>
                ))}
                {errorData.detail.errors.length > 3 && (
                  <li style={{ fontSize: '12px', fontStyle: 'italic' }}>
                    +{errorData.detail.errors.length - 3} more errors
                  </li>
                )}
              </ul>
            </div>,
            { duration: 8000 }
          );
        } else if (errorData?.detail) {
          // Single error message
          const message = typeof errorData.detail === 'string' 
            ? errorData.detail 
            : errorData.detail.message || 'Validation failed';
          toast.error(message, { duration: 5000 });
        } else if (errorData?.errors && Array.isArray(errorData.errors)) {
          // Pydantic validation errors format
          const errorMessages = errorData.errors.slice(0, 3).map(e => e.msg || e.message || JSON.stringify(e));
          toast.error(`Validation errors: ${errorMessages.join(', ')}`, { duration: 5000 });
        } else {
          toast.error('Validation failed. Please check all required fields.');
        }
      } else {
        toast.error('Failed to complete certificate. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  // Validate button - show inline errors
  const handleValidate = () => {
    const isValid = validateForm();
    if (isValid) {
      toast.success('All required fields are valid!');
    } else {
      toast.error('Please fix the highlighted validation errors.');
    }
  };

  // Handle tax collection frequency change
  const handleFrequencyChange = (newFrequency) => {
    // Check if using new nested structure
    const usingNewStructure = isNewJurisdictionStructure(certificateData);
    
    if (usingNewStructure) {
      // NEW STRUCTURE: Handle installment auto-generation for jurisdictions[]
      const hasExistingInstallments = certificateData.jurisdictions.some(
        jur => jur.installments && jur.installments.length > 0 && 
               jur.installments.some(inst => inst.installment_name || inst.amount_due)
      );
      
      if (hasExistingInstallments) {
        // Show confirmation modal before regenerating installments
        setPendingFrequency(newFrequency);
        setShowConfirmModal(true);
      } else {
        // No confirmation needed - auto-generate installments
        setCertificateData(prev => ({ ...prev, collection_frequency: newFrequency }));
        regenerateNestedInstallments(newFrequency);
      }
    } else {
      // LEGACY STRUCTURE: Existing behavior
      if (certificateData.current_tax_summary.length <= 1) {
        // No confirmation needed for single row
        setCertificateData(prev => ({ ...prev, collection_frequency: newFrequency }));
        generateTaxSummaryRows(newFrequency);
      } else {
        // Show confirmation modal for destructive changes
        setPendingFrequency(newFrequency);
        setShowConfirmModal(true);
      }
    }
  };
  
  /**
   * Regenerate installments for NEW nested structure based on frequency
   * @param {string} frequency - Tax collection frequency
   * @param {boolean} forceRegenerate - If true, regenerate ALL jurisdictions regardless of manual edits
   */
  const regenerateNestedInstallments = (frequency, forceRegenerate = false) => {
    const labels = DEFAULT_INSTALLMENT_LABELS[frequency] || ['Annual'];
    
    setCertificateData(prev => ({
      ...prev,
      collection_frequency: frequency,
      jurisdictions: prev.jurisdictions.map(jur => {
        // Feb 19, 2026 — Specialty Installments preservation:
        // Split out specialty rows (SB / SA / Water / Sewer / etc.) so we
        // can rebuild the standard installment skeleton from frequency
        // labels WITHOUT touching processor-curated specialty bills.
        // The user explicitly requested this mixed-grid behaviour and we
        // have regression coverage at:
        //   frontend/src/lib/__tests__/specialtyInstallments.test.js
        const [standardCurrent, specialtyCurrent] = partitionInstallments(jur.installments || []);

        // If forceRegenerate is true, skip the manual edits check and regenerate all
        if (!forceRegenerate) {
          // Check if user has manually edited STANDARD installments with actual meaningful data.
          // Specialty rows are always preserved, so they don't participate in this check —
          // otherwise an SB row alone would cause every standard row to be retained as well.
          const hasManualEdits = standardCurrent.some(inst => {
            // Check for actual user-entered financial data
            const hasAmount = inst.amount_due !== null && inst.amount_due !== '' && inst.amount_due !== 0;
            const hasDueDate = inst.due_date !== null && inst.due_date !== '';
            const hasPaidDate = inst.paid_date !== null && inst.paid_date !== '';
            const hasBalance = inst.balance_amount !== null && inst.balance_amount !== '' && inst.balance_amount !== 0;
            return hasAmount || hasDueDate || hasPaidDate || hasBalance;
          });
          
          // If user has made meaningful edits and not forcing, preserve installments (don't overwrite)
          if (hasManualEdits) {
            console.log('[REGENERATE] Skipping jurisdiction - has manual edits:', jur.jurisdiction_name);
            return jur;
          }
        }
        
        // Generate new STANDARD installments based on frequency — tax_year blank by default
        const newStandard = labels.map((label, idx) => ({
          id: crypto.randomUUID(),
          tax_year: '',
          installment_name: label,
          installment_index: idx + 1,
          due_date: null,
          base_tax: null,
          amount_due: null,
          discount_penalty: 0,
          paid_amount: null,
          notes: '',
          pay_status: 'Due',
          paid_date: null,
          balance_amount: null,
          dlq_date: null,
          mailing_issue_date: null
        }));

        // Append preserved specialty rows after the standard skeleton.
        // Re-index installment_index across the merged list so downstream
        // ordering / split-tax helpers stay deterministic.
        const merged = [...newStandard, ...specialtyCurrent].map((inst, idx) => ({
          ...inst,
          installment_index: idx + 1,
        }));

        console.log('[REGENERATE] Regenerating installments for jurisdiction:', jur.jurisdiction_name, 'standard:', newStandard.length, 'specialty_preserved:', specialtyCurrent.length, 'forceRegenerate:', forceRegenerate);
        
        return {
          ...jur,
          installments: merged
        };
      })
    }));
    
    toast.success(`Installments regenerated for ${frequency.replace('_', '-')} collection.`);
  };

  // Generate tax summary rows based on frequency
  const generateTaxSummaryRows = (frequency) => {
    let installmentLabels = [];
    
    switch (frequency) {
      case 'annually':
        installmentLabels = ['Full'];
        break;
      case 'semi_annually':
        installmentLabels = ['1st', '2nd'];
        break;
      case 'quarterly':
        installmentLabels = ['1st', '2nd', '3rd', '4th'];
        break;
      default:
        // Default to annually if unknown frequency
        installmentLabels = ['Full'];
    }
    
    // Get property county for auto-population
    const propertyCounty = orderData?.property_county || orderData?.county || certificateData?.county;
    const autoJurisdiction = propertyCounty ? `${propertyCounty.trim()} County` : '';
    
    // Preserve values from first row
    const firstRowData = certificateData.current_tax_summary[0] || {};

    // Feb 19, 2026 — Specialty Installments preservation (legacy summary):
    // Partition the existing rows so specialty rows (SB / SA / Water / etc.)
    // survive a Tax Collection frequency flip. We rebuild the standard
    // skeleton from `installmentLabels` and append the specialty rows
    // verbatim. The partition helper checks `installment_name` (the
    // legacy schema also uses `installment` — try both for safety).
    const currentRows = certificateData.current_tax_summary || [];
    const specialtyLegacy = currentRows.filter((r) => {
      const name = r.installment_name || r.installment || '';
      // Match SB / SB-1 / SB-2 / SA / SA-1 / Water / Sewer / Storm Water / Water & Sewer
      // by deferring to the same helper used in the new nested structure.
      return name && partitionInstallments([{ installment_name: name }])[1].length > 0;
    });

    const newStandard = installmentLabels.map((label, index) => ({
      jurisdiction: index === 0 ? (firstRowData.jurisdiction || autoJurisdiction) : autoJurisdiction,
      tax_year: index === 0 ? firstRowData.tax_year || '' : '',
      mailing_issue_date: index === 0 ? firstRowData.mailing_issue_date || '' : '',
      installment: label,
      base_tax: index === 0 ? firstRowData.base_tax || '' : '',
      due_date: index === 0 ? firstRowData.due_date || '' : '',
      amounts_fees: index === 0 ? firstRowData.amounts_fees || '' : '',
      pay_status: index === 0 ? firstRowData.pay_status || 'Due' : 'Due',
      paid_date: index === 0 ? firstRowData.paid_date || '' : '',
      dlq_date: index === 0 ? firstRowData.dlq_date || '' : ''
    }));

    const newRows = [...newStandard, ...specialtyLegacy];
    
    setCertificateData(prev => ({
      ...prev,
      collection_frequency: frequency,
      current_tax_summary: newRows
    }));
  };

  // Confirm regeneration
  const confirmRegeneration = () => {
    // Check if using new nested structure
    const usingNewStructure = isNewJurisdictionStructure(certificateData);
    
    if (usingNewStructure) {
      // NEW STRUCTURE: Regenerate installments for ALL jurisdictions
      // Pass forceRegenerate=true since user explicitly confirmed they want to regenerate
      setCertificateData(prev => ({ ...prev, collection_frequency: pendingFrequency }));
      regenerateNestedInstallments(pendingFrequency, true); // Force regenerate all
    } else {
      // LEGACY STRUCTURE: Existing behavior
      generateTaxSummaryRows(pendingFrequency);
    }
    
    setShowConfirmModal(false);
    setPendingFrequency('');
    toast.success('Tax summary rows regenerated successfully');
  };

  // Handle assessed value changes with auto-calculation
  const handleAssessedValueChange = (field, value) => {
    const numericValue = value === '' ? null : parseFloat(value) || 0;
    
    // Validate: Assessed values must be whole dollars (no cents)
    if (numericValue !== null && numericValue > 0) {
      const hasCents = (numericValue % 1) !== 0;
      if (hasCents) {
        setValidationErrors(prev => ({
          ...prev,
          [field]: 'Assessed values must be whole dollars (no cents)'
        }));
      } else {
        // Clear error if value is valid
        setValidationErrors(prev => {
          const updated = { ...prev };
          delete updated[field];
          return updated;
        });
      }
    } else {
      // Clear error if field is empty
      setValidationErrors(prev => {
        const updated = { ...prev };
        delete updated[field];
        return updated;
      });
    }
    
    setCertificateData(prev => {
      // When breakdown is ON and user tries to edit total, ignore the change (field should be disabled anyway)
      if (field === 'assessed_total_value' && prev.use_assessed_breakdown !== false) {
        return prev; // No change - total is locked when breakdown is ON
      }
      
      const updated = { ...prev, [field]: numericValue };
      
      // Auto-calculate total when breakdown toggle is ON
      if (field === 'assessed_land_value' || field === 'assessed_building_value') {
        // Always auto-calculate when breakdown is enabled (toggle ON)
        if (prev.use_assessed_breakdown !== false) {
          const landValue = field === 'assessed_land_value' ? numericValue : (prev.assessed_land_value || 0);
          const buildingValue = field === 'assessed_building_value' ? numericValue : (prev.assessed_building_value || 0);
          updated.assessed_total_value = landValue + buildingValue;
        }
      }
      
      return updated;
    });
  };

  // Callback for compact parcel field changes from JurisdictionManager
  // Routes APN through handleChange, assessed values through handleAssessedValueChange
  const handleCompactParcelFieldChange = (field, value) => {
    if (field === 'parcel_apns' || field === 'exemption_other' ||
        field === 'assessed_value_na' || field === 'assessed_value_na_reason' ||
        field === 'assessed_value_na_explanation') {
      handleChange(field, value);
    } else {
      handleAssessedValueChange(field, value);
    }
  };

  // Reset total assessed value to auto-calculated
  const resetTotalAssessedValue = () => {
    const landValue = certificateData.assessed_land_value || 0;
    const buildingValue = certificateData.assessed_building_value || 0;
    
    setCertificateData(prev => ({
      ...prev,
      assessed_total_value: landValue + buildingValue,
      assessed_total_manual: false
    }));
    
    toast.success('Total assessed value reset to auto-calculated');
  };

  // Remove tax summary row
  const removeTaxSummaryRow = (index) => {
    if (certificateData.current_tax_summary.length <= 1) {
      toast.error('At least one tax summary row is required');
      return;
    }
    
    setCertificateData(prev => ({
      ...prev,
      current_tax_summary: prev.current_tax_summary.filter((_, i) => i !== index)
    }));
  };

  // Format currency for display
  const formatCurrency = (value) => {
    if (value === null || value === undefined || value === '') return '';
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(value);
  };

  const isFormValid = () => {
    // Basic required fields
    const apnArray = formatAPNsArray(certificateData.parcel_apns);
    const basicFieldsValid = certificateData.full_address.trim() && 
           certificateData.city.trim() && 
           certificateData.state.trim() && 
           certificateData.zip.trim() && 
           certificateData.county.trim() && 
           apnArray.length > 0 && 
           certificateData.borrower_owner_name.trim() && 
           certificateData.make_checks_payable_to.trim() &&
           certificateData.current_tax_summary.some(row => 
             row.jurisdiction.trim() && row.tax_year.trim() && row.base_tax.trim()
           );

    // Delinquent taxes validation
    const delinquentValid = certificateData.has_delinquent !== null && (
      certificateData.has_delinquent === false || (
        certificateData.has_delinquent === true &&
        certificateData.dlq_payoff_amount > 0 &&
        certificateData.dlq_good_thru_date &&
        new Date(certificateData.dlq_good_thru_date) > new Date() &&
        certificateData.delinquent_taxes_notes.trim() &&
        certificateData.current_tax_summary.some(row => 
          row.pay_status === 'Delinquent' || row.pay_status === 'Partially Dlq'
        )
      )
    );
    
    // Exemption validation
    const exemptionValid = !certificateData.exemption_types.includes('Other') || 
                          (certificateData.exemption_other && certificateData.exemption_other.trim());
    
    // Assessed values validation - must be whole dollars (no cents)
    const assessedValuesValid = !validationErrors.assessed_land_value && 
                               !validationErrors.assessed_building_value && 
                               !validationErrors.assessed_total_value;

    return basicFieldsValid && delinquentValid && exemptionValid && assessedValuesValid;
  };

  // ============================================================================
  // PHASE 3D: DELETE CONFIRMATION MODAL COMPONENT
  // ============================================================================
  
  const DeleteParcelModal = () => {
    if (!deleteModalOpen || !parcelToDelete) return null;
    
    const handleConfirmDelete = () => {
      console.log('[MODAL] User confirmed deletion of Parcel', parcelToDelete.parcelNumber);
      
      // Step 1: Remove the parcel from array
      const updatedParcels = parcels.filter((_, index) => index !== parcelToDelete.index);
      
      // Step 2: Reindex remaining parcels
      const reindexedParcels = updatedParcels.map((parcel, index) => ({
        ...parcel,
        parcel_index: index + 1
      }));
      
      setParcels(reindexedParcels);
      
      // Step 3: Update activeParcelIndex
      const newIndex = parcelToDelete.index >= reindexedParcels.length 
        ? reindexedParcels.length - 1 
        : parcelToDelete.index;
      
      setActiveParcelIndex(newIndex);
      
      // Step 4: If only 1 parcel remains, set multiple_parcels to false
      if (reindexedParcels.length === 1) {
        setCertificateData(prev => ({ ...prev, multiple_parcels: false }));
      }
      
      // Close modal
      setDeleteModalOpen(false);
      setParcelToDelete(null);
      
      toast.success(`Parcel ${parcelToDelete.parcelNumber} deleted`);
      console.log('[MODAL] Deleted Parcel', parcelToDelete.parcelNumber, '- now have', reindexedParcels.length, 'parcels');
    };
    
    const handleCancel = () => {
      console.log('[MODAL] User cancelled deletion');
      setDeleteModalOpen(false);
      setParcelToDelete(null);
    };
    
    return (
      <>
        {/* Overlay */}
        <div 
          className="fixed inset-0 bg-black bg-opacity-50 z-50 transition-opacity"
          onClick={handleCancel}
        />
        
        {/* Modal */}
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div 
            className="bg-white rounded-2xl shadow-2xl max-w-md w-full transform transition-all"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div className="px-6 py-5 border-b border-slate-200">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 rounded-full bg-red-100 flex items-center justify-center">
                  <Trash2 className="h-6 w-6 text-red-600" />
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-slate-900">
                    Delete Parcel {parcelToDelete.parcelNumber}
                  </h3>
                  <p className="text-sm text-slate-600 mt-0.5">
                    (APN: {parcelToDelete.apn || 'N/A'})
                  </p>
                </div>
              </div>
            </div>
            
            {/* Body */}
            <div className="px-6 py-4">
              <p className="text-sm text-slate-700">
                This action cannot be undone.
              </p>
            </div>
            
            {/* Footer */}
            <div className="px-6 py-4 bg-slate-50 rounded-b-2xl flex items-center justify-end gap-3">
              <Button
                variant="outline"
                size="sm"
                onClick={handleCancel}
                className="px-4 py-2"
              >
                Cancel
              </Button>
              <Button
                variant="default"
                size="sm"
                onClick={handleConfirmDelete}
                className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white"
              >
                Delete Parcel
              </Button>
            </div>
          </div>
        </div>
      </>
    );
  };

  // Autosave / lock status chips — shared by the Tax Certificate header and
  // (MLS-HEADER-CLEANUP) the compact MLS parcel bar.
  const saveStatusChips = !readOnly && (
    <div className="flex items-center gap-2" data-testid="tax-cert-save-status">
      {isSaving && (
        <div className="flex items-center gap-1.5 text-xs text-gray-600">
          <div className="w-3 h-3 border-2 border-gray-400 border-t-transparent rounded-full animate-spin" />
          <span>Saving...</span>
        </div>
      )}
      {lockedError && (
        <div className="flex items-center gap-1.5 text-xs text-amber-700 bg-amber-50 border border-amber-200 px-2 py-1 rounded">
          <svg className="w-3.5 h-3.5 text-amber-600" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z" clipRule="evenodd" />
          </svg>
          <span data-testid="tax-cert-locked-banner">{lockedError.message}</span>
        </div>
      )}
      {saveError && !lockedError && (
        <div className="flex items-center gap-1.5 text-xs text-red-600">
          <svg className="w-3.5 h-3.5" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
          </svg>
          <span>Save failed: {saveError}</span>
        </div>
      )}
      {hasUnsavedChanges && !isSaving && !saveError && !lockedError && (
        <div className="flex items-center gap-1.5 text-xs text-orange-600">
          <svg className="w-3.5 h-3.5" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
          </svg>
          <span>Unsaved changes</span>
        </div>
      )}
    </div>
  );

  return (
    <div className="relative">
      {/* Feb 27, 2026 — neutral loading state while the external-portal
          resolver is in flight. Prevents the wrong panel from flashing
          briefly for MCTDT / future product-driven external_portal
          orders before the resolver responds. */}
      {!externalPortalResolved && (
        <div
          className="flex items-center justify-center py-12"
          data-testid="ext-portal-resolver-loading"
        >
          <div className="text-sm text-gray-500 flex items-center gap-2">
            <span className="inline-block h-4 w-4 rounded-full border-2 border-gray-300 border-t-blue-500 animate-spin" />
            Loading workflow…
          </div>
        </div>
      )}
      
      {externalPortalResolved && (
        <>
      {/* Delete Confirmation Modal */}
      <DeleteParcelModal />

      {/*
        ── v7.5 (May 18, 2026) — New Parcel turn-OFF confirmation ──
        Three explicit actions on disable:
          1. Revert & Clear — remove auto-generated Refer Note +
             $0 amounts, wipe stored notes/overrides, restore the
             standard tax workflow cleanly.
          2. Keep Existing Values — flip the mode off but preserve
             the currently entered tax rows and values.
          3. Cancel — keep New Parcel enabled, make no changes.
      */}
      <AlertDialog
        open={showNewParcelOffConfirm}
        onOpenChange={(open) => {
          if (!open) setShowNewParcelOffConfirm(false);
        }}
      >
        <AlertDialogContent
          className="max-w-lg"
          data-testid="new-parcel-off-confirm-dialog"
        >
          <AlertDialogHeader>
            <AlertDialogTitle data-testid="new-parcel-off-confirm-title">
              Turn off New Parcel mode?
            </AlertDialogTitle>
            <AlertDialogDescription className="text-sm leading-relaxed text-slate-600">
              Turning off New Parcel will revert this parcel back to the
              standard tax workflow. Would you like to clear the
              auto-generated New Parcel tax values and notes?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end sm:gap-2">
            <button
              type="button"
              data-testid="new-parcel-off-cancel-btn"
              onClick={() => setShowNewParcelOffConfirm(false)}
              className="inline-flex items-center justify-center rounded-md border border-slate-300 bg-white px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-slate-400"
            >
              Cancel
            </button>
            <button
              type="button"
              data-testid="new-parcel-off-keep-values-btn"
              onClick={() => applyNewParcelOff(false)}
              className="inline-flex items-center justify-center rounded-md border border-slate-300 bg-white px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-slate-400"
            >
              Keep Existing Values
            </button>
            <button
              type="button"
              data-testid="new-parcel-off-revert-clear-btn"
              onClick={() => applyNewParcelOff(true)}
              className="inline-flex items-center justify-center rounded-md bg-red-600 px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
            >
              Revert &amp; Clear
            </button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
      
      {/* MLS-HEADER-CLEANUP (Sep 2026): on MLS dual orders the product badge /
          Days Late header is redundant (order already identified as MLS, tabs
          below) — the parcel bar sits directly under the Fulfillment tabs. */}
      <div className="bg-white rounded-2xl border border-slate-200" style={{ boxShadow: '0 8px 24px rgba(15,23,42,0.06)' }}>
      {/* MLS-HEADER-CLEANUP (Sep 2026): on MLS dual orders the product badge /
          Days Late header is redundant (order already identified as MLS, tabs
          below) — the parcel bar sits directly under the Fulfillment tabs. */}
      {(
        <div className="tcp-hero tcp-fade-in px-6 py-4" data-testid="tax-cert-hero" data-hero-mode={mlsParcelMode ? 'mls' : 'tax'}>
          <img src={mlsParcelMode ? '/assets/mls-header-art.png' : '/assets/tax-cert-header-art.png'} alt="" aria-hidden="true" className="tcp-hero-art" />
          <div className="tcp-hero-content flex items-center justify-between gap-4 pr-0 md:pr-[180px] lg:pr-[240px]">
            <div className="flex items-center gap-4 min-w-0">
              <div className="tcp-hero-icon">
                {mlsParcelMode ? <HomeIcon className="w-7 h-7" /> : <FileText className="w-7 h-7" />}
              </div>
              <div className="min-w-0">
                <h2 className="text-2xl font-bold leading-tight tracking-tight truncate" data-testid="tax-cert-title">
                  {mlsParcelMode ? 'MLS Parcel' : isMlsDual ? 'Tax Certificate' : isMLS ? orderData?.client_product || 'MLS' : 'Tax Certificate'}
                </h2>
                <p className="text-sm text-white/80 mt-0.5 truncate">
                  {mlsParcelMode
                    ? 'Manage MLS search details, code violations, permits, utilities and supporting documents for this parcel.'
                    : 'Manage tax certificate details, records and workflow for this parcel.'}
                </p>
              </div>
            </div>
            {/* Days Late Indicator */}
            {!mlsParcelMode && (() => {
              const daysLateInfo = calculateDaysLate(orderData);
              return (
                <span className={`tcp-late-chip ${daysLateInfo.colorClass}`} data-testid="tax-cert-days-late">
                  <Clock className="w-4 h-4" />
                  Days Late: {daysLateInfo.displayText}
                </span>
              );
            })()}
            {/* Feb 2026 — Removed legacy "View Generated PDFs" / "Regenerate"
                read-only header buttons per UI Cleanup request. */}
          </div>
        </div>
      )}
      
      {/* PHASE 3C: Parcel Navigation UI.
          MLS-PARCEL-NAV-001 (Sep 2026): in MLS parcel mode the strip drives MLS parcels
          (mls-parcel-nav / mls-parcel-add events) and is gated on the MLS-specific
          writable flag — NOT on the Tax component lock. Tax-only controls stay behind !readOnly. */}
      {(!readOnly || (mlsParcelMode && mlsParcelState.writable)) && (
        <div className="px-6 py-3 bg-white border-b border-slate-200" data-testid="parcel-nav-strip" data-mode={mlsParcelMode ? 'mls' : 'tax'}>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              {/* Parcel Indicator - PHASE 5: With error indicator */}
              <div className="flex items-center gap-2">
                <span className="text-[15px] font-semibold text-slate-900 flex items-center gap-2" data-testid="parcel-nav-indicator">
                  {mlsParcelMode ? `MLS Parcel ${mlsParcelState.index} of ${mlsParcelState.count}` : `Parcel ${activeParcelIndex + 1} of ${parcels.length}`}
                  {/* PHASE 5: Show red dot if current parcel has errors */}
                  {parcelsWithErrors.has(activeParcelIndex) && (
                    <span className="inline-flex items-center justify-center w-2 h-2 bg-red-500 rounded-full" title="This parcel has validation errors"></span>
                  )}
                </span>
              </div>
              
              {/* Navigation Buttons */}
              <div className="inline-flex items-center rounded-lg border border-slate-200 bg-white overflow-hidden">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={mlsParcelMode ? () => window.dispatchEvent(new CustomEvent('mls-parcel-nav', { detail: { delta: -1 } })) : handlePrevParcel}
                  disabled={mlsParcelMode ? mlsParcelState.index <= 1 : activeParcelIndex === 0}
                  data-testid="parcel-prev-btn"
                  className="h-8 px-3 text-xs rounded-none border-r border-slate-200"
                >
                  <ChevronLeft className="h-4 w-4 mr-1" />
                  Prev
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={mlsParcelMode ? () => window.dispatchEvent(new CustomEvent('mls-parcel-nav', { detail: { delta: 1 } })) : handleNextParcel}
                  disabled={mlsParcelMode ? mlsParcelState.index >= mlsParcelState.count : activeParcelIndex === parcels.length - 1}
                  data-testid="parcel-next-btn"
                  className="h-8 px-3 text-xs rounded-none"
                >
                  Next
                  <ChevronLeft className="h-4 w-4 ml-1 rotate-180" />
                </Button>
              </div>
              <div className="flex items-center gap-2">
                {/* PHASE 3D: Delete Parcel Button */}
                {!mlsParcelMode && parcels.length > 1 && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handleDeleteParcel}
                    data-testid="delete-parcel-btn"
                    className="h-8 px-3 text-xs text-red-600 border-red-200 hover:bg-red-50 hover:border-red-300"
                  >
                    <Trash2 className="h-4 w-4 mr-1" />
                    Delete
                  </Button>
                )}
              </div>
              {/* Autosave / lock chips (Saving…, Unsaved, Locked, Save failed) live inline here */}
              {saveStatusChips}
            </div>
            
            {/* Add Parcel Button */}
            <Button
              variant="default"
              size="sm"
              onClick={mlsParcelMode ? () => window.dispatchEvent(new CustomEvent('mls-parcel-add')) : handleAddParcelWithCopy}
              disabled={mlsParcelMode ? (mlsParcelState.count >= 10 || !mlsParcelState.writable) : parcels.length >= 10}
              data-testid="add-parcel-btn"
              className="h-9 px-4 text-sm font-semibold rounded-lg text-white shadow-md hover:shadow-lg transition-[box-shadow,transform] duration-150 hover:-translate-y-px"
              style={{ background: 'linear-gradient(90deg, #4c2aff 0%, #6d4aff 100%)' }}
              title={
                (mlsParcelMode ? mlsParcelState.count : parcels.length) >= 10
                  ? 'Maximum 10 parcels reached'
                  : undefined
              }
            >
              <Plus className="h-4 w-4 mr-1" />
              Add Parcel
            </Button>
          </div>
          
          {/* Max parcels warning */}
          {(mlsParcelMode ? mlsParcelState.count : parcels.length) >= 10 && (
            <div className="mt-2 text-xs text-amber-600 flex items-center gap-1">
              <AlertCircle className="h-3.5 w-3.5" />
              <span>Maximum of 10 parcels reached</span>
            </div>
          )}
        </div>
      )}
      
      {/* PHASE 3C: Parcel Navigation UI - Read-Only Version */}
      {readOnly && !(mlsParcelMode && mlsParcelState.writable) && parcels.length > 1 && (
        <div className="px-6 py-3 bg-slate-50 border-b border-slate-200">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              {/* Parcel Indicator */}
              <div className="flex items-center gap-2">
                <span className="text-sm font-medium text-slate-600">
                  Viewing Parcel {activeParcelIndex + 1} of {parcels.length}
                </span>
              </div>
              
              {/* Navigation Buttons */}
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handlePrevParcel}
                  disabled={activeParcelIndex === 0}
                  className="h-7 px-2 text-xs"
                >
                  <ChevronLeft className="h-3 w-3 mr-1" />
                  Prev
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleNextParcel}
                  disabled={activeParcelIndex === parcels.length - 1}
                  className="h-7 px-2 text-xs"
                >
                  Next
                  <ChevronLeft className="h-3 w-3 ml-1 rotate-180" />
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
      
      {/* ── Property Summary Strip ── */}
      {/* Compact read-only header showing address, borrower, county — always visible */}
      {/* Data sourced from certificateData (order-level, stable across parcel switches) */}
      {!isLegacyMls && (
        <div className="px-6 py-2.5 bg-white border-b border-slate-200" data-testid="property-summary-strip">
          <div className="flex flex-wrap items-center gap-x-2 text-sm">
            <MapPin className="w-3.5 h-3.5 text-slate-400 flex-shrink-0" />
            <span className="font-medium text-slate-900" data-testid="strip-address">
              {/* Feb 2026 — Normalize the address parts on render so the
                  Fulfillment header matches the canonical formatting used by
                  Order Summary (SummaryTab.jsx L158) and by the rest of the
                  app (OrderSummaryPopup, Billing dashboard rows). Stored
                  raw values like "2068 HENDRIe st" now display as
                  "2068 Hendrie St, Canton, MI, 48187" everywhere. State is
                  uppercased per USPS convention; ZIP is rendered verbatim. */}
              {[
                formatStreetAddress(certificateData.full_address),
                formatCity(certificateData.city),
                (certificateData.state || '').toString().trim().toUpperCase(),
                certificateData.zip
              ]
                .map(v => (v === '—' ? '' : v))   // drop em-dash placeholders
                .filter(Boolean)
                .join(', ') || '—'}
            </span>
            <span className="text-slate-300 mx-1">·</span>
            <User className="w-3.5 h-3.5 text-slate-400 flex-shrink-0" />
            <span className="text-slate-700" data-testid="strip-borrower">
              {/* Feb 2026 — UI-side defensive normalization. Stored
                  borrower value is preserved on disk; render shows
                  canonical title-case to match the Tax Cert PDF
                  (pdf_service.py:_normalize_borrower_name). */}
              {normalizeBorrowerName(certificateData.borrower_owner_name) || '—'}
            </span>
            {certificateData.county && (
              <>
                <span className="text-slate-300 mx-1">·</span>
                <Building className="w-3.5 h-3.5 text-slate-400 flex-shrink-0" />
                <span className="text-slate-700" data-testid="strip-county">
                  {certificateData.county}
                </span>
              </>
            )}
          </div>
        </div>
      )}
      
      <div className="px-5 pt-4 pb-4 space-y-6">
        {/* Auto-linking Banner for Update Tax Search */}
        {!readOnly && previousOrder && (
          <div className="mx-6 mt-4 p-4 bg-blue-50 border border-blue-200 rounded-lg">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
                  <svg className="w-5 h-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.102m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" />
                  </svg>
                </div>
                <div>
                  <h4 className="text-sm font-medium text-blue-900">
                    Previous Tax Certificate found: {previousOrder.order_number}
                  </h4>
                  <p className="text-xs text-blue-700">
                    Completed {previousOrder.completed_at ? formatDateOnlyET(previousOrder.completed_at) : 'Recently'}
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <Button
                  size="sm"
                  variant="outline"
                  className="text-blue-700 border-blue-300 hover:bg-blue-100 text-xs"
                  onClick={openPreviousOrder}
                >
                  Open
                </Button>
                <Button
                  size="sm"
                  className="bg-blue-600 hover:bg-blue-700 text-white text-xs"
                  onClick={isPrefilled ? handleReapplyData : handlePrefill}
                  disabled={isPrefilling}
                >
                  {isPrefilling ? 'Loading...' : (isPrefilled ? 'Re-apply data' : 'Prefill from previous')}
                </Button>
              </div>
            </div>
          </div>
        )}

        {/* Property / Borrower Section — REPLACED by compact Property Summary Strip above */}
        {/* The strip is always read-only; order-level data is populated from the order during init */}

        {/* MLS-DUAL-001 UI refinement: compact component summary + workspace
            tabs at the top of Fulfillment. The Tax workspace below is hidden
            (CSS only — stays mounted so autosave/dirty state survives) while
            the MLS workspace is active. */}
        {isMlsDual && (
          <MlsDualSection
            orderId={orderData.id}
            orderData={orderData}
            readOnly={readOnlyProp}
            onRollupChange={handleMlsRollupChange}
            activeTab={mlsActiveTab}
            onTabChange={setMlsWsTabChoice}
            refreshKey={mlsRefreshKey}
          />
        )}
        <div className={isMlsDual && mlsActiveTab === 'mls' ? 'hidden' : ''} data-testid="tax-search-workspace">

        {/* Parcel-Specific Fields Section — Hidden for Internal Portal (moved into JurisdictionManager compact section) */}
        {/* Show only for External Portal or legacy structures that don't use JurisdictionManager */}
        {(hideTaxDetails || !isNewJurisdictionStructure(certificateData)) && (
        <div>
          <div className="bg-gradient-to-r from-[#4c2aff] to-[#784bff] text-white font-bold text-base px-4 py-2.5 rounded-lg shadow-md mb-4">
            Parcel Details
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            <div>
              <Label htmlFor="parcel_apns">Parcel/APN *</Label>
              <Input
                id="parcel_apns"
                value={certificateData.parcel_apns || ''}
                onChange={(e) => handleChange('parcel_apns', e.target.value)}
                className={`${validationErrors.parcel_apns ? 'border-red-500' : ''} ${
                  isFieldPrefilled('parcel_apns') ? 'bg-blue-50 border-blue-200' : ''
                }`}
                placeholder="Enter parcel number(s), separated by commas"
                disabled={readOnly}
              />
              {isFieldPrefilled('parcel_apns') && (
                <PrefillIndicator 
                  isVisible={true} 
                  timestamp={prefilledAt} 
                />
              )}
              {validationErrors.parcel_apns && (
                <p className="text-red-500 text-xs mt-1">{validationErrors.parcel_apns}</p>
              )}
            </div>
          </div>
        </div>
        )}

        {/* ============================================================================
            EXTERNAL PORTAL: Uses same internal jurisdiction layout.
            Tax Summary tab/section is hidden. Tax details managed externally.
            ============================================================================ */}

        {/* Assessed Values Section — Hidden for Internal Portal (moved into JurisdictionManager compact section) */}
        {/* Show only for External Portal fallback or legacy structures */}
        {!hideTaxDetails && !isNewJurisdictionStructure(certificateData) && (
        <div>
          <div className="flex items-center justify-between mb-4">
            <div className="bg-gradient-to-r from-[#4c2aff] to-[#784bff] text-white font-bold text-base px-4 py-2.5 rounded-lg shadow-md flex-grow">
              Assessed Values
            </div>
            <PrefillIndicator fieldName="assessed_values" />
          </div>
          
          {/* Auto-save paused indicator */}
          {(validationErrors.assessed_land_value || validationErrors.assessed_building_value || validationErrors.assessed_total_value) && (
            <div className="bg-amber-50 border border-amber-200 rounded-lg px-4 py-2 mb-4 flex items-center gap-2">
              <svg className="w-5 h-5 text-amber-600 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
              </svg>
              <span className="text-sm text-amber-800">
                Auto-save is paused until validation errors are fixed.
              </span>
            </div>
          )}
          
          {/* Toggle: TAV breakdowns N/A */}
          <div className="mb-4 p-4 bg-slate-50 border border-slate-200 rounded-lg">
            <div className="flex items-center justify-between">
              <div className="flex-1">
                <label htmlFor="use_breakdown_toggle" className="text-sm font-medium text-gray-900">
                  TAV breakdowns N/A
                </label>
              </div>
              <button
                id="use_breakdown_toggle"
                type="button"
                onClick={() => {
                  const newValue = certificateData.use_assessed_breakdown === false;
                  setCertificateData(prev => {
                    const landValue = prev.assessed_land_value || 0;
                    const buildingValue = prev.assessed_building_value || 0;
                    
                    return {
                      ...prev,
                      use_assessed_breakdown: newValue,
                      // When turning OFF breakdown, clear land/building and keep total editable
                      ...(newValue === false && {
                        assessed_land_value: '',
                        assessed_building_value: '',
                        assessed_total_manual: true
                      }),
                      // When turning ON breakdown, recalculate total from land+building and lock it
                      ...(newValue === true && {
                        assessed_total_value: landValue + buildingValue,
                        assessed_total_manual: false
                      })
                    };
                  });
                }}
                disabled={readOnly}
                className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 ${
                  certificateData.use_assessed_breakdown !== false ? 'bg-blue-600' : 'bg-gray-300'
                } ${readOnly ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
              >
                <span
                  className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                    certificateData.use_assessed_breakdown !== false ? 'translate-x-6' : 'translate-x-1'
                  }`}
                />
              </button>
            </div>
          </div>
          
          {/* Jul 2026 — AV-NA-001: Assessed Value Not Available (legacy layout).
              Independent of Fully Exempt and of TAV breakdowns N/A. Values are
              retained (grayed) and restored if the toggle is turned back off. */}
          <div className="mb-4 p-4 bg-slate-50 border border-slate-200 rounded-lg">
            <div className="flex items-center justify-between">
              <div className="flex-1">
                <label htmlFor="assessed_value_na_toggle" className="text-sm font-medium text-gray-900">
                  Assessed Value Not Available
                </label>
                <p className="text-xs text-gray-500 mt-0.5">
                  Use when the assessor does not provide assessed values. The certificate will show N/A (never $0.00).
                </p>
              </div>
              <button
                id="assessed_value_na_toggle"
                type="button"
                data-testid="assessed-value-na-toggle"
                onClick={() => {
                  const turningOn = !certificateData.assessed_value_na;
                  setCertificateData(prev => ({
                    ...prev,
                    assessed_value_na: turningOn,
                    ...(turningOn ? {} : { assessed_value_na_reason: null, assessed_value_na_explanation: '' })
                  }));
                }}
                disabled={readOnly}
                className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 ${
                  certificateData.assessed_value_na ? 'bg-blue-600' : 'bg-gray-300'
                } ${readOnly ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
              >
                <span
                  className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                    certificateData.assessed_value_na ? 'translate-x-6' : 'translate-x-1'
                  }`}
                />
              </button>
            </div>
            {certificateData.assessed_value_na && (
              <div className="mt-3 flex flex-col sm:flex-row gap-3">
                <div className="flex-1">
                  <Label className="text-xs text-gray-600">Reason <span className="text-red-500">*</span></Label>
                  <select
                    value={certificateData.assessed_value_na_reason || ''}
                    onChange={(e) => setCertificateData(prev => ({ ...prev, assessed_value_na_reason: e.target.value || null }))}
                    disabled={readOnly}
                    data-testid="assessed-value-na-reason"
                    className="mt-1 w-full h-9 px-2 text-sm border border-slate-300 rounded-md bg-white"
                  >
                    <option value="">Select a reason…</option>
                    <option value="not_provided_by_assessor">Not provided by assessor</option>
                    <option value="assessor_records_unavailable">Assessor records unavailable</option>
                    <option value="exempt_property">Exempt property</option>
                    <option value="other">Other</option>
                  </select>
                </div>
                {certificateData.assessed_value_na_reason === 'other' && (
                  <div className="flex-1">
                    <Label className="text-xs text-gray-600">Explanation <span className="text-red-500">*</span></Label>
                    <Input
                      value={certificateData.assessed_value_na_explanation || ''}
                      onChange={(e) => setCertificateData(prev => ({ ...prev, assessed_value_na_explanation: e.target.value }))}
                      readOnly={readOnly}
                      data-testid="assessed-value-na-explanation"
                      className="mt-1 h-9 text-sm"
                      placeholder="Short explanation (required)"
                      maxLength={200}
                    />
                  </div>
                )}
              </div>
            )}
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            <div>
              <Label htmlFor="assessed_land_value">Land Value</Label>
              <div className="mt-1 relative">
                <span className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-500">$</span>
                <Input
                  id="assessed_land_value"
                  type="number"
                  min="0"
                  step="1"
                  value={certificateData.assessed_land_value || ''}
                  onChange={(e) => handleAssessedValueChange('assessed_land_value', e.target.value)}
                  className={`pl-8 text-right ${
                    validationErrors.assessed_land_value 
                      ? 'border-red-500 focus:border-red-500 focus:ring-red-500' 
                      : isFieldPrefilled('assessed_land_value') 
                        ? 'bg-blue-50 border-blue-200' 
                        : certificateData.use_assessed_breakdown === false
                          ? 'bg-gray-100'
                          : ''
                  }`}
                  placeholder="0"
                  readOnly={readOnly}
                  disabled={certificateData.use_assessed_breakdown === false || certificateData.assessed_value_na === true}
                />
              </div>
              {validationErrors.assessed_land_value && (
                <p className="text-xs text-red-600 mt-1">{validationErrors.assessed_land_value}</p>
              )}
            </div>
            <div>
              <Label htmlFor="assessed_building_value">Building Value</Label>
              <div className="mt-1 relative">
                <span className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-500">$</span>
                <Input
                  id="assessed_building_value"
                  type="number"
                  min="0"
                  step="1"
                  value={certificateData.assessed_building_value || ''}
                  onChange={(e) => handleAssessedValueChange('assessed_building_value', e.target.value)}
                  className={`pl-8 text-right ${
                    validationErrors.assessed_building_value 
                      ? 'border-red-500 focus:border-red-500 focus:ring-red-500' 
                      : isFieldPrefilled('assessed_building_value') 
                        ? 'bg-blue-50 border-blue-200' 
                        : certificateData.use_assessed_breakdown === false
                          ? 'bg-gray-100'
                          : ''
                  }`}
                  placeholder="0"
                  readOnly={readOnly}
                  disabled={certificateData.use_assessed_breakdown === false || certificateData.assessed_value_na === true}
                />
              </div>
              {validationErrors.assessed_building_value && (
                <p className="text-xs text-red-600 mt-1">{validationErrors.assessed_building_value}</p>
              )}
            </div>
            <div>
              <Label htmlFor="assessed_total_value">
                Total Assessed Value
              </Label>
              <div className="mt-1 relative">
                <span className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-500">$</span>
                <Input
                  id="assessed_total_value"
                  type="number"
                  min="0"
                  step="1"
                  value={certificateData.assessed_total_value || ''}
                  onChange={(e) => handleAssessedValueChange('assessed_total_value', e.target.value)}
                  className={`pl-8 text-right ${
                    validationErrors.assessed_total_value 
                      ? 'border-red-500 focus:border-red-500 focus:ring-red-500' 
                      : certificateData.use_assessed_breakdown !== false
                        ? 'bg-gray-100 border-gray-300'
                        : ''
                  }`}
                  placeholder="0"
                  readOnly={readOnly}
                  disabled={certificateData.use_assessed_breakdown !== false || certificateData.assessed_value_na === true}
                />
                {/* Show lock icon when breakdown is ON (field is auto-calculated and locked) */}
                {certificateData.use_assessed_breakdown !== false && (
                  <span className="absolute right-3 top-1/2 transform -translate-y-1/2 text-xs text-gray-500" title="Auto-calculated from Land + Building values">
                    🔒
                  </span>
                )}
              </div>
              {validationErrors.assessed_total_value && (
                <p className="text-xs text-red-600 mt-1">{validationErrors.assessed_total_value}</p>
              )}
            </div>
            <div>
              <Label htmlFor="exemption_types">Exemption Type(s)</Label>
              <div className="mt-1.5">
                <ExemptionPillSelector
                  value={certificateData.exemption_types}
                  onChange={handleExemptionTypeChange}
                  readOnly={readOnly}
                />
              </div>
            </div>
            {certificateData.exemption_types.includes('Other') && (
              <div>
                <Label htmlFor="exemption_other">Other description *</Label>
                <Input
                  id="exemption_other"
                  value={certificateData.exemption_other || ''}
                  onChange={(e) => handleChange('exemption_other', e.target.value)}
                  className="mt-1"
                  placeholder="Describe the other exemption type..."
                  readOnly={readOnly}
                />
              </div>
            )}
            <div>
              <Label htmlFor="exemption_value">Exemption Value ($)</Label>
              <div className="mt-1 relative">
                <span className={`absolute left-3 top-1/2 transform -translate-y-1/2 ${certificateData.exemption_types?.includes('No Exemptions (N/A)') ? 'text-gray-300' : 'text-slate-500'}`}>$</span>
                <Input
                  id="exemption_value"
                  type="number"
                  min="0"
                  step="0.01"
                  value={certificateData.exemption_types?.includes('No Exemptions (N/A)') ? '0.00' : (certificateData.exemption_value || '')}
                  onChange={(e) => handleAssessedValueChange('exemption_value', e.target.value)}
                  className={`pl-8 text-right ${certificateData.exemption_types?.includes('No Exemptions (N/A)') ? 'bg-[#F9FAFB] text-[#9CA3AF] cursor-not-allowed' : ''}`}
                  placeholder="0.00"
                  readOnly={readOnly || certificateData.exemption_types?.includes('No Exemptions (N/A)')}
                  title={certificateData.exemption_types?.includes('No Exemptions (N/A)') ? 'Disabled because No Exemptions is selected' : ''}
                  data-testid="exemption-value-input"
                />
              </div>
            </div>
          </div>
        </div>
        )}

        {/* Current Tax Summary - Card Layout */}
        {/* Always show JurisdictionManager for new structure; Tax Collected is per-jurisdiction */}
        {(hideTaxDetails || isNewJurisdictionStructure(certificateData)) ? (
          <div>
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-[11px] font-semibold uppercase tracking-wider text-slate-500">
                {hideTaxDetails ? 'Jurisdiction Setup' : 'Current Tax Summary *'}
              </h3>
            </div>

          {/* External Portal info banner */}
          {isExternalPortalClient && (
            <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-md">
              <p className="text-sm text-blue-800">
                <strong>External Portal:</strong> Tax details are managed via the external portal. Only jurisdiction setup, contact info, source/evidence, and MLC fields are shown.
              </p>
            </div>
          )}

          {/* MLS Product info banner — LEGACY (non-dual) MLS only */}
          {isLegacyMls && !isExternalPortalClient && (
            <div className="mb-4 p-3 bg-indigo-50 border border-indigo-200 rounded-md" data-testid="mls-info-banner">
              <p className="text-sm text-indigo-800">
                <strong>MLS Product:</strong> Tax summary, installments, and assessed values are not applicable for MLS orders. Only jurisdiction setup, contact info, source/evidence, and MLC fields are shown.
              </p>
            </div>
          )}
          
          {/* MLS-DUAL-001 UI refinement: MlsDualSection moved to the top of
              Fulfillment (workspace tabs) — no longer rendered inline here */}
          
          {validationErrors.current_tax_summary && (
            <p className="text-red-500 text-xs mb-2">{validationErrors.current_tax_summary}</p>
          )}
          
          {/* ============================================================================
              PHASE 3: JURISDICTION MANAGER - Master-Detail Workspace Layout
              Replaces vertical stacking with sidebar list + tabbed detail panel
              ============================================================================ */}
          {isNewJurisdictionStructure(certificateData) ? (
            <div data-testid="new-jurisdiction-structure">
              <JurisdictionManager
                jurisdictions={certificateData.jurisdictions}
                onJurisdictionsChange={handleJurisdictionsChange}
                onJurisdictionTypeChange={(type, jurisdiction, countyName) => {
                  // Find the index of this jurisdiction
                  const jurIndex = certificateData.jurisdictions.findIndex(j => j.id === jurisdiction.id);
                  if (jurIndex >= 0) {
                    handleNestedJurisdictionTypeSelect(type, jurIndex, countyName);
                  }
                }}
                onClearJurisdiction={(jurisdiction) => {
                  const jurIndex = certificateData.jurisdictions.findIndex(j => j.id === jurisdiction.id);
                  if (jurIndex >= 0) {
                    handleNestedClearJurisdiction(jurIndex);
                  }
                }}
                onNotesChange={handleAutoNotesChange}
                county={certificateData.county}
                readOnly={readOnly}
                getRowState={getRowState}
                onAcceptFuzzySuggestion={handleAcceptFuzzySuggestion}
                createEmptyJurisdiction={() => createEmptyJurisdiction()}
                saveValidationWarnings={saveValidationWarnings}
                orderFiles={orderFiles}
                workflowActions={workflowActions ? {
                  ...workflowActions,
                  // MLS-DUAL-001 Phase 2: parent Complete visibly disabled with
                  // the outstanding-component reason (backend 409 stays authoritative)
                  mlsGuard: mlsGuard,
                  // WF-CONSOLIDATE: dual-aware Complete — while the TAX
                  // component is open the button completes the component.
                  dualTax: isMlsDual ? {
                    phase: taxComponentStatus !== 'complete' ? 'component' : 'parent',
                    busy: taxComponentBusy,
                    onCompleteComponent: handleCompleteTaxComponent,
                    // TAX-QC-BYPASS-001: QC-required processors — QC happens at order level once both components are complete
                    qcRequiredNote: !!user?.requires_qc && !['admin', 'team_leader', 'tax_search_team_leader', 'title_search_team_leader'].includes(user?.role),
                  } : null,
                  // Jun 2026 — UI↔PDF notes parity: the Review Tax
                  // Certification modal renders the STORED draft
                  // (preview-pdf). Notes regenerated client-side after a
                  // notes-engine update are absorbed by the settle-window
                  // watcher (never marked dirty), so the stored draft can
                  // lag what's on screen. Flush a save before the modal
                  // opens so the PDF always matches the UI notes.
                  onReviewTaxSheet: async () => {
                    if (!readOnly) {
                      try {
                        await manualSave();
                      } catch (e) {
                        // Don't block the review on a failed flush — the
                        // modal will render the last persisted draft.
                        console.error('Pre-review draft flush failed:', e);
                      }
                    }
                    workflowActions.onReviewTaxSheet?.();
                  },
                  hasParcelAPN: !!(parcels?.[activeParcelIndex]?.parcel_apn),
                  parcelAPN: parcels?.[activeParcelIndex]?.parcel_apn || '',
                  totalAssessedValue: parcels?.[activeParcelIndex]?.assessed_total_value || '',
                  exemptionTypes: parcels?.[activeParcelIndex]?.exemption_types || certificateData.exemption_types || []
                } : null}
                orderId={orderData?.id}
                canEditContactFields={canEditJurisdictionContactFields}
                canLinkCollector={canLinkJurisdictionCollector}
                canEditMLCFields={canEditJurisdictionContactFields}
                isExternalPortalClient={hideTaxDetails}
                activeParcelIndex={activeParcelIndex}
                totalParcels={parcels.length}
                activeParcelAPN={parcels[activeParcelIndex]?.parcel_apn || ''}
                allParcelsData={parcels}
                canAccessParcel={canAccessParcel}
                canAddParcel={canAddParcel}
                isParcelDataComplete={isParcelDataComplete}
                getParcelMissingFields={getParcelMissingFields}
                onParcelSwitch={handleParcelSwitchFromJurisdiction}
                onAddParcelWithCopy={handleAddParcelWithCopy}
                parcelFieldValues={{
                  parcel_apns: certificateData.parcel_apns,
                  assessed_land_value: certificateData.assessed_land_value,
                  assessed_building_value: certificateData.assessed_building_value,
                  assessed_total_value: certificateData.assessed_total_value,
                  assessed_value_na: certificateData.assessed_value_na,
                  assessed_value_na_reason: certificateData.assessed_value_na_reason,
                  assessed_value_na_explanation: certificateData.assessed_value_na_explanation,
                  use_assessed_breakdown: certificateData.use_assessed_breakdown,
                }}
                onParcelFieldChange={handleCompactParcelFieldChange}
                parcelValidationErrors={validationErrors}
                onExemptionTypeChange={(pill, jurisdictionIndex) => {
                  // Per-jurisdiction exemption toggle
                  setCertificateData(prev => {
                    const juris = [...(prev.jurisdictions || [])];
                    if (!juris[jurisdictionIndex]) return prev;
                    const jur = { ...juris[jurisdictionIndex] };
                    const current = jur.exemption_types || [];
                    const NA = 'No Exemptions (N/A)';
                    let next;
                    if (pill.isNone) {
                      next = current.includes(NA) ? [] : [NA];
                    } else {
                      next = current.filter(v => v !== NA);
                      if (next.includes(pill.stored)) {
                        next = next.filter(v => v !== pill.stored);
                      } else {
                        next.push(pill.stored);
                      }
                    }
                    jur.exemption_types = next;
                    // Auto-set exemption_na flag
                    jur.exemption_na = next.includes(NA);
                    juris[jurisdictionIndex] = jur;
                    return { ...prev, jurisdictions: juris };
                  });
                }}
                onExemptionValueChange={(value, jurisdictionIndex) => {
                  setCertificateData(prev => {
                    const juris = [...(prev.jurisdictions || [])];
                    if (!juris[jurisdictionIndex]) return prev;
                    juris[jurisdictionIndex] = { ...juris[jurisdictionIndex], exemption_value: value };
                    return { ...prev, jurisdictions: juris };
                  });
                }}
                onExemptionOtherChange={(value, jurisdictionIndex) => {
                  setCertificateData(prev => {
                    const juris = [...(prev.jurisdictions || [])];
                    if (!juris[jurisdictionIndex]) return prev;
                    juris[jurisdictionIndex] = { ...juris[jurisdictionIndex], exemption_other: value };
                    return { ...prev, jurisdictions: juris };
                  });
                }}
                onFullyExemptChange={(checked, jurisdictionIndex) => {
                  setCertificateData(prev => {
                    const juris = [...(prev.jurisdictions || [])];
                    if (!juris[jurisdictionIndex]) return prev;
                    const jur = { ...juris[jurisdictionIndex], fully_exempt: checked };
                    // When toggling ON, clear "No Exemptions (N/A)" since an exemption type is now required
                    if (checked) {
                      const NA = 'No Exemptions (N/A)';
                      if ((jur.exemption_types || []).includes(NA)) {
                        jur.exemption_types = [];
                        jur.exemption_na = false;
                      }
                    }
                    juris[jurisdictionIndex] = jur;
                    return { ...prev, jurisdictions: juris };
                  });
                }}
                onTavBreakdownToggle={() => {
                  const newValue = certificateData.use_assessed_breakdown === false;
                  setCertificateData(prev => {
                    const landValue = prev.assessed_land_value || 0;
                    const buildingValue = prev.assessed_building_value || 0;
                    return {
                      ...prev,
                      use_assessed_breakdown: newValue,
                      ...(newValue === false && {
                        assessed_land_value: '',
                        assessed_building_value: '',
                        assessed_total_manual: true
                      }),
                      ...(newValue === true && {
                        assessed_total_value: landValue + buildingValue,
                        assessed_total_manual: false
                      })
                    };
                  });
                }}
                /* ── New Parcel (May 17, 2026) ── */
                isNewParcel={!!certificateData.is_new_parcel}
                newParcelFirstYear={certificateData.new_parcel_first_year || ''}
                newParcelFirstDueDate={certificateData.new_parcel_first_due_date || ''}
                onNewParcelToggle={() => {
                  // v7.3 (May 18, 2026) — write the toggle to BOTH
                  // certificateData AND parcels[active] in the same
                  // tick. The previous handler only mutated
                  // certificateData; any other state change (sync,
                  // re-render, autosave debounce) could read stale
                  // parcels[active].is_new_parcel = false and ship
                  // that to MongoDB even though the toggle visually
                  // showed ON. By mirroring into parcels[] right
                  // here we make parcels[] the single source of
                  // truth and eliminate the race.
                  //
                  // v7.5 (May 18, 2026) — when the user clicks the
                  // toggle while it is ON, do NOT immediately disable
                  // the workflow. Instead open the 3-action
                  // confirmation modal so the user explicitly chooses
                  // between "Revert & Clear" (wipe New Parcel-generated
                  // defaults) and "Keep Existing Values" (just flip the
                  // mode off, preserve typed-in rows). This prevents
                  // the silent mixed-state bug where a parcel renders
                  // with the standard workflow but New Parcel zeros and
                  // Refer Note rows still appear underneath.
                  const turningOnGlobal = !certificateData.is_new_parcel;
                  if (!turningOnGlobal) {
                    // Turning OFF — gate behind the confirmation modal.
                    setShowNewParcelOffConfirm(true);
                    return;
                  }
                  setCertificateData(prev => {
                    const turningOn = !prev.is_new_parcel;
                    // May 17, 2026 — when turning ON, flip every
                    // installment row's `Paid` / `Partially Paid`
                    // pay_status to `Refer Note` AND zero the financial
                    // amounts (base_tax, paid_amount, total_amount,
                    // balance_amount) so the row defaults line up with
                    // the spec. We never touch `Due` or `Delinquent`
                    // rows' pay_status — those communicate their own
                    // valid intent and shouldn't be overridden.
                    const rewriteJurisdictions = (jurs) => (jurs || []).map((jur) => ({
                      ...jur,
                      installments: (jur.installments || []).map((inst) => {
                        const ps = (inst.pay_status || '').toLowerCase();
                        const next = { ...inst };
                        if (turningOn && (ps === 'paid' || ps === 'partially paid')) {
                          next.pay_status = 'Refer Note';
                        }
                        if (turningOn) {
                          // Seed financial amounts to 0 so the row visually
                          // matches the New Parcel spec ($0.00 everything).
                          // Processors can still override later.
                          next.base_tax = 0;
                          next.paid_amount = 0;
                          next.total_amount = 0;
                          next.balance_amount = 0;
                        }
                        return next;
                      })
                    }));
                    return {
                      ...prev,
                      is_new_parcel: turningOn,
                      // Wipe overrides when turning off so a future
                      // toggle-on starts clean and falls back to the
                      // first-row values.
                      ...(turningOn ? {} : {
                        new_parcel_first_year: '',
                        new_parcel_first_due_date: ''
                      }),
                      jurisdictions: turningOn
                        ? rewriteJurisdictions(prev.jurisdictions)
                        : (prev.jurisdictions || [])
                    };
                  });
                  // ── v7.3 parcels[] mirror ───────────────────────
                  // Mirror the same change into parcels[active] so
                  // the array-of-record IS the source of truth.
                  setParcels(prevParcels => {
                    const next = [...prevParcels];
                    const idx = activeParcelIndex;
                    if (!next[idx]) return prevParcels;
                    const rewriteJurs = (jurs) => (jurs || []).map((jur) => ({
                      ...jur,
                      installments: (jur.installments || []).map((inst) => {
                        const ps = (inst.pay_status || '').toLowerCase();
                        const ninst = { ...inst };
                        if (turningOnGlobal && (ps === 'paid' || ps === 'partially paid')) {
                          ninst.pay_status = 'Refer Note';
                        }
                        if (turningOnGlobal) {
                          ninst.base_tax = 0;
                          ninst.paid_amount = 0;
                          ninst.total_amount = 0;
                          ninst.balance_amount = 0;
                        }
                        return ninst;
                      })
                    }));
                    next[idx] = {
                      ...next[idx],
                      is_new_parcel: turningOnGlobal,
                      ...(turningOnGlobal ? {} : {
                        new_parcel_first_year: '',
                        new_parcel_first_due_date: ''
                      }),
                      jurisdictions: turningOnGlobal
                        ? rewriteJurs(next[idx].jurisdictions)
                        : (next[idx].jurisdictions || [])
                    };
                    return next;
                  });
                  // Force dirty so the autosave debounce fires even
                  // if React batches and certificateData reference
                  // looks unchanged at the hook boundary.
                  setHasUnsavedChanges(true);
                }}
                onNewParcelOverrideChange={(field, value) => {
                  if (field !== 'new_parcel_first_year' && field !== 'new_parcel_first_due_date') return;
                  setCertificateData(prev => ({ ...prev, [field]: value }));
                  // v7.3 — also mirror override into parcels[active]
                  setParcels(prevParcels => {
                    const next = [...prevParcels];
                    const idx = activeParcelIndex;
                    if (!next[idx]) return prevParcels;
                    next[idx] = { ...next[idx], [field]: value };
                    return next;
                  });
                  setHasUnsavedChanges(true);
                }}
                onResetParcelDetails={() => {
                  // Clear parcel fields in BOTH certificateData (flat view) AND parcels[] (source of truth)
                  // to prevent syncParcelToFlat from restoring old values
                  //
                  // v7.7 (May 18, 2026) — Reset must also fully clear
                  // New Parcel mode so the jurisdiction returns to a
                  // clean standard state. Previously the Reset handler
                  // only wiped APN/assessed/exemption fields and left
                  // is_new_parcel / new_parcel_first_year /
                  // new_parcel_first_due_date / auto_generated_notes
                  // dangling — the green New Parcel banner stayed up
                  // and the PDF preview kept showing the BRAND NEW
                  // PARCEL sentence even after a full reset.
                  setCertificateData(prev => ({
                    ...prev,
                    parcel_apns: '',
                    assessed_land_value: '',
                    assessed_building_value: '',
                    assessed_total_value: '',
                    assessed_total_manual: false,
                    use_assessed_breakdown: true,
                    assessed_value_na: false,
                    assessed_value_na_reason: null,
                    assessed_value_na_explanation: '',
                    exemption_types: [],
                    exemption_value: '',
                    exemption_other: '',
                    exemption_na: false,
                    // v7.7: full New Parcel teardown on Reset
                    is_new_parcel: false,
                    new_parcel_first_year: '',
                    new_parcel_first_due_date: '',
                    auto_generated_notes: ''
                  }));
                  setParcels(prev => prev.map((p, i) =>
                    i === activeParcelIndex ? {
                      ...p,
                      parcel_apn: '',
                      assessed_land_value: '',
                      assessed_building_value: '',
                      assessed_total_value: '',
                      assessed_total_manual: false,
                      use_assessed_breakdown: true,
                      exemption_types: [],
                      exemption_value: '',
                      exemption_other: '',
                      exemption_na: false,
                      // v7.7: mirror full New Parcel teardown into
                      // parcels[active] so the v7.3 dual-state
                      // invariant holds and autosave ships the
                      // disabled state to MongoDB.
                      is_new_parcel: false,
                      new_parcel_first_year: '',
                      new_parcel_first_due_date: ''
                    } : p
                  ));
                  // Ensure the change is persisted without requiring
                  // a second user action.
                  setHasUnsavedChanges(true);
                }}
              />
            </div>
          ) : (
            /* ============================================================================
               LEGACY: RENDER FLAT CURRENT_TAX_SUMMARY STRUCTURE
               For existing orders that use the flat row structure
               ============================================================================ */
            <>
          {/* Card-based layout - no horizontal scroll */}
          <div className="space-y-4">
            {certificateData.current_tax_summary.map((row, index) => {
              const isExpanded = expandedJurisdictions.has(index);
              const validationErrorsList = getJurisdictionValidationErrors(row, index);
              const hasErrors = validationErrorsList.length > 0;
              
              // Check if this row is a child installment of a previous row
              const isChildInstallment = isChildInstallmentRow(certificateData.current_tax_summary, index);
              const parentIndex = isChildInstallment ? getParentJurisdictionIndex(certificateData.current_tax_summary, index) : -1;
              
              return (
              <div 
                key={index} 
                ref={el => jurisdictionCardRefs.current[index] = el}
                className={`border rounded-lg bg-white shadow-sm overflow-hidden transition-all duration-200 ${
                  hasErrors && !isExpanded ? 'border-red-300 border-2' : 'border-gray-200'
                } ${isChildInstallment ? 'ml-6 border-l-4 border-l-green-400' : ''}`}
                data-testid={`jurisdiction-card-${index}`}
              >
                
                {/* ============================================================================
                    ACCORDION HEADER - Clickable to expand/collapse
                    Shows jurisdiction summary when collapsed
                    For child installments, show simplified header
                    ============================================================================ */}
                <div 
                  className={`${isChildInstallment 
                    ? 'bg-gradient-to-r from-[#4a8b42] to-[#6aa862] px-4 py-2' 
                    : 'bg-gradient-to-r from-[#2d5a27] to-[#4a8b42] px-4 py-3'
                  } cursor-pointer select-none transition-all duration-200 ${
                    !isExpanded ? 'hover:from-[#3a6b32] hover:to-[#5a9b52] hover:shadow-md' : ''
                  }`}
                  onClick={() => toggleJurisdictionExpand(index)}
                  data-testid={`jurisdiction-header-${index}`}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      {/* Expand/Collapse Toggle */}
                      <button
                        type="button"
                        className="flex items-center justify-center w-6 h-6 rounded-full bg-white/20 hover:bg-white/30 transition-colors"
                        onClick={(e) => {
                          e.stopPropagation();
                          toggleJurisdictionExpand(index);
                        }}
                        aria-label={isExpanded ? 'Collapse jurisdiction' : 'Expand jurisdiction'}
                        data-testid={`jurisdiction-toggle-${index}`}
                      >
                        {isExpanded ? (
                          <ChevronUp className="w-4 h-4 text-white" />
                        ) : (
                          <ChevronDown className="w-4 h-4 text-white" />
                        )}
                      </button>
                      
                      <h4 className="text-base font-bold text-white">
                        {isChildInstallment ? (
                          <>
                            <span className="text-white/80 text-sm">↳</span> Installment #{index + 1}
                            {row.installment && (
                              <span className="font-semibold ml-2 text-white/95">— {row.installment}</span>
                            )}
                            <span className="text-xs text-white/70 ml-2">(same as #{parentIndex + 1})</span>
                          </>
                        ) : (
                          <>
                            Jurisdiction #{index + 1}
                            {row.jurisdiction && (
                              <span className="font-semibold ml-2 text-white/95">— {row.jurisdiction}</span>
                            )}
                          </>
                        )}
                      </h4>
                      
                      {/* Error indicator when collapsed */}
                      {hasErrors && !isExpanded && (
                        <div 
                          className="flex items-center gap-1 bg-red-500/90 px-2 py-0.5 rounded text-xs text-white"
                          title={`Required missing: ${validationErrorsList.join(', ')}`}
                        >
                          <AlertCircle className="w-3 h-3" />
                          <span>{validationErrorsList.length} required missing</span>
                        </div>
                      )}
                    </div>
                    
                    <div className="flex items-center gap-2" onClick={(e) => e.stopPropagation()}>
                      {/* Collapsed Summary Badges - Hidden for External Portal (tax-specific) */}
                      {!isExpanded && !hideTaxDetails && (
                        <div className="flex items-center gap-2 mr-2">
                          {row.tax_year && (
                            <span className="bg-white/20 px-2 py-0.5 rounded text-xs text-white font-medium" data-testid={`tax-year-badge-${row.id || ''}`}>
                              {formatTaxYear(row.tax_year)}
                            </span>
                          )}
                          {row.pay_status && (
                            <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                              row.pay_status === 'Paid' ? 'bg-green-500/80 text-white' :
                              row.pay_status === 'Due' ? 'bg-yellow-500/80 text-white' :
                              ['Delinquent', 'Partially Dlq'].includes(row.pay_status) ? 'bg-red-500/80 text-white' :
                              'bg-white/20 text-white'
                            }`}>
                              {row.pay_status}
                            </span>
                          )}
                        </div>
                      )}
                      
                      {/* Action Buttons - Always visible */}
                      <div className="flex items-center gap-1">
                        {index > 0 && (
                          <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            onClick={() => {
                              const newSummary = [...certificateData.current_tax_summary];
                              [newSummary[index], newSummary[index - 1]] = [newSummary[index - 1], newSummary[index]];
                              setCertificateData(prev => ({ ...prev, current_tax_summary: newSummary }));
                              // Update expanded state to follow the moved item
                              setExpandedJurisdictions(prev => {
                                const newSet = new Set(prev);
                                if (newSet.has(index)) {
                                  newSet.delete(index);
                                  newSet.add(index - 1);
                                }
                                if (newSet.has(index - 1) && !prev.has(index)) {
                                  newSet.delete(index - 1);
                                  newSet.add(index);
                                }
                                return newSet;
                              });
                            }}
                            className="h-7 w-7 p-0 text-white border-white/30 hover:bg-white/20"
                            title="Move Up"
                          >
                            ↑
                          </Button>
                        )}
                        {index < certificateData.current_tax_summary.length - 1 && (
                          <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            onClick={() => {
                              const newSummary = [...certificateData.current_tax_summary];
                              [newSummary[index], newSummary[index + 1]] = [newSummary[index + 1], newSummary[index]];
                              setCertificateData(prev => ({ ...prev, current_tax_summary: newSummary }));
                              // Update expanded state to follow the moved item
                              setExpandedJurisdictions(prev => {
                                const newSet = new Set(prev);
                                if (newSet.has(index)) {
                                  newSet.delete(index);
                                  newSet.add(index + 1);
                                }
                                if (newSet.has(index + 1) && !prev.has(index)) {
                                  newSet.delete(index + 1);
                                  newSet.add(index);
                                }
                                return newSet;
                              });
                            }}
                            className="h-7 w-7 p-0 text-white border-white/30 hover:bg-white/20"
                            title="Move Down"
                          >
                            ↓
                          </Button>
                        )}
                        {!readOnly && (
                          <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            onClick={() => removeTaxSummaryRow(index)}
                            className="h-7 w-7 p-0 text-white border-white/30 hover:bg-red-500/90"
                            title="Delete Jurisdiction"
                            disabled={certificateData.current_tax_summary.length <= 1}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        )}
                        {/* ADD INSTALLMENT BUTTON - Add another installment for this same jurisdiction */}
                        {/* Hidden for External Portal - installments are tax-summary specific */}
                        {!readOnly && !hideTaxDetails && row.jurisdiction_id && (
                          <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            onClick={(e) => {
                              e.stopPropagation();
                              // Create a new row inheriting jurisdiction-level data from this row
                              const newInstallmentRow = {
                                ...row,
                                // Reset installment-level fields
                                installment: '',
                                mailing_issue_date: '',
                                due_date: '',
                                base_tax: null,
                                amounts_fees: null,
                                pay_status: 'Due',
                                paid_date: '',
                                balance_amount: null,
                                dlq_date: ''
                              };
                              // Insert after current row
                              const newSummary = [...certificateData.current_tax_summary];
                              newSummary.splice(index + 1, 0, newInstallmentRow);
                              setCertificateData(prev => ({ ...prev, current_tax_summary: newSummary }));
                              toast.info(`Added new installment for ${row.jurisdiction || 'jurisdiction'}`);
                            }}
                            className="h-7 px-2 text-xs text-white border-white/30 hover:bg-green-600/90"
                            title="Add Installment for this Jurisdiction"
                          >
                            <Plus className="w-3 h-3 mr-1" />
                            Installment
                          </Button>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
                
                {/* ============================================================================
                    COLLAPSED SUMMARY BAR - Shows key info when section is collapsed
                    ============================================================================ */}
                {!isExpanded && (
                  <div 
                    className="px-4 py-3 bg-gray-50 border-b border-gray-200 cursor-pointer hover:bg-gray-100 transition-colors"
                    onClick={() => toggleJurisdictionExpand(index)}
                    data-testid={`jurisdiction-summary-${index}`}
                  >
                    <div className={`grid ${hideTaxDetails ? 'grid-cols-2 md:grid-cols-2' : 'grid-cols-2 md:grid-cols-5'} gap-4 text-sm`}>
                      <div>
                        <span className="text-gray-500 text-xs">Type:</span>
                        <p className="font-medium text-gray-900 truncate">
                          {row.jurisdiction_type || <span className="text-gray-400 italic">Not set</span>}
                        </p>
                      </div>
                      {!hideTaxDetails && (
                      <div>
                        <span className="text-gray-500 text-xs">Tax Year:</span>
                        <p className="font-medium text-gray-900" data-testid={`tax-year-display-${row.id || ''}`}>
                          {row.tax_year ? formatTaxYear(row.tax_year) : <span className="text-gray-400 italic">Not set</span>}
                        </p>
                      </div>
                      )}
                      {!hideTaxDetails && (
                      <div>
                        <span className="text-gray-500 text-xs">Installment:</span>
                        <p className="font-medium text-gray-900">
                          {row.installment || <span className="text-gray-400 italic">Not set</span>}
                        </p>
                      </div>
                      )}
                      {!hideTaxDetails && (
                      <div>
                        <span className="text-gray-500 text-xs">Base Tax:</span>
                        <p className="font-medium text-gray-900">
                          {row.base_tax != null && row.base_tax !== '' 
                            ? `$${parseFloat(row.base_tax || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` 
                            : <span className="text-gray-400 italic">Not set</span>
                          }
                        </p>
                      </div>
                      )}
                      <div>
                        <span className="text-gray-500 text-xs">Source:</span>
                        <p className={`font-medium truncate ${row.source ? 'text-gray-900' : 'text-amber-600'}`}>
                          {row.source ? (
                            row.source === 'calling' ? '📞 Calling' :
                            row.source === 'online' ? '🌐 Online' :
                            row.source === 'calling_online' ? '📞🌐 Both' :
                            row.source === 'email' ? '📧 Email' :
                            row.source === 'fax' ? '📠 Fax' :
                            row.source === 'mail' ? '📬 Mail' :
                            row.source === 'other' ? '📎 Other' :
                            row.source
                          ) : <span className="italic">⚠️ Required</span>}
                        </p>
                      </div>
                    </div>
                    <div className="mt-2 text-xs text-gray-500 flex items-center gap-1">
                      <ChevronDown className="w-3 h-3" />
                      Click to expand and edit
                    </div>
                  </div>
                )}
                
                {/* ============================================================================
                    EXPANDABLE CONTENT - All sections hidden when collapsed
                    ============================================================================ */}
                {isExpanded && (
                  <>
                {/* FOR CHILD INSTALLMENTS: Show only installment-level fields */}
                {/* Hidden for External Portal / MLS - child installments are tax-summary specific */}
                {!hideTaxDetails && isChildInstallment ? (
                  <div className="p-4 bg-green-50/30">
                    <div className="mb-3 p-2 bg-blue-50 border border-blue-200 rounded text-sm text-blue-800">
                      <strong>Inherited from Jurisdiction #{parentIndex + 1}:</strong> {row.jurisdiction || 'N/A'} ({row.jurisdiction_type || 'N/A'})
                      <br />
                      <span className="text-xs text-blue-600">Jurisdiction-level fields are shared. Only installment-level fields shown below.</span>
                    </div>
                    
                    {/* INSTALLMENT-LEVEL FIELDS ONLY */}
                    <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3">
                      {/* Installment Name */}
                      <div>
                        <Label className="text-sm font-medium text-gray-700 mb-1">Installment *</Label>
                        <select
                          value={row.installment || ''}
                          onChange={(e) => handleArrayChange('current_tax_summary', index, 'installment', e.target.value)}
                          className="w-full border rounded px-3 py-2 text-sm focus:ring-2 focus:ring-green-500"
                          disabled={readOnly}
                        >
                          <option value="">Select...</option>
                          <option value="Annual">Annual</option>
                          <option value="1st Half">1st Half</option>
                          <option value="2nd Half">2nd Half</option>
                          <option value="Q1">Q1</option>
                          <option value="Q2">Q2</option>
                          <option value="Q3">Q3</option>
                          <option value="Q4">Q4</option>
                          <option value="1st">1st</option>
                          <option value="2nd">2nd</option>
                          <option value="3rd">3rd</option>
                          <option value="4th">4th</option>
                        </select>
                      </div>
                      
                      {/* Due Date */}
                      <div>
                        <Label className="text-sm font-medium text-gray-700 mb-1">Due Date</Label>
                        <DatePickerInput
                          value={row.due_date || ''}
                          onChange={(val) => handleArrayChange('current_tax_summary', index, 'due_date', val)}
                          placeholder="MM/DD/YYYY"
                          disabled={readOnly}
                        />
                      </div>
                      
                      {/* Base Tax */}
                      <div>
                        <Label className="text-sm font-medium text-gray-700 mb-1">Base Tax</Label>
                        <Input
                          value={row.base_tax || ''}
                          onChange={(e) => handleArrayChange('current_tax_summary', index, 'base_tax', e.target.value)}
                          placeholder="$0.00"
                          disabled={readOnly}
                        />
                      </div>
                      
                      {/* Amounts/Fees */}
                      <div>
                        <Label className="text-sm font-medium text-gray-700 mb-1">Disc/Pen</Label>
                        <Input
                          value={row.amounts_fees || ''}
                          onChange={(e) => handleArrayChange('current_tax_summary', index, 'amounts_fees', e.target.value)}
                          placeholder="$0.00"
                          disabled={readOnly}
                        />
                      </div>
                      
                      {/* Pay Status */}
                      <div>
                        <Label className="text-sm font-medium text-gray-700 mb-1">Pay Status *</Label>
                        <select
                          value={row.pay_status || 'Due'}
                          onChange={(e) => handleArrayChange('current_tax_summary', index, 'pay_status', e.target.value)}
                          className="w-full border rounded px-3 py-2 text-sm focus:ring-2 focus:ring-green-500"
                          disabled={readOnly}
                        >
                          <option value="Paid">Paid</option>
                          <option value="Due">Due</option>
                          <option value="Partially Paid">Partially Paid</option>
                          <option value="Partially Dlq">Partially Dlq</option>
                          <option value="Delinquent">Delinquent</option>
                        </select>
                      </div>
                      
                      {/* Paid Date */}
                      <div>
                        <Label className="text-sm font-medium text-gray-700 mb-1">Paid Date</Label>
                        <DatePickerInput
                          value={row.paid_date || ''}
                          onChange={(val) => handleArrayChange('current_tax_summary', index, 'paid_date', val)}
                          placeholder="MM/DD/YYYY"
                          disabled={readOnly || row.pay_status !== 'Paid'}
                        />
                      </div>
                      
                      {/* Balance Amount */}
                      <div>
                        <Label className="text-sm font-medium text-gray-700 mb-1">Balance</Label>
                        <Input
                          value={row.balance_amount || ''}
                          onChange={(e) => handleArrayChange('current_tax_summary', index, 'balance_amount', e.target.value)}
                          placeholder="$0.00"
                          disabled={readOnly}
                        />
                      </div>
                    </div>
                  </div>
                ) : (
                  <>
                {/* FULL JURISDICTION VIEW - Taxing Authority / Remittance Body */}
                <div className="p-4 bg-green-50/30 border-b border-gray-200">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    {/* Jurisdiction Type Dropdown - PRIMARY SELECTOR - EDITABLE for all processing roles */}
                    <div className="md:col-span-2">
                      <Label htmlFor={`jurisdiction_type_${index}`} className="text-sm font-medium text-gray-700 mb-1">
                        Jurisdiction Type <span className="text-red-500">*</span>
                      </Label>
                      <select
                        id={`jurisdiction_type_${index}`}
                        value={row.jurisdiction_type || ''}
                        onChange={(e) => {
                            const selectedType = e.target.value === '' ? null : e.target.value;
                            
                            if (!selectedType) {
                              // Clear jurisdiction type AND all related auto-populated fields
                              console.log('[JURISDICTION] Type cleared - resetting all related fields for row', index);
                              // Clear row-specific UI state
                              setRowState(index, {
                                showSubJurisdictionSelector: false,
                                subJurisdictions: [],
                                jurisdictionMatchError: null,
                                selectedCountyForSubJurisdiction: null
                              });
                              setCertificateData(prev => {
                                const isMultiJurisdiction = prev.current_tax_summary?.length > 1;
                                const updatedData = {
                                  ...prev,
                                  current_tax_summary: prev.current_tax_summary.map((item, i) => {
                                    if (i === index) {
                                      return { 
                                        ...item, 
                                        jurisdiction_type: null,
                                        jurisdiction: '',
                                        jurisdiction_id: null,
                                        make_checks_payable_to: '',
                                        tax_collector_email: '',
                                        tax_collector_website: '',
                                        assessor_website: '',
                                        tax_collector_phone: '',
                                        tax_collector_phone_ext: '',
                                        tax_collector_working_hours: '',
                                        mlc_charges: null,
                                        mlc_24_month_charges: null,
                                        mlc_eta_days: null,
                                        sase_required: false,
                                        info_obtained_back: null,
                                        changed_by_user: false
                                      };
                                    }
                                    return item;
                                  })
                                };
                                // Only clear global state for single-jurisdiction or row 0
                                if (!isMultiJurisdiction || index === 0) {
                                  updatedData.jurisdiction_type = null;
                                  updatedData.jurisdiction_id = null;
                                  updatedData.make_checks_payable_to = '';
                                  updatedData.mlc_charges = null;
                                  updatedData.mlc_24_month_charges = null;
                                  updatedData.mlc_eta_days = null;
                                  updatedData.sase_required = false;
                                  updatedData.info_obtained_back = null;
                                  updatedData.tax_collector_email = '';
                                  updatedData.tax_collector_website = '';
                                  updatedData.assessor_website = '';
                                  updatedData.tax_collector_phone = '';
                                  updatedData.tax_collector_phone_ext = '';
                                  updatedData.tax_collector_working_hours = '';
                                }
                                return updatedData;
                              });
                              toast.info('Jurisdiction type cleared. All related fields have been reset.');
                              return;
                            }
                            
                            // PHASE 5: Delegate to main handler which has sub-jurisdiction selector logic
                            handleJurisdictionTypeSelect(selectedType, index);
                          }}
                        className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-green-500 focus:border-green-500"
                        disabled={readOnly || jurisdictionsLoading}
                      >
                        <option value="">-- Select Jurisdiction Type --</option>
                        {/* Feb 2026 — Bug fix. Replaced the hardcoded 5-item
                            short list with the canonical 9-item taxonomy
                            from lib/jurisdictionTaxonomy.js so Admin / Tax
                            Team Leader / Tax Processor all see identical
                            options. */}
                        {JX_TYPE_OPTIONS.map((opt) => (
                          <option key={opt.value} value={opt.value}>{opt.label}</option>
                        ))}
                        {(() => {
                          const _saved = getRowState(index)?.jurisdiction_type;
                          if (_saved && !jxIsCanonicalType(_saved)) {
                            return (
                              <option value={_saved} disabled>
                                {jxTypeLabel(_saved)}
                              </option>
                            );
                          }
                          return null;
                        })()}
                      </select>
                      
                      {/* Row-specific jurisdiction match error */}
                      {getRowState(index).jurisdictionMatchError && (
                        <div className="mt-2 p-2 bg-amber-50 border border-amber-200 rounded-md">
                          <p className="text-sm text-amber-800">{getRowState(index).jurisdictionMatchError}</p>
                          {(getRowState(index).fuzzySuggestions || []).length > 0 && (
                            <div className="mt-2 flex flex-wrap gap-2" data-testid={`fuzzy-suggestions-${index}`}>
                              {(getRowState(index).fuzzySuggestions || []).map((s) => (
                                <button
                                  key={s.id}
                                  type="button"
                                  onClick={() => handleAcceptFuzzySuggestion(
                                    s,
                                    getRowState(index).fuzzyQueryContext || { rowIndex: index }
                                  )}
                                  className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium bg-white border border-amber-300 text-amber-900 hover:bg-amber-100 hover:border-amber-400 transition-colors"
                                  data-testid={`fuzzy-suggestion-chip-${index}-${s.id}`}
                                >
                                  <span>{s.jurisdiction_name}</span>
                                  <span className="text-amber-600 font-mono text-[10px]">
                                    {Math.round((s.similarity || 0) * 100)}%
                                  </span>
                                </button>
                              ))}
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                    
                    {/* ========== SUB-JURISDICTION SELECTOR - Shows when Township/City/Village/School/Special District is selected ========== */}
                    {/* Uses row-specific state for complete isolation */}
                    {getRowState(index).showSubJurisdictionSelector && 
                      row.jurisdiction_type && 
                      SUB_JURISDICTION_TYPES.includes(row.jurisdiction_type.toLowerCase())
                    && (
                      <div className="md:col-span-2 animate-in slide-in-from-top-2 duration-300">
                        <Label htmlFor={`sub_jurisdiction_${index}`} className="flex items-center gap-1 text-sm font-medium text-gray-700 mb-1">
                          <span>Jurisdiction Name</span>
                          <span className="text-red-500">*</span>
                          {getRowState(index).selectedCountyForSubJurisdiction && (
                            <span className="text-xs text-gray-500 ml-2">
                              (Under {getRowState(index).selectedCountyForSubJurisdiction} County)
                            </span>
                          )}
                        </Label>
                        <select
                          id={`sub_jurisdiction_${index}`}
                          value={row.jurisdiction_id || ''}
                          onChange={(e) => {
                            const jurisdictionId = e.target.value;
                            if (jurisdictionId) {
                              handleSubJurisdictionSelect(jurisdictionId, index);
                            } else {
                              // Clear jurisdiction name/ID and contact fields
                              setCertificateData(prev => ({
                                ...prev,
                                current_tax_summary: prev.current_tax_summary.map((item, i) =>
                                  i === index ? {
                                    ...item,
                                    jurisdiction_id: null,
                                    jurisdiction: '',
                                    make_checks_payable_to: '',
                                    tax_collector_phone: '',
                                    tax_collector_phone_ext: '',
                                    tax_collector_email: '',
                                    tax_collector_website: '',
                                    assessor_website: '',
                                    tax_collector_working_hours: '',
                                    mlc_charges: null,
                                    mlc_24_month_charges: null,
                                    mlc_eta_days: null,
                                    sase_required: false,
                                    info_obtained_back: null
                                  } : item
                                )
                              }));
                            }
                          }}
                          className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-green-500 focus:border-green-500"
                          disabled={readOnly || getRowState(index).subJurisdictionsLoading}
                        >
                          <option value="">
                            {getRowState(index).subJurisdictionsLoading ? 'Loading jurisdictions...' : 
                             getRowState(index).subJurisdictions.length === 0 ? '-- No jurisdictions found --' :
                             '-- Select Jurisdiction Name --'}
                          </option>
                          {getRowState(index).subJurisdictions.map((j) => (
                            <option key={j.id} value={j.id}>
                              {toTitleCase(j.jurisdiction_name || j.name)}
                              {j.jurisdiction_type ? ` (${j.jurisdiction_type.charAt(0).toUpperCase() + j.jurisdiction_type.slice(1).replace('_', ' ')}` : ''}
                              {j.state ? `, ${j.state.toUpperCase()})` : j.jurisdiction_type ? ')' : ''}
                            </option>
                          ))}
                        </select>
                        <p className="text-xs text-gray-500 mt-1">
                          {getRowState(index).subJurisdictionsLoading ? 'Loading available jurisdictions...' :
                           getRowState(index).subJurisdictions.length === 0 ? (
                             <span className="text-amber-600">
                               No {row.jurisdiction_type} jurisdictions under this county.
                             </span>
                           ) : (
                             `${getRowState(index).subJurisdictions.length} jurisdiction(s) available. Select one to populate remittance details.`
                           )}
                        </p>
                    </div>
                    )}
                    
                    {/* ========== SOURCE EVIDENCE FIELD - Per Jurisdiction Row ========== */}
                    {/* Mandatory field for audit/QC compliance - validates evidence attachments */}
                    <div className="md:col-span-2 mt-3">
                      <Label htmlFor={`source_${index}`} className="text-sm font-medium text-gray-700 mb-1 flex items-center gap-2">
                        Information Source <span className="text-red-500">*</span>
                        <span className="text-xs text-gray-500 font-normal">(Required for completion)</span>
                      </Label>
                      <select
                        id={`source_${index}`}
                        data-testid={`source-select-${index}`}
                        value={row.source || ''}
                        onChange={(e) => {
                          handleArrayChange('current_tax_summary', index, 'source', e.target.value || null);
                        }}
                        className={`w-full p-2 border rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                          !row.source ? 'border-amber-300 bg-amber-50/50' : 'border-gray-300'
                        }`}
                        disabled={readOnly}
                      >
                        <option value="">-- Select Source --</option>
                        <option value="calling">Calling</option>
                        <option value="online">Online</option>
                        <option value="calling_online">Calling / Online</option>
                        <option value="email">Email</option>
                        <option value="fax">Fax</option>
                        <option value="mail">Mail</option>
                        <option value="other">Other</option>
                      </select>
                      
                      {/* Source-specific evidence guidance */}
                      {row.source && (
                        <div className={`mt-2 p-3 rounded-md text-sm ${
                          row.source === 'calling' ? 'bg-purple-50 border border-purple-200' :
                          row.source === 'online' ? 'bg-blue-50 border border-blue-200' :
                          row.source === 'calling_online' ? 'bg-indigo-50 border border-indigo-200' :
                          row.source === 'email' ? 'bg-cyan-50 border border-cyan-200' :
                          row.source === 'fax' ? 'bg-orange-50 border border-orange-200' :
                          row.source === 'mail' ? 'bg-green-50 border border-green-200' :
                          'bg-gray-50 border border-gray-200'
                        }`}>
                          {/* ========== PHASE 2: SMART EVIDENCE PROMPT ========== */}
                          {row.source === 'calling' && (
                            <div className="space-y-2">
                              <p className="text-purple-800">
                                📞 <strong>Call verification selected.</strong> Upload call recording evidence.
                              </p>
                              <Button
                                type="button"
                                variant="outline"
                                size="sm"
                                className="bg-white hover:bg-purple-100 border-purple-300 text-purple-700"
                                onClick={() => {
                                  // Navigate to Files tab with pre-selected category
                                  window.dispatchEvent(new CustomEvent('openFilesTab', { 
                                    detail: { category: 'audio', jurisdictionRow: index }
                                  }));
                                }}
                                disabled={readOnly}
                                data-testid={`upload-call-recording-${index}`}
                              >
                                <Upload className="w-4 h-4 mr-2" />
                                Upload Call Recording
                              </Button>
                              <p className="text-xs text-purple-600">Accepts: MP3, WAV, M4A (max 50MB)</p>
                            </div>
                          )}
                          {row.source === 'online' && (
                            <div className="space-y-2">
                              <p className="text-blue-800">
                                🌐 <strong>Online source selected.</strong> Upload screenshot or document.
                              </p>
                              <Button
                                type="button"
                                variant="outline"
                                size="sm"
                                className="bg-white hover:bg-blue-100 border-blue-300 text-blue-700"
                                onClick={() => {
                                  window.dispatchEvent(new CustomEvent('openFilesTab', { 
                                    detail: { category: 'image', jurisdictionRow: index }
                                  }));
                                }}
                                disabled={readOnly}
                                data-testid={`upload-source-document-${index}`}
                              >
                                <Upload className="w-4 h-4 mr-2" />
                                Upload Source Document
                              </Button>
                              <p className="text-xs text-blue-600">Accepts: PDF, PNG, JPG (max 15MB)</p>
                            </div>
                          )}
                          {row.source === 'calling_online' && (
                            <div className="space-y-2">
                              <p className="text-indigo-800">
                                📞🌐 <strong>Calling/Online verification selected.</strong> Upload <strong>both</strong> evidence types.
                              </p>
                              <div className="flex flex-wrap gap-2">
                                <Button
                                  type="button"
                                  variant="outline"
                                  size="sm"
                                  className="bg-white hover:bg-purple-100 border-purple-300 text-purple-700"
                                  onClick={() => {
                                    window.dispatchEvent(new CustomEvent('openFilesTab', { 
                                      detail: { category: 'audio', jurisdictionRow: index }
                                    }));
                                  }}
                                  disabled={readOnly}
                                  data-testid={`upload-call-recording-both-${index}`}
                                >
                                  <Upload className="w-4 h-4 mr-2" />
                                  Upload Call Recording
                                </Button>
                                <Button
                                  type="button"
                                  variant="outline"
                                  size="sm"
                                  className="bg-white hover:bg-blue-100 border-blue-300 text-blue-700"
                                  onClick={() => {
                                    window.dispatchEvent(new CustomEvent('openFilesTab', { 
                                      detail: { category: 'image', jurisdictionRow: index }
                                    }));
                                  }}
                                  disabled={readOnly}
                                  data-testid={`upload-source-document-both-${index}`}
                                >
                                  <Upload className="w-4 h-4 mr-2" />
                                  Upload Source Document
                                </Button>
                              </div>
                              <p className="text-xs text-indigo-600">Both files required for completion</p>
                            </div>
                          )}
                          {row.source === 'email' && (
                            <div className="space-y-2">
                              <p className="text-cyan-800">
                                📧 <strong>Email source selected.</strong> Upload email evidence.
                              </p>
                              <Button
                                type="button"
                                variant="outline"
                                size="sm"
                                className="bg-white hover:bg-cyan-100 border-cyan-300 text-cyan-700"
                                onClick={() => {
                                  window.dispatchEvent(new CustomEvent('openFilesTab', { 
                                    detail: { category: 'email_evidence', jurisdictionRow: index }
                                  }));
                                }}
                                disabled={readOnly}
                                data-testid={`upload-email-evidence-${index}`}
                              >
                                <Upload className="w-4 h-4 mr-2" />
                                Upload Email Evidence
                              </Button>
                              <p className="text-xs text-cyan-600">Accepts: PDF, PNG, JPG, EML (max 15MB)</p>
                            </div>
                          )}
                          {row.source === 'fax' && (
                            <div className="space-y-2">
                              <p className="text-orange-800">
                                📠 <strong>Fax source selected.</strong> Upload fax evidence.
                              </p>
                              <Button
                                type="button"
                                variant="outline"
                                size="sm"
                                className="bg-white hover:bg-orange-100 border-orange-300 text-orange-700"
                                onClick={() => {
                                  window.dispatchEvent(new CustomEvent('openFilesTab', { 
                                    detail: { category: 'fax_evidence', jurisdictionRow: index }
                                  }));
                                }}
                                disabled={readOnly}
                                data-testid={`upload-fax-evidence-${index}`}
                              >
                                <Upload className="w-4 h-4 mr-2" />
                                Upload Fax Evidence
                              </Button>
                              <p className="text-xs text-orange-600">Accepts: PDF, PNG, JPG, TIFF (max 15MB)</p>
                            </div>
                          )}
                          {row.source === 'mail' && (
                            <div className="space-y-2">
                              <p className="text-green-800">
                                📬 <strong>Mail source selected.</strong> Upload mail evidence.
                              </p>
                              <Button
                                type="button"
                                variant="outline"
                                size="sm"
                                className="bg-white hover:bg-green-100 border-green-300 text-green-700"
                                onClick={() => {
                                  window.dispatchEvent(new CustomEvent('openFilesTab', { 
                                    detail: { category: 'mail_evidence', jurisdictionRow: index }
                                  }));
                                }}
                                disabled={readOnly}
                                data-testid={`upload-mail-evidence-${index}`}
                              >
                                <Upload className="w-4 h-4 mr-2" />
                                Upload Mail Evidence
                              </Button>
                              <p className="text-xs text-green-600">Accepts: PDF, PNG, JPG (max 15MB)</p>
                            </div>
                          )}
                          {row.source === 'other' && (
                            <div className="space-y-2">
                              <p className="text-gray-800">
                                📎 <strong>Other source selected.</strong> Upload supporting documentation.
                              </p>
                              <Button
                                type="button"
                                variant="outline"
                                size="sm"
                                className="bg-white hover:bg-gray-100 border-gray-300 text-gray-700"
                                onClick={() => {
                                  window.dispatchEvent(new CustomEvent('openFilesTab', { 
                                    detail: { category: 'supporting_document', jurisdictionRow: index }
                                  }));
                                }}
                                disabled={readOnly}
                                data-testid={`upload-other-evidence-${index}`}
                              >
                                <Upload className="w-4 h-4 mr-2" />
                                Upload Document
                              </Button>
                            </div>
                          )}
                        </div>
                      )}
                      
                      {!row.source && (
                        <p className="text-xs text-amber-600 mt-1">
                          ⚠️ Source selection is required before completing the order
                        </p>
                      )}
                    </div>
                    
                    {/* ========== CONDITIONAL FIELDS - Only show after Jurisdiction Type is selected ========== */}
                    {row.jurisdiction_type ? (
                      <>
                        {/* Make Checks Payable To - RESTRICTED for Tax Processor/Tax Team Leader */}
                        <div className="md:col-span-2 animate-in slide-in-from-top-2 duration-300">
                          <Label htmlFor={`make_checks_payable_to_${index}`} className="text-sm font-medium text-gray-700 mb-1">
                            Make Checks Payable To <span className="text-red-500">*</span>
                          </Label>
                          <textarea
                            id={`make_checks_payable_to_${index}`}
                            value={row.make_checks_payable_to || ''}
                            onChange={(e) => {
                              if (!jurisdictionContactFieldsReadOnly) {
                                handleArrayChange('current_tax_summary', index, 'make_checks_payable_to', e.target.value);
                                if (!row.changed_by_user) {
                                  handleArrayChange('current_tax_summary', index, 'changed_by_user', true);
                                }
                              }
                            }}
                            className={`w-full p-2 border rounded-md resize-none min-h-[5rem] ${
                              validationErrors[`tax_summary_${index}_make_checks_payable_to`] ? 'border-red-500' : 'border-gray-300'
                            } ${jurisdictionContactFieldsReadOnly ? 'bg-white cursor-default' : ''}`}
                            rows={4}
                            placeholder="Tax Collector Name&#10;Address Line&#10;City, State ZIP&#10;PHONE: (XXX) XXX-XXXX"
                            readOnly={jurisdictionContactFieldsReadOnly}
                          />
                          {validationErrors[`tax_summary_${index}_make_checks_payable_to`] && (
                            <p className="text-red-500 text-xs mt-1">{validationErrors[`tax_summary_${index}_make_checks_payable_to`]}</p>
                          )}
                        </div>
                        
                        {/* Tax Collector Email & Website - RESTRICTED for Tax Processor/Tax Team Leader */}
                        <div className="animate-in slide-in-from-top-2 duration-300 delay-75">
                          <Label htmlFor={`tax_collector_email_${index}`} className="text-sm font-medium text-gray-700 mb-1">
                            Tax Collector Email
                          </Label>
                          <Input
                            id={`tax_collector_email_${index}`}
                            type="email"
                            value={row.tax_collector_email || ''}
                            onChange={(e) => !jurisdictionContactFieldsReadOnly && handleArrayChange('current_tax_summary', index, 'tax_collector_email', e.target.value)}
                            className={`w-full ${jurisdictionContactFieldsReadOnly ? 'bg-white cursor-default' : ''}`}
                            placeholder="email@example.com"
                            readOnly={jurisdictionContactFieldsReadOnly}
                          />
                        </div>
                        <div className="animate-in slide-in-from-top-2 duration-300 delay-75">
                          <Label htmlFor={`tax_collector_website_${index}`} className="text-sm font-medium text-gray-700 mb-1">
                            Tax Collector Website
                          </Label>
                          <Input
                            id={`tax_collector_website_${index}`}
                            type="url"
                            value={row.tax_collector_website || ''}
                            onChange={(e) => !jurisdictionContactFieldsReadOnly && handleArrayChange('current_tax_summary', index, 'tax_collector_website', e.target.value)}
                            className={`w-full ${jurisdictionContactFieldsReadOnly ? 'bg-white cursor-default' : ''}`}
                            placeholder="https://..."
                            readOnly={jurisdictionContactFieldsReadOnly}
                          />
                        </div>
                        
                        {/* Assessor Website - RESTRICTED for Tax Processor/Tax Team Leader */}
                        <div className="md:col-span-2 animate-in slide-in-from-top-2 duration-300 delay-100">
                          <Label htmlFor={`assessor_website_${index}`} className="text-sm font-medium text-gray-700 mb-1">
                            Assessor Website
                          </Label>
                          <Input
                            id={`assessor_website_${index}`}
                            type="url"
                            value={row.assessor_website || ''}
                            onChange={(e) => !jurisdictionContactFieldsReadOnly && handleArrayChange('current_tax_summary', index, 'assessor_website', e.target.value)}
                            className={`w-full ${jurisdictionContactFieldsReadOnly ? 'bg-white cursor-default' : ''}`}
                            placeholder="https://..."
                            readOnly={jurisdictionContactFieldsReadOnly}
                          />
                        </div>
                        
                        {/* Phone Number & Extension - RESTRICTED for Tax Processor/Tax Team Leader */}
                        <div className="animate-in slide-in-from-top-2 duration-300 delay-150">
                          <Label htmlFor={`tax_collector_phone_${index}`} className="text-sm font-medium text-gray-700 mb-1">
                            Tax Collector Phone
                          </Label>
                          <Input
                            id={`tax_collector_phone_${index}`}
                            type="tel"
                            value={row.tax_collector_phone || ''}
                            onChange={(e) => !jurisdictionContactFieldsReadOnly && handleArrayChange('current_tax_summary', index, 'tax_collector_phone', e.target.value)}
                            className={`w-full ${jurisdictionContactFieldsReadOnly ? 'bg-white cursor-default' : ''}`}
                            placeholder="(555) 555-5555"
                            readOnly={jurisdictionContactFieldsReadOnly}
                          />
                        </div>
                        <div className="animate-in slide-in-from-top-2 duration-300 delay-150">
                          <Label htmlFor={`tax_collector_phone_ext_${index}`} className="text-sm font-medium text-gray-700 mb-1">
                            Phone Extension
                          </Label>
                          <Input
                            id={`tax_collector_phone_ext_${index}`}
                            type="text"
                            value={row.tax_collector_phone_ext || ''}
                            onChange={(e) => !jurisdictionContactFieldsReadOnly && handleArrayChange('current_tax_summary', index, 'tax_collector_phone_ext', e.target.value)}
                            className={`w-full ${jurisdictionContactFieldsReadOnly ? 'bg-white cursor-default' : ''}`}
                            placeholder="Ext"
                            readOnly={jurisdictionContactFieldsReadOnly}
                          />
                        </div>
                        
                        {/* Working Hours - RESTRICTED for Tax Processor/Tax Team Leader */}
                        <div className="md:col-span-2 animate-in slide-in-from-top-2 duration-300 delay-200">
                          <Label htmlFor={`tax_collector_working_hours_${index}`} className="text-sm font-medium text-gray-700 mb-1">
                            Working Hours
                          </Label>
                          <Input
                            id={`tax_collector_working_hours_${index}`}
                            type="text"
                            value={row.tax_collector_working_hours || ''}
                            onChange={(e) => !jurisdictionContactFieldsReadOnly && handleArrayChange('current_tax_summary', index, 'tax_collector_working_hours', e.target.value)}
                            className={`w-full ${jurisdictionContactFieldsReadOnly ? 'bg-white cursor-default' : ''}`}
                            placeholder="Mon-Fri 9AM-5PM"
                            readOnly={jurisdictionContactFieldsReadOnly}
                          />
                        </div>
                        
                        {/* MLC & Workflow Information Section - READ-ONLY display from jurisdiction data */}
                        <div className="md:col-span-2 mt-4 animate-in slide-in-from-top-2 duration-300 delay-250">
                          <div className="bg-gradient-to-r from-[#4c2aff] to-[#784bff] text-white font-semibold text-sm px-3 py-1.5 rounded-md shadow-sm mb-3">
                            MLC & Workflow Information
                          </div>
                          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 p-3 bg-gray-50 rounded-lg border border-gray-200">
                            {/* MLC Charges */}
                            <div>
                              <Label className="text-xs text-gray-600 mb-1">MLC Charges ($)</Label>
                              <div className="h-8 px-2 border rounded-md bg-white flex items-center">
                                <span className="text-sm text-gray-700">
                                  {row.mlc_charges ? `$${parseFloat(row.mlc_charges).toFixed(2)}` : '-'}
                                </span>
                              </div>
                            </div>
                            {/* 24-Month MLC Charges */}
                            <div>
                              <Label className="text-xs text-gray-600 mb-1">24-Month MLC ($)</Label>
                              <div className="h-8 px-2 border rounded-md bg-white flex items-center">
                                <span className="text-sm text-gray-700">
                                  {row.mlc_24_month_charges ? `$${parseFloat(row.mlc_24_month_charges).toFixed(2)}` : '-'}
                                </span>
                              </div>
                            </div>
                            {/* MLC ETA */}
                            <div>
                              <Label className="text-xs text-gray-600 mb-1">MLC ETA (days)</Label>
                              <div className="h-8 px-2 border rounded-md bg-white flex items-center">
                                <span className="text-sm text-gray-700">
                                  {row.mlc_eta_days ? `${row.mlc_eta_days} days` : '-'}
                                </span>
                              </div>
                            </div>
                            {/* SASE Required */}
                            <div>
                              <Label className="text-xs text-gray-600 mb-1">SASE Required</Label>
                              <div className="h-8 px-2 border rounded-md bg-white flex items-center">
                                <span className={`text-sm ${row.sase_required ? 'text-orange-600 font-medium' : 'text-gray-700'}`}>
                                  {row.sase_required ? 'Yes' : 'No'}
                                </span>
                              </div>
                            </div>
                            {/* Info Obtained Back - full width */}
                            <div className="col-span-2 md:col-span-4">
                              <Label className="text-xs text-gray-600 mb-1">Info Obtained Back</Label>
                              <div className="h-8 px-2 border rounded-md bg-white flex items-center">
                                <span className="text-sm text-gray-700">
                                  {row.info_obtained_back ? row.info_obtained_back.charAt(0).toUpperCase() + row.info_obtained_back.slice(1) : '-'}
                                </span>
                              </div>
                            </div>
                          </div>
                          <p className="text-xs text-gray-500 mt-2 italic">
                            These fields are auto-populated from the jurisdiction directory and are read-only.
                          </p>
                        </div>
                      </>
                    ) : null}
                  </div>
                </div>
                
                {/* ============================================================================
                    SECTION 2: CURRENT TAX SUMMARY (PURPLE) - COMES SECOND
                    Tax details section - Jurisdiction name is auto-synced from above
                    ONLY VISIBLE WHEN JURISDICTION TYPE IS SELECTED
                    HIDDEN FOR EXTERNAL PORTAL / MLS CLIENTS - Tax details managed externally
                    ============================================================================ */}
                {!hideTaxDetails && row.jurisdiction_type && (
                  <>
                <div className="bg-gradient-to-r from-[#4c2aff] to-[#784bff] px-4 py-3">
                  <h4 className="text-base font-bold text-white">
                    Current Tax Summary – Jurisdiction #{index + 1}
                    {row.jurisdiction && ` – ${row.jurisdiction}`}
                  </h4>
                </div>
                
                {/* Current Tax Summary Body */}
                <div className="p-4 space-y-4">
                  {/* Jurisdiction Field - Auto-synced from above, but editable for manual override */}
                  <div>
                    <Label htmlFor={`jurisdiction_${index}`} className="text-sm font-medium text-gray-700 mb-1">
                      Jurisdiction Name <span className="text-red-500">*</span>
                      {row.jurisdiction_type && (
                        <span className="ml-2 text-xs text-green-600 bg-green-100 px-2 py-0.5 rounded">
                          ✓ Auto-filled from {row.jurisdiction_type}
                        </span>
                      )}
                    </Label>
                    <Input
                      id={`jurisdiction_${index}`}
                      value={row.jurisdiction}
                      onChange={(e) => handleJurisdictionChange(index, e.target.value)}
                      className="w-full"
                      placeholder="County/City (e.g., Delaware County, PA)"
                      disabled={readOnly}
                    />
                  </div>

                  {/* Line 1: Tax Year, Installment, Pay Status */}
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div>
                      <Label htmlFor={`tax_year_${index}`} className="text-sm font-medium text-gray-700 mb-1">
                        Tax Year <span className="text-red-500">*</span>
                      </Label>
                      <Input
                        id={`tax_year_${index}`}
                        value={row.tax_year}
                        onChange={(e) => handleArrayChange('current_tax_summary', index, 'tax_year', e.target.value)}
                        className="w-full"
                        placeholder="2024"
                        disabled={readOnly}
                      />
                    </div>
                    <div>
                      <Label htmlFor={`installment_${index}`} className="text-sm font-medium text-gray-700 mb-1">
                        Installment <span className="text-red-500">*</span>
                      </Label>
                      <select
                        id={`installment_${index}`}
                        value={row.installment}
                        onChange={(e) => handleArrayChange('current_tax_summary', index, 'installment', e.target.value)}
                        className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        disabled={readOnly}
                      >
                        <option value="">Select</option>
                        <option value="Full">Full</option>
                        <option value="1st">1st</option>
                        <option value="2nd">2nd</option>
                        <option value="3rd">3rd</option>
                        <option value="4th">4th</option>
                      </select>
                    </div>
                    <div>
                      <Label htmlFor={`pay_status_${index}`} className="text-sm font-medium text-gray-700 mb-1">
                        Pay Status <span className="text-red-500">*</span>
                      </Label>
                      <select
                        id={`pay_status_${index}`}
                        value={row.pay_status}
                        onChange={(e) => handlePayStatusChange(index, e.target.value)}
                        onBlur={() => validateAllDateFieldsForRow(index)}
                        className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        disabled={readOnly}
                      >
                        <option value="Paid">Paid</option>
                        <option value="Due">Due</option>
                        <option value="Partially Paid">Partially Paid</option>
                        <option value="Partially Dlq">Partially Dlq</option>
                        <option value="Delinquent">Delinquent</option>
                      </select>
                    </div>
                  </div>

                  {/* Line 2: Mailing/Issue Date, Due Date, Paid Date */}
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div>
                      <Label htmlFor={`mailing_date_${index}`} className="text-sm font-medium text-gray-700 mb-1">
                        Mailing / Issue Date <span className="text-red-500">*</span>
                      </Label>
                      <DatePickerInput
                        id={`mailing_date_${index}`}
                        value={row.mailing_issue_date}
                        onChange={(e) => handleArrayChange('current_tax_summary', index, 'mailing_issue_date', e.target.value)}
                        onBlur={() => validateDateField(index, 'mailing_issue_date')}
                        className={`w-full ${validationErrors[`tax_summary_${index}_mailing_issue_date`] ? 'border-red-500' : ''}`}
                        disabled={readOnly}
                      />
                      {validationErrors[`tax_summary_${index}_mailing_issue_date`] && (
                        <p className="mt-1 text-sm text-red-600">{validationErrors[`tax_summary_${index}_mailing_issue_date`]}</p>
                      )}
                    </div>
                    <div>
                      <Label htmlFor={`due_date_${index}`} className="text-sm font-medium text-gray-700 mb-1">
                        Due Date <span className="text-red-500">*</span>
                      </Label>
                      <DatePickerInput
                        id={`due_date_${index}`}
                        value={row.due_date}
                        onChange={(e) => handleDueDateChange(index, e.target.value)}
                        onBlur={() => validateDateField(index, 'due_date')}
                        className={`w-full ${validationErrors[`tax_summary_${index}_due_date`] ? 'border-red-500' : ''}`}
                        disabled={readOnly}
                      />
                      {validationErrors[`tax_summary_${index}_due_date`] && (
                        <p className="mt-1 text-sm text-red-600">{validationErrors[`tax_summary_${index}_due_date`]}</p>
                      )}
                    </div>
                    <div>
                      <Label htmlFor={`paid_date_${index}`} className="text-sm font-medium text-gray-700 mb-1">
                        Paid Date {getFieldEnabledState(row.pay_status).paidDate && (row.pay_status === 'Paid' || row.pay_status === 'PAID' || row.pay_status === 'paid' || row.pay_status?.includes('Partially')) && <span className="text-red-500">*</span>}
                      </Label>
                      <DatePickerInput
                        id={`paid_date_${index}`}
                        value={row.paid_date}
                        onChange={(e) => handleArrayChange('current_tax_summary', index, 'paid_date', e.target.value)}
                        onBlur={() => validateDateField(index, 'paid_date')}
                        className={`w-full ${!getFieldEnabledState(row.pay_status).paidDate ? 'bg-gray-100 cursor-not-allowed' : ''} ${validationErrors[`tax_summary_${index}_paid_date`] ? 'border-red-500' : ''}`}
                        disabled={readOnly || !getFieldEnabledState(row.pay_status).paidDate}
                      />
                      {validationErrors[`tax_summary_${index}_paid_date`] && (
                        <p className="mt-1 text-sm text-red-600">{validationErrors[`tax_summary_${index}_paid_date`]}</p>
                      )}
                    </div>
                  </div>

                  {/* Line 3: Base Tax, Amount(s)/Fees */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <div>
                      <Label htmlFor={`base_tax_${index}`} className="text-sm font-medium text-gray-700 mb-1">
                        Base Tax <span className="text-red-500">*</span>
                      </Label>
                      <div className="relative">
                        <span className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500">$</span>
                        <Input
                          id={`base_tax_${index}`}
                          type="text"
                          inputMode="decimal"
                          value={row.base_tax}
                          onChange={(e) => handleArrayChange('current_tax_summary', index, 'base_tax', handleCurrencyChange(e.target.value))}
                          onBlur={(e) => {
                            const formatted = handleCurrencyBlur(e.target.value);
                            if (formatted) {
                              handleArrayChange('current_tax_summary', index, 'base_tax', formatted);
                            }
                          }}
                          className="w-full pl-7"
                          placeholder="0.00"
                          disabled={readOnly}
                        />
                      </div>
                    </div>
                    <div>
                      <Label htmlFor={`amounts_fees_${index}`} className="text-sm font-medium text-gray-700 mb-1">
                        Tax Amount <span className="text-red-500">*</span>
                      </Label>
                      <div className="relative">
                        <span className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500">$</span>
                        <Input
                          id={`amounts_fees_${index}`}
                          type="text"
                          inputMode="decimal"
                          value={row.amounts_fees}
                          onChange={(e) => handleArrayChange('current_tax_summary', index, 'amounts_fees', handleCurrencyChange(e.target.value))}
                          onBlur={(e) => {
                            const formatted = handleCurrencyBlur(e.target.value);
                            if (formatted !== e.target.value) {
                              handleArrayChange('current_tax_summary', index, 'amounts_fees', formatted);
                            }
                          }}
                          className={`w-full pl-7 ${validationErrors[`tax_summary_${index}_amounts_fees`] ? 'border-red-500' : ''}`}
                          placeholder="0.00"
                          disabled={readOnly}
                        />
                      </div>
                      {validationErrors[`tax_summary_${index}_amounts_fees`] && (
                        <p className="text-red-500 text-xs mt-1">{validationErrors[`tax_summary_${index}_amounts_fees`]}</p>
                      )}
                    </div>
                    <div>
                      <Label htmlFor={`balance_amount_${index}`} className="text-sm font-medium text-gray-700 mb-1">
                        Balance Amount {getFieldEnabledState(row.pay_status).balanceAmount && <span className="text-red-500">*</span>}
                      </Label>
                      <div className="relative">
                        <span className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500">$</span>
                        <Input
                          id={`balance_amount_${index}`}
                          type="text"
                          inputMode="decimal"
                          value={row.balance_amount}
                          onChange={(e) => handleArrayChange('current_tax_summary', index, 'balance_amount', handleCurrencyChange(e.target.value))}
                          onBlur={(e) => {
                            const formatted = handleCurrencyBlur(e.target.value);
                            if (formatted !== e.target.value) {
                              handleArrayChange('current_tax_summary', index, 'balance_amount', formatted);
                            }
                          }}
                          className={`w-full pl-7 ${!getFieldEnabledState(row.pay_status).balanceAmount ? 'bg-gray-100 cursor-not-allowed' : ''} ${validationErrors[`tax_summary_${index}_balance_amount`] ? 'border-red-500' : ''}`}
                          placeholder="0.00"
                          disabled={readOnly || !getFieldEnabledState(row.pay_status).balanceAmount}
                        />
                      </div>
                      {validationErrors[`tax_summary_${index}_balance_amount`] && (
                        <p className="mt-1 text-sm text-red-600">{validationErrors[`tax_summary_${index}_balance_amount`]}</p>
                      )}
                    </div>
                  </div>

                  {/* DLQ Date - Full Width */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <div>
                      <Label htmlFor={`dlq_date_${index}`} className="text-sm font-medium text-gray-700 mb-1">
                        Delinquency Date {getFieldEnabledState(row.pay_status).delinquencyDate && <span className="text-red-500">*</span>}
                      </Label>
                      <DatePickerInput
                        id={`dlq_date_${index}`}
                        value={row.dlq_date}
                        onChange={(e) => handleArrayChange('current_tax_summary', index, 'dlq_date', e.target.value)}
                        onBlur={() => validateDateField(index, 'dlq_date')}
                        className={`w-full ${!getFieldEnabledState(row.pay_status).delinquencyDate ? 'bg-gray-100 cursor-not-allowed' : ''} ${validationErrors[`tax_summary_${index}_dlq_date`] ? 'border-red-500' : ''}`}
                        disabled={readOnly || !getFieldEnabledState(row.pay_status).delinquencyDate}
                      />
                      {validationErrors[`tax_summary_${index}_dlq_date`] && (
                        <p className="mt-1 text-sm text-red-600">{validationErrors[`tax_summary_${index}_dlq_date`]}</p>
                      )}
                      {!getFieldEnabledState(row.pay_status).delinquencyDate && (row.pay_status?.toUpperCase() === 'DELINQUENT' || row.pay_status?.toUpperCase()?.includes('PARTIALLY DLQ') || row.pay_status?.toUpperCase()?.includes('PARTIALLY DELINQUENT')) && (
                        <p className="text-xs text-gray-500 mt-1 italic">Not applicable (status already indicates delinquency)</p>
                      )}
                    </div>
                  </div>
                </div>
                
                {/* ============================================================================
                    SECTION 3: DELINQUENT TAXES (ORANGE) - Only shows when Pay Status = Delinquent
                    Each jurisdiction has its own independent delinquent taxes section
                    ALSO REQUIRES JURISDICTION TYPE TO BE SELECTED
                    ============================================================================ */}
                {getFieldEnabledState(row.pay_status).delinquentFields && (
                  <div className="border-t-2 border-orange-300">
                    <div className="bg-gradient-to-r from-orange-500 to-orange-600 px-4 py-3">
                      <h4 className="text-base font-bold text-white">
                        Delinquent Taxes – Jurisdiction #{index + 1}
                        {row.jurisdiction && ` – ${row.jurisdiction}`}
                        <span className="ml-2 text-xs font-normal bg-white/20 px-2 py-0.5 rounded">
                          Auto-enabled (Pay Status: {row.pay_status})
                        </span>
                      </h4>
                    </div>
                    
                    <div className="p-4 bg-orange-50/50 space-y-4">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        {/* Payoff Amount */}
                        <div>
                          <Label htmlFor={`dlq_payoff_amount_${index}`} className="text-sm font-medium text-gray-700 mb-1">
                            Payoff Amount <span className="text-red-500">*</span>
                            {row.dlq_payoff_manual && (
                              <span className="ml-1 text-xs text-amber-600 font-normal">(manual)</span>
                            )}
                          </Label>
                          <div className="relative">
                            <span className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500">$</span>
                            <Input
                              id={`dlq_payoff_amount_${index}`}
                              data-testid={`dlq-payoff-amount-${index}`}
                              type="text"
                              inputMode="decimal"
                              value={row.dlq_payoff_amount !== null && row.dlq_payoff_amount !== undefined 
                                ? (typeof row.dlq_payoff_amount === 'number' 
                                  ? row.dlq_payoff_amount.toFixed(2) 
                                  : row.dlq_payoff_amount) 
                                : ''}
                              onChange={(e) => {
                                const cleaned = handleCurrencyChange(e.target.value);
                                const val = cleaned === '' ? null : parseFloat(cleaned);
                                // Mark as manually overridden and set value in one update
                                setCertificateData(prev => ({
                                  ...prev,
                                  current_tax_summary: prev.current_tax_summary.map((item, i) =>
                                    i === index ? { ...item, dlq_payoff_amount: val, dlq_payoff_manual: true } : item
                                  )
                                }));
                              }}
                              onBlur={(e) => {
                                const formatted = handleCurrencyBlur(e.target.value);
                                if (formatted !== '' && formatted !== e.target.value) {
                                  const val = parseFloat(formatted);
                                  setCertificateData(prev => ({
                                    ...prev,
                                    current_tax_summary: prev.current_tax_summary.map((item, i) =>
                                      i === index ? { ...item, dlq_payoff_amount: isNaN(val) ? null : val, dlq_payoff_manual: true } : item
                                    )
                                  }));
                                }
                              }}
                              className={`pl-7 ${validationErrors[`tax_summary_${index}_dlq_payoff_amount`] ? 'border-red-500' : ''}`}
                              placeholder="0.00"
                              disabled={readOnly}
                            />
                          </div>
                          {validationErrors[`tax_summary_${index}_dlq_payoff_amount`] && (
                            <p className="mt-1 text-sm text-red-600">{validationErrors[`tax_summary_${index}_dlq_payoff_amount`]}</p>
                          )}
                          {row.dlq_payoff_manual ? (
                            <button
                              type="button"
                              data-testid={`dlq-payoff-reset-${index}`}
                              className="mt-1 text-xs text-blue-600 hover:text-blue-800 underline"
                              onClick={() => {
                                const baseTax = parseFloat(row.base_tax) || 0;
                                const fees = parseFloat(row.amounts_fees) || 0;
                                const calc = baseTax + fees;
                                setCertificateData(prev => ({
                                  ...prev,
                                  current_tax_summary: prev.current_tax_summary.map((item, i) =>
                                    i === index ? { ...item, dlq_payoff_amount: calc > 0 ? calc : null, dlq_payoff_manual: false } : item
                                  )
                                }));
                              }}
                              disabled={readOnly}
                            >
                              Use calculated amount (${((parseFloat(row.base_tax) || 0) + (parseFloat(row.amounts_fees) || 0)).toFixed(2)})
                            </button>
                          ) : (
                            <p className="mt-1 text-xs text-gray-500">
                              Auto-synced from Base Tax + Tax Amount
                            </p>
                          )}
                        </div>
                        
                        {/* Good Thru Date */}
                        <div>
                          <Label htmlFor={`dlq_good_thru_date_${index}`} className="text-sm font-medium text-gray-700 mb-1">
                            Good Thru Date <span className="text-red-500">*</span>
                          </Label>
                          <DatePickerInput
                            id={`dlq_good_thru_date_${index}`}
                            value={row.dlq_good_thru_date || ''}
                            onChange={(e) => {
                              handleArrayChange('current_tax_summary', index, 'dlq_good_thru_date', e.target.value);
                            }}
                            className={validationErrors[`tax_summary_${index}_dlq_good_thru_date`] ? 'border-red-500' : ''}
                            minDate={new Date(Date.now() + 86400000)}
                            disabled={readOnly}
                          />
                          {validationErrors[`tax_summary_${index}_dlq_good_thru_date`] && (
                            <p className="mt-1 text-sm text-red-600">{validationErrors[`tax_summary_${index}_dlq_good_thru_date`]}</p>
                          )}
                        </div>
                      </div>
                      
                      {/* Tax Certification Notes for this Jurisdiction */}
                      <div>
                        <Label htmlFor={`delinquent_taxes_notes_${index}`} className="text-sm font-medium text-gray-700 mb-1">
                          Tax Certification Notes (Jurisdiction #{index + 1})
                        </Label>
                        <textarea
                          id={`delinquent_taxes_notes_${index}`}
                          value={row.delinquent_taxes_notes || ''}
                          onChange={(e) => handleArrayChange('current_tax_summary', index, 'delinquent_taxes_notes', e.target.value)}
                          className="w-full min-h-[100px] p-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-orange-500 focus:border-orange-500 resize-y"
                          placeholder="Delinquent tax certification notes for this jurisdiction..."
                          disabled={readOnly}
                          rows={3}
                        />
                      </div>
                    </div>
                  </div>
                )}
                  </>
                )}
                  </>
                )}
                  </>
                )}
              </div>
            );
            })}
          </div>
          
          {/* Add Jurisdiction Button - Positioned below jurisdiction cards */}
          <div className="flex justify-start mt-4 mb-4">
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => {
                // Create a completely NEW, independent jurisdiction entity
                // with its own Taxing Authority / Remittance data (all blank)
                const newJurisdictionEntity = {
                  // Tax Summary fields (blank)
                  jurisdiction: '',
                  tax_year: '',
                  mailing_issue_date: '',
                  installment: '',
                  base_tax: '',
                  due_date: '',
                  amounts_fees: '',
                  pay_status: 'Due',
                  paid_date: '',
                  dlq_date: '',
                  balance_amount: '',
                  
                  // Taxing Authority / Remittance fields (blank - independent)
                  jurisdiction_type: null,
                  jurisdiction_id: null,
                  make_checks_payable_to: '',
                  tax_collector_email: '',
                  tax_collector_website: '',
                  assessor_website: '',
                  tax_collector_working_hours: '',
                  tax_collector_phone: '',
                  tax_collector_phone_ext: '',
                  mlc_charges: null,
                  mlc_24_month_charges: null,
                  mlc_eta_days: null,
                  sase_required: false,
                  info_obtained_back: null,
                  changed_by_user: false,
                  
                  // Delinquent Taxes fields (blank - per-jurisdiction)
                  dlq_payoff_amount: null,
                  dlq_good_thru_date: null,
                  delinquent_taxes_notes: ''
                };
                
                // Get the index of the new jurisdiction (current length becomes the new index)
                const newIndex = certificateData.current_tax_summary.length;
                
                addRow('current_tax_summary', newJurisdictionEntity);
                
                // Auto-expand the new jurisdiction and scroll to it
                expandAndScrollToJurisdiction(newIndex);
                
                toast.info('New jurisdiction added. Please fill in the Taxing Authority / Remittance details for this jurisdiction.');
              }}
              className="flex items-center gap-1"
              disabled={readOnly}
              data-testid="add-jurisdiction-btn"
            >
              <Plus className="w-4 h-4" /> Add Jurisdiction
            </Button>
          </div>
            </>
          )}
          </div>
        ) : null}
        
        {/* Else branch - handled by JurisdictionManager, no gating needed */}


        {/* Action Buttons */}
        <div className="flex items-center justify-between pt-4">
          <div className="flex items-center gap-4">
            {/* Auto-save status indicator */}
            {isSaving ? (
              <span className="tcp-save-chip tcp-fade-in text-blue-700 bg-blue-50 border border-blue-100" data-testid="tax-cert-autosave-chip">
                <div className="w-3 h-3 border-2 border-blue-600 border-t-transparent rounded-full animate-spin" />
                Saving...
              </span>
            ) : hasUnsavedChanges ? (
              <span className="tcp-save-chip tcp-fade-in text-orange-700 bg-orange-50 border border-orange-100" data-testid="tax-cert-autosave-chip">
                <div className="w-2 h-2 bg-orange-500 rounded-full" />
                Unsaved changes
              </span>
            ) : lastSaved ? (
              <span className="tcp-save-chip tcp-fade-in text-emerald-700 bg-emerald-50 border border-emerald-100" data-testid="tax-cert-autosave-chip">
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
                All changes saved
              </span>
            ) : null}
          </div>
          
          {/* Spacer - removed manual Save button, auto-save handles everything */}
          <div />
        </div>
        </div>{/* end tax-search-workspace (MLS-DUAL-001 UI refinement) */}
      </div>
      
      {/* Confirmation Modal for Tax Collection Schedule Changes */}
      {showConfirmModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-2xl p-6 max-w-md w-full mx-4" style={{ boxShadow: '0 8px 24px rgba(15,23,42,0.06)' }}>
            <h3 className="text-lg font-bold text-slate-900 mb-4">Confirm Schedule Change</h3>
            <p className="text-slate-600 mb-6">
              Changing the tax collection schedule will regenerate the Current Tax Summary rows. 
              You may lose data in extra rows. Continue?
            </p>
            <div className="flex gap-3 justify-end">
              <Button
                variant="outline"
                onClick={() => {
                  setShowConfirmModal(false);
                  setPendingFrequency('');
                }}
                className="text-slate-600 border-slate-200 hover:bg-slate-50"
              >
                Cancel
              </Button>
              <Button
                onClick={confirmRegeneration}
                className="bg-blue-600 hover:bg-blue-700 text-white"
              >
                Regenerate
              </Button>
            </div>
          </div>
        </div>
      )}
      
      {/* Scroll to Top Button for Fulfillment page — positioned above floating Add Parcel button */}
      <ScrollToTopButton className={parcels.length > 0 && parcels.some(p => p.jurisdictions?.some(j => j.jurisdiction_type)) && !readOnly ? 'bottom-[4.75rem]' : undefined} />
      
      {/* Sticky Floating Add Parcel Button — always accessible without scrolling */}
      {parcels.length > 0 && parcels.some(p => p.jurisdictions?.some(j => j.jurisdiction_type)) && !readOnly && (
        <button
          onClick={handleAddParcelWithCopy}
          disabled={parcels.length >= 10}
          className={`fixed bottom-6 right-6 z-40 flex items-center gap-2 px-4 py-2.5 rounded-full shadow-lg transition-all duration-200 text-sm font-medium ${
            parcels.length >= 10
              ? 'bg-slate-200 text-slate-400 cursor-not-allowed'
              : 'bg-[#4c2aff] hover:bg-[#3d21cc] text-white hover:shadow-xl hover:scale-105 active:scale-95'
          }`}
          title={
            parcels.length >= 10 ? 'Maximum 10 parcels reached' : `Add Parcel ${parcels.length + 1} with shared jurisdiction setup`
          }
          data-testid="floating-add-parcel-btn"
        >
          <Plus className="w-4 h-4" />
          Add Parcel
        </button>
      )}
      </div>
      </>
      )}
    </div>
  );
};


// ============================================
// EXPORTS
// ============================================
// PERF: Wrap in React.memo to prevent re-renders from unrelated parent state changes
// (e.g., review modal checkbox, redirect countdown, etc.)
const MemoizedTaxCertificatePanel = React.memo(TaxCertificatePanel);

export { MemoizedTaxCertificatePanel as TaxCertificatePanel, ReceivedStatusToggles, ExemptionPillSelector, PrefillIndicator };
export default MemoizedTaxCertificatePanel;
